// TruePour — React Frontend (client/src/App.jsx)
// All AI calls proxy to /api/* on the Express backend
// No API keys in this file

import { useState, useRef, useEffect, useCallback, useId } from "react";
import { flushSync } from "react-dom";
import html2canvas from "html2canvas";
import { WINE_DATABASE as WINE_LABELS } from './wineDatabase';
import { supabase } from './supabaseClient';
import { REGION_TILES } from './regionTiles';
import { makeGenerationId, claimGeneration, buildProduceEntry, buildUpdateWines } from './lib/historyGeneration';
import { decideRatingToast, shouldShowProfileBadge, shouldShowCauseBannerOnEntry } from './step7Pure';

// Every valid value `screen` can hold (one render branch per value). Used as a
// render safety net (unknown screen → recovery view, never blank white) and to
// guard navigateBack from popping a stale/orphan screen. Keep in sync when a
// new screen branch is added.
const KNOWN_SCREENS = new Set([
  'home', 'meal', 'quiz', 'archetype', 'auth', 'resetRequest', 'setNewPassword',
  'results', 'activity', 'cellar', 'profile',
  'restauranttype', 'winelist', 'winelistscan', 'winescan',
  'storescan',
  'homepath', 'homespecific', 'homefew', 'homebottles', 'homescan',
  'homebottlematch', 'homestyleguide',
]);

function fireEvent(eventName, params) {
  try {
    if (window.gtag) {
      window.gtag('event', eventName, params || {});
      console.log('[TruePour] GA event fired:', eventName, params);
    } else {
      console.warn('[TruePour] gtag not available for event:', eventName);
    }
  } catch(e) {
    console.error('[TruePour] GA event error:', eventName, e);
  }
}

// ─── TOKENS ──────────────────────────────────────────────────────────────────
const C = {
  burgundy:     "#911234",
  burgundyLight:"#B03050",
  burgundyBg:   "#F7EAEE",
  burgundyMid:  "#E3C5CC",
  cream:        "#FFFAF8",
  warmWhite:    "#FFF5F0",
  charcoal:     "#2D2D2D",
  charcoalMid:  "#5A5A5A",
  charcoalSoft: "#8A8A8A",
  divider:      "#F0E8E4",
  cardBg:       "#FFFFFF",
  navy:         "#1E3A5F",
  navyLight:    "#2A5080",
  navyBg:       "#EEF3FF",
  navyBorder:   "#C8D8F0",
  green:        "#2D7A4F",
  greenBg:      "#E8F5EE",
  error:        "#CC3333",
  errorBg:      "#FFF0F0",
  errorBorder:  "#FFB0B0",
  amber:        "#f57f17",
  amberBg:      "#fff8e1",
};

const F = {
  display: "'Outfit', sans-serif",
  body:    "'DM Sans', sans-serif",
};

// ─── PERSISTENCE ─────────────────────────────────────────────────────────────
const SK = "truepour_v1";
function loadData() { try { const s = localStorage.getItem(SK); return s ? JSON.parse(s) : null; } catch { return null; } }
function saveData(d) { try { localStorage.setItem(SK, JSON.stringify(d)); } catch {} }

// ─── DEFAULTS ─────────────────────────────────────────────────────────────────
const DEFAULT_SLIDERS = { sweetness: null, fruit: null, body: null, acidity: null, adventure: null };

const TASTE_QUESTIONS = [
  {
    id: "sweetness", axis: "Sweetness",
    question: "You're at a bakery. What are you most drawn to?",
    leftEmoji: "🥐", rightEmoji: "🍰",
    positions: [
      "Plain croissant",
      "Cheese danish",
      "Fruit tart",
      "Cinnamon roll",
      "Glazed donut",
      "The sweetest thing on the counter",
    ],
  },
  {
    id: "fruit", axis: "Fruit & Flavour",
    question: "Pick your ideal dessert.",
    leftEmoji: "🍋", rightEmoji: "🍫",
    positions: [
      "Lemon sorbet",
      "Fruit gelato",
      "Strawberry ice cream",
      "Vanilla custard",
      "Chocolate chip cookie",
      "Dark chocolate fudge",
    ],
  },
  {
    id: "body", axis: "Body & Richness",
    question: "Dream dinner tonight?",
    leftEmoji: "🐟", rightEmoji: "🥩",
    positions: [
      "Grilled fish or salad",
      "Chicken or veggie dish",
      "Pasta or risotto",
      "Burger or pulled pork",
      "Filet mignon",
      "Bone-in steak, rare",
    ],
  },
  {
    id: "acidity", axis: "Acidity Preference",
    question: "Salt & vinegar crisps — yay or nay?",
    leftEmoji: "😬", rightEmoji: "😌",
    positions: [
      "Plain only — no sharpness",
      "Almost no tang please",
      "Prefer mostly mild",
      "A little tang is nice",
      "Really enjoy the tang",
      "Love the zing, pass me the bag",
    ],
  },
  {
    id: "adventure", axis: "Discovery Appetite",
    question: "New restaurant — how do you order?",
    leftEmoji: "🗺️", rightEmoji: "🏠",
    positions: [
      "Most unusual thing on the menu",
      "Something I've never tried before",
      "Something interesting but familiar",
      "A reliable classic",
      "My usual order",
      "The thing I know I love",
    ],
  },
];

const SYSTEM_PROMPT = `You are TruePour, a knowledgeable best friend who happens to know everything about wine. You speak plainly, with warmth and a touch of wit — never with jargon or snobbery.

You recommend SPECIFIC bottles only — real producer names, real wine names, realistic recent vintages. Never just varietals.

AVAILABILITY RULES (non-negotiable):
- Only recommend wines with national or wide US distribution — minimum ~10,000 cases annually
- Every recommendation must be findable on Wine.com, Total Wine, or Vivino marketplace
- Never recommend: micro-productions, winery-direct-only labels, mailing list wines, or allocations
- If recommending from a scanned list or shelf, ONLY recommend wines explicitly visible — no substitutions

RECOMMENDATION RULES:
- Specific producer AND wine name (e.g. "Caymus Vineyards Napa Valley Cabernet Sauvignon 2022")
- Match precisely to taste profile (1–5 axes), budget, occasion, and wine color if specified
- Treat wines the user already loves as your primary calibration anchor
- Explain HOW their answers influenced each pick — in plain, conversational English. No wine jargon.
- For "Surprise me" color: choose the most unexpected but perfectly matched option
- Confidence scores: be honest, don't pad above 95 unless the match is genuinely exceptional
- search_term: exactly what someone would type into Wine.com — producer + wine name, no vintage

Tone: Direct, encouraging, witty. Like a friend who says "trust me on this one" and means it.

Respond ONLY with a valid JSON array of exactly 3 objects. No markdown, no preamble.

Each object:
{
  "producer": string,
  "wine": string,
  "vintage": string,
  "varietal": string,
  "region": string,
  "country": string,
  "price_low": number,
  "price_high": number,
  "confidence": number 75-99,
  "description": "1-2 sentences, witty and specific, why this wine is a great pick",
  "pairing_reason": "1 sentence, why it matches this specific meal or occasion",
  "profile_match": "1 sentence, how their taste answers pointed to this wine — in plain language",
  "style_tags": ["3-4 short descriptors"],
  "search_term": "best retail search string"
}`;

// ─── EXTENDED TASTE QUESTIONS (Q6–Q10) ───────────────────────────────────────
const EXTENDED_TASTE_QUESTIONS = [
  {
    id:"color", axis:"Color Preference",
    question:"When you open a bottle of wine, what are you most likely reaching for?",
    leftEmoji:"🍷", rightEmoji:"🥂",
    positions:[
      "Always red, it's my go-to",
      "Usually red, open to white or rosé",
      "I mix it up equally",
      "Usually white, rosé, or sparkling",
      "Almost always white or sparkling",
    ],
  },
  {
    id:"tannin", axis:"Tannin Preference",
    question:"How do you take your coffee?",
    leftEmoji:"☕", rightEmoji:"🖤",
    positions:[
      "I don't drink coffee — too bitter",
      "Lots of milk and sugar",
      "A little milk and sugar",
      "Milk, no sugar",
      "Black with a little sugar",
      "Black only — stronger the better",
    ],
  },
  {
    id:"oak", axis:"Oak Preference",
    question:"You're choosing a cheese board. What sounds better?",
    leftEmoji:"🧀", rightEmoji:"🏔️",
    positions:[
      "Fresh mozzarella",
      "Young brie",
      "Mild cheddar",
      "Aged cheddar",
      "Aged parmesan",
      "Blue cheese",
    ],
  },
  {
    id:"alcohol", axis:"Alcohol & Intensity",
    question:"Your ideal end to a long week",
    leftEmoji:"🥃", rightEmoji:"🫧",
    positions:[
      "A hard seltzer or light beer",
      "A gin & tonic or spritz",
      "A margarita or mojito",
      "A negroni or old fashioned",
      "Whisky or tequila, neat",
    ],
  },
  {
    id:"aromatics", axis:"Aromatic Profile",
    question:"You're choosing a candle for your home. What appeals more?",
    leftEmoji:"🕯️", rightEmoji:"🌸",
    positions:[
      "Unscented — keep it clean",
      "Very light, barely there",
      "Subtle and fresh",
      "Noticeably fragrant",
      "Rich and aromatic",
      "The more fragrant the better",
    ],
  },
  {
    id:"complexity", axis:"Complexity Appetite",
    question:"Someone hands you a mystery wine and says 'try to guess what's in it.' How do you feel?",
    subheading:"No right answer — just go with your gut lean.",
    leftEmoji:"🙈", rightEmoji:"🧩",
    positions:[
      "I'd rather just enjoy it",
      "Not really my thing",
      "Kind of fun, I'd give it a go",
      "I enjoy the challenge",
      "I love this kind of thing",
      "This is my favourite game",
    ],
  },
];

// ─── ALL TASTE QUESTIONS (unified 10-question flow) ───────────────────────────
const ALL_TASTE_QUESTIONS = [...TASTE_QUESTIONS, ...EXTENDED_TASTE_QUESTIONS];

// ─── ARCHETYPES ───────────────────────────────────────────────────────────────
const ARCHETYPES = {
  CRISP_PURIST: {
    emoji:"🌊", name:"The Crisp Purist", tagline:"High acidity, bone dry, nothing in the way.",
    description:"You're drawn to wines with high acidity and a clean, unadorned expression of fruit — the kind of wines that make your mouth water and leave you reaching for another sip. Residual sugar feels cloying to you. Oak feels like it's getting in the way. What you're really looking for is tension — that electric, almost saline quality you find in wines grown in cool climates and chalky soils. In sommelier terms, you have a classically European palate: you want the wine to taste like somewhere specific, not like something.",
    regions:"High-versatility whites: Sauvignon Blanc from the Loire, Chablis, northern Italian whites, Vinho Verde, Albariño, cool-climate Riesling, dry Alsatian Riesling, dry Provence rosé",
    shareText:"I'm a Crisp Purist — my palate craves energy and precision over richness. Discover your wine personality at truepour.ai",
    chips:["high acidity","unoaked","bone dry"],
  },
  BOLD_TRADITIONALIST: {
    emoji:"🔥", name:"The Bold Traditionalist", tagline:"Full body, serious tannin, built to last.",
    description:"High tannins don't intimidate you — they interest you. You're drawn to wines with backbone, where the grip of the tannin and the depth of the fruit are in conversation with each other over the course of an evening. You appreciate what time does to wine: how it softens the edges, deepens the complexity, and turns something powerful into something profound. In sommelier terms, you have the palate patience for great wine — you're not looking for immediate gratification. You're looking for a wine that reveals itself slowly.",
    regions:"Nebbiolo, Sangiovese, Cabernet Sauvignon from structured appellations, Tempranillo Gran Reserva, Syrah from the northern Rhône, Barossa Valley Shiraz, Douro reds, old-vine Zinfandel, age-worthy Napa Cabernet",
    shareText:"I'm a Bold Traditionalist — my palate responds to structure, tannin, and the reward of age. Discover your wine personality at truepour.ai",
    chips:["full body","high tannin","age-worthy"],
  },
  BOLD_MODERNIST: {
    emoji:"🍷", name:"The Bold Modernist", tagline:"Full-bodied, oak-forward, and unapologetically New World.",
    description:"You're drawn to wines with presence, generosity, and power — wines that fill the mouth with rich fruit, wrap it in warm oak, and leave a finish that lingers. You're not looking for restraint or austerity. You want a wine that announces itself, delivers immediately, and satisfies completely. Napa Cabernet, Sonoma Syrah, Paso Robles Cab, Washington State Cabernet, Barossa Shiraz. These are your wines. In sommelier terms, you have a full-body, high-oak-tolerance palate with a strong preference for fruit richness over structure — the combination that defines the great New World red tradition.",
    regions:"Napa Valley Cabernet Sauvignon, Sonoma Syrah and Zinfandel, Paso Robles Cabernet, Washington State Cabernet, Barossa Valley Shiraz, Mendoza Malbec at the structured end, Duckhorn, Jordan, Stag's Leap, Ridge, Caymus, Silver Oak — wines built for the moment",
    shareText:"I'm a Bold Modernist — my palate loves full-bodied, oak-forward New World reds that deliver power and presence from the first sip. Discover your wine personality at truepour.ai",
    chips:["full body","oak-forward","New World"],
    colorVariants: {
      red: {
        description: "You're drawn to wines with presence, generosity, and power — rich fruit, warm oak, and a finish that lingers. Napa Cabernet, Sonoma Syrah, Barossa Shiraz. Wines that announce themselves and satisfy completely. You know what you like and you've found it.",
        regions: "Napa Valley Cabernet Sauvignon, Sonoma Syrah, Paso Robles Cab, Washington State Cabernet, Barossa Valley Shiraz — powerful, generous, unmistakably New World",
        chips: ["full body", "oak-forward", "New World"],
      },
      neutral: {
        description: "You're drawn to wines with presence and power — rich fruit, warm oak, a finish that means business. Whether red or white, you want a wine that delivers immediately and satisfies completely. You've found your style. You drink it well.",
        regions: "Napa Valley Cabernet, Sonoma Syrah, oaked California Chardonnay, Paso Robles Cab, Barossa Shiraz — powerful, generous, New World",
        chips: ["full body", "oak-forward", "New World"],
      },
    },
  },
  VELVET_TOUCH: {
    emoji:"🍒", name:"The Velvet Touch", tagline:"Silky, fruit-forward, and immediately satisfying.",
    description:"You're drawn to wines that feel generous from the very first sip — where the fruit is ripe, the tannins are soft, and the texture coats your mouth like velvet. This isn't a lack of sophistication. It's a clear, confident read of what your palate actually responds to: wines that deliver pleasure immediately, without demanding patience or decoding. In sommelier terms, you have a low tannin threshold and a strong fruit-forward preference — the combination that points toward the world's most universally loved styles. Malbec, Pinot Noir, plush Merlot, smooth Cabernet. Wines that make people happy.",
    regions:"Malbec from Mendoza, Pinot Noir from California and Oregon, fruit-forward Merlot, smooth Cabernet Sauvignon, Carménère, approachable Grenache, Côtes du Rhône, soft Italian reds",
    shareText:"I'm a Velvet Touch — my palate loves silky, fruit-forward wines that deliver pleasure from the first sip. Discover your wine personality at truepour.ai",
    chips:["silky","fruit-forward","low tannin"],
  },
  BRIGHT_ADVENTURER: {
    emoji:"⚡", name:"The Bright Adventurer", tagline:"High acid, curious palate, always off the beaten path.",
    description:"You have a high tolerance for the unexpected in the glass — unusual aromatics, unfamiliar grapes, wines that taste like somewhere specific and make you want to learn more. Your acidity preference is high, which means you're naturally drawn to wines that feel electric and alive rather than soft and comfortable. In sommelier terms, you have an exploratory palate with a strong acid bias — the combination that leads people toward the world's more interesting and less-celebrated bottles.",
    regions:"Grüner Veltliner, Assyrtiko, Txakoli, Cru Beaujolais, Picpoul de Pinet, Aligoté, Jura whites, orange wines, Torrontés, German Spätburgunder, Crémant d'Alsace, Alsatian Riesling",
    shareText:"I'm a Bright Adventurer — my palate is driven by curiosity, acidity, and wines I've never tried before. Discover your wine personality at truepour.ai",
    chips:["high acidity","adventurous","aromatic"],
    colorVariants: {
      red: {
        description: "You have a high tolerance for the unexpected — unusual grapes, unfamiliar regions, wines that taste like somewhere specific. Your acidity preference is high, which means you're naturally drawn to wines that feel electric and alive. You're the person who orders the thing nobody else at the table has heard of, and you're usually right.",
        regions: "Cru Beaujolais, Mencía from Ribeira Sacra, Nerello Mascalese from Etna, natural Grenache, Blaufränkisch, Gamay, Txakoli — electric, site-specific, off the beaten path",
        chips: ["high acidity", "adventurous", "discovery-driven"],
      },
      white: {
        description: "You have a high tolerance for the unexpected — unusual grapes, unfamiliar regions, wines that crackle with energy and minerality. Your acidity preference is high, which pulls you toward the world's most electric whites: the ones that taste like somewhere specific and make you want to learn more. You're the person who orders the thing nobody else at the table has heard of, and you're usually right.",
        regions: "Grüner Veltliner, Assyrtiko from Santorini, Txakoli, Picpoul de Pinet, Aligoté, Jura whites, dry Alsatian Riesling, Torrontés — crisp, mineral, electric whites from places you haven't tried yet",
        chips: ["high acidity", "mineral", "discovery-driven"],
      },
      neutral: {
        description: "You have a high tolerance for the unexpected — unusual grapes, unfamiliar regions, wines that taste like somewhere specific and make you want to learn more. Your acidity preference is high, which means you're naturally drawn to wines that feel electric and alive rather than soft and comfortable. You're the person who orders the thing nobody else at the table has heard of, and you're usually right.",
        regions: "Grüner Veltliner, Assyrtiko, Cru Beaujolais, Picpoul de Pinet, Mencía, Jura whites, natural Grenache — high acid, curious, always off the beaten path",
        chips: ["high acidity", "adventurous", "aromatic"],
      },
    },
  },
  SWEET_SIPPER: {
    emoji:"🍯", name:"The Sweet Sipper", tagline:"Fruit-forward sweetness, soft and easy to love.",
    description:"Residual sugar isn't a flaw to you — it's a feature. You're drawn to wines where the fruit is generous and the finish is smooth, with acidity playing a supporting role rather than dominating. In sommelier terms, you have a sweet-leaning palate with low acidity sensitivity — a combination that points toward off-dry and naturally sweet styles made with genuine craft. These wines have centuries of tradition behind them and a devoted following among people who trust their own taste.",
    regions:"Moscato d'Asti, Spätlese and Auslese Riesling, Gewürztraminer, Demi-Sec Vouvray, off-dry Chenin Blanc, Lambrusco, Asti Spumante, Sauternes",
    shareText:"I'm a Sweet Sipper — my palate gravitates toward fruit-forward, smooth wines and I'm not apologizing for it. Discover your wine personality at truepour.ai",
    chips:["off-dry","fruit-forward","low tannin"],
    colorVariants: {
      red: {
        description: "Residual sweetness isn't a flaw to you — it's a feature. You're drawn to wines where the fruit is generous and the finish is smooth and warming. Off-dry reds and fruit-forward styles with a touch of sweetness are your comfort zone. This isn't unsophisticated. These wines have centuries of tradition and a devoted following among people who trust their own palate.",
        regions: "Off-dry Lambrusco, fruit-forward Zinfandel with a touch of sweetness, Brachetto d'Acqui, sweet Shiraz styles — warming, generous, smooth",
        chips: ["off-dry", "fruit-forward", "smooth"],
      },
      white: {
        description: "Residual sweetness isn't a flaw to you — it's a feature. You're drawn to wines where the fruit is generous, the finish lingers, and a touch of sweetness makes everything feel complete. Moscato, Riesling Spätlese, off-dry Gewurztraminer. This isn't unsophisticated. These wines have centuries of tradition and a devoted following among people who trust their own palate.",
        regions: "Moscato d'Asti, Spätlese and Auslese Riesling, Gewurztraminer, demi-sec Vouvray, off-dry Chenin Blanc, Asti Spumante — fragrant, sweet, genuinely craft",
        chips: ["off-dry", "aromatic", "fruit-forward"],
      },
      neutral: {
        description: "Residual sugar isn't a flaw to you — it's a feature. You're drawn to wines where the fruit is generous and the finish is smooth, with acidity playing a supporting role rather than dominating. In sommelier terms, you have a sweet-leaning palate that points toward off-dry and naturally sweet styles made with genuine craft.",
        regions: "Moscato d'Asti, Spätlese Riesling, Gewurztraminer, demi-sec Vouvray, off-dry Chenin Blanc, Lambrusco, Asti Spumante — sweet-leaning, fruit-forward, honest about what they are",
        chips: ["off-dry", "fruit-forward", "low tannin"],
      },
    },
  },
  TERROIR_HUNTER: {
    emoji:"🌿", name:"The Terroir Hunter", tagline:"Complexity, earthiness, and the story behind every glass.",
    description:"You're not looking for a wine that's immediately pleasing — you're looking for one that's interesting. You're drawn to wines with tertiary characteristics: the earthy, mineral, and sometimes funky notes that develop when great grapes grow in distinctive soils and are given time to evolve. In sommelier terms, you have a complexity-seeking palate with a strong preference for terroir expression over fruit extraction — the combination that points toward the world's most site-specific wines, where what's in the glass is inseparable from where it was grown.",
    regions:"Burgundy, Etna Rosso, old-vine Grenache, Jura, aged Nebbiolo, natural wine from distinctive terroirs, cool-climate Pinot Noir from Willamette and Burgundy village level, German Spätburgunder, Douro reds, minimal-intervention Syrah",
    shareText:"I'm a Terroir Hunter — my palate seeks complexity, earth, and the sense of a specific place in the glass. Discover your wine personality at truepour.ai",
    chips:["high complexity","earthy","discovery-driven"],
    colorVariants: {
      red: {
        description: "You're not looking for a wine that's immediately pleasing — you're looking for one that's interesting. You're drawn to earthy, complex reds where what's in the glass is inseparable from where it was grown. Tertiary notes, mineral edges, the faint suggestion of somewhere specific. In sommelier terms, you have a complexity-seeking palate that points toward the world's most site-specific red wines.",
        regions: "Burgundy Pinot Noir, Etna Rosso, old-vine Grenache, aged Nebbiolo, Jura reds, cool-climate Syrah, natural wine from distinctive terroirs — earthy, site-specific, profound",
        chips: ["earthy", "high complexity", "site-specific"],
      },
      white: {
        description: "You're not looking for a wine that's immediately pleasing — you're looking for one that's interesting. You're drawn to complex, mineral whites where what's in the glass is inseparable from where it was grown. Chablis that tastes like chalk. Grüner that tastes like the Wachau. In sommelier terms, you have a complexity-seeking palate that points toward the world's most site-specific white wines.",
        regions: "Chablis, Grüner Veltliner from Wachau, white Burgundy, Muscadet sur lie, Savennières, Alsatian Riesling Grand Cru — mineral, site-specific, wines that taste like somewhere",
        chips: ["mineral", "high complexity", "site-specific"],
      },
      neutral: {
        description: "You're not looking for a wine that's immediately pleasing — you're looking for one that's interesting. You're drawn to wines with tertiary characteristics: earthy, mineral, sometimes funky notes that develop when great grapes grow in distinctive soils. In sommelier terms, you have a complexity-seeking palate with a strong preference for terroir expression over fruit extraction.",
        regions: "Burgundy, Chablis, Etna Rosso, old-vine Grenache, Grüner Veltliner, Jura, aged Nebbiolo, natural wine from distinctive terroirs — earthy, mineral, always site-specific",
        chips: ["high complexity", "earthy", "discovery-driven"],
      },
    },
  },
  ELEGANT_CLASSICIST: {
    emoji:"🥂", name:"The Elegant Classicist", tagline:"Balanced, structured, rewards patience.",
    description:"You're drawn to wines where every element — acidity, tannin, fruit, alcohol — is in its right proportion, with nothing dominating or out of place. You appreciate complexity that reveals itself gradually rather than announcing itself immediately. In sommelier terms, you have a classically trained palate that responds to restraint and harmony — the combination that points toward wines built on long tradition, where balance is the goal and age is the reward. You're not looking for a wine that impresses on first contact. You're looking for one that's still interesting an hour later.",
    regions:"White Burgundy, vintage Champagne, rosé Champagne, aged Rioja Reserva, structured and restrained Napa Cabernet, Chianti Classico Riserva, white Rhône blends, cool-climate Pinot Noir at premier cru level, Franciacorta, English sparkling wine",
    shareText:"I'm an Elegant Classicist — my palate values balance, restraint, and wines that reveal themselves slowly. Discover your wine personality at truepour.ai",
    chips:["complexity","restraint","age-worthy"],
    colorVariants: {
      red: {
        description: "You're drawn to structured reds where every element — acidity, tannin, fruit, alcohol — is in its right proportion. You appreciate complexity that reveals itself gradually rather than announcing itself immediately. Restraint is a feature, not a flaw. You're not looking for a wine that impresses on first contact — you want one that's still interesting an hour later.",
        regions: "Aged Rioja Reserva, structured Napa Cabernet, Chianti Classico Riserva, Saint-Julien Bordeaux, cool-climate Pinot Noir — balanced, structured, rewards patience",
        chips: ["structured", "age-worthy", "restrained"],
      },
      white: {
        description: "You're drawn to structured whites where every element — acidity, texture, fruit, minerality — is in its right proportion. You appreciate complexity that reveals itself gradually. White Burgundy, aged Riesling, serious Champagne. Restraint is a feature, not a flaw. You're not looking for a wine that impresses on first contact — you want one that's still interesting an hour later.",
        regions: "White Burgundy, vintage Champagne, aged Riesling from Alsace or Germany, white Rhône blends, Franciacorta — precise, structured, reveals itself slowly",
        chips: ["mineral", "structured", "age-worthy"],
      },
      neutral: {
        description: "You're drawn to wines where every element — acidity, tannin, fruit, alcohol — is in its right proportion, with nothing dominating or out of place. You appreciate complexity that reveals itself gradually rather than announcing itself immediately. Restraint is a feature, not a flaw. You're not looking for a wine that impresses on first contact. You want one that's still interesting an hour later.",
        regions: "White Burgundy, vintage Champagne, aged Rioja Reserva, structured Napa Cabernet, Chianti Classico Riserva, white Rhône blends — balanced, structured, rewards patience and age",
        chips: ["complexity", "restraint", "age-worthy"],
      },
    },
  },
  CELEBRATION_SPECIALIST: {
    emoji:"🫧", name:"The Celebration Specialist", tagline:"Light, fresh, sparkling — always the right mood.",
    description:"You're drawn to wines with energy and lift — the kind that make a moment feel marked. High acidity feels celebratory to you rather than austere. Bubbles amplify that quality, which is why sparkling wines and rosé feel like natural expressions of your palate rather than occasional indulgences. In sommelier terms, you have a freshness-driven palate with a strong preference for wines that feel alive and immediate — the combination that points toward the world's great sparkling wines and Provence-style rosés.",
    regions:"Champagne, rosé Champagne, Crémant, Prosecco, Cava, Provence rosé, sparkling rosé, pét-nat, Lambrusco, English sparkling",
    shareText:"I'm a Celebration Specialist — my palate loves freshness, effervescence, and wines that make any moment feel marked. Discover your wine personality at truepour.ai",
    chips:["sparkling","low tannin","fruit-forward"],
  },
  RICH_WHITE_DEVOTEE: {
    emoji:"🍋", name:"The Rich White Devotee", tagline:"Full-bodied, oaked whites — rich, warm, and satisfying.",
    description:"You're drawn to whites with presence — wines that fill the mouth, linger on the finish, and reward attention. You're comfortable with oak influence: the vanilla, toast, and creamy texture it adds feels like depth to you rather than interference. In sommelier terms, you have a full-bodied white palate with high oak tolerance — a relatively rare combination that points toward the world's most serious white wines, where fermentation in barrel and extended aging on lees create a texture and complexity that lighter whites simply can't achieve.",
    regions:"White Burgundy, oaked Chardonnay from California, white Rhône blends — Roussanne, Marsanne, Viognier — aged white Rioja, Condrieu, aged Alsatian Gewürztraminer, Torrontés",
    shareText:"I'm a Rich White Devotee — my palate favors full-bodied whites with texture, oak, and presence. Discover your wine personality at truepour.ai",
    chips:["oaked","full body","high alcohol"],
  },
  EVERYDAY_ENTHUSIAST: {
    emoji:"🍷", name:"The Everyday Enthusiast", tagline:"Reliably good, immediately enjoyable, always right.",
    description:"You know what you like, and you like it consistently. You're not looking for wine to be a puzzle or a performance — you want something that's good every time, pairs with whatever you're eating, and feels like a reward rather than an assignment. That's not a low bar. That's a high one, and you've already cleared it. In sommelier terms, you have a moderate, balanced palate with a preference for approachable, consistent styles over complexity and challenge. The wines that make you happiest are the ones that make everyone else happy too — which means you'll never order a bottle that disappoints a table. That's rarer than it sounds.",
    regions:"Approachable Cabernet Sauvignon, Pinot Grigio, crowd-pleasing Chardonnay, fruit-forward Zinfandel, soft Merlot, easy-drinking Rosé, reliable Prosecco and Cava",
    shareText:"I'm an Everyday Enthusiast — I know what I like, I like it consistently, and I've never ordered a wine that disappointed a table. Discover your wine personality at truepour.ai",
    chips:["approachable","consistent","fruit-forward"],
    colorVariants: {
      red: {
        description: "You know what you like, and you like it consistently. You reach for approachable reds that deliver every time — fruit-forward, smooth, and never demanding. You're not looking for wine to be a puzzle. You want something that feels like a reward, pairs with whatever you're eating, and makes everyone at the table happy. That's not a low bar. That's a high one, and you've already cleared it.",
        regions: "Fruit-forward Cabernet Sauvignon, smooth Merlot, approachable Malbec, easy-drinking Pinot Noir, soft Zinfandel — wines that are consistent, crowd-pleasing, and always satisfying",
        chips: ["smooth", "fruit-forward", "approachable"],
      },
      white: {
        description: "You know what you like, and you like it consistently. You reach for crisp, easy-drinking whites that deliver every time — fresh, clean, and never complicated. You're not looking for wine to be a puzzle. You want something that feels like a reward, pairs with whatever you're eating, and makes everyone at the table happy. That's not a low bar. That's a high one, and you've already cleared it.",
        regions: "Crisp Pinot Grigio, unoaked Chardonnay, approachable Sauvignon Blanc, easy Moscato, dry Rosé — wines that are refreshing, consistent, and always the right call",
        chips: ["crisp", "refreshing", "approachable"],
      },
      neutral: {
        description: "You know what you like, and you like it consistently. You reach for approachable wines that deliver every time — whether that's a smooth red or a crisp white depends on the mood, but reliable pleasure is always the goal. You're not looking for wine to be a puzzle. You want something that feels like a reward, pairs with whatever you're eating, and makes everyone at the table happy.",
        regions: "Pinot Grigio, unoaked Chardonnay, approachable Sauvignon Blanc, fruit-forward Cabernet, smooth Merlot, easy-drinking Rosé — whatever's approachable, consistent, and reliably good",
        chips: ["approachable", "consistent", "easy-drinking"],
      },
    },
  },
};

const LOWERCASE_TO_ARCHETYPE_KEY = {
  crisp_purist: 'CRISP_PURIST',
  bold_traditionalist: 'BOLD_TRADITIONALIST',
  bold_modernist: 'BOLD_MODERNIST',
  smooth_romantic: 'VELVET_TOUCH',
  bright_adventurer: 'BRIGHT_ADVENTURER',
  sweet_sipper: 'SWEET_SIPPER',
  terroir_hunter: 'TERROIR_HUNTER',
  elegant_classicist: 'ELEGANT_CLASSICIST',
  celebration_specialist: 'CELEBRATION_SPECIALIST',
  rich_white_devotee: 'RICH_WHITE_DEVOTEE',
  everyday_enthusiast: 'EVERYDAY_ENTHUSIAST',
};

function toArchetypeKey(lowercaseKey) {
  if (!lowercaseKey) return null;
  return LOWERCASE_TO_ARCHETYPE_KEY[lowercaseKey.toLowerCase()] || lowercaseKey.toUpperCase();
}

function calculateArchetype(sliders, extSliders) {
  const sw = sliders.sweetness || 3;
  const fr = sliders.fruit     || 3;
  const bo = sliders.body      || 3;
  const ac = sliders.acidity   || 3;
  const ad = sliders.adventure || 3;
  const ta = extSliders.tannin     || 3;
  const oa = extSliders.oak        || 3;
  const al = extSliders.alcohol    || 3;
  const ar = extSliders.aromatics  || 3;
  const co = extSliders.complexity || 3;
  const cl = extSliders.color      || 3;

  const dim = (ideal, actual, weight) => {
    const diff = Math.abs(ideal - actual);
    return ((5 - diff) / 5) * weight;
  };
  const dimFive = (ideal, actual, weight) => {
    const diff = Math.abs(ideal - actual);
    return ((4 - diff) / 4) * weight;
  };

  const cap = (score) => Math.min(score, 99);

  const scores = {
    CRISP_PURIST: cap((
      dim(6,ac,5) + dim(1,sw,3) + dim(1,bo,2) + dim(1,oa,2) + dim(4,cl,2)
    ) / 14 * 100),

    BOLD_TRADITIONALIST: cap((
      dim(6,ta,5) + dim(6,bo,4) + dimFive(5,al,3) + dim(1,oa,2) + dim(1,fr,2) + dim(1,cl,3)
    ) / 19 * 100),

    BOLD_MODERNIST: cap((
      dim(6,bo,5) + dim(5,oa,5) + dim(5,ta,4) + dim(6,fr,3) + dimFive(5,al,3) + dim(1,cl,3)
    ) / 23 * 100),

    VELVET_TOUCH: cap((
      dim(2,ta,4) + dim(6,fr,3) + dim(2,cl,2) + dim(4,sw,1) + dim(3,bo,1)
    ) / 11 * 100),

    BRIGHT_ADVENTURER: cap((
      dim(6,ac,3) + dim(6,ad,5) + dim(1,bo,3) + dim(1,ta,3) + dim(2,cl,3) + dim(6,ar,1)
    ) / 18 * 100),

    SWEET_SIPPER: cap((
      dim(6,sw,4) + dim(1,ta,2) + dim(1,ac,2) + dim(6,fr,2) + dim(1,bo,1)
    ) / 11 * 100),

    TERROIR_HUNTER: cap((
      dim(6,co,4) + dim(6,ad,3) + dim(6,ar,3) + dim(1,sw,1)
    ) / 11 * 100),

    ELEGANT_CLASSICIST: cap((
      dim(6,co,3) + dim(5,ac,3) + dim(5,ta,2) + dim(1,sw,2) + dim(4,ad,2)
    ) / 12 * 100),

    CELEBRATION_SPECIALIST: cap((
      dim(1,ta,3) + dim(6,ac,3) + dim(1,bo,3) + dim(4,cl,2) + dimFive(1,al,1)
    ) / 12 * 100),

    RICH_WHITE_DEVOTEE: cap((
      dim(6,oa,4) + dim(6,bo,3) + dimFive(5,al,3) + dim(5,cl,5) + dim(1,ac,1)
    ) / 16 * 100),

    EVERYDAY_ENTHUSIAST: cap((
      dim(3,ta,2) + dim(3,ac,2) + dim(3,bo,2) + dim(5,fr,2) + dim(5,ad,2) + dim(2,cl,2)
    ) / 12 * 100),
  };

  // Hard constraints
  if (cl <= 2) scores.RICH_WHITE_DEVOTEE = 0;
  if (cl <= 2) scores.CELEBRATION_SPECIALIST *= 0.3;
  if (cl >= 4) scores.VELVET_TOUCH *= 0.15;
  if (cl === 3) scores.VELVET_TOUCH *= 0.5;
  if (cl === 3 && bo <= 2 && ta <= 1 && al <= 2) scores.CELEBRATION_SPECIALIST *= 0.4;
  if (cl >= 4) { scores.BOLD_TRADITIONALIST *= 0.3; scores.BOLD_MODERNIST = 0; }
  if (ad <= 3) scores.BRIGHT_ADVENTURER *= 0.3;
  if (ta <= 2) {
    scores.BOLD_TRADITIONALIST *= 0.3;
    scores.BOLD_MODERNIST *= 0.2;
  }
  if (bo <= 3) scores.BOLD_MODERNIST *= 0.15;
  if (oa <= 2) scores.BOLD_MODERNIST *= 0.2;
  if (oa >= 5 && bo >= 5 && cl <= 2) scores.BOLD_MODERNIST = Math.min(scores.BOLD_MODERNIST * 1.3, 99);
  if (sw <= 3 || fr <= 3) scores.SWEET_SIPPER = 0;
  if (sw >= 5) scores.CRISP_PURIST = 0;
  if (bo >= 5) scores.CELEBRATION_SPECIALIST = 0;
  if (co <= 2) {
    scores.TERROIR_HUNTER *= 0.4;
    scores.ELEGANT_CLASSICIST *= 0.5;
  }
  if (ad >= 5 && co >= 5 && cl <= 3) scores.TERROIR_HUNTER = Math.min(scores.TERROIR_HUNTER * 1.25, 99);
  if (ac <= 2) scores.CRISP_PURIST *= 0.4;
  if (ta >= 5 && bo >= 4) {
    scores.EVERYDAY_ENTHUSIAST *= 0.3;
    scores.VELVET_TOUCH *= 0.5;
  }
  if (ta >= 5 && cl <= 2) scores.BOLD_TRADITIONALIST = Math.min(scores.BOLD_TRADITIONALIST * 1.35, 99);
  if (ta >= 5 && cl <= 2) scores.BOLD_MODERNIST *= 0.75;
  if (ad <= 2) scores.EVERYDAY_ENTHUSIAST *= 0.4;

  const allZeroed = Object.values(scores).every(v => v === 0);
  if (allZeroed) { /* fallback — scores stay as-is */ }

  const sorted = Object.entries(scores).sort((a,b) => b[1]-a[1]);
  const [topKey, topScore] = sorted[0];
  const [secondKey, secondScore] = sorted[1];

  if ((topKey === 'EVERYDAY_ENTHUSIAST' || topKey === 'ELEGANT_CLASSICIST')
      && (topScore - secondScore) < 5) {
    return secondKey;
  }

  return topKey;
}

function getTopInfluencers(sliders, extSliders, archetypeKey) {
  const sw = sliders.sweetness || 3;
  const fr = sliders.fruit     || 3;
  const bo = sliders.body      || 3;
  const ac = sliders.acidity   || 3;
  const ad = sliders.adventure || 3;
  const ta = extSliders.tannin     || 3;
  const oa = extSliders.oak        || 3;
  const al = extSliders.alcohol    || 3;
  const ar = extSliders.aromatics  || 3;
  const co = extSliders.complexity || 3;
  const cl = extSliders.color      || 3;

  const allSliders = [
    { key:"sweetness", label:"Coffee preference", value:sw,
      answers:["Black, no sugar","Just a splash of milk","Milk, no sugar","Milk & a little sugar","Sweet & creamy","Dessert in a cup"] },
    { key:"fruit", label:"Dessert preference", value:fr,
      answers:["Lemon sorbet","Fruit gelato","Strawberry ice cream","Vanilla or caramel","Chocolate chip","Dark chocolate fudge"] },
    { key:"body", label:"Dinner preference", value:bo,
      answers:["Grilled fish or salad","Chicken or veggie dish","Pasta or risotto","Burger or lamb","Ribeye or braised short rib","Bone-in steak, rare"] },
    { key:"acidity", label:"Salt & vinegar crisps", value:ac,
      answers:["Plain only — no sharpness","Almost no tang please","Prefer mostly mild","A little tang is nice","Really enjoy the tang","Love the zing — whole bag"] },
    { key:"adventure", label:"Restaurant ordering", value:ad,
      answers:["Most unusual thing on menu","Something I've never tried","Something interesting but familiar","A reliable classic","My usual order","The thing I know I love"] },
    { key:"tannin", label:"Steak preference", value:ta,
      answers:["Cooked through — soft & easy","Well done","Medium well","Medium","Medium rare","Rare — bold & bloody"] },
    { key:"oak", label:"Cheese preference", value:oa,
      answers:["Fresh mozzarella or ricotta","Young gouda or brie","Mild cheddar","Aged cheddar or gruyère","Sharp aged parmesan","Extra aged — the sharper the better"] },
    { key:"alcohol", label:"Weekend drink", value:al,
      answers:["A hard seltzer or light beer","A gin & tonic or spritz","A margarita or mojito","A negroni or old fashioned","Whisky or tequila, neat"] },
    { key:"aromatics", label:"Scent preference", value:ar,
      answers:["Unscented — keep it clean","Very light, barely there","Subtle and fresh","Noticeably fragrant","Rich and aromatic","The more fragrant the better"] },
    { key:"complexity", label:"Unfamiliar cuisine", value:co,
      answers:["Stick to what I know","Mostly familiar with a twist","Open to trying new things","Love exploring new cuisines","The more unusual the better","Take me somewhere I've never been"] },
    { key:"color", label:"Wine color preference", value:cl,
      answers:["Always red","Usually red","Mix it up","Usually white/rosé/sparkling","Almost always white/sparkling"] },
  ];

  const ideals = {
    CRISP_PURIST:           { sweetness:1, fruit:1, body:1, acidity:6, adventure:3, tannin:6, oak:1, alcohol:2, aromatics:3, complexity:3, color:4 },
    BOLD_TRADITIONALIST:    { sweetness:1, fruit:1, body:6, acidity:3, adventure:3, tannin:1, oak:1, alcohol:1, aromatics:3, complexity:4, color:1 },
    BOLD_MODERNIST:         { sweetness:2, fruit:6, body:6, acidity:3, adventure:4, tannin:2, oak:5, alcohol:1, aromatics:3, complexity:3, color:1 },
    VELVET_TOUCH:           { sweetness:4, fruit:6, body:3, acidity:1, adventure:3, tannin:6, oak:3, alcohol:3, aromatics:3, complexity:3, color:2 },
    BRIGHT_ADVENTURER:      { sweetness:1, fruit:3, body:3, acidity:6, adventure:1, tannin:3, oak:1, alcohol:3, aromatics:6, complexity:6, color:3 },
    SWEET_SIPPER:           { sweetness:6, fruit:6, body:1, acidity:1, adventure:3, tannin:6, oak:3, alcohol:3, aromatics:3, complexity:3, color:4 },
    TERROIR_HUNTER:         { sweetness:1, fruit:3, body:3, acidity:3, adventure:1, tannin:3, oak:1, alcohol:3, aromatics:6, complexity:6, color:3 },
    ELEGANT_CLASSICIST:     { sweetness:1, fruit:3, body:3, acidity:5, adventure:4, tannin:3, oak:3, alcohol:3, aromatics:3, complexity:6, color:3 },
    CELEBRATION_SPECIALIST: { sweetness:3, fruit:6, body:1, acidity:6, adventure:3, tannin:6, oak:1, alcohol:6, aromatics:3, complexity:3, color:4 },
    RICH_WHITE_DEVOTEE:     { sweetness:3, fruit:3, body:6, acidity:1, adventure:3, tannin:6, oak:6, alcohol:1, aromatics:3, complexity:3, color:5 },
    EVERYDAY_ENTHUSIAST:    { sweetness:2, fruit:5, body:3, acidity:3, adventure:5, tannin:4, oak:2, alcohol:3, aromatics:3, complexity:3, color:3 },
  };

  const ideal = ideals[archetypeKey];
  if (!ideal) return [];

  const contributions = allSliders.map(s => {
    const idealVal = ideal[s.key] || 3;
    const diff = Math.abs(idealVal - s.value);
    const contribution = (5 - diff) / 5;
    return { ...s, contribution, answerLabel: s.answers[s.value - 1] || s.answers[2] };
  });

  return contributions.sort((a,b) => b.contribution - a.contribution).slice(0,3);
}

// ─── BACKEND API CALLS ───────────────────────────────────────────────────────
// All AI calls go through the Express backend (server.js).
// Your Anthropic API key lives only in Replit Secrets — never in this file.

async function migrateProfileToSupabase(session, payload) {
  try {
    const res = await fetch('/api/migrate-profile', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify(payload),
    });
    const result = await res.json();
    console.log('[CLIENT DEBUG] migrate result:', JSON.stringify(result), 'status:', res.status);
    return result;
  } catch (e) {
    console.error('[CLIENT DEBUG] Migration failed:', e);
    return null;
  }
}

async function fetchSupabaseProfile(session) {
  try {
    const res = await fetch('/api/profile', {
      headers: { 'Authorization': `Bearer ${session.access_token}` },
    });
    const data = await res.json();
    console.log('[CLIENT DEBUG] profile fetch result:', JSON.stringify(data), 'status:', res.status);
    return { profile: data.profile || null, status: res.status, raw: data };
  } catch (e) {
    console.error('[CLIENT DEBUG] Profile fetch failed:', e);
    return { profile: null, status: 0, raw: null, error: e.message };
  }
}

async function getRecommendationsStream(params, onWine, onDone, onError, onEnrich, session) {
  try {
    const response = await fetch("/api/recommend-stream", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}) },
      body: JSON.stringify(params),
    });
    if (!response.ok) { const e = new Error(`HTTP ${response.status}`); e.status = response.status; throw e; }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop();
      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === 'wine') onWine(data.wine, data.index);
            else if (data.type === 'done') onDone(data.total);
            else if (data.type === 'error') onError(new Error(data.message));
            else if (data.type === 'enrich' && onEnrich) onEnrich(data);
          } catch {}
        }
      }
    }
  } catch (err) {
    onError(err);
  }
}

async function getRecommendations(params, session) {
  const res = await fetch("/api/recommend", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}) },
    body: JSON.stringify(params),
  });
  let data;
  try {
    data = await res.json();
  } catch {
    const e = new Error(`Server error (${res.status}) — please try again.`); e.status = res.status; throw e;
  }
  if (!res.ok || data.error) { const e = new Error(data.error || `HTTP ${res.status}`); e.status = res.status; throw e; }
  // Return the FULL response object (not just .recommendations) so emptyReason
  // survives to the caller — mirrors evaluateBottle(s)/{recommendations,emptyReason}
  // already doing this correctly. The caller's generic unpacking already handles
  // both an object shape (recsArr = res.recommendations, emptyReason = res.emptyReason)
  // and a bare-array shape defensively — this makes getRecommendations consistent
  // with that instead of being the one wrapper that strips emptyReason.
  return data;
}

async function evaluateBottle(params, session) {
  const res = await fetch("/api/evaluate-bottle", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}) },
    body: JSON.stringify(params),
  });
  const data = await res.json();
  if (!res.ok || data.error) { const e = new Error(data.error || `HTTP ${res.status}`); e.status = res.status; throw e; }
  return data;
}

async function evaluateBottles(params, session) {
  const res = await fetch("/api/evaluate-bottles", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}) },
    body: JSON.stringify(params),
  });
  const data = await res.json();
  if (!res.ok || data.error) { const e = new Error(data.error || `HTTP ${res.status}`); e.status = res.status; throw e; }
  return data;
}

async function fetchStyleGuide(params, session) {
  const res = await fetch("/api/style-guide", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}) },
    body: JSON.stringify(params),
  });
  const data = await res.json();
  if (!res.ok || data.error) { const e = new Error(data.error || `HTTP ${res.status}`); e.status = res.status; throw e; }
  return data;
}

// Client-side image compression for the scan upload (Commit 3.2). Modern phone
// photos routinely exceed Anthropic's 5 MB image ceiling (a failed live scan was
// 5,321,924 bytes), so we resize + JPEG-encode BEFORE upload. Choices: longest
// side capped at 2000px — a 12MP phone photo is ~4032px, and 2000px keeps small
// menu text / handwritten prices legible (scan accuracy depends on it) while
// cutting size ~4x; quality is stepped DOWN first (preserves resolution, which
// matters most for OCR) and only then are dimensions shrunk, targeting 4.5 MB to
// leave margin under the limit. Also normalizes HEIC/PNG to JPEG, which the API
// accepts. Falls back to the raw bytes if the browser can't decode the file.
const SCAN_MAX_BYTES = 4500000; // margin under the 5,242,880-byte API limit
function base64Bytes(b64) {
  const len = (b64 || '').length;
  const pad = b64.endsWith('==') ? 2 : b64.endsWith('=') ? 1 : 0;
  return Math.floor(len * 3 / 4) - pad;
}
function loadImageElement(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('image decode failed')); };
    img.src = url;
  });
}
async function compressImageForScan(file) {
  let img;
  try {
    img = await loadImageElement(file);
  } catch {
    // Can't decode in-browser — send original bytes, server still guards size.
    const base64 = await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = e => res(String(e.target.result).split(',')[1] || '');
      r.onerror = rej;
      r.readAsDataURL(file);
    });
    return { base64, mediaType: file.type || 'image/jpeg', previewUrl: null };
  }
  let maxDim = 2000;
  const qualitySteps = [0.82, 0.72, 0.62, 0.52, 0.45, 0.4];
  for (let attempt = 0; attempt < qualitySteps.length; attempt++) {
    const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, w, h); // flatten any alpha (JPEG has none)
    ctx.drawImage(img, 0, 0, w, h);
    const dataUrl = canvas.toDataURL('image/jpeg', qualitySteps[attempt]);
    const base64 = dataUrl.split(',')[1] || '';
    const last = attempt === qualitySteps.length - 1;
    if (base64Bytes(base64) <= SCAN_MAX_BYTES || last) {
      return { base64, mediaType: 'image/jpeg', previewUrl: dataUrl };
    }
    if (attempt >= 2) maxDim = Math.round(maxDim * 0.85); // shrink dims once quality alone isn't enough
  }
}

async function scanImage(base64, mediaType, mode, session) {
  const res = await fetch("/api/scan", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}) },
    body: JSON.stringify({ base64, mediaType, mode }),
  });
  const data = await res.json();
  // Thrown = genuine failure (timeout / 500 / network) → callers show the
  // "scan didn't go through" branch, driven by the ACTUAL failure not by "".
  if (!res.ok || data.error) { const e = new Error(data.error || "Scan failed. Try a clearer photo."); e.status = res.status; throw e; }
  // Honest status from the server: 'ok' | 'no_wines'. Fall back to inferring
  // from the text for backward-compat if an older server omits it.
  const result = data.result || "";
  const status = data.status || (result.trim() ? 'ok' : 'no_wines');
  // Structured rows (restaurant/store scan). Absent on older servers / other
  // modes → []. Carried alongside the text so downstream can use menu price +
  // service format without disturbing the existing text pipeline.
  const rows = Array.isArray(data.rows) ? data.rows : [];
  return { result, status, rows };
}

// Merge structured scan rows across multiple scans, deduped by producer+wine
// identity (keeps first), mirroring the text dedupe so rows and scannedWines
// stay aligned.
function scanRowIdentity(r) {
  return ((r && r.producer) || '').toLowerCase().replace(/[^a-z0-9]/g, '') + '|' +
         ((r && r.wine) || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}
function mergeScanRows(prev, incoming) {
  const out = Array.isArray(prev) ? [...prev] : [];
  const seen = new Set(out.map(scanRowIdentity));
  for (const r of (Array.isArray(incoming) ? incoming : [])) {
    const id = scanRowIdentity(r);
    if (id !== '|' && !seen.has(id)) { seen.add(id); out.push(r); }
  }
  return out;
}

// ─── RESTAURANT BUDGET TIER DERIVATION ───────────────────────────────────────
// Tiers are DERIVED from the scanned menu's own captured prices (Commit 3.1), not
// hardcoded — restaurant lists span $45 bistro to $1100+ fine-dining, so no fixed
// set fits both. Derived from the rows matching the chosen wine COLOR and service
// FORMAT, so a suggested range never returns nothing (see deriveRestaurantBudgetTiers).
function normWineColor(c) {
  const s = (c || '').toLowerCase();
  if (!s) return null;
  if (s.includes('ros')) return 'rose';
  if (s.includes('spark') || s.includes('champ')) return 'sparkling';
  if (s.includes('red')) return 'red';
  if (s.includes('white')) return 'white';
  if (s.includes('orange')) return 'orange';
  return s;
}
// The chosen-format price a row shows — mirrors the server's chosenMenuPrice().
function chosenRowPrice(row, orderType) {
  const chosen = orderType === 'glass' ? row.priceGlass : row.priceBottle;
  if (chosen != null) return chosen;
  const other = orderType === 'glass' ? row.priceBottle : row.priceGlass;
  return (row.formatKnown === false && other != null) ? other : null;
}
function rowFormatEligibleClient(row, orderType) {
  if (!row || row.formatKnown === false) return true;
  const f = Array.isArray(row.availableFormats) ? row.availableFormats : [];
  return f.includes(orderType);
}
// Place a tier boundary on a HUMAN-nice value that sits strictly inside the gap
// (a,b] between two adjacent priced clusters — never below the lower cluster, so
// the bottom tier is never empty, and labels look deliberate ($60 not $62).
function niceBoundaryInGap(a, b) {
  const mid = (a + b) / 2;
  for (const step of [100, 50, 25, 20, 10, 5]) {
    const r = Math.round(mid / step) * step;
    if (r > a && r <= b) return r;
  }
  let r = Math.round(mid / 5) * 5;
  if (r <= a) r = Math.ceil((a + 1) / 5) * 5;
  if (r > b) r = b;
  return r;
}
// Derive 2–3 budget tiers (+ "No limit") from the captured chosen-format prices
// of rows matching color+format. Returns null when there's nothing worth
// filtering (too few prices, <2 distinct, negligible spread) → caller renders no
// control. Boundaries fall in real price gaps so every tier is non-empty.
// The sorted captured chosen-format prices of the color+format-eligible rows —
// the single input both the tier derivation and the degenerate-case chips read,
// so "what we derive from" is defined in exactly one place.
function restaurantPricedPrices(rows, orderType, wineColor) {
  const wantColor = wineColor && wineColor !== 'surprise' ? normWineColor(wineColor) : null;
  return (Array.isArray(rows) ? rows : [])
    .filter(r => rowFormatEligibleClient(r, orderType))
    .filter(r => !wantColor || normWineColor(r.color) === wantColor)
    .map(r => chosenRowPrice(r, orderType))
    .filter(p => typeof p === 'number' && p > 0)
    .sort((a, b) => a - b);
}
function deriveRestaurantBudgetTiers(rows, orderType, wineColor) {
  const prices = restaurantPricedPrices(rows, orderType, wineColor);
  if (prices.length < 3) return null;                 // too few priced rows to derive
  const min = prices[0], max = prices[prices.length - 1];
  const distinct = [...new Set(prices)];
  if (distinct.length < 2) return null;               // all one price — nothing to split
  if ((max - min) / max < 0.15) return null;          // negligible spread — a control can't discriminate
  const k = distinct.length >= 4 ? 2 : 1;             // 3 tiers when enough distinct prices, else 2
  const bounds = [];
  for (let j = 1; j <= k; j++) {
    const f = j / (k + 1);
    let i = Math.min(prices.length - 2, Math.max(0, Math.round(f * (prices.length - 1))));
    while (i < prices.length - 2 && prices[i] === prices[i + 1]) i++;   // walk out of a run of equal prices
    if (prices[i] === prices[i + 1]) { let d = i; while (d > 0 && prices[d] === prices[d + 1]) d--; i = d; }
    if (prices[i] < prices[i + 1]) {
      const b = niceBoundaryInGap(prices[i], prices[i + 1]);
      if (b > min && b < max) bounds.push(b);
    }
  }
  const uniq = [...new Set(bounds)].sort((a, b) => a - b).filter((b, idx, arr) => idx === 0 || b > arr[idx - 1]);
  if (uniq.length === 0) return null;                 // no usable interior boundary
  // Top tier is BOUNDED at the actual max (rounded up to a nice ceiling so the
  // priciest wine is included) — NOT open-ended. That leaves "No limit" a
  // distinct meaning: an explicit choice not to filter. e.g. reds [54,54,54,145]
  // → Under $100 | $100–$145 | No limit.
  const ceil = Math.ceil(max / 5) * 5;
  const tiers = [];
  let lo = 0;
  uniq.forEach((b, idx) => { tiers.push({ label: idx === 0 ? `Under $${b}` : `$${lo}–$${b}`, range: [lo, b] }); lo = b; });
  tiers.push({ label: `$${lo}–$${ceil}`, range: [lo, ceil] });
  tiers.push({ label: 'No limit', range: [0, 9999] });
  return tiers;
}

// ─── LOGO MARK — Wine drop resolving into a spark burst ──────────────────────
// The pour: a wine drop falling from above, its tail forming a spark/neural burst
// at the base — the "aha" moment where wine meets intelligence.
function TruePourMark({ size=40, animated=false, color=C.burgundy, diamondColor=null }) {
  const [opacities, setOpacities] = useState([0.4,0.28,0.18,0.1,0.06]);
  const maskId = useId();
  useEffect(() => {
    if (!animated) return;
    let t = 0;
    const id = setInterval(() => {
      t += 0.12;
      setOpacities([
        0.4 + 0.4 * Math.sin(t - 0.6),
        0.28 + 0.37 * Math.sin(t - 1.2),
        0.18 + 0.32 * Math.sin(t - 1.8),
        0.1 + 0.25 * Math.sin(t - 2.4),
        0.06 + 0.16 * Math.sin(t - 3.0),
      ]);
    }, 50);
    return () => clearInterval(id);
  }, [animated]);
  const dColor = diamondColor || color;
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <mask id={maskId}>
          <rect width="48" height="48" fill="white"/>
          <g transform="translate(1.1866,0.1913) scale(0.04460)">
            <g transform="translate(0.000000,1054.000000) scale(0.100000,-0.100000)" fill="black" stroke="black" strokeWidth="25">
              <path d="M5078 5703 c-16 -2 -28 -9 -28 -14 0 -5 -9 -9 -20 -9 -24 0 -39 -28 -21 -39 8 -5 -17 -44 -76 -120 -48 -63 -98 -129 -111 -147 -12 -19 -27 -34 -32 -34 -6 0 -10 -8 -10 -18 0 -10 -16 -34 -36 -53 -20 -18 -44 -50 -54 -69 -10 -19 -27 -44 -37 -55 -10 -11 -48 -58 -83 -104 -36 -46 -93 -121 -128 -165 -35 -45 -75 -100 -90 -123 -22 -34 -31 -41 -49 -36 -17 4 -23 1 -23 -11 0 -9 5 -16 10 -16 16 0 -4 -32 -53 -88 -23 -27 -56 -69 -74 -94 -26 -38 -36 -45 -54 -41 -19 5 -21 3 -16 -17 4 -15 -4 -40 -24 -75 -16 -28 -29 -64 -29 -80 0 -33 81 -159 159 -248 28 -32 56 -68 62 -80 7 -12 29 -42 49 -67 49 -59 60 -82 53 -108 -4 -17 -1 -22 14 -22 21 0 95 -73 128 -125 11 -17 38 -55 62 -86 24 -30 43 -59 43 -64 0 -4 17 -26 38 -49 61 -69 175 -216 200 -258 12 -22 52 -75 88 -117 36 -42 71 -87 78 -101 18 -40 58 -69 95 -70 37 0 111 37 111 55 0 7 10 25 23 40 12 15 42 56 67 91 63 90 183 245 220 284 29 31 90 116 90 126 0 7 119 162 128 166 4 2 24 29 44 60 30 45 41 55 57 51 23 -6 42 24 22 36 -12 8 43 85 136 190 27 30 55 68 63 85 12 23 21 30 38 28 22 -3 33 23 12 30 -6 2 8 27 30 57 35 46 40 60 40 105 0 42 -6 62 -32 101 -18 26 -32 60 -33 74 0 18 -6 27 -20 29 -28 6 -49 26 -123 122 -37 47 -92 116 -124 153 -32 38 -58 73 -58 79 0 5 -38 60 -84 122 -45 61 -94 127 -107 146 -25 38 -112 144 -215 265 -34 39 -70 87 -79 106 -20 38 -84 125 -131 175 -32 34 -43 36 -106 27z"/>
            </g>
          </g>
        </mask>
      </defs>
      <g mask={`url(#${maskId})`}>
        <circle cx="24" cy="28" r="6.4" stroke={color} strokeWidth="0.45" opacity={opacities[0]} fill="none"/>
        <circle cx="24" cy="28" r="9.0" stroke={color} strokeWidth="0.35" opacity={opacities[1]} fill="none"/>
        <circle cx="24" cy="28" r="11.7" stroke={color} strokeWidth="0.28" opacity={opacities[2]} fill="none"/>
        <circle cx="24" cy="28" r="14.6" stroke={color} strokeWidth="0.22" opacity={opacities[3]} fill="none"/>
        <circle cx="24" cy="28" r="16.9" stroke={color} strokeWidth="0.16" opacity={opacities[4]} fill="none"/>
      </g>
      <g transform="translate(1.1866,0.1913) scale(0.04460)">
        <g transform="translate(0.000000,1054.000000) scale(0.100000,-0.100000)" fill={color} stroke="none">
          <path d="M4948 9510 c-225 -22 -297 -69 -298 -196 0 -35 -8 -65 -24 -95 -23 -40 -25 -56 -26 -169 -1 -69 -1 -139 -1 -156 1 -19 13 -46 32 -70 l31 -39 -6 -115 c-3 -63 -10 -268 -16 -455 -26 -866 -34 -958 -95 -1090 -48 -106 -105 -185 -299 -410 -59 -69 -132 -184 -172 -270 -42 -90 -86 -223 -79 -235 3 -5 1 -12 -5 -15 -6 -4 -8 -15 -5 -26 4 -10 2 -19 -3 -19 -5 0 -15 -42 -23 -92 -13 -93 -15 -672 -3 -898 3 -52 6 -261 6 -465 0 -203 4 -347 8 -320 4 29 7 -66 7 -220 -1 -596 3 -955 10 -955 5 0 8 -231 8 -513 0 -501 9 -1053 21 -1272 8 -134 27 -187 93 -248 53 -49 131 -90 172 -90 16 0 26 -3 24 -7 -4 -6 38 -15 245 -51 105 -19 456 -31 715 -25 314 7 436 19 644 64 56 12 69 18 55 24 -14 6 -11 7 11 4 18 -2 47 5 75 19 59 30 131 102 123 122 -4 9 1 21 10 28 9 7 24 30 33 51 14 34 18 115 25 584 14 862 24 1854 23 2230 0 190 3 329 7 310 4 -19 7 152 6 380 -1 228 2 449 5 491 4 41 8 210 9 375 3 414 -21 565 -121 765 -60 121 -106 193 -156 244 -21 21 -39 42 -39 47 0 6 -157 198 -197 239 -9 9 -14 20 -10 23 3 3 0 6 -6 6 -7 0 -11 3 -10 8 1 4 -16 43 -39 87 -49 98 -60 130 -73 221 -10 78 -25 437 -36 874 -3 151 -9 349 -13 440 -5 129 -4 167 6 173 34 21 43 65 43 214 0 129 -3 154 -20 188 -12 23 -20 59 -20 87 0 85 -43 152 -117 183 -74 31 -375 51 -535 35z m572 -235 c0 -31 8 -66 20 -89 16 -32 19 -61 21 -168 1 -117 -1 -132 -19 -151 -30 -33 -40 -90 -37 -212 2 -60 6 -195 9 -300 10 -334 16 -498 22 -587 l6 -88 -420 0 -420 0 -6 23 c-3 12 -5 36 -5 52 1 29 1 29 8 5 4 -14 11 83 15 215 3 132 11 366 17 521 5 155 7 292 4 306 l-6 25 128 6 c70 4 166 7 213 7 47 0 110 4 140 8 36 4 -46 9 -235 12 l-290 5 -3 146 -3 146 38 7 c21 3 110 6 198 7 139 2 151 3 90 10 -38 5 -121 9 -184 9 -112 1 -113 1 -106 23 4 12 9 46 11 76 2 30 6 56 8 58 2 2 45 -3 95 -11 200 -33 478 -29 619 8 59 16 72 3 72 -69z m28 -1837 c11 -174 34 -270 92 -390 34 -69 120 -188 150 -206 11 -7 19 -16 16 -20 -3 -5 24 -42 60 -83 81 -93 205 -276 239 -353 63 -142 103 -345 105 -526 2 -284 -8 -1411 -13 -1416 -3 -4 0 -19 8 -35 18 -34 20 -171 4 -202 -13 -23 -17 -159 -23 -712 -2 -187 -6 -376 -9 -420 -5 -83 -22 -1231 -22 -1508 0 -86 -3 -157 -7 -157 -5 0 -8 -20 -8 -45 0 -51 -30 -120 -64 -148 -34 -29 -111 -66 -158 -76 -460 -103 -1419 -85 -1679 32 -93 42 -137 112 -140 219 0 26 -2 29 -8 13 -4 -11 -8 82 -9 205 0 124 -1 250 -3 280 -2 30 0 71 4 90 4 19 4 27 0 18 -5 -10 -8 110 -8 265 0 155 -3 419 -7 587 -4 168 -7 456 -7 640 0 239 -3 331 -11 320 -8 -11 -10 41 -8 181 1 169 -1 199 -15 219 -24 35 -23 130 3 180 17 34 19 47 10 114 -5 41 -7 211 -4 380 2 167 1 307 -4 310 -10 6 -7 762 3 808 5 21 3 33 -6 39 -11 6 -9 12 5 28 12 14 19 37 19 71 1 32 4 46 10 38 5 -8 6 -3 3 15 -4 15 -3 27 2 27 5 0 16 25 26 55 9 30 38 97 63 148 26 50 46 93 45 94 -1 1 7 13 18 25 17 21 59 73 232 290 63 80 89 116 88 123 -1 12 31 65 40 65 13 0 73 127 66 138 -3 6 -1 12 5 14 6 2 11 24 12 50 1 25 5 44 9 42 7 -5 11 85 10 211 -1 54 2 73 8 60 7 -16 9 -11 9 23 l1 42 418 0 419 0 11 -162z M4765 6942 c-11 -51 -93 -179 -176 -274 -153 -177 -262 -364 -308 -528 -15 -53 -17 -76 -9 -81 6 -4 8 -15 4 -25 -6 -16 -9 -16 -17 -4 -8 11 -9 3 -5 -28 4 -29 3 -41 -4 -37 -6 4 -10 -4 -10 -18 0 -26 28 -45 35 -24 2 6 9 44 15 82 23 145 87 300 185 452 30 45 51 84 48 87 -3 3 21 31 53 63 33 31 58 60 57 64 -1 4 4 13 12 19 8 7 15 9 15 4 0 -13 31 34 35 54 6 29 61 123 68 115 4 -3 8 0 8 8 1 8 2 19 4 24 1 6 3 16 3 23 1 7 7 10 12 7 14 -9 12 7 -4 28 -13 17 -15 16 -21 -11z M5098 5792 c-9 -9 -20 -26 -26 -37 -9 -19 -7 -20 39 -17 43 4 48 6 39 22 -5 9 -7 20 -4 23 3 3 -3 10 -14 16 -15 8 -23 7 -34 -7z M4239 5708 c0 -2 -3 -85 -5 -186 -4 -172 -3 -183 13 -180 13 3 17 12 15 33 -1 17 -2 99 -2 183 1 98 -3 152 -10 152 -5 0 -10 -1 -11 -2z M4257 3534 c-11 -19 0 -934 12 -948 6 -8 9 -36 6 -67 -12 -131 33 -790 54 -797 6 -2 9 -8 5 -13 -3 -5 0 -9 5 -9 6 0 11 12 11 28 0 17 -3 23 -8 14 -5 -7 -8 22 -8 65 2 135 -28 640 -35 603 -4 -19 -6 35 -6 120 1 85 1 229 0 319 -3 144 -5 162 -18 151 -13 -11 -15 6 -15 140 0 168 8 202 11 47 1 -53 4 -97 8 -97 8 0 1 409 -8 433 -4 11 -10 16 -14 11z M5050 2866 c0 -14 70 -56 84 -50 9 3 13 10 10 16 -3 5 2 16 12 23 17 13 12 14 -43 15 -35 0 -63 -2 -63 -4z"/>
        </g>
      </g>
      <path d="M 23.98 21.73 L 28.75 27.98 L 23.98 34.22 L 19.21 27.98 Z" fill={dColor}/>
    </svg>
  );
}

// Bottle-match evaluation loader
function BottleMatchLoader() {
  const msgs = ["Evaluating your bottle...", "Checking against your palate...", "Almost there..."];
  const [textIdx, setTextIdx] = useState(0);
  const [textVisible, setTextVisible] = useState(true);
  useEffect(() => {
    const interval = setInterval(() => {
      setTextVisible(false);
      setTimeout(() => {
        setTextIdx(i => (i + 1) % msgs.length);
        setTextVisible(true);
      }, 400);
    }, 1800);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div style={{
      position: "fixed", inset: 0, background: "#FFFAF8", zIndex: 9999,
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      animation: "tpLoaderFadeIn 0.3s ease both",
    }}>
      <style>{`
        @keyframes tpLoaderFadeIn { from { opacity:0 } to { opacity:1 } }
        @keyframes tpBreath { 0%,100%{transform:scale(1)} 50%{transform:scale(1.08)} }
      `}</style>
      <div style={{ animation: "tpBreath 1.8s ease-in-out infinite" }}>
        <TruePourMark size={160} animated diamondColor="#1A1A1A" />
      </div>
      <div style={{
        marginTop: 32, fontSize: 16, fontFamily: F.body, color: C.burgundy,
        textAlign: "center",
        opacity: textVisible ? 1 : 0,
        transition: "opacity 0.4s ease",
      }}>
        {msgs[textIdx]}
      </div>
    </div>
  );
}

// Simple spark for inline uses (badges, buttons etc)
function FullScreenLoader({ homeFlowPath, context, scannedWines }) {
  const messages = [
    "Reading your taste profile...",
    "Searching thousands of bottles...",
    "Finding your perfect match...",
  ];
  const notsureMessages = [
    "Reading your taste profile...",
    "Finding your perfect styles...",
    "Almost there...",
  ];
  const restaurantMessages = [
    "Reading your scanned menu...",
    "Matching wines to your palate...",
    "Finding your best options...",
  ];
  const isRestaurantScan = context === "restaurant" && scannedWines && scannedWines.length > 0;
  const [textIdx, setTextIdx] = useState(0);
  const [textVisible, setTextVisible] = useState(true);
  useEffect(() => {
    if (homeFlowPath === "few") return;
    const activeMessages = isRestaurantScan ? restaurantMessages : homeFlowPath === "notsure" ? notsureMessages : messages;
    const interval = setInterval(() => {
      setTextVisible(false);
      setTimeout(() => {
        setTextIdx(i => (i + 1) % activeMessages.length);
        setTextVisible(true);
      }, 400);
    }, 1800);
    return () => clearInterval(interval);
  }, [homeFlowPath, isRestaurantScan]); // eslint-disable-line react-hooks/exhaustive-deps
  const activeMessages = isRestaurantScan ? restaurantMessages : homeFlowPath === "notsure" ? notsureMessages : messages;
  return (
    <div style={{
      position: "fixed", inset: 0, background: "#FFFAF8", zIndex: 9999,
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      animation: "tpLoaderFadeIn 0.3s ease both",
    }}>
      <style>{`
        @keyframes tpLoaderFadeIn { from { opacity:0 } to { opacity:1 } }
        @keyframes tpBreath { 0%,100%{transform:scale(1)} 50%{transform:scale(1.08)} }
      `}</style>
      <div style={{ animation: "tpBreath 1.8s ease-in-out infinite" }}>
        <TruePourMark size={160} animated diamondColor="#1A1A1A" />
      </div>
      <div style={{
        marginTop: 32, fontSize: 16, fontFamily: F.body, color: C.burgundy,
        textAlign: "center",
        opacity: textVisible ? 1 : 0,
        transition: "opacity 0.4s ease",
      }}>
        {homeFlowPath === "few" ? "Ranking your bottles for tonight..." : activeMessages[textIdx]}
      </div>
    </div>
  );
}

function Spark({ size=20, color=C.burgundy, animated=false }) {
  const s = animated ? { animation:"sparkPulse 1.5s ease-in-out infinite" } : {};
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={s}>
      <path d="M12 2L13.8 9.2L21 12L13.8 14.8L12 22L10.2 14.8L3 12L10.2 9.2L12 2Z" fill={color} />
      <path d="M19 3L19.9 6.1L23 7L19.9 7.9L19 11L18.1 7.9L15 7L18.1 6.1L19 3Z" fill={color} opacity="0.55"/>
      <path d="M5 16L5.6 18.4L8 19L5.6 19.6L5 22L4.4 19.6L2 19L4.4 18.4L5 16Z" fill={color} opacity="0.35"/>
      <style>{`@keyframes sparkPulse{0%,100%{transform:scale(1) rotate(0deg);opacity:1}50%{transform:scale(1.2) rotate(15deg);opacity:0.8}}`}</style>
    </svg>
  );
}

// ─── PROFILE SUMMARY HELPER ───────────────────────────────────────────────────

// ─── SPECTRUM SLIDER ──────────────────────────────────────────────────────────
function SpectrumSlider({ question, value, onChange }) {
  const positions = question.positions;
  return (
    <div style={{ marginBottom:28, padding:"20px 16px 24px", background:C.cardBg, borderRadius:16, border:`1px solid ${C.divider}`, boxShadow:"0 2px 8px rgba(145,18,52,0.06)" }}>
      <div style={{ marginBottom:16 }}>
        <div style={{ fontSize:10, color:C.burgundy, letterSpacing:2, textTransform:"uppercase", marginBottom:4, fontFamily:F.body, fontWeight:600 }}>{question.axis}</div>
        <div style={{ fontSize:16, fontWeight:700, color:C.charcoal, fontFamily:F.display, lineHeight:1.3 }}>{question.question}</div>
        {question.subheading && <div style={{ fontSize:14, color:C.charcoalSoft, fontFamily:F.body, marginTop:4, lineHeight:1.4 }}>{question.subheading}</div>}
      </div>
      <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
        {positions.map((label, idx) => {
          const pos = idx + 1;
          const selected = value !== null && value !== 0 && value === pos;
          return (
            <div key={pos} onClick={() => onChange(pos)}
              style={{ minHeight:44, padding:"10px 16px", borderRadius:10, border:selected?`1.5px solid ${C.burgundy}`:"1px solid #ecddd7", background:selected?C.burgundyBg:"#ffffff", cursor:"pointer", display:"flex", alignItems:"center", gap:8 }}>
              <span style={{ fontSize:12, color:selected?C.burgundy:"#ccc", fontFamily:F.body, minWidth:16, marginRight:10, flexShrink:0, lineHeight:1 }}>{pos}</span>
              <span style={{ fontSize:15, fontFamily:F.body, fontWeight:selected?600:400, color:selected?C.burgundy:"#3a2a27", lineHeight:1.3 }}>
                {label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── STAR RATING ──────────────────────────────────────────────────────────────
function TeachStars({ onDone }) {
  const [teachValue, setTeachValue] = useState(0);
  const [fading, setFading] = useState(false);
  useEffect(() => {
    let current = 0;
    const interval = setInterval(() => {
      current += 0.5;
      setTeachValue(current);
      if (current >= 5) {
        clearInterval(interval);
        setTimeout(() => {
          setFading(true);
          setTimeout(() => { onDone(); }, 500);
        }, 500);
      }
    }, 180);
    return () => clearInterval(interval);
  }, []);
  const StarSVGTeach = ({ fill }) => (
    <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
      <defs>
        <clipPath id="teach-half-clip">
          <rect x="0" y="0" width="22" height="44" />
        </clipPath>
      </defs>
      <polygon points="22,4 27.5,17 42,18.5 31.5,28.5 34.5,42 22,35 9.5,42 12.5,28.5 2,18.5 16.5,17" fill="#F0E8E4" />
      {fill === 'full' && (
        <polygon points="22,4 27.5,17 42,18.5 31.5,28.5 34.5,42 22,35 9.5,42 12.5,28.5 2,18.5 16.5,17" fill={C.burgundy} />
      )}
      {fill === 'half' && (
        <polygon points="22,4 27.5,17 42,18.5 31.5,28.5 34.5,42 22,35 9.5,42 12.5,28.5 2,18.5 16.5,17" fill={C.burgundy} clipPath="url(#teach-half-clip)" />
      )}
    </svg>
  );
  return (
    <div style={{
      position: 'absolute', top: 0, left: 0,
      display: 'flex', gap: 4,
      pointerEvents: 'none',
      opacity: fading ? 0 : 1,
      transition: fading ? 'opacity 0.5s ease-out' : 'none',
      zIndex: 2,
    }}>
      {[1,2,3,4,5].map(i => {
        const fill = teachValue >= i ? 'full' : teachValue >= i - 0.5 ? 'half' : 'empty';
        return <StarSVGTeach key={i} fill={fill} />;
      })}
    </div>
  );
}

function StarRating({ value, onChange, hintVisible, onHintDismiss }) {
  const [hover, setHover] = useState(0);
  const displayValue = hover || value;
  const containerRef = useRef(null);
  const starsRef = useRef([]);
  const instanceId = useRef(`sr-${Math.random().toString(36).slice(2)}`).current;

  const StarSVG = ({ fill }) => (
    <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block', pointerEvents: 'none' }}>
      <defs>
        <clipPath id={`half-${instanceId}`}>
          <rect x="0" y="0" width="22" height="44" />
        </clipPath>
      </defs>
      <polygon points="22,4 27.5,17 42,18.5 31.5,28.5 34.5,42 22,35 9.5,42 12.5,28.5 2,18.5 16.5,17" fill="#F0E8E4" />
      {fill === 'full' && (
        <polygon points="22,4 27.5,17 42,18.5 31.5,28.5 34.5,42 22,35 9.5,42 12.5,28.5 2,18.5 16.5,17" fill={C.burgundy} />
      )}
      {fill === 'half' && (
        <polygon points="22,4 27.5,17 42,18.5 31.5,28.5 34.5,42 22,35 9.5,42 12.5,28.5 2,18.5 16.5,17" fill={C.burgundy} clipPath={`url(#half-${instanceId})`} />
      )}
    </svg>
  );

  return (
    <div>
      <div ref={containerRef}
        onTouchStart={(e) => {}}
        onTouchMove={(e) => {
          e.preventDefault();
          const touch = e.touches[0];
          let val = 0.5;
          for (let idx = 0; idx < starsRef.current.length; idx++) {
            const star = starsRef.current[idx];
            if (!star) continue;
            const rect = star.getBoundingClientRect();
            if (touch.clientX >= rect.left) {
              const mid = rect.left + rect.width / 2;
              val = touch.clientX < mid ? (idx + 1) - 0.5 : idx + 1;
            }
          }
          setHover(Math.max(0.5, Math.min(5, val)));
        }}
        onTouchEnd={() => {
          if (hover > 0) {
            onChange(hover);
            if (onHintDismiss) onHintDismiss();
          }
          setHover(0);
        }}
        style={{ display: 'flex', gap: 4 }}>
        {[1, 2, 3, 4, 5].map(i => {
          const fill = displayValue >= i ? 'full' : displayValue >= i - 0.5 ? 'half' : 'empty';
          return (
            <div key={i} ref={el => starsRef.current[i - 1] = el}
              style={{ position: 'relative', width: 44, height: 44, flexShrink: 0, cursor: 'pointer' }}
              onClick={(e) => {
                if (e.type === 'click') e.stopPropagation();
                const newVal = value === i ? i - 0.5 : i;
                onChange(newVal);
                if (onHintDismiss) onHintDismiss();
              }}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(0)}>
              <StarSVG fill={fill} />
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ─── CELLAR NOTE MODAL ────────────────────────────────────────────────────────
function CellarNoteModal({ wineName, onClose, rating, onRate, session, onAuthExpired }) {
  const [notes, setNotes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [confirmedName, setConfirmedName] = useState(null);
  const [showRatingHint, setShowRatingHint] = useState(true);
  const [noteError, setNoteError] = useState(null); // { status, message } | null — see STATUS_COPY
  const lookupName = confirmedName || wineName;
  const orderUrl = `https://click.linksynergy.com/deeplink?id=WUGIyhMYkU4&mid=2025&murl=${encodeURIComponent('https://www.wine.com/search/' + wineName.trim() + '/0')}`;

  useEffect(() => {
    setNotes(null);
    setSuggestions(null);
    setNoteError(null);
    const cacheKey = `truepour_cellarnotes_v2_${encodeURIComponent(lookupName)}`;
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) { setNotes(JSON.parse(cached)); return; }
    } catch {}
    setLoading(true);
    fetch('/api/cellar-notes', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}) },
      body: JSON.stringify({ wineName: lookupName })
    })
      .then(r => r.json().then(data => ({ status: r.status, data })))
      .then(({ status, data }) => {
        if (status === 401) { if (onAuthExpired) onAuthExpired({ status: 401 }); return; }
        if (data.error) { setNoteError({ status, message: typeof data.error === 'string' ? data.error : null }); return; }
        if (data.found === false) {
          setSuggestions(data.suggestions || []);
        } else {
          setNotes(data);
          try { localStorage.setItem(cacheKey, JSON.stringify(data)); } catch {}
        }
      })
      .catch(() => { setNoteError({ status: null, message: null }); })
      .finally(() => setLoading(false));
  }, [lookupName]);

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9000, display: 'flex', alignItems: 'flex-end' }}>
      <style>{`@keyframes tpSlideUp{from{transform:translateY(100%)}to{transform:translateY(0)}}`}</style>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#FFFAF8', borderRadius: '24px 24px 0 0', width: '100%', maxHeight: '85vh', overflowY: 'auto', padding: '20px 20px 40px', boxSizing: 'border-box', animation: 'tpSlideUp 0.25s ease' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 24, color: C.charcoalSoft, cursor: 'pointer', padding: '0 0 0 20px', lineHeight: 1 }}>×</button>
        </div>
        {notes && notes.producer ? (
          <>
            <div style={{ fontSize: 13, color: C.charcoalSoft, fontFamily: F.body, fontWeight: 400, marginBottom: 2, textTransform: 'uppercase', letterSpacing: 1.2 }}>{notes.producer}</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: C.charcoal, fontFamily: F.display, lineHeight: 1.2, marginBottom: 8 }}>{notes.wineName || wineName}</div>
          </>
        ) : (
          <div style={{ fontSize: 20, fontWeight: 800, color: C.charcoal, fontFamily: F.display, lineHeight: 1.2, marginBottom: 8 }}>{wineName}</div>
        )}
        {notes && (notes.region || notes.price_low || notes.ws_price_average) && (
          <div style={{ fontSize: 11, color: C.charcoalSoft, letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 14, fontFamily: F.body }}>
            {notes.ws_price_average ? (
              <>
                {notes.region && <span>{notes.region} · </span>}
                <span style={{ color: C.charcoal, fontWeight: 700 }}>Avg. ${Math.round(notes.ws_price_average)}</span>
                {notes.ws_score >= 90 && <span style={{ color: C.burgundy, fontWeight: 700 }}> · Critically Acclaimed</span>}
              </>
            ) : (
              [notes.region, (notes.price_low && notes.price_high) ? `$${notes.price_low}–$${notes.price_high}` : null].filter(Boolean).join(' · ')
            )}
          </div>
        )}
        {notes && notes.ws_price_average && (
          <div style={{ fontSize: 11, color: C.charcoalSoft, fontFamily: F.body, marginBottom: 8 }}>Data supplied by Wine-Searcher</div>
        )}
        {loading && (
          <div style={{ fontSize: 13, color: C.charcoalSoft, fontFamily: F.body, fontStyle: 'italic', marginBottom: 16 }}>Finding tasting notes…</div>
        )}
        {!loading && noteError && (() => { const copy = resolveErrorCopy(noteError); return (
          <div style={{ padding:"11px 14px", background:C.errorBg, border:`1px solid ${C.errorBorder}`, borderRadius:10, fontSize:13, color:C.error, fontFamily:F.body, marginBottom:16 }}>
            <div style={{ fontWeight:700, marginBottom:2 }}>{copy.heading}</div>
            <div>{copy.body}</div>
          </div>
        ); })()}
        {notes && notes.tasting_note && (
          <div style={{ fontSize: 14, color: C.charcoalMid, lineHeight: 1.65, fontFamily: F.body, marginBottom: 14 }}>{notes.tasting_note}</div>
        )}
        {notes && notes.food_pairings && (
          <div style={{ background: '#F7F0EC', borderRadius: 12, padding: '12px 14px', marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: C.burgundy, letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 3, fontFamily: F.body, fontWeight: 600 }}>Pairs well with</div>
            <div style={{ fontSize: 14, color: C.charcoalMid, lineHeight: 1.6, fontFamily: F.body }}>{notes.food_pairings}</div>
          </div>
        )}
        {suggestions !== null && !loading && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, color: C.charcoalSoft, fontFamily: F.body, fontStyle: 'italic', marginBottom: suggestions.length > 0 ? 10 : 0 }}>Tap to confirm this wine</div>
            {suggestions.map(s => (
              <button key={s} onClick={() => setConfirmedName(s)} style={{ display: 'block', width: '100%', textAlign: 'left', background: '#F7F0EC', border: 'none', borderRadius: 10, padding: '10px 14px', marginBottom: 8, fontSize: 14, fontFamily: F.body, color: C.charcoal, cursor: 'pointer' }}>{s}</button>
            ))}
          </div>
        )}
        <div onClick={(e) => e.stopPropagation()} style={{ marginBottom: 20 }}>
          <StarRating value={rating || 0} onChange={onRate} hintVisible={showRatingHint} onHintDismiss={() => setShowRatingHint(false)} />
        </div>
        <a href={orderUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'block', width: '100%', padding: '13px', borderRadius: 12, textAlign: 'center', textDecoration: 'none', background: C.burgundy, color: '#fff', fontSize: 14, fontWeight: 700, fontFamily: F.display, boxSizing: 'border-box' }}>
          Order this bottle →
        </a>
      </div>
    </div>
  );
}

// ─── CONFIDENCE TIER HELPER ───────────────────────────────────────────────────
// Untouched by claude/lowevidence-experience-build — every in-lean call
// site (matchStage absent or 'number') still resolves through this exact,
// unmodified function. The low-evidence badge/building/early_number cases
// are handled by getDisplayTier below, which wraps this rather than
// changing it.
const getConfidenceTier = (score) => {
  if (score >= 90) return { label: "True Match", symbol: "✦", color: C.burgundy, background: C.burgundyBg, description: "Built for your palate" };
  if (score >= 78) return { label: "Strong Match", symbol: "★", color: C.burgundy, background: "#fff3f0", description: "You'll love this" };
  if (score >= 65) return { label: "Good Match", symbol: "◈", color: "#E8956D", background: "#fff8f5", description: "Worth trying" };
  if (score >= 50) return { label: "Partial Match", symbol: "◎", color: "#E67E22", background: "#fff8f0", description: "Hit or miss" };
  if (score >= 35) return { label: "Style Mismatch", symbol: "⚠", color: "#999", background: "#f8f8f8", description: "Not your style" };
  return { label: "Wrong Fit", symbol: "✕", color: "#999", background: "#f8f8f8", description: "Skip this one" };
};

// ─── LOW-EVIDENCE OFF-LANE DISPLAY (design 100734b, decision B; copy
// revised in claude/offlane-structural-copy-build) ─────────────────────────
// A distinct badge, not a degraded percentage — the contract's own framing.
// 'building' and 'early_number' soften the transition rather than switching
// straight from no-number to an exact one the moment n stops being 0
// (matchStageForN, server.js): a first rating landing on a low real
// percentage would still read as "poor recommendation" one evidence step
// later, not never.
//
// Label: "New to your palate" was ambiguous (new to whom? the palate, the
// wine, the app?) and its old subtext ("We don't know your taste here
// yet") reads as system failure — a user who just finished a quiz and got
// an archetype expects to already be known, and "we don't know" sounds
// like a bug, not a feature that improves with input. "Still Calibrating"
// names an ongoing, unfinished PROCESS (nothing broken, a direction to
// grow in) rather than an absence — matching what the copy actually means:
// the taste profile is real and working, just calibrated toward the
// user's lean color so far.
const LOW_EVIDENCE_LABEL = { symbol: "✦", color: C.burgundy, background: C.burgundyBg };
const BUILDING_LABEL = { label: "Sharpening", symbol: "◐", color: "#E8956D", background: "#fff8f5" };

// Colors are named dynamically from server-attached leanColorLabel/
// offLaneColorLabel (server.js: LEAN_COLOR_LABEL/offLaneColorLabel) — never
// hardcoded red/white. Falls back to color-neutral wording only when a
// record predates this build and carries neither field (e.g. a persisted
// history entry).
function lowEvidenceDescription(wine, fallback) {
  if (wine && wine.leanColorLabel && wine.offLaneColorLabel) {
    return `Your taste profile is calibrated toward ${wine.leanColorLabel} — as you rate ${wine.offLaneColorLabel}, your matches will get sharper.`;
  }
  return fallback;
}

// Single shared entry point every match-percentage-rendering surface should
// call instead of getConfidenceTier directly, so all four surfaces (main
// card, TRUE MATCH ribbon, activity/history replay, and — once evaluate-*
// flows get off-lean evidence plumbing — their own cards) read the same
// stage from the same place. wine.matchStage absent or 'number' resolves
// through the untouched getConfidenceTier exactly as before this build —
// this is the proof mechanism for requirement 1, not an assertion: any
// caller that never sees a matchStage field takes an identical code path
// to before this build existed.
const getDisplayTier = (wine) => {
  const stage = wine && wine.matchStage;
  if (stage === 'badge') return {
    ...LOW_EVIDENCE_LABEL, stage, label: "Still Calibrating",
    description: lowEvidenceDescription(wine, "Your taste profile is still calibrated toward your usual lane — rate this to sharpen it."),
  };
  if (stage === 'building') return {
    ...BUILDING_LABEL, stage,
    description: lowEvidenceDescription(wine, "Sharpening your matches in this color as you rate."),
    progress: typeof wine.offLeanStructureWeight === 'number' ? wine.offLeanStructureWeight : 0,
  };
  if (stage === 'early_number') return { ...getConfidenceTier(wine ? wine.confidence : 0), stage, qualifier: 'Still learning' };
  return { ...getConfidenceTier(wine ? wine.confidence : 0), stage: 'number' };
};

// ─── VERDICT HELPER ───────────────────────────────────────────────────────────
// claude/confidence-contract-commit2 (goal 3): getVerdict and
// getConfidenceTier used to be two fully independent tier systems reading
// the same confidence number with different boundaries AND different
// labels — a real, observed contradiction: the bottle-match verdict panel
// (getVerdict) and the "Why It Works" section label directly below it on
// the SAME card (getConfidenceTier, called separately at its own render
// site) could show "Partial Match" and "Good Match" for the identical
// score (65-77 sat in getVerdict's 50-77 "partial" bucket but in
// getConfidenceTier's own 65-77 "Good Match" band). Resolved to ONE
// system: badge/symbol/color now come directly from getConfidenceTier —
// the same lookup every other surface in this file already uses — so the
// word on the verdict panel's badge and the word any other surface shows
// for the same number can never diverge again. The panel's richer visual
// language (background tint, border, headline, subline) getConfidenceTier
// doesn't carry is kept, grouped into the SAME three tone-bands this
// function already used (78/50 boundaries — both real getConfidenceTier
// band edges, not arbitrary) — no wine's headline tone moves as a result
// of this change, only the badge/symbol/color a reader could previously
// see contradict a second label on the same screen.
const getVerdict = (confidence) => {
  const tier = getConfidenceTier(confidence);
  let bg, border, headline, subline, bucket;
  if (confidence >= 78) {
    bucket = 'strong'; bg = '#F0F7F4'; border = 'rgba(46,125,82,0.25)';
    headline = 'Pour it.'; subline = 'This is exactly your style';
  } else if (confidence >= 50) {
    bucket = 'partial'; bg = '#FDF6EC'; border = 'rgba(199,125,30,0.25)';
    headline = 'Worth a glass.'; subline = 'A different style than you usually reach for';
  } else {
    bucket = 'mismatch'; bg = '#F5F0EE'; border = '#EDE8E6';
    headline = 'Skip it.'; subline = 'Not aligned with your palate';
  }
  return {
    tier: bucket,
    color: tier.color,
    bg,
    border,
    symbol: tier.symbol,
    badge: tier.label,
    headline,
    subline,
  };
};

// ─── WINE CARD ────────────────────────────────────────────────────────────────
function WineCardSkeleton({ index }) {
  const isTop = index === 0;
  return (
    <div style={{ background:C.cardBg, border:`2px solid ${C.divider}`, borderRadius:20, marginBottom:12, overflow:"hidden", boxShadow:"0 2px 12px rgba(0,0,0,0.06)", transform:`scale(${isTop?1:0.985})`, padding:"16px 18px" }}>
      <style>{`@keyframes shimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}`}</style>
      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:12 }}>
        <div style={{ flex:1 }}>
          <div style={{ height:10, width:"40%", borderRadius:6, background:"linear-gradient(90deg,#f0e8e4 25%,#fffaf8 50%,#f0e8e4 75%)", backgroundSize:"200% 100%", animation:"shimmer 1.5s infinite", marginBottom:8 }} />
          <div style={{ height:16, width:"60%", borderRadius:6, background:"linear-gradient(90deg,#f0e8e4 25%,#fffaf8 50%,#f0e8e4 75%)", backgroundSize:"200% 100%", animation:"shimmer 1.5s infinite", marginBottom:6 }} />
          <div style={{ height:12, width:"45%", borderRadius:6, background:"linear-gradient(90deg,#f0e8e4 25%,#fffaf8 50%,#f0e8e4 75%)", backgroundSize:"200% 100%", animation:"shimmer 1.5s infinite" }} />
        </div>
        <div style={{ width:70 }}>
          <div style={{ height:20, borderRadius:6, background:"linear-gradient(90deg,#f0e8e4 25%,#fffaf8 50%,#f0e8e4 75%)", backgroundSize:"200% 100%", animation:"shimmer 1.5s infinite", marginBottom:4 }} />
          <div style={{ height:10, borderRadius:6, background:"linear-gradient(90deg,#f0e8e4 25%,#fffaf8 50%,#f0e8e4 75%)", backgroundSize:"200% 100%", animation:"shimmer 1.5s infinite" }} />
        </div>
      </div>
      <div style={{ height:10, width:"30%", borderRadius:6, background:"linear-gradient(90deg,#f0e8e4 25%,#fffaf8 50%,#f0e8e4 75%)", backgroundSize:"200% 100%", animation:"shimmer 1.5s infinite", marginBottom:12 }} />
      <div style={{ height:12, width:"100%", borderRadius:6, background:"linear-gradient(90deg,#f0e8e4 25%,#fffaf8 50%,#f0e8e4 75%)", backgroundSize:"200% 100%", animation:"shimmer 1.5s infinite", marginBottom:8 }} />
      <div style={{ height:12, width:"90%", borderRadius:6, background:"linear-gradient(90deg,#f0e8e4 25%,#fffaf8 50%,#f0e8e4 75%)", backgroundSize:"200% 100%", animation:"shimmer 1.5s infinite", marginBottom:8 }} />
      <div style={{ height:12, width:"75%", borderRadius:6, background:"linear-gradient(90deg,#f0e8e4 25%,#fffaf8 50%,#f0e8e4 75%)", backgroundSize:"200% 100%", animation:"shimmer 1.5s infinite", marginBottom:16 }} />
      <div style={{ height:44, borderRadius:12, background:"linear-gradient(90deg,#f0e8e4 25%,#fffaf8 50%,#f0e8e4 75%)", backgroundSize:"200% 100%", animation:"shimmer 1.5s infinite" }} />
    </div>
  );
}

const REGIONAL_STYLES = {
  tuscany:     { bg:'#3a232b', img:'tuscany' },
  bordeaux:    { bg:'#4a3e28', img:'bordeaux' },
  burgundy:    { bg:'#3d2e22', img:'burgundy' },
  loire:       { bg:'#2b3438', img:'loire' },
  champagne:   { bg:'#635f4d', img:'champagne' },
  rhone:       { bg:'#3e3448', img:'rhone' },
  rioja:       { bg:'#4a2820', img:'rioja' },
  napa:        { bg:'#3d3420', img:'napa' },
  sonoma:      { bg:'#aacab5', img:'sonoma' },
  paso:        { bg:'#3b3720', img:'paso' },
  willamette:  { bg:'#352840', img:'willamette' },
  barossa:     { bg:'#5c3c2c', img:'barossa' },
  marlborough: { bg:'#b0e0c8', img:'marlborough' },
  mendoza:     { bg:'#2c2438', img:'mendoza' },
  piedmont:    { bg:'#3b3428', img:'piedmont' },
};

function getRegionalStyle(wine) {
  if (!wine) return null;
  const region    = (wine.region    || '').toLowerCase().trim();
  const subregion = (wine.subregion || '').toLowerCase().trim();
  const country   = (wine.country   || '').toLowerCase().trim();
  const combined  = region + ' ' + subregion;

  if (/tuscany|toscana|chianti|brunello|montalcino|montepulciano|bolgheri|super.?tuscan|tignanello|sassicaia|ornellaia/.test(combined)) return REGIONAL_STYLES.tuscany;
  if (/piedmont|piemonte|barolo|barbaresco|barbera d.asti|barbera d.alba|moscato d.asti|langhe|alba|asti(?! spumante)/.test(combined)) return REGIONAL_STYLES.piedmont;
  if (/bordeaux|pauillac|margaux|saint.?estephe|st.?estephe|saint.?julien|st.?julien|pomerol|saint.?emilion|st.?emilion|pessac|leognan|sauternes|barsac|graves|medoc|listrac|moulis/.test(combined)) return REGIONAL_STYLES.bordeaux;
  if (/burgundy|bourgogne|gevrey|chambolle|vosne|nuits.?saint|beaune|pommard|volnay|meursault|puligny|chassagne|montrachet|chablis|macon|pouilly.?fuisse|givry|mercurey|rully/.test(combined)) return REGIONAL_STYLES.burgundy;
  if (/champagne|reims|epernay/.test(combined)) return REGIONAL_STYLES.champagne;
  if (/rh[oô]ne|chateauneuf|châteauneuf|gigondas|vacqueyras|rasteau|cairanne|lirac|tavel|crozes.?hermitage|hermitage|cornas|saint.?joseph|condrieu|c[oô]te.?r[oô]tie/.test(combined)) return REGIONAL_STYLES.rhone;
  if (/loire|sancerre|pouilly.?fum[eé]|muscadet|vouvray|chinon|bourgueil|anjou|touraine|savennieres|quarts.?de.?chaume|coteaux.?du.?layon/.test(combined)) return REGIONAL_STYLES.loire;
  if (/rioja|ribera.?del.?duero|priorat|penedes|rias.?baixas|albari[nñ]o|rueda|toro|jumilla|monastrell/.test(combined)) return REGIONAL_STYLES.rioja;
  if (/napa|rutherford|oakville|stags.?leap|howell.?mountain|mount.?veeder|atlas.?peak|diamond.?mountain|spring.?mountain|st.?.?helena|calistoga|yountville|carneros(?=.*napa)/.test(combined)) return REGIONAL_STYLES.napa;
  if (/sonoma|russian.?river|alexander.?valley|dry.?creek|knights.?valley|chalk.?hill|bennett.?valley|moon.?mountain|petaluma/.test(combined)) return REGIONAL_STYLES.sonoma;
  if (/paso.?robles|paso|adelaida|templeton/.test(combined)) return REGIONAL_STYLES.paso;
  if (/willamette|dundee|eola|chehalem|yamhill|ribbon.?ridge/.test(combined) || (country.includes('oregon') && /pinot/.test((wine.varietal||'').toLowerCase()))) return REGIONAL_STYLES.willamette;
  if (/barossa|mclaren.?vale|eden.?valley|clare.?valley|coonawarra|margaret.?river|yarra|hunter.?valley|heathcote/.test(combined) || country.includes('australia')) return REGIONAL_STYLES.barossa;
  if (/marlborough|hawke|central.?otago|martinborough/.test(combined) || country.includes('new zealand')) return REGIONAL_STYLES.marlborough;
  if (/mendoza|lujan.?de.?cuyo|uco.?valley|valle.?de.?uco|malbec(?=.*argentina)|patagonia/.test(combined) || country.includes('argentina')) return REGIONAL_STYLES.mendoza;
  if (country.includes('france') && /alsace|provence|roussillon|languedoc|cahors|madiran|gaillac/.test(combined)) return REGIONAL_STYLES.rhone;
  return null;
}

function WineCard({ wine, index, isExpanded, onToggle, onRate, rating, tried, onTried, tags, onTag, onSave, saved, context, onImplicitSignal, onAddManually }) {
  const [showBuy, setShowBuy] = useState(false);
  const isTop = index === 0;
  if (wine.unknown) {
    // Honest "couldn't confirm this scan" state — no confidence tier, no
    // save/order CTA (nothing here has been identified), just the recovery path.
    return (
      <div style={{ background:C.cardBg, border:`2px solid ${C.divider}`, borderRadius:20, marginBottom:12, overflow:"hidden", boxShadow:"0 2px 12px rgba(0,0,0,0.06)", transform:`scale(${isTop?1:0.985})`, isolation:'isolate' }}>
        <div style={{ padding:"20px 16px" }}>
          <div style={{ fontSize:15, fontWeight:700, color:C.charcoal, fontFamily:F.display, marginBottom:8 }}>{wine.producer}</div>
          <div style={{ fontSize:14, color:C.charcoalSoft, lineHeight:1.6, fontFamily:F.body, marginBottom:16 }}>{wine.description}</div>
          <button onClick={onAddManually} style={{ width:"100%", padding:"12px", borderRadius:12, fontSize:14, fontWeight:600, cursor:"pointer", background:"transparent", border:`1px solid ${C.divider}`, color:C.charcoalSoft, fontFamily:F.body }}>
            Add it manually
          </button>
        </div>
      </div>
    );
  }
  const showExpanded = isExpanded || isTop;
  const retailers = wine.purchaseLinks || [
    { store:"Wine.com", url:(()=>{ const st=(wine.search_term||((wine.producer||'')+' '+(wine.wine||''))).trim(); return `https://click.linksynergy.com/deeplink?id=WUGIyhMYkU4&mid=2025&murl=${encodeURIComponent('https://www.wine.com/search/'+st+'/0')}`; })(), note:"Ships to most states" },
    { store:"Vivino", url:`https://www.vivino.com/search/wines?q=${encodeURIComponent(wine.search_term||wine.wine)}`, note:"Check local availability" },
    { store:"Total Wine", url:`https://www.totalwine.com/search/all?text=${encodeURIComponent(wine.search_term||wine.wine)}`, note:"In-store & ship" },
  ];
  return (
    <div style={{ background:C.cardBg, border:`2px solid ${C.divider}`, borderRadius:20, marginBottom:12, overflow:"hidden", boxShadow:"0 2px 12px rgba(0,0,0,0.06)", transform:`scale(${isTop?1:0.985})`, isolation:'isolate' }}>
      {isTop && wine.confidence >= 90 && !wine.lowEvidenceOffLane && (
        <div style={{ background:`linear-gradient(90deg, ${C.burgundy}, ${C.burgundyLight})`, padding:"6px 16px", display:"flex", alignItems:"center", gap:8 }}>
          <Spark size={14} color="#fff" />
          <span style={{ fontSize:13, fontWeight:700, letterSpacing:2, textTransform:"uppercase", color:"#fff", fontFamily:F.body }}>True Match</span>
        </div>
      )}
      {(() => {
        const rs = getRegionalStyle(wine);
        const tileImg = rs ? REGION_TILES[rs.img] : null;

        // Country fallback: dark panel with country-derived accent when no regional tile
        const countryFallback = (() => {
          const c = (wine.country || '').toLowerCase();
          if (c.includes('portugal'))     return { bg:'#3a1a2e', accent:'#E070B0' };
          if (c.includes('spain'))        return { bg:'#2e1a1a', accent:'#C84040' };
          if (c.includes('germany'))      return { bg:'#1a2030', accent:'#6080C0' };
          if (c.includes('austria'))      return { bg:'#1e2820', accent:'#60A060' };
          if (c.includes('south africa')) return { bg:'#1a2818', accent:'#FFB612' };
          if (c.includes('chile'))        return { bg:'#1a2228', accent:'#4090A0' };
          if (c.includes('australia'))    return { bg:'#2a1e18', accent:'#C08040' };
          if (c.includes('new zealand'))  return { bg:'#182820', accent:'#40A870' };
          if (c.includes('argentina'))    return { bg:'#1a1f2e', accent:'#74ACDF' };
          if (c.includes('france'))       return { bg:'#28203a', accent:'#C0A0E0' };
          if (c.includes('italy'))        return { bg:'#2a1a1a', accent:'#C07060' };
          if (c.includes('united states') || c.includes('usa') || c.includes('u.s')) return { bg:'#1e1a1a', accent:'#C04040' };
          return { bg:'#1a1520', accent:C.burgundy };
        })();

        const panelBg = (rs && tileImg) ? rs.bg : countryFallback.bg;
        const panelAccent = countryFallback.accent;
        const isLightTile = rs && rs.img === 'sonoma';
        const isBarossaTile = rs && rs.img === 'barossa';

        return (
          <div style={{ position:'relative', minHeight:124, background:panelBg, display:'flex', alignItems:'center', borderRadius:'0px' }}>
            {rs && tileImg && (
              <>
                <div style={{
                  position:'absolute', left:0, right:0, top:0, bottom:0,
                  backgroundImage:`url(${tileImg})`,
                  backgroundRepeat:'no-repeat',
                  backgroundSize:'auto 132px',
                  backgroundPosition: isBarossaTile ? 'right -20px center' : 'right center',
                }} />
                <div style={{
                  position:'absolute', left:0, top:0, bottom:0, width:'100%', zIndex:1,
                  background:`linear-gradient(to right, ${panelBg} 0%, ${panelBg} 58%, ${panelBg}fa 68%, ${panelBg}ee 74%, ${panelBg}99 84%, ${panelBg}22 92%, transparent 100%)`,
                }} />
              </>
            )}
            {!(rs && tileImg) && (
              <>
                <div style={{
                  position:'absolute', right:0, top:0, bottom:0, width:'55%',
                  background:`linear-gradient(to left, ${panelAccent}88 0%, ${panelAccent}44 40%, transparent 100%)`,
                  zIndex:1,
                }} />
                <div style={{
                  position:'absolute', right:14, top:'50%', transform:'translateY(-50%)',
                  fontSize:52, opacity:0.13, color:panelAccent, zIndex:1,
                  fontFamily:'serif', userSelect:'none', lineHeight:1,
                }} >✦</div>
              </>
            )}
            <div style={{ position:'relative', zIndex:2, padding:'0 20px', maxWidth:'74%' }}>
              <div style={{ fontSize:10, color: isLightTile ? 'rgba(20,30,24,0.65)' : 'rgba(255,255,255,0.5)', letterSpacing:1.8, textTransform:'uppercase', marginBottom:3, fontFamily:F.body }}>
                {(() => {
                  const region = wine.region || wine.subregion || '';
                  const country = (wine.country || '').trim();
                  const isUS = country.toLowerCase().includes('united states') || country.toLowerCase() === 'usa' || country.toLowerCase() === 'u.s.';
                  if (!country) return region;
                  if (isUS) {
                    // For US wines extract state from region string if present
                    const stateMatch = region.match(/,\s*(California|Oregon|Washington|New York|Texas|Virginia|Colorado|Arizona|Idaho|New Mexico|Michigan|Ohio|Missouri|Maryland|Pennsylvania|North Carolina|Georgia|Florida)$/i);
                    const state = stateMatch ? stateMatch[1] : null;
                    if (state) return `${region.replace(stateMatch[0], '')} · ${state}`;
                    // Check subregion for state
                    const sub = (wine.subregion || '').trim();
                    if (sub && sub !== region) return `${region} · ${sub}`;
                    return region;
                  }
                  // For non-US: show region · country but avoid redundancy
                  if (region.toLowerCase().includes(country.toLowerCase())) return region;
                  return region ? `${region} · ${country}` : country;
                })()}
              </div>
              <div style={{ fontSize:17, fontWeight:700, color:'#fff', fontFamily:F.display, lineHeight:1.2, marginBottom:2 }}>{wine.producer}</div>
              <div style={{ fontSize:13, color:'rgba(255,255,255,0.62)', fontFamily:F.body, marginBottom:4 }}>
                {wine.wine}{wine.vintage && wine.vintage !== 'NV' ? ` ${wine.vintage}` : ''}
              </div>
              {(() => {
                // RESTAURANT (Commit 2): show the CAPTURED MENU price for the chosen
                // service format — never the external Wine-Searcher average (wildly
                // wrong at restaurant markups). Absent price → honest "check the menu",
                // never 0/NaN/borrowed. Gated to restaurant only; home/online/store
                // keep the ws_price_average path below exactly as before.
                if (context === 'restaurant') {
                  // formatUncertain (Commit 2.1): we couldn't confirm this wine is
                  // available in the user's chosen service format (no clear header,
                  // price, or model signal). Kept eligible, but say so honestly and
                  // quietly — same non-alarming tone as the absent-price state.
                  const priceNode = (wine.menuPriceCaptured && wine.menuPrice != null)
                    ? <div style={{ fontSize:15, fontWeight:700, color:'#fff', fontFamily:F.display }}>${Math.round(wine.menuPrice)}{wine.serviceFormat === 'glass' ? ' / glass' : ''}</div>
                    : <div style={{ fontSize:12, fontWeight:600, color:'rgba(255,255,255,0.72)', fontFamily:F.body }}>Check the menu for pricing</div>;
                  return (<>
                    {priceNode}
                    {/* claude/wine-quality-signal-unification (goal 3): this branch never
                        checked ws_score — it's nested nowhere near the WS-price gate below
                        (restaurant deliberately never shows ws_price_average, which is
                        unrelated to whether the wine itself carries a real critic score),
                        so the badge was structurally unreachable here even though both
                        the structured and legacy restaurant paths already attach ws_score. */}
                    {wine.ws_score >= 90 && (
                      <div style={{ fontSize:9, color:'rgba(255,255,255,0.85)', letterSpacing:1.5, textTransform:'uppercase', fontFamily:F.body, marginTop:2 }}>Critically Acclaimed</div>
                    )}
                    {wine.formatUncertain && (
                      <div style={{ fontSize:10, fontWeight:600, color:'rgba(255,255,255,0.6)', fontFamily:F.body, marginTop:2 }}>Couldn't confirm by the {wine.serviceFormat === 'glass' ? 'glass' : 'bottle'} — check the menu</div>
                    )}
                    {wine.budgetBypassed && (
                      <div style={{ fontSize:10, fontWeight:600, color:'rgba(255,255,255,0.6)', fontFamily:F.body, marginTop:2 }}>Price unreadable — may be outside your budget</div>
                    )}
                  </>);
                }
                // STORE (Commit 2): show the CAPTURED SHELF price — never the external
                // Wine-Searcher national average, which is misleading in-aisle (e.g. WS
                // returning $61.27 for a Napa Hess bottle whose shelf tag reads $16.79).
                // Absent/ambiguous capture → honest "Check the shelf tag", same quiet
                // treatment as restaurant's absent state. Gated to store only — does NOT
                // fall through to ws_price_average below. home/online unaffected.
                if (context === 'store') {
                  return (wine.shelfPriceCaptured && wine.shelfPrice != null)
                    ? <div style={{ fontSize:15, fontWeight:700, color:'#fff', fontFamily:F.display }}>${Math.round(wine.shelfPrice)}</div>
                    : <div style={{ fontSize:12, fontWeight:600, color:'rgba(255,255,255,0.72)', fontFamily:F.body }}>Check the shelf tag</div>;
                }
                if (wine.ws_price_average) return (<>
                  <div style={{ fontSize:15, fontWeight:700, color:'#fff', fontFamily:F.display }}>Avg. ${Math.round(wine.ws_price_average)}</div>
                  {wine.ws_score >= 90 && <div style={{ fontSize:9, color: isLightTile ? 'rgba(20,30,24,0.75)' : 'rgba(255,255,255,0.7)', letterSpacing:1.5, textTransform:'uppercase', fontFamily:F.body, marginTop:2 }}>Critically Acclaimed</div>}
                </>);
                if (wine.cipher982_price) return <div style={{ fontSize:15, fontWeight:700, color:'#fff', fontFamily:F.display }}>~${Math.round(wine.cipher982_price)}</div>;
                if (wine.price_low && wine.price_high) return <div style={{ fontSize:15, fontWeight:700, color:'#fff', fontFamily:F.display }}>${wine.price_low}–${wine.price_high}</div>;
                return null;
              })()}
            </div>
          </div>
        );
      })()}
        <div style={{ padding:"0 16px 16px" }}>
        <div style={{ height:12 }} />
        <div style={{ display:"flex", gap:5, marginBottom:10, flexWrap:"wrap" }}>
          {(wine.style_tags||wine.styleTags||[]).map(t=>(
            <span key={t} style={{ fontSize:12, color:C.burgundy, background:C.burgundyBg, padding:"2px 9px", borderRadius:20, border:`1px solid ${C.burgundyMid}`, fontFamily:F.body, fontWeight:500 }}>{t}</span>
          ))}
        </div>

        {(()=>{ const tier = getDisplayTier(wine); return (
          <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:10 }}>
            <div style={{ display:"flex", flexDirection:"column", gap:2 }}>
              <div style={{ fontSize:13, fontWeight:700, color:tier.color, fontFamily:"var(--fd)" }}>{tier.symbol} {tier.label}</div>
              <div style={{ fontSize:12, color:"#bbb", fontFamily:"var(--fb)" }}>{tier.description}</div>
            </div>
            {tier.stage === 'badge' ? null :
             tier.stage === 'building' ? (
              <div style={{ width:64, height:6, background:"rgba(0,0,0,0.06)", borderRadius:6, overflow:"hidden", alignSelf:"center" }}>
                <div style={{ width:`${Math.round((tier.progress||0)*100)}%`, height:"100%", background:tier.color, borderRadius:6 }} />
              </div>
            ) : (
              <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:2 }}>
                <div style={{ fontSize:14, color:C.burgundy, fontFamily:F.body, fontWeight:700, alignSelf:"flex-end" }}>{wine.confidence}% match</div>
                {tier.stage === 'early_number' && <div style={{ fontSize:10, color:"#999", fontFamily:F.body, fontWeight:600, textTransform:"uppercase", letterSpacing:1 }}>{tier.qualifier}</div>}
              </div>
            )}
          </div>
        ); })()}
        {wine.communityRating && wine.communityCount >= 3 ? (
          <div style={{ display:'flex', alignItems:'center', gap:20, paddingTop:10, marginTop:10, borderTop:'1px solid #f0e8e4' }}>
            {wine.communityRating && wine.communityCount >= 3 && (
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <div style={{ display:'flex', alignItems:'center', gap:3 }}>
                  <span style={{ fontSize:13 }}>⭐</span>
                  <span style={{ fontSize:13, fontWeight:700, color:'#3a2a27', fontFamily:'var(--fb)' }}>{wine.communityRating}</span>
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:1 }}>
                  <span style={{ fontSize:11, fontWeight:700, color:'#888', fontFamily:'var(--fd)', letterSpacing:1.2, textTransform:'uppercase' }}>Members</span>
                  <span style={{ fontSize:12, color:'#bbb', fontFamily:'var(--fb)' }}>{wine.communityCount} {wine.communityCount === 1 ? 'rating' : 'ratings'}</span>
                </div>
              </div>
            )}
          </div>
        ) : null}

        <div style={{ fontSize:15, color:C.charcoalMid, lineHeight:1.65, fontFamily:F.body, marginBottom:12 }}>{wine.description}</div>

        {showExpanded && (
          <div style={{ marginBottom:12 }}>
            <div style={{ padding:"10px 14px", background:"#fff", borderRadius:10, borderLeft:`3px solid ${C.burgundy}`, marginBottom:8 }}>
              <div style={{ fontSize:12, color:C.burgundy, letterSpacing:1.5, textTransform:"uppercase", marginBottom:3, fontFamily:F.body, fontWeight:600 }}>Why it works</div>
              <div style={{ fontSize:14, color:C.charcoalMid, lineHeight:1.6, fontFamily:F.body }}>{wine.pairing_reason || wine.pairingReason}</div>
            </div>
          </div>
        )}

        {wine.ws_price_average && (context === 'online' || context === 'home') && (
          <div style={{ fontSize:10, color:C.charcoalSoft, fontFamily:F.body, marginTop:3 }}>Data supplied by Wine-Searcher</div>
        )}
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          {/* Primary CTA — full width */}
          {context === "online" || context === undefined ? (
            <button onClick={()=>{ fireEvent('find_bottle_clicked', { producer: wine.producer || '', wine_name: wine.wine || wine.name || '', confidence: wine.confidence || 0 }); const searchTerm = (wine.search_term || ((wine.producer||'') + ' ' + (wine.name||wine.wine||''))).trim(); const dest = encodeURIComponent(`https://www.wine.com/search/${searchTerm}/0`); window.location.href = `https://click.linksynergy.com/deeplink?id=WUGIyhMYkU4&mid=2025&murl=${dest}`; }} style={{ width:"100%", padding:"13px", borderRadius:12, fontSize:14, fontWeight:700, cursor:"pointer", background:C.burgundy, color:"#fff", border:"none", fontFamily:F.display, transition:"all 0.2s" }}>
              Order this bottle →
            </button>
          ) : context === "restaurant" ? (
            <button onClick={()=>{ onSave(); onImplicitSignal('add_to_cellar'); }} style={{ width:"100%", padding:"13px", borderRadius:12, fontSize:14, fontWeight:700, cursor:"pointer", background:C.burgundy, color:"#fff", border:"none", fontFamily:F.display, transition:"all 0.2s" }}>
              {saved ? "✓ Saved to cellar" : "Save to my cellar →"}
            </button>
          ) : context === "store" ? (
            <button onClick={()=>{ onSave(); onImplicitSignal('add_to_cellar'); }} style={{ width:"100%", padding:"13px", borderRadius:12, fontSize:14, fontWeight:700, cursor:"pointer", background:C.burgundy, color:"#fff", border:"none", fontFamily:F.display, transition:"all 0.2s" }}>
              {saved ? "✓ Saved to cellar" : "Save to my cellar →"}
            </button>
          ) : context === "home" ? (
            <button onClick={()=>{ onSave(); onImplicitSignal('add_to_cellar'); }} style={{ width:"100%", padding:"13px", borderRadius:12, fontSize:14, fontWeight:700, cursor:"pointer", background:C.burgundy, color:"#fff", border:"none", fontFamily:F.display, transition:"all 0.2s" }}>
              {saved ? "✓ Saved — reorder from cellar anytime" : "Open this tonight →"}
            </button>
          ) : null}
          {/* Secondary row */}
          <div style={{ display:"flex", gap:8 }}>
            {context === "online" || context === undefined ? (
              <button onClick={()=>{ onSave(); onImplicitSignal('add_to_cellar'); }} style={{ flex:1, padding:"10px", borderRadius:12, fontSize:13, cursor:"pointer", background:saved?C.burgundyBg:"transparent", border:`${saved?"1.5px":"1px"} solid ${C.burgundy}`, color:C.burgundy, fontFamily:F.body, transition:"all 0.2s" }}>
                {saved ? "✓ Saved" : "Save it — find it again anytime"}
              </button>
            ) : context === "home" ? (
              <button onClick={()=>{ fireEvent('find_bottle_clicked', { producer: wine.producer || '', wine_name: wine.wine || wine.name || '', confidence: wine.confidence || 0 }); const searchTerm = (wine.search_term || ((wine.producer||'') + ' ' + (wine.name||wine.wine||''))).trim(); const dest = encodeURIComponent(`https://www.wine.com/search/${searchTerm}/0`); window.location.href = `https://click.linksynergy.com/deeplink?id=WUGIyhMYkU4&mid=2025&murl=${dest}`; }} style={{ flex:1, padding:"10px", borderRadius:12, fontSize:13, fontWeight:600, cursor:"pointer", background:"transparent", border:`1px solid ${C.divider}`, color:C.charcoalSoft, fontFamily:F.body, transition:"all 0.2s" }}>
                Order this bottle →
              </button>
            ) : null}
            {index > 0 && (
              <button onClick={()=>{ onToggle(); if (!isExpanded) onImplicitSignal('why_tapped'); }} style={{ flex:1, padding:"10px", borderRadius:12, fontSize:13, cursor:"pointer", background:"transparent", border:`1px solid ${C.divider}`, color:C.charcoalSoft, fontFamily:F.body }}>
                {isExpanded ? "Less" : "Why this wine?"}
              </button>
            )}
          </div>
        </div>

        {showBuy && (
          <div style={{ marginTop:10, display:"flex", flexDirection:"column", gap:6 }}>
            <div style={{ fontSize:12, color:C.charcoalSoft, fontFamily:F.body, marginBottom:2 }}>Searching for "{wine.producer} {wine.wine}":</div>
            {retailers.map(r=>(
              <a key={r.name} href={r.url} target="_blank" rel="noopener noreferrer" style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 14px", borderRadius:10, textDecoration:"none", background:C.burgundyBg, border:`1px solid ${C.burgundyMid}`, transition:"background 0.2s" }}
                onMouseEnter={e=>e.currentTarget.style.background=C.burgundyMid}
                onMouseLeave={e=>e.currentTarget.style.background=C.burgundyBg}>
                <span style={{ fontSize:14, fontWeight:600, color:C.charcoal, fontFamily:F.body }}>{r.store||r.name}</span>
                <span style={{ fontSize:13, color:C.charcoalSoft, fontFamily:F.body }}>{r.note} →</span>
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── IMAGE CAPTURE ─────────────────────────────────────────────────────────────
function ImageCapture({ mode, onCapture, onClose, session, onAuthExpired }) {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [capturedRows, setCapturedRows] = useState([]);
  const [scanError, setScanError] = useState(null);
  const cfg = {
    restaurant: { title:"Scan the wine list", sub:"Any page works — point and shoot", icon:"📋" },
    store:      { title:"Scan the shelf", sub:"Point at the wines in front of you", icon:"🛒" },
    home:       { title:"Scan your bottles", sub:"Point at your wine rack or any bottle", icon:"🍾" },
    dish:       { title:"Scan the menu item", sub:"Show us what you're ordering", icon:"🍽️" },
    winescan:   { title:"Point at the label", sub:"We'll identify the wine instantly", icon:"🍷" },
  }[mode] || { title:"Take a photo", sub:"", icon:"📷" };

  const handleFile = async (file) => {
    if (!file) return;
    setScanning(true);
    setScanError(null);
    setResult(null);
    try {
      const { base64, mediaType, previewUrl } = await compressImageForScan(file);
      if (previewUrl) setPreview(previewUrl);
      const { result: res, status, rows } = await scanImage(base64, mediaType, mode, session);
      // Only a non-empty parsed result (status 'ok') is a usable "found wines"
      // state. 'no_wines'/empty and thrown errors go to scanError — NEVER to
      // result, so the error/empty text can never be passed to onCapture.
      if (status === 'ok' && res && res.trim()) {
        setResult(res);
        setCapturedRows(Array.isArray(rows) ? rows : []);
      } else {
        setScanError("No wines detected — try a clearer photo, better lighting, or enter manually.");
      }
    } catch (err) {
      if (onAuthExpired && onAuthExpired(err)) {
        if (onClose) onClose();
      } else {
        const tooLarge = /too large|exceeds|image_too_large/i.test((err && err.message) || '');
        setScanError(tooLarge ? "That photo is too large to process — try again or use a slightly smaller image." : "Scan didn't go through — check your connection and try again.");
      }
    }
    setScanning(false);
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(45,45,45,0.7)", backdropFilter:"blur(4px)", zIndex:100, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
      <div style={{ background:C.cream, borderRadius:24, padding:24, maxWidth:360, width:"100%", boxShadow:"0 20px 60px rgba(0,0,0,0.2)" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:18 }}>
          <div>
            <div style={{ fontSize:17, fontWeight:700, color:C.charcoal, fontFamily:F.display }}>{cfg.title}</div>
            <div style={{ fontSize:13, color:C.charcoalSoft, fontFamily:F.body, marginTop:2 }}>{cfg.sub}</div>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", color:C.charcoalSoft, fontSize:20, cursor:"pointer", lineHeight:1 }}>✕</button>
        </div>

        {!preview ? (
          <div>
            <div onClick={()=>fileRef.current?.click()} style={{ borderRadius:16, padding:"32px 20px", textAlign:"center", cursor:"pointer", background:C.burgundy, display:"flex", flexDirection:"column", alignItems:"center", gap:10 }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={C.cream} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                <circle cx="12" cy="13" r="4"/>
              </svg>
              <div style={{ fontSize:15, fontWeight:600, color:C.cream, fontFamily:F.display }}>Tap to take a photo</div>
              <div style={{ fontSize:13, color:C.cream, opacity:0.8, fontFamily:F.body }}>or upload from your camera roll</div>
            </div>
            <input ref={fileRef} type="file" accept="image/*" style={{ display:"none" }} onChange={e=>handleFile(e.target.files?.[0])} />
          </div>
        ) : (
          <div>
            <img src={preview} alt="scan" style={{ width:"100%", borderRadius:12, marginBottom:12, maxHeight:180, objectFit:"cover" }} />
            {scanning ? (
              <div style={{ textAlign:"center", padding:20 }}>
                <div style={{ display:"flex", justifyContent:"center", marginBottom:8 }}><Spark size={32} color={C.burgundy} animated /></div>
                <div style={{ fontSize:15, color:C.charcoalMid, fontFamily:F.body }}>Finding the wines…</div>
              </div>
            ) : scanError ? (
              <div>
                <div style={{ fontSize:13, color:C.error, fontFamily:F.body, background:C.errorBg, border:`1px solid ${C.errorBorder}`, borderRadius:10, padding:"11px 14px", lineHeight:1.5, marginBottom:12 }}>{scanError}</div>
                <button onClick={()=>{ setPreview(null); setResult(null); setScanError(null); }} style={{ width:"100%", padding:9, borderRadius:10, background:"transparent", border:`1px solid ${C.divider}`, color:C.charcoalMid, cursor:"pointer", fontFamily:F.body, fontSize:13 }}>Retake</button>
              </div>
            ) : result && (
              <div>
                <div style={{ fontSize:13, color:C.charcoalSoft, fontFamily:F.body, marginBottom:6 }}>Here's what we found:</div>
                <div style={{ fontSize:13, color:C.charcoal, fontFamily:F.body, background:C.burgundyBg, borderRadius:10, padding:10, maxHeight:120, overflowY:"auto", lineHeight:1.8, whiteSpace:"pre-wrap", marginBottom:12 }}>{result}</div>
                <div style={{ display:"flex", gap:8 }}>
                  <button onClick={()=>{ setPreview(null); setResult(null); setScanError(null); }} style={{ flex:1, padding:9, borderRadius:10, background:"transparent", border:`1px solid ${C.divider}`, color:C.charcoalMid, cursor:"pointer", fontFamily:F.body, fontSize:13 }}>Retake</button>
                  <button onClick={()=>onCapture(result, capturedRows)} style={{ flex:2, padding:9, borderRadius:10, background:"transparent", border:`1.5px solid ${C.burgundy}`, color:C.burgundy, cursor:"pointer", fontFamily:F.display, fontSize:14, fontWeight:700 }}>{mode === 'winescan' ? 'Check my match →' : 'Use these →'}</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── MY CELLAR DRAWER ─────────────────────────────────────────────────────────
// ─── WINE LABEL DATABASE — instant, no API needed ────────────────────────────

function normalizeStr(str) {
  return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
}

function searchWineLabels(query) {
  if (!query || query.length < 2) return [];
  const tokens = normalizeStr(query).split(/\s+/).filter(t => t.length > 1);
  if (tokens.length === 0) return [];
  const scored = WINE_LABELS
    .filter(w => {
      const lower = normalizeStr(w);
      return tokens.every(t => lower.includes(t));
    })
    .map(w => {
      const lower = normalizeStr(w);
      const score = lower.startsWith(tokens[0]) ? 0 : lower.includes(tokens[0]) ? 1 : 2;
      return { w, score };
    })
    .sort((a, b) => a.score - b.score);
  return scored.slice(0, 6).map(s => s.w);
}


function WineAutocomplete({ onSelect, mode = "signal" }) {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [focused, setFocused] = useState(false);
  const [pending, setPending] = useState(null);
  const [notFound, setNotFound] = useState(false);
  // Evaluation mode only: suggestions come from the real server catalog
  // (see /api/wine-suggest) instead of the smaller client-bundled
  // WINE_LABELS list — debounced and cached so typing never waits on it.
  // Submission (attemptAdd) never awaits this; it's a suggestion, not a gate.
  const suggestCacheRef = useRef(new Map());
  const suggestDebounceRef = useRef(null);
  const suggestAbortRef = useRef(null);
  useEffect(() => () => { if (suggestDebounceRef.current) clearTimeout(suggestDebounceRef.current); }, []);

  const fetchCatalogSuggestions = (val) => {
    if (suggestDebounceRef.current) clearTimeout(suggestDebounceRef.current);
    if (val.length < 2) { setSuggestions([]); return; }
    const cached = suggestCacheRef.current.get(val);
    if (cached) { setSuggestions(cached); return; }
    suggestDebounceRef.current = setTimeout(async () => {
      if (suggestAbortRef.current) suggestAbortRef.current.abort();
      const ctrl = new AbortController();
      suggestAbortRef.current = ctrl;
      try {
        const res = await fetch(`/api/wine-suggest?q=${encodeURIComponent(val)}`, { signal: ctrl.signal });
        if (!res.ok) return;
        const data = await res.json();
        const list = Array.isArray(data.suggestions) ? data.suggestions : [];
        suggestCacheRef.current.set(val, list);
        setSuggestions(list);
      } catch (e) { /* offline/aborted — leave typing unaffected, no suggestions is fine */ }
    }, 300);
  };

  const findMatch = (input) => {
    if (!input || input.length < 2) return null;
    const norm = s => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const inp = norm(input);
    const exact = WINE_LABELS.find(w => norm(w) === inp);
    if (exact) return exact;
    const contained = WINE_LABELS.find(w => norm(w).length >= 10 && inp.includes(norm(w)));
    if (contained) return contained;
    const STOPS = new Set(['wine','wines','winery','vineyards','vineyard','cellars','cellar','estate','estates','family','valley','napa','sonoma','the','and','von','de','du','di','del','by','chateau','domaine','maison','bodega','castello','tenuta','weingut']);
    const VARS = new Set(['cabernet','sauvignon','pinot','noir','blanc','chardonnay','merlot','zinfandel','syrah','shiraz','riesling','malbec','grenache','viognier','rose','prosecco','champagne','cava','sangiovese','nebbiolo','tempranillo','mourvedre','primitivo','barbera','cab']);
    const inputTokens = input.toLowerCase().split(/\s+/).map(norm).filter(t => t.length > 2);
    const producerTokens = inputTokens.filter(t => !STOPS.has(t) && !VARS.has(t));
    const varietalTokens = inputTokens.filter(t => VARS.has(t));
    if (producerTokens.length === 0) return null;
    let bestScore = 0, bestMatch = null;
    for (const label of WINE_LABELS) {
      const parts = label.split(' ');
      const labelProducerTokens = parts.slice(0, Math.ceil(parts.length / 2)).map(norm).filter(t => t.length > 2 && !STOPS.has(t));
      if (labelProducerTokens.length === 0) continue;
      const producerMatch = labelProducerTokens.some(pt =>
        producerTokens.some(it => (it.length >= 4 && pt.startsWith(it)) || (pt.length >= 4 && it.startsWith(pt)))
      );
      if (!producerMatch) continue;
      if (varietalTokens.length > 0) {
        const winePartNorm = norm(parts.slice(Math.ceil(parts.length / 2)).join(' '));
        const allMatch = varietalTokens.every(vt => {
          if (vt === 'cab') return winePartNorm.includes('cabernet');
          return winePartNorm.includes(vt);
        });
        if (!allMatch) continue;
      }
      const score = labelProducerTokens.filter(pt =>
        producerTokens.some(it => (it.length >= 4 && pt.startsWith(it)) || (pt.length >= 4 && it.startsWith(pt)))
      ).length * 3;
      if (score > bestScore) { bestScore = score; bestMatch = label; }
    }
    return bestScore >= 3 ? bestMatch : null;
  };

  const handleChange = (val) => {
    setQuery(val);
    setNotFound(false);
    if (mode === "evaluation") { fetchCatalogSuggestions(val); return; }
    if (val.length < 2) { setSuggestions([]); return; }
    setSuggestions(searchWineLabels(val));
  };

  const attemptAdd = (input) => {
    const trimmed = input.trim();
    if (!trimmed) return;
    if (mode === "evaluation") {
      // Typed text reaches the server exactly like a scanned label does —
      // never gated on a match against any client-side name list. The
      // catalog suggestions above are a convenience, not a requirement.
      onSelect(trimmed);
      setQuery(""); setSuggestions([]); setNotFound(false);
      return;
    }
    const match = findMatch(trimmed);
    if (match) {
      if (mode === "signal") {
        onSelect(match);
        setQuery(""); setSuggestions([]); setNotFound(false);
      } else {
        setPending({ input: trimmed, match });
        setQuery(""); setSuggestions([]);
      }
    } else {
      setNotFound(true);
      setQuery(""); setSuggestions([]);
    }
  };

  const handleSelect = (wine) => {
    // Evaluation mode: a clicked suggestion fills the box for the user to
    // review/edit and submit themselves — it never auto-submits, so a
    // suggestion is always optional, never a forced choice.
    if (mode === "evaluation") { setQuery(wine); setSuggestions([]); setFocused(false); return; }
    attemptAdd(wine); setFocused(false);
  };
  const handleAdd = () => { attemptAdd(query); };
  const confirmPending = () => { if (pending) { onSelect(pending.match); setPending(null); } };
  const rejectPending = () => { setPending(null); setNotFound(false); };

  return (
    <div style={{ marginBottom: 14 }}>
      {pending && (
        <div style={{ padding:"12px 14px", background:"#fff", border:`1.5px solid ${C.burgundy}`, borderRadius:12, marginBottom:10 }}>
          <div style={{ fontSize:13, color:C.charcoalSoft, fontFamily:F.body, marginBottom:6 }}>Is this the wine you meant?</div>
          <div style={{ fontSize:15, fontWeight:600, color:C.charcoal, fontFamily:F.display, marginBottom:10 }}>{pending.match}</div>
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={confirmPending} style={{ flex:1, padding:"9px", borderRadius:10, background:"transparent", border:`1.5px solid ${C.burgundy}`, color:C.burgundy, fontSize:14, fontWeight:700, fontFamily:F.display, cursor:"pointer" }}>✓ Yes, that's it</button>
            <button onClick={rejectPending} style={{ flex:1, padding:"9px", borderRadius:10, background:"transparent", border:`1px solid ${C.divider}`, color:C.charcoalSoft, fontSize:14, fontFamily:F.body, cursor:"pointer" }}>✕ Not quite</button>
          </div>
        </div>
      )}
      {notFound && !pending && (
        <div style={{ padding:"10px 14px", background:C.errorBg, border:`1px solid ${C.errorBorder}`, borderRadius:10, marginBottom:10, fontSize:13, color:C.error, fontFamily:F.body }}>
          {mode === "evaluation"
            ? "We don't recognize this wine yet — our catalog is constantly growing and we may have it soon."
            : "We don't recognize this wine yet — it won't be used as a taste signal."}
          <span onClick={()=>setNotFound(false)} style={{ marginLeft:8, cursor:"pointer", textDecoration:"underline" }}>{mode === "evaluation" ? "Choose another bottle" : "Try again"}</span>
        </div>
      )}
      {!pending && (
        <div style={{ position: "relative" }}>
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ flex: 1, position: "relative" }}>
              <input
                value={query}
                onChange={e => handleChange(e.target.value)}
                onFocus={() => setFocused(true)}
                onBlur={() => setTimeout(() => setFocused(false), 150)}
                onKeyDown={e => { if (e.key === "Enter") handleAdd(); if (e.key === "Escape") setSuggestions([]); }}
                placeholder="e.g. Whispering Angel rosé…"
                style={{ width: "100%", padding: "10px 12px", borderRadius: 10, fontSize: 16, background: focused ? C.burgundyBg : "#fff", border: `1.5px solid ${focused ? C.burgundy : C.divider}`, color: C.charcoal, fontFamily: F.body, outline: "none", boxSizing: "border-box", transition: "border-color 0.2s" }}
              />
            </div>
            <button onClick={handleAdd} disabled={!query.trim()} style={{ padding: "10px 14px", borderRadius: 10, background: query.trim() ? C.burgundyBg : "#f0ece8", border: `1.5px solid ${query.trim() ? C.burgundy : C.divider}`, color: query.trim() ? C.burgundy : C.charcoalSoft, cursor: query.trim() ? "pointer" : "default", fontFamily: F.display, fontWeight: 700, fontSize: 14, transition: "all 0.2s", whiteSpace: "nowrap" }}>Add</button>
          </div>
          {focused && suggestions.length > 0 && (
            <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 56, background: "#fff", borderRadius: 12, border: `1px solid ${C.burgundyMid}`, boxShadow: "0 8px 24px rgba(145,18,52,0.12)", zIndex: 200, overflow: "hidden" }}>
              {suggestions.map((s, i) => (
                <button key={i} onMouseDown={() => handleSelect(s)}
                  style={{ width: "100%", padding: "10px 14px", textAlign: "left", background: "none", border: "none", borderBottom: i < suggestions.length - 1 ? `1px solid ${C.divider}` : "none", cursor: "pointer", fontSize: 14, color: C.charcoal, fontFamily: F.body, transition: "background 0.15s" }}
                  onMouseEnter={e => e.currentTarget.style.background = C.burgundyBg}
                  onMouseLeave={e => e.currentTarget.style.background = "none"}>
                  {s}
                </button>
              ))}
            </div>
          )}
          {focused && query.length >= 2 && suggestions.length === 0 && (
            <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 56, background: "#fff", borderRadius: 12, border: `1px solid ${C.divider}`, padding: "10px 14px", fontSize: 13, color: C.charcoalSoft, fontFamily: F.body, fontStyle: "italic" }}>
              {mode === "evaluation"
                ? "No suggestions yet — you can still add it exactly as typed"
                : "No matches found — try typing the producer name"}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── SCAN SCREEN ─────────────────────────────────────────────────────────────
let _scanTipsShown = false;

// Belt-and-suspenders: reject scanned lines that are clearly NOT wines — a
// leading "#" (markdown header) or a header/count/summary phrase like
// "5 Wine Bottles Identified" / "Bottles Found". Conservative by design: a real
// producer/wine name never starts with "#" nor contains "bottles/wines" next to
// "identified/found/detected", so this cannot drop a legitimate wine line.
function isNonWineLine(line) {
  const t = (line || "").trim();
  if (!t) return true;
  if (t.startsWith("#")) return true;
  if (/\b(bottles?|wines?)\s+(identified|found|detected)\b/i.test(t)) return true;
  return false;
}

function normalizeForDedup(str) {
  return str
    .toLowerCase()
    .replace(/\b(19|20)\d{2}\b/g, "")
    .replace(/\b(the|wines|winery|cellars|estate)\b/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function dedupeWineLines(lines) {
  const seen = new Set();
  return lines.filter(l => {
    const k = normalizeForDedup(l);
    if (!k || seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

function dedupeWineText(text) {
  const lines = (text || "").split("\n").map(l => l.trim()).filter(l => l && !isNonWineLine(l));
  return dedupeWineLines(lines).join("\n");
}

function ScanScreen({ title, sub, icon, scanMode, scannedWines, setScannedWines, setScanMode, setScanTarget, setShowScanner, onContinue, onSkip, scanAddedCount, requireScan=false, continueLabel="Find my wine →", scanLabel="Tap to scan", onDirectScanResult, session, onAuthExpired }) {
  const [showTips, setShowTips] = useState(() => {
    if (sessionStorage.getItem('truepour_scan_tips_shown')) return false;
    sessionStorage.setItem('truepour_scan_tips_shown', '1');
    return true;
  });
  const [scanCount, setScanCount] = useState(0);
  const [showAdded, setShowAdded] = useState(false);
  const [addedN, setAddedN] = useState(0);
  const [isDirectScanning, setIsDirectScanning] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [scanError, setScanError] = useState(null); // { kind:'empty'|'failed', msg } — never data
  const directFileRef = useRef(null);
  const autoPickedRef = useRef(false); // guard: auto-open the picker at most ONCE per mount

  const openPicker = () => {
    directFileRef.current?.click();
  };

  // AUTO-open only: fire the .click() AFTER the current render commit has
  // painted, not synchronously during it. Double-rAF waits one full
  // commit+paint frame, so the modal-teardown repaint (first session) or the
  // fresh-mount paint (repeat session) settles BEFORE the native picker is
  // invoked — otherwise the app frame repaints over the OS sheet as it presents
  // and the sheet appears behind the burgundy CTA (commit 5694879 regression).
  // Direct user taps (the fallback CTA) call openPicker() straight — no race.
  const deferOpenPicker = () => {
    requestAnimationFrame(() => requestAnimationFrame(() => openPicker()));
  };

  // Auto-open the file picker ONLY on repeat-session entry — i.e. when the tips
  // modal was already dismissed earlier this session so showTips is false at
  // mount. Mount-only ([] deps): if showTips is true at mount (FIRST session),
  // this never fires, and it does NOT re-run when the user later dismisses the
  // modal — so the first scan requires a manual tap on the burgundy CTA.
  //   Why: on the first-session (post-modal) path the picker was triggered
  //   programmatically (effect + rAF) rather than by a direct tap on a visible
  //   element; the hidden display:none file input has no layout rect, so
  //   WKWebView anchored the native sheet poorly (rendered detached/stacked
  //   below the CTA). A direct tap on the visible CTA anchors the sheet
  //   correctly, so first-session now uses the manual CTA tap (same proven code
  //   path as the CTA's own onClick). Repeat-session mount auto-open anchors
  //   correctly and is preserved unchanged.
  // autoPickedRef keeps it to once per mount; the visible CTA is the primary
  // action for first session and the fallback/retry for repeat session.
  useEffect(() => {
    if (!showTips && !autoPickedRef.current) {
      autoPickedRef.current = true;
      deferOpenPicker();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (scanAddedCount > 0) {
      setScanCount(c => c + 1);
      setAddedN(scanAddedCount);
      setShowAdded(true);
      const t = setTimeout(() => setShowAdded(false), 2000);
      return () => clearTimeout(t);
    }
  }, [scanAddedCount]);

  const handleDirectFile = async (file) => {
    if (!file) return;
    setIsDirectScanning(true);
    setScanError(null);
    try {
      const { base64, mediaType } = await compressImageForScan(file);
      const { result: res, status, rows } = await scanImage(base64, mediaType, scanMode, session);
      // Three distinct outcomes, never conflated, driven by the server status:
      // (1) 'ok' + wines → merge as before; (2) 'no_wines'/empty → honest
      // "no wines" message; (3) thrown (timeout/500/network) → failure message.
      // Empty/error NEVER flow into scannedWines (onDirectScanResult unreached).
      if (status === 'ok' && res && res.trim()) {
        if (onDirectScanResult) onDirectScanResult(res, rows);
      } else {
        setScanError({ kind: 'empty', msg: "No wines detected in that photo — try better lighting or a closer shot, or add manually below." });
      }
    } catch (err) {
      if (!(onAuthExpired && onAuthExpired(err))) {
        const tooLarge = /too large|exceeds|image_too_large/i.test((err && err.message) || '');
        setScanError({ kind: 'failed', msg: tooLarge ? "That photo is too large to process — try again or use a slightly smaller image." : "Scan didn't go through — check your connection and try again." });
      }
    }
    setIsDirectScanning(false);
    if (directFileRef.current) directFileRef.current.value = "";
  };

  const bottles = scannedWines ? scannedWines.split("\n").map(l => l.trim()).filter(l => l && !isNonWineLine(l)) : [];
  const bottleCount = bottles.length;

  const removeBottle = (idx) => {
    const updated = bottles.filter((_, i) => i !== idx).join("\n");
    setScannedWines(updated || null);
  };

  return (
    <div style={{ paddingTop:36 }}>
      <style>{`@keyframes fadeInOut{0%{opacity:0;transform:translateY(-4px)}15%{opacity:1;transform:translateY(0)}75%{opacity:1}100%{opacity:0}}`}</style>

      {showTips && (
        <div style={{ position:"fixed", inset:0, background:"rgba(45,45,45,0.75)", backdropFilter:"blur(4px)", zIndex:100, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
          <div style={{ background:C.cream, borderRadius:24, padding:28, maxWidth:360, width:"100%", boxShadow:"0 20px 60px rgba(0,0,0,0.2)" }}>
            <div style={{ fontSize:20, fontWeight:800, color:C.charcoal, fontFamily:F.display, marginBottom:4 }}>Quick scanning tips</div>
            <div style={{ fontSize:14, color:C.charcoalSoft, fontFamily:F.body, marginBottom:20 }}>For the best results:</div>
            <div style={{ display:"flex", flexDirection:"column", gap:14, marginBottom:28 }}>
              {({
                restaurant: [
                  "Fill the frame with the wine list",
                  "Avoid glare on glossy menus",
                  "Scan multiple pages — we'll combine them",
                ],
                store: [
                  "Capture the shelf tags or bottle labels",
                  "Good lighting helps — avoid glare",
                  "Scan multiple shelves — we'll combine them",
                ],
                home: [
                  "Line up your bottles so labels are readable",
                  "Good lighting helps — avoid glare",
                  "Scan multiple shots — we'll combine them",
                ],
                winescan: [
                  "Fill the frame with the bottle label",
                  "Good lighting helps — avoid glare",
                  "Hold steady so the text is sharp",
                ],
              }[scanMode] || [
                "Fill the frame with the labels",
                "Good lighting helps — avoid glare",
                "Scan multiple shots — we'll combine them",
              ]).map((tip, i) => (
                <div key={i} style={{ fontSize:15, color:C.charcoal, fontFamily:F.body, lineHeight:1.5 }}>{tip}</div>
              ))}
            </div>
            <button onClick={()=>{ _scanTipsShown=true; setShowTips(false); /* first session: dismiss only — user taps the burgundy CTA to open the picker (direct tap anchors the native sheet correctly) */ }}
              style={{ width:"100%", padding:"16px", borderRadius:14, fontSize:16, fontWeight:700, background:C.burgundy, color:"#fff", border:"none", cursor:"pointer", fontFamily:F.display, letterSpacing:0.3, boxShadow:"none" }}>
              Got it — let's scan
            </button>
          </div>
        </div>
      )}

      <div style={{ fontSize:10, color:C.burgundy, letterSpacing:2, textTransform:"uppercase", marginBottom:8, fontFamily:F.body, fontWeight:600 }}>{scanMode==="restaurant" ? "Step 2 of 3" : "Step 1"}</div>
      <div style={{ fontSize:26, fontWeight:800, color:C.charcoal, fontFamily:F.display, lineHeight:1.2, marginBottom:6 }}>{title}</div>
      <div style={{ fontSize:15, color:C.charcoalSoft, fontFamily:F.body, lineHeight:1.6, marginBottom:16 }}>{sub}</div>

      {bottleCount === 0 && <div onClick={() => openPicker()}
        style={{ borderRadius:12, padding:"36px 24px", textAlign:"center", cursor:"pointer", background:C.burgundy, marginBottom:16, display:"flex", flexDirection:"column", alignItems:"center", gap:10 }}>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={C.cream} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
          <circle cx="12" cy="13" r="4"/>
        </svg>
        <div style={{ fontSize:16, fontWeight:700, color:C.cream, fontFamily:F.display }}>Tap to scan or upload a photo</div>
      </div>}

      {/* Scan history bar */}
      {scanCount > 0 && (
        <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:12, flexWrap:"wrap" }}>
          {Array.from({ length: scanCount }, (_, i) => (
            <span key={i} style={{ fontSize:13, color:C.green, fontFamily:F.body, fontWeight:600, background:C.greenBg, border:`1px solid ${C.green}`, borderRadius:20, padding:"3px 10px" }}>Scan {i+1} ✓</span>
          ))}
        </div>
      )}

      {/* Running bottle count + animated confirmation */}
      {bottleCount > 0 && (
        <div style={{ marginBottom:16 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:4 }}>
            <div style={{ fontSize:22, fontWeight:800, color:C.charcoal, fontFamily:F.display }}>{scanMode==="restaurant" ? `${bottleCount} wine${bottleCount!==1?"s":""} found` : `${bottleCount} bottle${bottleCount!==1?"s":""} found`}</div>
            {showAdded && (
              <div style={{ fontSize:14, color:C.green, fontFamily:F.body, fontWeight:700, animation:"fadeInOut 2s ease forwards" }}>+{addedN} added ✓</div>
            )}
          </div>
          {bottleCount < 3 && (
            <div style={{ fontSize:13, color:C.amber, fontFamily:F.body, fontWeight:500 }}>
              ⚠️ Only {bottleCount} wine{bottleCount!==1?"s":""} detected — try scanning again with better lighting or a closer shot, or add wines manually below.
            </div>
          )}
        </div>
      )}

      {/* Hidden file input for restaurant direct scan */}
      <input ref={directFileRef} type="file" accept="image/*" style={{ display:"none" }} onClick={e => { e.target.value = ''; }} onChange={e=>handleDirectFile(e.target.files?.[0])} />

      {isDirectScanning && <FullScreenLoader context="restaurant" scannedWines={scannedWines} />}

      {/* Distinct, non-blocking scan feedback: empty (no wines) vs failed (retry). */}
      {!isDirectScanning && scanError && (
        <div style={{ background:C.errorBg, border:`1px solid ${C.errorBorder}`, borderRadius:12, padding:"12px 14px", marginBottom:16 }}>
          <div style={{ fontSize:13, color:C.error, fontFamily:F.body, lineHeight:1.5, marginBottom:10 }}>
            {scanError.kind === 'failed' ? '⚠️ ' : '📷 '}{scanError.msg}
          </div>
          <button onClick={()=>{ setScanError(null); openPicker(); }}
            style={{ width:"100%", padding:10, borderRadius:10, background:"transparent", border:`1px solid ${C.divider}`, color:C.charcoalMid, cursor:"pointer", fontFamily:F.body, fontSize:13, fontWeight:600 }}>
            {scanError.kind === 'failed' ? 'Try again' : 'Scan another photo'}
          </button>
        </div>
      )}

      {!isDirectScanning && bottleCount > 0 && (
        <>
          {/* Scrollable wine chip list */}
          <div style={{ display:"flex", flexWrap:"wrap", gap:6, maxHeight:168, overflowY:"auto", padding:"2px 0", marginBottom:14 }}>
            {bottles.map((wine, i) => (
              <div key={i} style={{ display:"flex", alignItems:"center", gap:5, padding:"5px 10px 5px 12px", borderRadius:20, background:"#fff", border:`1px solid ${C.burgundy}`, fontSize:13, color:C.charcoal, fontFamily:F.body, lineHeight:1.3 }}>
                <span>{wine}</span>
                <button onClick={()=>removeBottle(i)} style={{ background:"none", border:"none", color:C.charcoalSoft, cursor:"pointer", fontSize:15, padding:0, lineHeight:1, marginLeft:2 }}>×</button>
              </div>
            ))}
          </div>

          {/* Manual wine entry */}
          <div style={{ marginBottom:12 }}>
            <div style={{ fontSize:13, color:C.charcoalSoft, fontFamily:F.body, marginBottom:6 }}>Add a wine manually:</div>
            <WineAutocomplete onSelect={(wine)=>{
              const existing = scannedWines ? scannedWines.split("\n").filter(Boolean) : [];
              const norm = s => (s||'').toLowerCase().replace(/[^a-z0-9]/g,'');
              if (!existing.some(e => norm(e) === norm(wine))) {
                setScannedWines([...existing, wine].join("\n"));
              }
            }} mode="evaluation" />
          </div>

          {/* Scan more */}
          <button onClick={openPicker}
            style={{ width:"100%", padding:"12px", borderRadius:11, background:"transparent", border:`2px solid ${C.burgundy}`, color:C.burgundy, cursor:"pointer", fontSize:15, fontFamily:F.display, fontWeight:700, transition:"all 0.2s", marginBottom:8 }}
            onMouseEnter={e=>{ e.currentTarget.style.background=C.burgundyBg; }}
            onMouseLeave={e=>{ e.currentTarget.style.background="transparent"; }}>
            {scanMode === "restaurant" ? "+ Scan another page of the menu" : "+ Scan another section of the shelf"}
          </button>

          <div style={{ textAlign:"center", fontSize:13, color:C.charcoalSoft, fontFamily:F.body, fontStyle:"italic", marginBottom:4 }}>
            The more wines you add, the better your match
          </div>
        </>
      )}

      {!requireScan && scanMode !== "home" && (
        <div style={{ textAlign:"center", margin:"20px 0" }}>
          <button onClick={onSkip} style={{ background:"none", border:"none", color:C.charcoalSoft, fontSize:13, fontFamily:F.body, cursor:"pointer", textDecoration:"underline" }}>
            Skip — give me general recommendations
          </button>
        </div>
      )}

      {((!requireScan && scanMode !== "home") || bottleCount > 0) && (
        <button style={{ width:"100%", padding:"16px", borderRadius:14, fontSize:16, fontWeight:700, background:bottleCount>0?C.burgundy:"#f0ece8", color:bottleCount>0?"#fff":C.charcoalSoft, border:`1px solid ${bottleCount>0?C.burgundy:C.divider}`, cursor:"pointer", fontFamily:F.display, letterSpacing:0.3, transition:"all 0.2s", marginTop: requireScan ? 20 : 0 }}
          onClick={onContinue}>
          {bottleCount > 0 ? continueLabel : "Continue without scanning →"}
        </button>
      )}
    </div>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
function getArchetypeVariant(archetypeKey, extSliders) {
  const arch = ARCHETYPES[archetypeKey];
  if (!arch || !arch.colorVariants) return null;
  const cl = (extSliders && extSliders.color) || 3;
  if (cl >= 4) return arch.colorVariants.white;
  if (cl <= 2) return arch.colorVariants.red;
  return arch.colorVariants.neutral;
}

function BottomTabBar({ activeTab, onTabChange, cellarCount, onScan, hasProfileBadge, cellarBadge }) {
  const NAV_ACTIVE_STROKE = 2.5;
  const NAV_INACTIVE_STROKE = 2;
  const leftTabs = [
    {
      id: 'discover',
      label: 'Discover',
      icon: (active) => (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H4a1 1 0 01-1-1V9.5z" stroke={active ? C.burgundy : '#8A8580'} strokeWidth={active ? NAV_ACTIVE_STROKE : NAV_INACTIVE_STROKE} strokeLinejoin="round"/>
          <path d="M9 21V12h6v9" stroke={active ? C.burgundy : '#8A8580'} strokeWidth={active ? NAV_ACTIVE_STROKE : NAV_INACTIVE_STROKE} strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
    },
    {
      id: 'activity',
      label: 'Activity',
      icon: (active) => (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M3 12h4l3-7 4 14 3-7h4" stroke={active ? C.burgundy : '#8A8580'} strokeWidth={active ? NAV_ACTIVE_STROKE : NAV_INACTIVE_STROKE} strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
    },
  ];
  const rightTabs = [
    {
      id: 'cellar',
      label: 'My Cellar',
      icon: (active) => (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <circle cx="8" cy="15" r="4" stroke={active ? C.burgundy : '#8A8580'} strokeWidth={active ? NAV_ACTIVE_STROKE : NAV_INACTIVE_STROKE}/>
          <path d="M11.5 11.5L20 3" stroke={active ? C.burgundy : '#8A8580'} strokeWidth={active ? NAV_ACTIVE_STROKE : NAV_INACTIVE_STROKE} strokeLinecap="round"/>
          <path d="M18 5l1.5 1.5" stroke={active ? C.burgundy : '#8A8580'} strokeWidth={active ? NAV_ACTIVE_STROKE : NAV_INACTIVE_STROKE} strokeLinecap="round"/>
          <path d="M16 7l1.5 1.5" stroke={active ? C.burgundy : '#8A8580'} strokeWidth={active ? NAV_ACTIVE_STROKE : NAV_INACTIVE_STROKE} strokeLinecap="round"/>
        </svg>
      ),
    },
    {
      id: 'profile',
      label: 'Profile',
      icon: (active) => (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="8" r="4" stroke={active ? C.burgundy : '#8A8580'} strokeWidth={active ? NAV_ACTIVE_STROKE : NAV_INACTIVE_STROKE}/>
          <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke={active ? C.burgundy : '#8A8580'} strokeWidth={active ? NAV_ACTIVE_STROKE : NAV_INACTIVE_STROKE} strokeLinecap="round"/>
        </svg>
      ),
    },
  ];

  const renderTab = (tab) => {
    const active = activeTab === tab.id;
    return (
      <button key={tab.id} onClick={() => onTabChange(tab.id)}
        style={{
          flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'flex-start', gap: 3, background: 'none', border: 'none',
          cursor: 'pointer', padding: '10px 0 0',
          WebkitTapHighlightColor: 'transparent', outline: 'none',
        }}>
        {tab.id === 'profile' ? (
          <div style={{ position:'relative', display:'inline-flex' }}>
            {tab.icon(active)}
            {hasProfileBadge && (
              <div style={{ position:'absolute', top:-4, right:-6, width:12, height:12, borderRadius:'50%', background:C.burgundy, border:'2.5px solid #fff', boxShadow:'0 1px 5px rgba(160,36,58,0.45)' }} />
            )}
          </div>
        ) : tab.id === 'cellar' ? (
          <div style={{ position:'relative', display:'inline-flex' }}>
            {tab.icon(active)}
            {cellarBadge && (
              <div style={{ position:'absolute', top:-4, right:-6, width:12, height:12, borderRadius:'50%', background:C.burgundy, border:'2.5px solid #fff', boxShadow:'0 1px 5px rgba(160,36,58,0.45)' }} />
            )}
          </div>
        ) : tab.icon(active)}
        <span style={{ fontSize: 10, color: active ? C.burgundy : '#8A8580', fontFamily: 'var(--fb)', fontWeight: active ? 600 : 400 }}>
          {tab.id === 'cellar' && cellarCount > 0 ? `My Cellar (${cellarCount})` : tab.label}
        </span>
      </button>
    );
  };

  return (
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 500,
      background: '#FFFFFF', borderTop: '1px solid #EDE8E3',
      display: 'flex', justifyContent: 'space-around', alignItems: 'flex-start',
      height: 'calc(70px + env(safe-area-inset-bottom))', paddingBottom: 'env(safe-area-inset-bottom)',
      boxShadow: '0 -2px 12px rgba(0,0,0,0.08)',
    }}>
      {leftTabs.map(renderTab)}

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', position: 'relative' }}>
        <button
          onClick={onScan}
          style={{
            position: 'absolute', top: -14,
            width: 52, height: 52, borderRadius: '50%',
            background: '#1A1A1A',
            border: '3px solid #FFFFFF',
            boxShadow: '0 4px 16px rgba(26,26,26,0.35)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', WebkitTapHighlightColor: 'transparent', outline: 'none',
          }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" stroke={C.cream} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="12" cy="13" r="4" stroke={C.cream} strokeWidth="2"/>
          </svg>
        </button>
      </div>

      {rightTabs.map(renderTab)}
    </div>
  );
}

const BottleOrderIcon = () => (
  <svg width="48" height="48" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M20.0848 3.12544C20.3641 2.39639 20.3563 1.92852 20.0848 1H16.3653C16.225 1.92479 16.2092 2.40108 16.3653 3.12544M20.0848 3.12544V10.5645C21.6602 14.002 22.3812 15.9423 23.4501 19.4205V21.723M20.0848 3.12544H16.3653M16.3653 3.12544V10.5645C14.8377 13.9563 14.1119 15.8987 13 19.4205V21.723M13 21.723H23.4501M13 21.723V30.0477M23.4501 21.723V30.0477M13 30.0477V33.9443C13.325 34.5989 13.5522 34.7756 14.0627 34.6528H22.7416L23.4501 33.9443V30.0477M13 30.0477H23.4501" stroke={C.burgundy} strokeWidth="0.5"/>
    <path d="M16.3653 8.08484H19.9077" stroke={C.burgundy} strokeWidth="0.5"/>
  </svg>
);

const GlassOrderIcon = () => (
  <svg width="48" height="48" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12.0937 34.4999C16.9999 35.1559 19.7497 35.1774 24.6562 34.4999C23.3926 33.0373 22.2938 32.5088 20.05 31.778C20.05 27.935 20.05 25.7804 20.05 21.9374C24.165 19.8795 25.6362 17.9137 26.9593 13.1437C27.0083 8.24893 26.1749 5.69331 24.6562 1L12.2876 1.05425C10.78 5.71305 9.95327 8.26782 10.002 13.1437C11.3252 17.9137 12.7964 19.8795 16.9114 21.9374V31.778C14.6676 32.5088 13.5688 33.0373 12.3052 34.4999" stroke={C.burgundy} strokeWidth="0.5"/>
    <path d="M10.2094 11.6781H26.75" stroke={C.burgundy} strokeWidth="0.5"/>
  </svg>
);

function RestaurantIcon() {
  return (
    <svg width="36" height="25" viewBox="0 0 23 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M15.1783 6.3421C15.4929 6.3423 15.7477 6.59777 15.7477 6.91241C15.7475 7.22689 15.4928 7.48155 15.1783 7.48175C14.8637 7.48175 14.6082 7.22702 14.608 6.91241C14.608 6.59764 14.8636 6.3421 15.1783 6.3421Z" stroke={C.burgundy} strokeWidth="0.5"/>
      <path d="M22.25 13.9841H8.31097V15.3165H22.25V13.9841Z" stroke={C.burgundy} strokeWidth="0.5"/>
      <path d="M16.408 9.26935C17.3688 9.40321 17.8463 9.63326 18.5603 10.3968C19.4186 11.1882 19.6506 11.7272 19.8927 12.7541" stroke={C.burgundy} strokeWidth="0.5"/>
      <path d="M9.02844 13.984C9.25631 12.1692 9.65502 11.2467 10.8733 9.78181C12.265 8.58446 13.196 8.22603 15.0755 8.03943C16.9763 8.07379 17.9838 8.4627 19.6877 9.78181C20.9622 11.1998 21.2579 12.152 21.4301 13.984" stroke={C.burgundy} strokeWidth="0.5"/>
      <path d="M1.44393 0.352417L0.316507 4.45214C-0.0542624 8.56245 1.1479 8.93861 3.3913 9.5768V13.8815L1.44393 14.8039" stroke={C.burgundy} strokeWidth="0.5"/>
      <path d="M6.50217 0.352417L7.6296 4.45214C8.00037 8.56245 6.79821 8.93861 4.55481 9.5768V13.8815L6.50217 14.8039" stroke={C.burgundy} strokeWidth="0.5"/>
      <path d="M1.44391 0.25H6.56856" stroke={C.burgundy} strokeWidth="0.5"/>
      <path d="M1.54645 14.9064C3.40145 15.0984 4.47688 15.1028 6.46612 14.9064" stroke={C.burgundy} strokeWidth="0.5"/>
      <path d="M0.521545 4.34978C1.42752 4.21566 1.9021 4.2089 2.6739 4.34978C3.37789 4.92871 3.8914 5.16017 4.92875 5.47721C5.57268 5.51598 5.93105 5.5089 6.56864 5.47721L7.59357 4.96474" stroke={C.burgundy} strokeWidth="0.5"/>
    </svg>
  );
}
function StoreIcon() {
  return (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M7.79998 7.30945H5.86153L5 20.3402H6.50768" stroke={C.burgundy} strokeWidth="0.5"/>
      <path d="M9.09222 5.58636C9.25952 3.78686 11.4436 3.04425 12.1076 3.00175C12.7715 2.95926 14.7236 3.68546 14.9076 5.58636V9.35558" stroke={C.burgundy} strokeWidth="0.5"/>
      <path d="M17.9231 7.20172L19 20.4478H12.8616" stroke={C.burgundy} strokeWidth="0.5"/>
      <path d="M11.6769 7.30945H17.923" stroke={C.burgundy} strokeWidth="0.5"/>
      <path d="M10.4923 6.44788H8.98462V10.6479C8.22739 10.9943 7.98644 11.3647 7.69232 12.1555V20.4478H11.7846V12.1555C11.5359 11.3031 11.2842 10.9408 10.4923 10.6479V6.44788Z" stroke={C.burgundy} strokeWidth="0.5"/>
    </svg>
  );
}
function HomeIcon() {
  return (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g transform="translate(-2, 0)">
        <path d="M12.6023 6.53133C12.4045 6.552 12.1947 6.57124 11.9688 6.59043C10.53 6.54644 9.7912 6.38353 8.58996 5.85132C7.78098 6.35711 7.22765 6.52198 6.05587 6.59043C5.63172 6.43947 5.4008 6.20665 5 5.53456V4.05634C5.23976 3.38986 5.46962 3.15511 6.05587 3.00047C7.09474 3.12756 7.65979 3.24115 8.58996 3.63399C9.78914 3.13099 10.5281 2.99071 11.9688 3.00047C13.4376 3.01069 14.2073 3.13444 15.4531 3.63399C16.3237 3.28856 16.9025 3.13889 18.1984 3.00047C18.7833 3.31985 18.9345 3.5545 18.9375 4.05634V5.53456C18.783 6.23817 18.5882 6.39777 18.1984 6.59043C17.0314 6.49988 16.4224 6.35429 15.4531 5.85132C14.398 6.26531 13.6756 6.41922 12.6023 6.53133ZM12.6023 6.53133V9.33569M12.6023 13.348L11.3352 12.1865L12.6023 11.3419L11.3352 10.4972L12.6023 9.86363V9.33569M12.6023 13.348C13.5164 13.4965 13.8735 13.6934 14.1861 14.0871L13.9749 19.3665C13.8064 19.7707 13.6546 19.9224 13.2358 20H10.9129C10.4776 19.9406 10.3188 19.799 10.1738 19.3665L9.75142 14.0871C10.3646 13.497 10.8699 13.3038 12.6023 13.348ZM9.75142 14.0871C10.4749 14.5674 10.9487 14.7252 11.9688 14.7206C12.873 14.7669 13.3566 14.6132 14.1861 14.0871M11.3352 6.59043V8.491L12.6023 9.33569" stroke={C.burgundy} strokeWidth="0.5"/>
        <rect x="10.8073" y="15.4598" width="0.211174" height="0.422349" fill={C.burgundy}/>
        <rect x="12.4967" y="18.205" width="0.422349" height="0.422349" fill={C.burgundy}/>
        <rect x="11.4409" y="16.5156" width="0.422349" height="0.422349" fill={C.burgundy}/>
      </g>
    </svg>
  );
}
function OnlineIcon() {
  return (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g transform="translate(0.5, 0)">
        <path d="M19.4523 7.59343L11.9574 4L4.3599 7.59343L11.9574 10.9815L19.4523 7.59343ZM19.4523 7.59343L19.6576 16.8337L11.9574 20.7206L4.00305 17.282" stroke={C.burgundy} strokeWidth="0.5"/>
        <path d="M14.0108 15.9097V18.1684" stroke={C.burgundy} strokeWidth="0.5"/>
        <path d="M14.9349 15.499L13.0869 16.2177" stroke={C.burgundy} strokeWidth="0.5"/>
        <path d="M7.64539 6.05341L14.9349 9.74951V12.7269L16.783 12.0082V8.82548L9.39076 5.23206" stroke={C.burgundy} strokeWidth="0.5"/>
        <path d="M4.00305 7.53918V17.282" stroke={C.burgundy} strokeWidth="0.5"/>
        <path d="M4.06811 17.3291C4.0539 17.3156 4.03969 17.302 4.02576 17.2896C4.01183 17.2771 3.9986 17.2662 4.00012 17.2621C4.00165 17.2581 4.01833 17.2612 4.05553 17.2963" stroke={C.burgundy} strokeWidth="0.5" strokeLinecap="round"/>
      </g>
    </svg>
  );
}

function CrispPuristIcon({size=100,color=C.burgundy}){return(<svg width={size} height={size} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0.968872 29.8672L17.1929 8L29.1846 29.8672L46.1141 8L60.9274 29.8672L76.4461 8L87.027 24.5768L97.6079 10.1162" stroke={color} strokeWidth="1.5"/><path d="M1.32153 51.3817L17.5456 29.5145L29.5373 51.3817L46.4668 29.5145L61.28 51.3817L76.7987 29.5145L87.3796 46.0913L97.9605 31.6307" stroke={color} strokeWidth="1.5"/><path d="M1.32153 73.2489L17.5456 51.3817L29.5373 73.2489L46.4668 51.3817L61.28 73.2489L76.7987 51.3817L87.3796 67.9585L97.9605 53.4979" stroke={color} strokeWidth="1.5"/><path d="M1.32153 93L17.5456 71.1328L29.5373 93L46.4668 71.1328L61.28 93L76.7987 71.1328L87.3796 87.7095L97.9605 73.2489" stroke={color} strokeWidth="1.5"/></svg>);}
function BoldModernistIcon({size=100,color=C.burgundy}){return(<svg width={size} height={size} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M48.309 1.75C74.0361 1.75 94.8928 22.6059 94.8929 48.333C94.8929 74.0603 74.0362 94.917 48.309 94.917C22.5818 94.9168 1.72595 74.0602 1.72595 48.333C1.72613 22.606 22.582 1.75018 48.309 1.75Z" stroke={color} strokeWidth="1.5"/><path d="M48.3096 9.08331C69.2381 9.08349 86.2254 26.3455 86.2256 47.6663C86.2256 68.9873 69.2382 86.2501 48.3096 86.2503C27.3808 86.2503 10.3926 68.9874 10.3926 47.6663C10.3928 26.3453 27.3809 9.08331 48.3096 9.08331Z" stroke={color} strokeWidth="1.5"/><path d="M48.6426 16.75C65.3342 16.75 78.8926 30.5771 78.8926 47.667C78.8924 64.7568 65.3341 78.583 48.6426 78.583C31.951 78.583 18.3927 64.7568 18.3926 47.667C18.3926 30.5771 31.9509 16.75 48.6426 16.75Z" stroke={color} strokeWidth="1.5"/><path d="M28.976 34C35.4522 26.6752 39.7112 24.0362 49.3093 24C56.8246 23.8072 60.7931 26.5179 67.6426 34" stroke={color} strokeWidth="1.5"/><path d="M70.3093 38.3333H25.976C24.5353 47.8081 24.8514 52.7398 27.976 60.6666C32.7329 67.7136 36.3224 70.2849 44.6426 72V90.3333C37.8794 92.3356 34.6199 94.1389 29.3093 98H66.976C61.6874 94.1127 58.4207 92.4093 51.976 90.3333L53.3093 72C59.9259 70.1031 63.4751 67.9375 69.3093 60.6666C71.326 51.9346 71.5177 47.0468 70.3093 38.3333Z" fill="#FFFAF8" stroke={color} strokeWidth="1.5"/></svg>);}
function BoldTraditionalistIcon({size=100,color=C.burgundy}){return(<svg width={size} height={size} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M28.7255 30.4277L49.7954 51.4975L70.8653 30.4277Z" fill={color} opacity="0.85"/><path d="M49.7953 51.4974L47.69 53.3396L47.69 72.2329L28.391 90.9617H70.1963L51.802 72.2329L51.802 53.3396Z" fill={color} opacity="0.85"/><path d="M88.5906 2H11.3344V10.0266H88.5906V2Z" stroke={color} strokeWidth="1.5"/><path d="M15.3478 10.3611V48.4876C18.2108 49.7945 19.6366 49.9125 21.7022 48.4876V10.3611H28.7255M84.243 10.3611V48.4876C81.892 49.9653 80.574 49.7614 78.223 48.4876V10.3611H70.8653M28.7255 10.3611V31.431L49.7954 51.4975L70.8653 31.431V10.3611M28.7255 10.3611H70.8653" stroke={color} strokeWidth="1.5"/><path d="M11 98.3195V91.6307H88.5907V98.3195H11Z" stroke={color} strokeWidth="1.5"/><path d="M15.0133 91.2962V52.8353C17.5009 50.7729 18.8794 50.7928 21.3677 52.8353V91.2962" stroke={color} strokeWidth="1.5"/><path d="M78.223 91.2962V52.5009C80.4421 50.8874 81.6889 50.901 83.9085 52.5009V91.2962" stroke={color} strokeWidth="1.5"/><path d="M51.802 53.3396L49.7953 51.4974L47.69 53.3396M51.802 53.3396L70.1963 70.2262V90.9617L51.802 72.2329V53.3396ZM47.69 53.3396L28.391 70.2262V90.9617L47.69 72.2329V53.3396Z" stroke={color} strokeWidth="1.5"/><path d="M29.3944 30.4277H71.5342" stroke={color} strokeWidth="1.5"/></svg>);}
function EverydayEnthusiastIcon({size=100,color=C.burgundy}){return(<svg width={size} height={size} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M69.4506 3C75.3075 19.8831 77.5958 30.1986 77.1453 41.1529C72.0701 54.9699 66.0105 59.2673 51.4963 62.9545V84.7562C57.6853 91.9797 62.4326 94.0814 71.6949 96.6188M29.7498 3C23.8929 19.8831 21.6047 30.1986 22.0551 41.1529C27.1304 54.9699 33.19 59.2673 47.7041 62.9545V84.7562C41.5152 91.9797 36.7679 94.0814 27.5055 96.6188" stroke={color} strokeWidth="1.5"/><path d="M28.0916 96.9394H70.733M29 3.32062H70" stroke={color} strokeWidth="1.5"/><ellipse cx="50.2138" cy="36.0231" rx="10.2596" ry="9.93899" fill={color}/></svg>);}
function ElegantClassicistIcon({size=100,color=C.burgundy}){return(<svg width={size} height={size} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M15.9716 94H83.2287" stroke={color} strokeWidth="1.5"/><path d="M22.2572 93.9999V86.4571H76.6286V93.9999" stroke={color} strokeWidth="1.5"/><path d="M27.2858 86.4572V79.5429H72.2286V86.4572" stroke={color} strokeWidth="1.5"/><path d="M32 79.2286V29.2571C34.6648 27.9476 36.2416 27.947 38.9143 29.2571V79.2286" stroke={color} strokeWidth="1.5"/><path d="M45.8286 79.2681V29.2966C48.4934 27.9871 50.0702 27.9865 52.7429 29.2966V79.2681" stroke={color} strokeWidth="1.5"/><path d="M60.2858 79.2681V29.2966C62.9506 27.9871 64.5274 27.9865 67.2001 29.2966V79.2681" stroke={color} strokeWidth="1.5"/><path d="M20.0571 6C13.1829 9.01601 11.2322 12.0886 10 19.2C11.2755 25.9591 14.0391 27.9406 20.0571 30.5143H24.7714C29.1539 28.2607 30.2969 26.0595 30.4286 20.7714C30.1657 15.2427 28.5659 13.6806 23.5143 13.2286C18.8279 13.8548 18.2717 15.9705 18.4857 20.7714" stroke={color} strokeWidth="1.5"/><path d="M79.4571 6C86.3314 9.01601 88.282 12.0886 89.5143 19.2C88.2388 25.9591 85.4752 27.9406 79.4571 30.5143H74.7428C70.3604 28.2607 69.2173 26.0595 69.0857 20.7714C69.3486 15.2427 70.9484 13.6806 76 13.2286C80.6864 13.8548 81.2425 15.9705 81.0286 20.7714" stroke={color} strokeWidth="1.5"/><path d="M19.7428 6H79.7714M32.9428 13.8571H66.2571M30.4285 21.4H69.0857" stroke={color} strokeWidth="1.5"/></svg>);}
function VelvetTouchIcon({size=100,color=C.burgundy}){
  return (
    <svg width={size} height={size} viewBox="0 0 161 161" xmlns="http://www.w3.org/2000/svg">
      <g transform="translate(0.000000,161.000000) scale(0.100000,-0.100000)" fill={color} stroke="none">
        <path d="M287 1543 c-16 -15 -6 -43 14 -43 95 0 179 -72 248 -213 l50 -102 -19 -29 -19 -30 -37 78 c-69 142 -168 226 -268 226 -43 0 -47 -2 -44 -22 2 -17 13 -24 58 -33 96 -19 138 -60 213 -210 l45 -90 -19 -24 c-11 -13 -21 -22 -23 -20 -2 2 -17 31 -34 65 -64 127 -151 204 -248 220 -30 4 -43 2 -53 -10 -19 -22 2 -46 41 -46 74 0 154 -73 217 -199 l40 -78 -24 -23 -23 -22 -26 55 c-33 68 -97 142 -145 166 -49 26 -115 43 -135 35 -21 -8 -20 -41 1 -47 10 -3 40 -11 66 -17 61 -16 120 -73 167 -161 l33 -63 -24 -15 c-13 -9 -49 -35 -81 -58 -267 -196 -224 -601 78 -740 76 -35 210 -44 294 -19 113 33 214 109 302 228 l52 70 41 -83 c71 -147 168 -229 269 -229 43 0 47 2 44 23 -2 17 -11 23 -44 27 -51 7 -124 50 -156 92 -14 18 -47 75 -72 127 -43 85 -46 96 -35 122 6 16 14 29 18 29 4 0 23 -33 43 -73 70 -142 156 -219 262 -233 38 -6 47 -4 52 11 9 22 -19 45 -53 45 -72 0 -152 78 -220 215 l-47 95 20 21 21 21 43 -77 c53 -98 112 -162 175 -191 59 -28 120 -32 129 -8 9 24 -1 32 -49 40 -93 13 -158 78 -231 228 -28 55 -28 56 -9 71 11 8 22 14 25 14 3 0 24 -33 47 -73 49 -85 111 -141 179 -162 61 -18 90 -18 98 2 9 24 2 30 -58 43 -72 16 -138 75 -186 164 -19 36 -33 67 -30 69 3 2 40 30 83 61 115 85 165 161 187 285 23 131 -19 270 -112 368 -171 181 -448 177 -644 -8 -28 -27 -71 -75 -95 -108 l-43 -59 -46 87 c-59 110 -106 163 -172 196 -49 24 -116 34 -131 19z m986 -85 c31 -15 57 -29 57 -31 0 -3 -23 2 -51 10 -35 10 -78 14 -133 11 -68 -4 -92 -10 -151 -40 -117 -60 -183 -129 -291 -303 -91 -147 -152 -214 -293 -320 -143 -107 -181 -152 -202 -238 -11 -46 -14 -49 -20 -27 -12 44 -9 120 7 158 20 49 53 83 149 150 118 84 192 164 291 313 95 146 180 241 256 290 74 48 121 59 228 56 78 -2 105 -8 153 -29z m54 -98 c98 -48 154 -127 153 -215 l-1 -30 -20 38 c-28 53 -87 109 -145 138 -67 33 -187 34 -256 2 -64 -29 -139 -101 -195 -187 -131 -200 -159 -238 -221 -298 -37 -35 -112 -96 -167 -135 -56 -39 -113 -88 -128 -110 -35 -51 -45 -119 -22 -162 9 -18 15 -35 13 -37 -2 -2 -18 5 -35 16 -63 38 -64 155 -3 239 16 22 80 78 142 124 160 119 236 206 343 391 36 62 125 158 183 198 109 75 242 86 359 28z m-106 -136 c24 -23 29 -36 29 -74 0 -38 -6 -53 -34 -87 -19 -22 -64 -59 -100 -82 l-64 -42 -86 41 c-47 23 -86 45 -86 49 0 14 55 89 104 140 90 94 175 114 237 55z m112 -12 c36 -38 42 -78 23 -146 -21 -73 -64 -120 -185 -204 -126 -88 -206 -172 -288 -301 -132 -208 -190 -271 -298 -322 -49 -24 -73 -29 -132 -29 -103 0 -173 28 -244 99 -56 56 -69 86 -69 155 l0 31 16 -32 c26 -53 79 -107 135 -138 49 -27 62 -30 151 -30 96 0 96 0 154 40 70 48 134 125 217 260 81 130 175 228 317 329 139 99 175 144 175 219 0 29 -4 62 -8 75 -11 30 4 28 36 -6z m106 -191 c3 -91 -44 -158 -172 -247 -132 -93 -190 -155 -302 -324 -139 -212 -222 -286 -359 -325 -78 -23 -177 -15 -251 19 -30 14 -55 28 -55 32 0 3 8 3 17 -1 46 -18 131 -24 192 -14 144 23 265 125 395 334 97 156 161 226 289 317 54 39 114 84 134 101 37 33 76 101 88 157 8 34 9 34 16 10 4 -14 7 -40 8 -59z m-511 -81 c39 -19 72 -38 72 -42 0 -4 -13 -16 -29 -27 l-28 -20 -67 35 -66 35 17 30 c10 16 20 28 23 27 3 -2 38 -19 78 -38z m-83 -97 c30 -15 55 -30 55 -33 -1 -3 -9 -15 -20 -28 l-19 -23 -67 31 -66 32 23 24 c12 13 26 24 31 24 4 0 32 -12 63 -27z m-25 -134 c0 -4 -8 -16 -17 -28 l-18 -22 -73 33 -72 32 26 27 26 28 64 -32 c35 -17 64 -34 64 -38z m-146 -59 l87 -40 -49 -72 c-54 -80 -136 -163 -177 -178 -53 -20 -95 -12 -131 24 -42 42 -46 95 -11 147 16 24 166 147 194 158 0 1 40 -17 87 -39z"/>
      </g>
    </svg>
  );
}

function BrightAdventurerIcon({size=100,color=C.burgundy}){
  return (
    <svg width={size} height={size} viewBox="0 0 167 167" xmlns="http://www.w3.org/2000/svg">
      <g transform="translate(0.000000,167.000000) scale(0.100000,-0.100000)" fill={color} stroke="none">
        <path d="M807 1567 c-10 -29 -20 -57 -25 -64 -4 -6 -41 -19 -82 -27 -209 -43 -402 -206 -475 -400 -13 -35 -29 -93 -36 -128 l-12 -64 -50 -20 c-28 -10 -49 -23 -48 -29 2 -5 24 -19 51 -30 46 -19 48 -21 54 -68 16 -114 93 -273 171 -351 29 -30 40 -35 51 -25 21 17 18 27 -27 77 -69 79 -125 193 -143 294 l-7 37 30 -10 c23 -8 30 -18 36 -47 14 -81 127 -272 160 -272 29 0 27 40 -4 76 -42 47 -89 142 -104 212 l-13 57 -68 24 c-36 13 -64 26 -62 29 3 2 33 14 68 26 l62 21 16 65 c25 97 66 168 135 236 68 67 155 116 239 135 51 11 55 15 70 53 8 23 21 55 27 71 l12 30 21 -69 22 -68 79 -23 c73 -21 82 -22 97 -8 10 9 15 20 12 24 -3 5 -37 18 -75 30 -51 16 -73 27 -79 43 -4 12 -4 23 2 24 16 7 112 -20 162 -45 l49 -25 20 21 21 20 -64 30 c-56 26 -90 37 -197 57 -16 3 -28 19 -44 64 -12 34 -25 63 -29 65 -4 2 -14 -19 -23 -48z m-56 -159 c-9 -26 -18 -31 -91 -53 -161 -49 -320 -217 -361 -380 -13 -52 -23 -65 -51 -65 -15 0 -15 5 -2 63 33 145 130 283 258 367 69 44 183 89 228 90 24 0 26 -3 19 -22z M1235 1418 c-44 -38 -120 -105 -170 -147 -49 -42 -179 -155 -289 -251 -109 -96 -213 -187 -230 -202 l-31 -27 51 -1 c28 0 79 -3 112 -6 l62 -7 -37 -51 c-81 -111 -373 -549 -369 -553 2 -2 66 52 142 119 77 68 238 209 359 314 121 105 240 210 265 232 l45 41 -107 1 c-60 0 -108 2 -108 5 0 3 90 141 200 305 110 165 196 300 192 300 -4 -1 -43 -33 -87 -72z M1252 1308 c-19 -19 -15 -26 33 -80 52 -58 108 -167 130 -252 8 -33 13 -61 11 -63 -2 -2 -15 -1 -28 2 -20 5 -27 17 -37 58 -17 65 -71 170 -115 222 -32 38 -36 39 -52 23 -16 -16 -15 -19 19 -65 50 -68 85 -141 100 -208 l13 -58 63 -22 c34 -13 64 -25 67 -28 3 -2 -26 -15 -62 -28 l-68 -24 -13 -57 c-40 -182 -196 -338 -375 -378 l-58 -13 -22 -68 c-12 -38 -24 -66 -27 -63 -3 3 -15 33 -26 66 l-21 61 -64 17 c-36 9 -72 20 -81 24 -9 4 -22 -1 -33 -13 -17 -19 -17 -20 11 -34 15 -8 50 -20 76 -27 36 -10 51 -19 58 -38 5 -14 8 -26 6 -28 -7 -7 -123 27 -169 50 -44 21 -49 22 -63 6 -8 -9 -15 -19 -15 -22 0 -12 141 -68 202 -80 l68 -13 21 -62 c11 -35 24 -63 29 -63 5 0 18 29 30 64 l21 64 50 6 c166 22 339 133 438 281 56 84 85 156 104 252 l12 66 60 27 c57 27 59 28 35 38 -93 37 -100 41 -100 64 0 85 -71 248 -150 346 -54 66 -59 69 -78 50z m163 -615 c-53 -212 -238 -399 -446 -447 -35 -9 -65 -14 -66 -12 -2 2 1 15 6 28 8 20 24 30 80 47 88 27 207 105 262 174 49 61 105 172 114 226 5 29 12 40 33 48 15 5 28 8 30 7 2 -1 -4 -33 -13 -71z"/>
      </g>
    </svg>
  );
}

function SweetSipperIcon({size=100,color=C.burgundy}){
  return (
    <svg width={size} height={size} viewBox="0 0 162 162" xmlns="http://www.w3.org/2000/svg">
      <g transform="translate(0.000000,162.000000) scale(0.100000,-0.100000)" fill={color} stroke="none">
        <path d="M792 1548 c-6 -7 -30 -56 -55 -109 -25 -53 -77 -147 -117 -209 -127 -198 -169 -313 -155 -424 16 -116 80 -208 183 -258 62 -31 74 -33 167 -33 95 0 103 2 163 34 73 40 116 85 148 156 66 143 32 285 -123 522 -32 48 -85 142 -118 207 -33 66 -65 121 -71 123 -6 2 -16 -2 -22 -9z m57 -165 c18 -37 64 -116 103 -176 109 -170 142 -246 147 -340 4 -95 -15 -152 -71 -213 -56 -61 -125 -89 -218 -88 -117 0 -202 47 -258 143 -24 41 -27 56 -27 141 0 85 3 102 31 160 17 36 65 119 107 186 41 66 90 151 108 187 18 37 36 67 39 67 3 0 21 -30 39 -67z M786 1273 c-10 -21 -46 -83 -80 -138 -84 -133 -105 -176 -116 -236 -15 -81 3 -143 59 -198 89 -89 222 -96 311 -15 51 46 72 94 72 163 0 77 -25 136 -126 301 -47 77 -86 145 -86 150 0 22 -17 8 -34 -27z M199 793 c-14 -36 -5 -198 17 -278 50 -188 210 -359 396 -425 87 -31 249 -38 338 -16 279 70 464 301 478 597 5 109 -4 149 -34 149 -8 0 -17 -8 -21 -17 -62 -181 -224 -329 -413 -378 -82 -22 -225 -22 -304 0 -76 20 -178 74 -239 126 -58 50 -129 148 -155 214 -23 58 -47 69 -63 28z m174 -270 c114 -106 284 -173 440 -173 190 1 401 104 512 251 l45 61 0 -34 c0 -50 -26 -140 -59 -206 -114 -225 -369 -349 -611 -298 -115 24 -193 68 -282 156 -56 56 -87 96 -109 144 -36 76 -59 156 -59 205 l0 34 28 -39 c16 -21 59 -67 95 -101z"/>
      </g>
    </svg>
  );
}

function TerroirHunterIcon({size=100,color=C.burgundy}){
  return (
    <svg width={size} height={size} viewBox="0 0 170 170" xmlns="http://www.w3.org/2000/svg">
      <g transform="translate(0.000000,170.000000) scale(0.100000,-0.100000)" fill={color} stroke="none">
        <path d="M818 1613 c-40 -43 -60 -92 -66 -159 -5 -68 8 -120 44 -174 13 -19 24 -46 24 -60 l-1 -25 -20 28 c-28 37 -93 73 -156 86 -76 15 -87 9 -80 -50 14 -118 100 -213 214 -237 l38 -8 3 -244 2 -243 -26 -40 c-18 -28 -43 -47 -91 -69 -59 -28 -76 -31 -172 -32 -108 -2 -154 -14 -166 -44 -10 -27 11 -36 52 -25 21 6 57 14 81 18 l43 7 -26 -26 c-32 -32 -57 -89 -49 -110 11 -29 42 -18 53 17 16 48 57 83 131 113 36 14 71 31 78 36 29 24 18 -1 -21 -48 -49 -58 -82 -126 -74 -148 10 -26 46 -19 53 9 3 14 10 25 15 25 8 0 27 -60 43 -132 7 -31 27 -36 46 -13 15 18 7 60 -27 143 l-26 63 28 37 c15 20 39 56 54 79 14 23 28 42 31 42 3 0 20 -23 38 -52 18 -29 42 -64 54 -79 l21 -27 -27 -60 c-34 -75 -34 -156 -1 -156 16 0 21 9 26 45 4 25 13 61 21 80 l15 35 17 -32 c18 -36 28 -40 46 -18 18 22 -4 75 -62 151 l-49 65 38 -19 c21 -11 55 -25 76 -32 52 -16 95 -55 114 -102 10 -26 21 -38 32 -36 26 5 24 45 -3 94 l-26 46 53 -6 c28 -3 63 -7 77 -8 19 -2 25 2 25 17 0 35 -49 50 -171 51 -108 1 -114 2 -175 36 -36 19 -74 49 -87 68 -22 33 -22 38 -20 278 l3 245 47 12 c101 25 175 104 199 211 10 43 9 56 -1 69 -28 33 -159 -10 -216 -71 l-34 -35 0 26 c0 15 10 40 21 57 47 65 60 161 34 239 -15 43 -68 119 -84 119 -4 0 -19 -12 -33 -27z m59 -90 c20 -50 14 -159 -11 -198 l-19 -29 -20 40 c-26 50 -28 145 -3 192 21 42 33 41 53 -5z m-179 -290 c49 -23 112 -109 112 -153 0 -26 -74 3 -123 49 -31 28 -67 91 -67 116 0 18 19 15 78 -12z m382 15 c-1 -29 -39 -95 -73 -125 -34 -30 -103 -60 -114 -50 -10 11 19 75 50 111 46 52 137 94 137 64z M140 975 c-10 -12 -10 -18 0 -30 11 -13 59 -15 320 -15 270 0 309 2 321 16 11 14 10 18 -6 30 -29 21 -617 20 -635 -1z M924 975 c-4 -8 -4 -22 0 -30 8 -23 617 -23 636 0 10 12 10 18 0 30 -11 13 -59 15 -322 15 -265 0 -309 -2 -314 -15z M134 766 c-3 -8 -3 -20 1 -26 4 -6 114 -10 326 -10 l320 0 -3 23 -3 22 -318 3 c-271 2 -318 0 -323 -12z M927 773 c-4 -3 -7 -14 -7 -24 0 -24 10 -25 350 -19 287 5 295 6 295 25 0 20 -8 20 -316 23 -173 1 -318 -1 -322 -5z M133 554 c-3 -8 -1 -20 4 -25 5 -5 135 -8 324 -7 306 3 314 3 314 23 0 20 -8 20 -318 23 -282 2 -318 1 -324 -14z M922 548 l3 -23 316 -3 c283 -2 317 -1 323 14 3 9 4 20 0 25 -3 5 -144 9 -325 9 l-320 0 3 -22z"/>
      </g>
    </svg>
  );
}

function CelebrationSpecialistIcon({size=100,color=C.burgundy}){
  return (
    <svg width={size} height={size} viewBox="0 0 166 166" xmlns="http://www.w3.org/2000/svg">
      <g transform="translate(0.000000,166.000000) scale(0.100000,-0.100000)" fill={color} stroke="none">
        <path d="M865 1589 c-51 -29 -60 -85 -20 -124 48 -49 125 -19 125 49 0 59 -59 101 -105 75z m50 -74 c0 -30 -39 -27 -43 3 -3 20 0 23 20 20 15 -2 23 -10 23 -23z M677 1410 c-69 -54 -24 -160 68 -160 16 0 39 12 57 30 26 27 30 36 25 67 -13 76 -91 109 -150 63z m88 -64 c0 -36 -26 -56 -49 -37 -16 14 -22 52 -9 65 4 4 19 6 33 4 20 -2 25 -8 25 -32z M882 1260 c-111 -68 -23 -240 98 -190 40 16 60 51 60 102 -1 89 -82 134 -158 88z m98 -59 c24 -46 3 -83 -47 -83 -28 0 -53 25 -53 53 0 45 79 68 100 30z M656 1084 c-14 -15 -26 -35 -26 -45 0 -17 -9 -18 -102 -15 -56 2 -108 2 -115 -1 -18 -7 -16 -79 4 -139 12 -35 35 -67 79 -112 71 -71 142 -108 240 -125 l64 -11 0 -211 c0 -166 -3 -215 -14 -231 -16 -23 -62 -44 -154 -70 -67 -18 -88 -35 -78 -59 5 -13 41 -15 278 -13 273 3 273 3 276 25 3 20 -7 25 -97 53 -64 20 -108 40 -125 57 l-26 26 0 211 0 212 64 11 c175 30 308 146 336 292 16 86 23 83 -218 82 l-212 -1 -14 34 c-17 41 -42 56 -94 56 -29 0 -46 -7 -66 -26z m102 -36 c7 -7 12 -27 12 -45 l0 -33 220 0 c222 0 236 -2 212 -35 -8 -12 -742 -15 -742 -3 0 3 -3 13 -6 22 -5 14 8 16 110 16 l116 0 0 33 c0 38 15 57 45 57 12 0 26 -5 33 -12z m402 -207 c-76 -107 -250 -169 -402 -143 -92 16 -198 72 -246 130 l-35 42 352 0 352 0 -21 -29z m-293 -707 c21 -14 39 -27 42 -30 2 -2 -33 -4 -79 -4 l-83 0 39 29 c21 16 40 30 41 30 1 1 19 -11 40 -25z M953 843 c-15 -6 -17 -49 -3 -58 6 -3 19 -3 31 0 15 5 19 14 17 33 -3 26 -20 36 -45 25z M865 774 c-11 -12 -12 -20 -5 -35 12 -22 33 -25 49 -5 25 30 -16 67 -44 40z"/>
      </g>
    </svg>
  );
}

const WINE_POOL_COLORS = { red:"#7A1030", white:"#E6C866", rosé:"#EBA9BE" };
const FLUTE_BASELINE_NUDGE = 2; // px upward offset to align flute foot with glass feet
function WineColorIcon({ colorId }) {
  if (colorId === "sparkling") {
    return (
      <span style={{ width:24, height:24, display:"flex", alignItems:"center", justifyContent:"center" }}>
        <span style={{ display:"inline-block", transform:`translateY(-${FLUTE_BASELINE_NUDGE}px)` }}>
          <svg width={24} height={24} viewBox="0 0 166 166" xmlns="http://www.w3.org/2000/svg">
            <g transform="translate(0.000000,166.000000) scale(0.100000,-0.100000)" fill={C.burgundy} stroke="none">
              <path d="M865 1589 c-51 -29 -60 -85 -20 -124 48 -49 125 -19 125 49 0 59 -59 101 -105 75z m50 -74 c0 -30 -39 -27 -43 3 -3 20 0 23 20 20 15 -2 23 -10 23 -23z M677 1410 c-69 -54 -24 -160 68 -160 16 0 39 12 57 30 26 27 30 36 25 67 -13 76 -91 109 -150 63z m88 -64 c0 -36 -26 -56 -49 -37 -16 14 -22 52 -9 65 4 4 19 6 33 4 20 -2 25 -8 25 -32z M882 1260 c-111 -68 -23 -240 98 -190 40 16 60 51 60 102 -1 89 -82 134 -158 88z m98 -59 c24 -46 3 -83 -47 -83 -28 0 -53 25 -53 53 0 45 79 68 100 30z M656 1084 c-14 -15 -26 -35 -26 -45 0 -17 -9 -18 -102 -15 -56 2 -108 2 -115 -1 -18 -7 -16 -79 4 -139 12 -35 35 -67 79 -112 71 -71 142 -108 240 -125 l64 -11 0 -211 c0 -166 -3 -215 -14 -231 -16 -23 -62 -44 -154 -70 -67 -18 -88 -35 -78 -59 5 -13 41 -15 278 -13 273 3 273 3 276 25 3 20 -7 25 -97 53 -64 20 -108 40 -125 57 l-26 26 0 211 0 212 64 11 c175 30 308 146 336 292 16 86 23 83 -218 82 l-212 -1 -14 34 c-17 41 -42 56 -94 56 -29 0 -46 -7 -66 -26z m102 -36 c7 -7 12 -27 12 -45 l0 -33 220 0 c222 0 236 -2 212 -35 -8 -12 -742 -15 -742 -3 0 3 -3 13 -6 22 -5 14 8 16 110 16 l116 0 0 33 c0 38 15 57 45 57 12 0 26 -5 33 -12z m402 -207 c-76 -107 -250 -169 -402 -143 -92 16 -198 72 -246 130 l-35 42 352 0 352 0 -21 -29z m-293 -707 c21 -14 39 -27 42 -30 2 -2 -33 -4 -79 -4 l-83 0 39 29 c21 16 40 30 41 30 1 1 19 -11 40 -25z M953 843 c-15 -6 -17 -49 -3 -58 6 -3 19 -3 31 0 15 5 19 14 17 33 -3 26 -20 36 -45 25z M865 774 c-11 -12 -12 -20 -5 -35 12 -22 33 -25 49 -5 25 30 -16 67 -44 40z"/>
            </g>
          </svg>
        </span>
      </span>
    );
  }
  return (
    <span style={{ width:24, height:24, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <svg width={17} height={17} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M69.4506 3C75.3075 19.8831 77.5958 30.1986 77.1453 41.1529C72.0701 54.9699 66.0105 59.2673 51.4963 62.9545V84.7562C57.6853 91.9797 62.4326 94.0814 71.6949 96.6188M29.7498 3C23.8929 19.8831 21.6047 30.1986 22.0551 41.1529C27.1304 54.9699 33.19 59.2673 47.7041 62.9545V84.7562C41.5152 91.9797 36.7679 94.0814 27.5055 96.6188" stroke="#5F5E5A" strokeWidth="5"/>
        <path d="M28.0916 96.9394H70.733M29 3.32062H70" stroke="#5F5E5A" strokeWidth="5"/>
        <ellipse cx="50.2138" cy="36.0231" rx="12" ry="11.5" fill={WINE_POOL_COLORS[colorId]}/>
      </svg>
    </span>
  );
}

function RichWhiteDevoteeIcon({size=100,color=C.burgundy}){
  return (
    <svg width={size} height={size} viewBox="0 0 169 169" xmlns="http://www.w3.org/2000/svg">
      <g transform="translate(0.000000,169.000000) scale(0.100000,-0.100000)" fill={color} stroke="none">
        <path d="M714 1615 c-98 -18 -137 -31 -222 -76 -248 -132 -403 -365 -427 -645 -12 -142 32 -326 109 -453 221 -362 657 -491 1030 -304 89 44 116 63 195 138 321 303 316 843 -11 1139 -192 175 -430 246 -674 201z m106 -65 c0 -14 -9 -20 -37 -25 -21 -3 -67 -12 -102 -20 -52 -12 -66 -13 -73 -3 -14 23 -9 27 45 42 75 21 88 24 130 25 30 1 37 -2 37 -19z m238 -5 c48 -15 51 -18 41 -36 -8 -15 -17 -19 -32 -15 -12 4 -59 14 -104 23 -71 13 -83 18 -83 36 0 18 5 19 63 14 34 -3 86 -13 115 -22z m-508 -49 c0 -2 3 -11 6 -19 3 -10 -12 -24 -50 -45 -30 -17 -70 -45 -89 -62 -33 -29 -35 -29 -51 -11 -16 17 -14 20 36 59 85 64 148 98 148 78z m701 -44 c94 -61 108 -77 87 -98 -14 -14 -19 -13 -51 15 -19 17 -59 46 -89 64 -37 22 -52 37 -50 50 5 26 23 21 103 -31z m-251 2 c114 -26 204 -77 289 -163 66 -66 134 -156 103 -137 -6 3 -13 -7 -17 -26 l-7 -32 -20 37 c-29 58 -140 169 -207 207 -43 26 -61 42 -63 59 -4 32 -28 39 -35 11 -3 -13 -11 -19 -19 -16 -43 16 -155 28 -218 22 -234 -21 -423 -171 -504 -398 -22 -62 -26 -90 -25 -183 0 -105 24 -275 45 -320 7 -14 4 -13 -11 5 -11 14 -32 53 -45 86 -120 298 13 654 296 793 46 23 108 47 138 55 73 19 219 19 300 0z m-11 -111 c181 -49 320 -189 372 -373 15 -54 16 -217 1 -232 -10 -10 -19 24 -42 162 -6 36 -27 99 -47 140 -110 227 -367 327 -604 235 -13 -5 -15 0 -11 24 5 34 29 46 127 60 70 9 129 5 204 -16z m-659 -28 c11 -13 9 -19 -8 -38 -11 -12 -38 -51 -59 -87 -27 -47 -43 -64 -55 -62 -10 2 -18 8 -18 13 0 25 103 189 119 189 5 0 14 -7 21 -15z m1109 -37 c45 -62 83 -133 74 -142 -10 -10 -43 -7 -43 4 0 5 -24 45 -54 89 -39 57 -51 82 -43 90 18 18 24 14 66 -41z m-849 -108 c-7 -30 -28 -89 -46 -131 -60 -137 -66 -219 -24 -332 45 -119 173 -214 303 -224 70 -6 158 14 213 48 l32 19 -10 -40 c-9 -33 -17 -44 -46 -55 -60 -26 -158 -37 -219 -25 -205 37 -339 192 -367 423 -7 55 -5 62 37 145 49 96 77 178 77 228 0 24 8 39 28 54 l27 22 3 -39 c2 -21 -2 -63 -8 -93z m380 68 c125 -38 230 -140 275 -268 22 -63 44 -209 33 -216 -27 -17 -118 -259 -118 -314 0 -25 -33 -60 -56 -60 -4 0 -4 27 -1 60 7 58 24 115 81 263 79 205 -29 423 -238 483 -83 23 -170 15 -248 -24 -57 -28 -59 -28 -54 -8 3 11 6 29 6 38 0 16 83 49 150 61 34 6 127 -2 170 -15z m-495 -75 c-28 -93 -74 -198 -79 -183 -3 8 -10 27 -15 41 -8 21 -5 35 11 65 19 37 85 124 93 124 2 0 -3 -21 -10 -47z m448 -28 c147 -38 231 -146 231 -295 0 -92 -24 -150 -86 -212 -73 -74 -169 -104 -270 -86 -73 13 -118 39 -171 98 -156 173 -68 445 160 499 55 13 73 13 136 -4z m542 -142 c20 -75 19 -229 -1 -307 -59 -228 -261 -424 -482 -467 -78 -15 -252 -7 -307 14 -22 9 -32 16 -22 16 9 1 17 8 17 17 0 14 7 15 38 10 215 -39 388 6 528 137 36 33 82 90 104 126 31 51 40 61 40 42 0 -15 4 -21 9 -15 12 13 41 225 41 299 0 60 -16 222 -26 260 -2 11 7 -1 21 -27 14 -26 32 -73 40 -105z m-1265 69 c0 -4 -7 -32 -15 -62 -8 -30 -15 -72 -15 -92 0 -34 -3 -38 -26 -38 -26 0 -27 1 -21 50 3 28 14 77 23 110 15 53 18 58 35 49 11 -5 19 -13 19 -17z m1365 -58 c20 -96 20 -134 -3 -134 -14 0 -21 16 -34 83 -9 45 -20 92 -23 104 -5 16 -2 22 12 25 27 7 32 -2 48 -78z m-1190 -175 c33 -160 67 -233 148 -314 141 -141 326 -181 510 -110 14 6 17 2 17 -23 0 -41 -35 -53 -171 -59 -172 -9 -292 39 -402 158 -102 109 -147 220 -147 359 0 70 8 126 16 117 2 -2 15 -59 29 -128z m-205 -56 c0 -20 7 -66 16 -101 14 -54 14 -66 3 -73 -8 -5 -17 -9 -20 -9 -9 0 -39 110 -46 170 -6 49 -5 50 21 50 23 0 26 -4 26 -37z m1410 0 c0 -36 -25 -156 -35 -173 -3 -5 -14 -6 -26 -3 -15 4 -19 11 -14 27 3 11 13 58 22 104 14 66 20 82 35 82 14 0 18 -8 18 -37z m-251 -133 c1 -18 -49 -103 -84 -145 -14 -16 -25 -25 -25 -19 0 15 36 118 63 181 l22 52 12 -27 c7 -15 12 -34 12 -42z m-848 -240 c31 -26 75 -58 97 -70 29 -17 41 -30 44 -51 3 -16 3 -29 1 -29 -2 0 -25 11 -51 24 -70 35 -164 109 -203 159 -30 39 -34 50 -31 90 l4 46 40 -60 c22 -33 67 -82 99 -109z m-168 17 c28 -36 29 -40 14 -55 -16 -15 -18 -15 -32 3 -35 44 -73 102 -90 134 -17 33 -17 35 1 48 16 11 21 7 47 -40 16 -28 43 -69 60 -90z m1201 125 c6 -10 -54 -117 -92 -162 -25 -29 -32 -33 -45 -22 -14 12 -9 23 39 93 30 44 54 84 54 89 0 12 37 13 44 2z m-1010 -293 c56 -34 69 -55 41 -65 -16 -7 -65 20 -143 79 -50 38 -52 41 -36 58 16 18 18 18 49 -10 18 -16 58 -44 89 -62z m811 24 c-22 -19 -67 -50 -100 -68 -59 -34 -60 -34 -68 -13 -4 11 -6 22 -5 23 2 1 26 14 53 30 28 16 68 43 89 62 37 30 41 32 55 17 15 -14 12 -19 -24 -51z m-572 -110 c71 -14 87 -20 87 -35 0 -24 -58 -24 -149 -2 -76 19 -84 24 -75 48 5 12 13 16 28 11 11 -3 60 -13 109 -22z m360 7 c4 -17 -3 -22 -46 -35 -29 -9 -80 -19 -114 -22 -58 -5 -63 -4 -63 14 0 17 11 22 73 33 39 6 81 16 92 20 34 14 52 11 58 -10z M779 1075 c-25 -8 -66 -33 -91 -55 -166 -146 -60 -423 162 -423 95 0 177 51 223 138 27 52 28 155 3 205 -59 114 -184 171 -297 135z"/>
      </g>
    </svg>
  );
}
// SVG takes precedence; emoji is the fallback for archetypes without a custom SVG
// yet (and a safety net if an SVG is ever removed). Unknown key → null, as before.
const ARCHETYPE_EMOJI={CRISP_PURIST:'🌊',BOLD_MODERNIST:'🍷',BOLD_TRADITIONALIST:'🔥',EVERYDAY_ENTHUSIAST:'🍷',ELEGANT_CLASSICIST:'🥂',VELVET_TOUCH:'🍒',BRIGHT_ADVENTURER:'⚡',SWEET_SIPPER:'🍯',TERROIR_HUNTER:'🌿',CELEBRATION_SPECIALIST:'🫧',RICH_WHITE_DEVOTEE:'🍋'};
function ArchetypeIcon({archetypeKey,size=100,color=C.burgundy}){const icons={CRISP_PURIST:CrispPuristIcon,BOLD_MODERNIST:BoldModernistIcon,BOLD_TRADITIONALIST:BoldTraditionalistIcon,EVERYDAY_ENTHUSIAST:EverydayEnthusiastIcon,ELEGANT_CLASSICIST:ElegantClassicistIcon,VELVET_TOUCH:VelvetTouchIcon,BRIGHT_ADVENTURER:BrightAdventurerIcon,SWEET_SIPPER:SweetSipperIcon,TERROIR_HUNTER:TerroirHunterIcon,CELEBRATION_SPECIALIST:CelebrationSpecialistIcon,RICH_WHITE_DEVOTEE:RichWhiteDevoteeIcon};const Icon=icons[archetypeKey];if(Icon)return <Icon size={size} color={color}/>;const emoji=ARCHETYPE_EMOJI[archetypeKey];if(emoji)return <span style={{fontSize:Math.round(size*0.72),lineHeight:1,display:'inline-block',verticalAlign:'middle'}} role="img" aria-label={archetypeKey}>{emoji}</span>;return null;}

// Profile-score curve. Fast, rewarding early climb (where ~90% of users live and
// the conversion battle happens), asymptotic toward 0.99 past ~11 ratings (never reaches it).
// Early bands are explicit milestones; the tail is a smooth exponential approach.
function palateScorePct(count) {
  const milestones = { 0: 0.40, 1: 0.53, 2: 0.58, 3: 0.63, 4: 0.68, 5: 0.72, 6: 0.76, 7: 0.79, 8: 0.82, 9: 0.85, 10: 0.88, 11: 0.90 };
  if (count <= 11) return milestones[count];
  // Asymptotic approach to 0.99: 0.90 at count 11 → ~0.94@15, ~0.97@20, ~0.986@30, →0.99
  return 0.99 - (0.99 - 0.90) * Math.exp(-(count - 11) / 6.5);
}
function getPalateProgress(count) {
  const pct = palateScorePct(count);
  if (count >= 20) return { label: "Razor-sharp matches. Keep refining.", sublabel: null, pct, showCta: false };
  if (count >= 11) return { label: "Deeply dialed in. Keep rating.", sublabel: null, pct, showCta: false };
  if (count >= 6)  return { label: "Highly personalized. Still refining.", sublabel: null, pct, showCta: false };
  if (count >= 3)  return { label: "Your picks are getting sharper.", sublabel: null, pct, showCta: true };
  if (count >= 1)  return { label: "Getting more precise. Keep rating.", sublabel: null, pct, showCta: true };
  return { label: "Rate wines to sharpen your profile.", sublabel: null, pct, showCta: true };
}
// Given current rating count, return the next round milestone score and how many
// more ratings reach it — for the numeric CTA. Returns null once effectively maxed.
function nextScoreMilestone(count) {
  const current = Math.round(palateScorePct(count) * 100);
  // Promise the score the NEXT rating actually produces — honest, no overshoot.
  // Find the smallest number of additional ratings that moves the displayed score at all.
  for (let n = count + 1; n <= count + 60; n++) {
    const next = Math.round(palateScorePct(n) * 100);
    if (next > current) {
      return { target: next, more: n - count };
    }
  }
  return null; // effectively maxed (score no longer moves)
}
// Best-effort haptic. No-op on iOS Safari/PWA (navigator.vibrate unsupported);
// works on Android. Structured as a helper so a future native WKWebView wrapper
// can route it to real iOS haptics.
function triggerHaptic() {
  try { if (navigator && typeof navigator.vibrate === 'function') navigator.vibrate([12, 40, 24]); } catch {}
}
function PalateProgressBar({ count, onCtaPress, context = "home", animateFrom = null, onAnimateDone = null }) {
  const p = getPalateProgress(count);
  const currentScore = Math.round(p.pct * 100);
  // Celebratory count-up: animate the displayed number from animateFrom → current.
  const [displayScore, setDisplayScore] = useState(currentScore);
  const [scorePulsing, setScorePulsing] = useState(false);
  const [celebrating, setCelebrating] = useState(false);
  const [landed, setLanded] = useState(false);
  const delta = (animateFrom != null && currentScore > animateFrom) ? currentScore - animateFrom : 0;
  useEffect(() => {
    if (animateFrom == null || animateFrom >= currentScore) { setDisplayScore(currentScore); setCelebrating(false); return; }
    setScorePulsing(true);
    setCelebrating(true);
    triggerHaptic();
    const from = animateFrom, to = currentScore, dur = 2000, t0 = performance.now();
    let raf, endTimer;
    const step = (now) => {
      const prog = Math.min((now - t0) / dur, 1);
      const eased = 1 - Math.pow(1 - prog, 3);
      setDisplayScore(Math.round(from + (to - from) * eased));
      if (prog < 1) { raf = requestAnimationFrame(step); }
      else {
        setScorePulsing(false);
        setLanded(true);
        // Clear AFTER the longest scaled animation finishes (2x): count-up ends at
        // ~2.0s, longest post-start animation is the +N badge at ~3.2s; this fires
        // ~4.2s from start, safely past all of them, so it never cuts off mid-celebration.
        endTimer = setTimeout(() => { setLanded(false); setCelebrating(false); if (onAnimateDone) onAnimateDone(); }, 2200);
      }
    };
    raf = requestAnimationFrame(step);
    return () => { cancelAnimationFrame(raf); if (endTimer) clearTimeout(endTimer); };
  }, [animateFrom, currentScore]); // eslint-disable-line react-hooks/exhaustive-deps
  if (context === "home") {
    return (
      <div style={{ padding: "0 18px 18px", position: "relative", zIndex: 1 }}>
        <style>{`
          @keyframes tpScorePop{0%{transform:scale(1)}40%{transform:scale(1.25)}100%{transform:scale(1)}}
          @keyframes tpScorePulse{0%,100%{transform:scale(1)}50%{transform:scale(1.12)}}
          @keyframes tpRingPulse{0%{transform:translate(-50%,-50%) scale(0.5);opacity:0.55}100%{transform:translate(-50%,-50%) scale(2.8);opacity:0}}
          @keyframes tpDeltaBadge{0%{opacity:0;transform:translateY(6px) scale(0.8)}18%{opacity:1;transform:translateY(0) scale(1)}80%{opacity:1;transform:translateY(0) scale(1)}100%{opacity:0;transform:translateY(-8px) scale(0.9)}}
        `}</style>
        <div style={{ display:"flex", alignItems:"baseline", gap:6, marginBottom:6 }}>
          <div style={{ position:"relative", display:"inline-flex", alignItems:"center" }}>
            {celebrating && [0,1,2].map(r => (
              <div key={r} style={{ position:"absolute", left:"50%", top:"50%", width:44, height:44, borderRadius:"50%", border:`2px solid ${C.burgundy}`, pointerEvents:"none", opacity:0, animation:`tpRingPulse 2s ease-out ${r*0.44}s` }} />
            ))}
            <div style={{ fontFamily:F.display, fontWeight:800, fontSize:28, color: (scorePulsing || celebrating) ? C.burgundyLight : "#FFFFFF", lineHeight:1, display:"inline-block", transformOrigin:"center", animation: landed ? "tpScorePop 1s ease-out" : scorePulsing ? "tpScorePulse 1s ease-in-out infinite" : "none", transition:"color 0.3s ease", position:"relative", zIndex:1 }}>{displayScore}</div>
          </div>
          <div style={{ fontFamily:F.body, fontWeight:400, fontSize:14, color:"rgba(255,255,255,0.6)", lineHeight:1 }}>/ 100 · Profile Score</div>
          {celebrating && delta > 0 && (
            <div style={{ marginLeft:2, background:C.burgundy, color:"#fff", fontFamily:F.display, fontWeight:800, fontSize:12, padding:"2px 8px", borderRadius:20, lineHeight:1.2, boxShadow:"0 2px 8px rgba(145,18,52,0.50)", animation:"tpDeltaBadge 3.2s ease forwards" }}>+{delta}</div>
          )}
        </div>
        <div style={{ display:"flex", gap:3, marginBottom:8 }}>
          {[0,1,2,3,4].map(seg => {
            const thresholds = [0.4, 0.53, 0.66, 0.84, 0.92];
            const segWidths = [3.2, 1.4, 1.1, 0.8, 0.5];
            const nextThreshold = thresholds[seg + 1] || 1;
            const segStart = thresholds[seg];
            const segFill = p.pct <= segStart ? 0 : p.pct >= nextThreshold ? 1 : (p.pct - segStart) / (nextThreshold - segStart);
            return (
              <div key={seg} style={{ flex:segWidths[seg], height:6, borderRadius:3, background:"rgba(255,255,255,0.15)", overflow:"hidden" }}>
                <div style={{ height:"100%", width:`${Math.round(segFill*100)}%`, borderRadius:3, background:"rgba(255,255,255,0.55)", transition:"width 0.6s ease" }} />
              </div>
            );
          })}
        </div>
        {(() => {
          const m = nextScoreMilestone(count);
          const ctaText = m
            ? `Keep sharpening your matches — rate ${m.more} more wine${m.more === 1 ? '' : 's'} to reach ${m.target}% →`
            : "Keep sharpening your matches — rate more to perfect your picks →";
          return (
            <div onClick={onCtaPress} style={{ fontSize: 13, color: "rgba(255,255,255,0.85)", fontWeight: 600, fontFamily: F.body, cursor: "pointer" }}>{ctaText}</div>
          );
        })()}
      </div>
    );
  }
  return (
    <div style={{ margin: "16px 0 0", padding: "0 4px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <div style={{ fontSize: 12, fontFamily: F.body, color: C.charcoalSoft, fontWeight: 500 }}>{p.label}</div>
        <div style={{ fontSize: 12, fontFamily: F.body, color: C.charcoalSoft, flexShrink: 0, marginLeft: 8 }}>{count} rated</div>
      </div>
      <div style={{ height: 5, borderRadius: 3, background: C.burgundyBg, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${Math.min(p.pct * 100, 100)}%`, borderRadius: 3, background: C.burgundy, transition: "width 0.6s ease" }} />
      </div>
      {p.showCta && onCtaPress && (
        <div onClick={onCtaPress} style={{ marginTop: 6, fontSize: 12, fontFamily: F.body, color: C.burgundy, fontWeight: 600, cursor: "pointer" }}>{p.sublabel}</div>
      )}
    </div>
  );
}

// ─── STATUS → COPY MAP ───────────────────────────────────────────────────────
// Mirrors the EMPTY_COPY pattern (results screen, ~7194): server tags a cause
// (here, an HTTP status instead of an emptyReason string), client maps it to
// curated copy, generic fallback + console.warn when unmapped. Declared at
// module level (EMPTY_COPY itself is local to the closure that renders it)
// because this map is read from CellarNoteModal, a separate top-level
// component defined earlier in this file, as well as from the main
// component's three banners — a local const in either place couldn't reach
// the other.
// 401 is deliberately absent: handleAuthExpired intercepts it before any of
// these render sites ever see it (never duplicate that job here).
const STATUS_COPY = {
  429: {
    // Verbatim from apiLimiter's `message` option (server.js ~761). Note:
    // scanLimiter's surfaces (ScanScreen, ImageCapture) do NOT read this map
    // — they still rewrite to their own hand-written copy at catch time, so a
    // real scanLimiter 429 does not yet render this string.
    heading: 'Just a moment',
    body: 'Too many requests — slow down and enjoy the wine.',
  },
  403: {
    // PLACEHOLDER — reachable only once entitlement enforcement ships. Rewrite
    // as benefit-led paywall copy during the paywall build.
    heading: 'Your free trial is complete',
    body: 'Subscribe to keep getting recommendations built around your palate.',
  },
};
const DEFAULT_STATUS_COPY = { heading: 'Something went wrong', body: 'Something went wrong — please try again.' };
function statusCopy(status) {
  const copy = STATUS_COPY[status];
  if (!copy) {
    console.warn('[STATUS COPY] Unmapped status reached error banner:', status);
    return DEFAULT_STATUS_COPY;
  }
  return copy;
}
// Error state holds { status, message }. status drives the map; message is
// only ever used for the one case with no status at all — the SSE
// recommend-stream path (App.jsx ~713), whose in-band { type: 'error' }
// event carries a server-authored, already-curated message and no HTTP
// status. That case is handled explicitly here (not left to fall through to
// the generic default) since the message itself is already safe to show.
function resolveErrorCopy(errState) {
  if (!errState) return null;
  const { status, message } = errState;
  if (status == null) {
    return message ? { heading: DEFAULT_STATUS_COPY.heading, body: message } : DEFAULT_STATUS_COPY;
  }
  return statusCopy(status);
}

export default function TruePour() {
  const [tilesReady, setTilesReady] = useState(false);
  useEffect(() => { const t = setTimeout(() => setTilesReady(true), 400); return () => clearTimeout(t); }, []);
  const [screen, setScreen] = useState("home");
  const [screenHistory, setScreenHistory] = useState([]);
  const [context, setContext] = useState(null);
  const [sliders, setSliders] = useState(DEFAULT_SLIDERS);
  const [knownWines, setKnownWines] = useState("");
  const [meal, setMeal] = useState("");
  const [occasion, setOccasion] = useState(null);
  const [wineColor, setWineColor] = useState(null);
  const [selectedBudget, setSelectedBudget] = useState(null);
  const [restaurantOrderType, setRestaurantOrderType] = useState(null);
  const [homeVibe, setHomeVibe] = useState(null);
  const [homeFlowPath, setHomeFlowPath] = useState(null);
  const [specificBottle, setSpecificBottle] = useState("");
  const [styleGuide, setStyleGuide] = useState(null);
  const [bottleMatch, setBottleMatch] = useState(null);
  const [bottleMatchLoading, setBottleMatchLoading] = useState(false);
  const [bottleMatchError, setBottleMatchError] = useState(null);
  const [styleGuideLoading, setStyleGuideLoading] = useState(false);
  const [styleGuideError, setStyleGuideError] = useState(null);
  const [selectedStyleCard, setSelectedStyleCard] = useState(null);
  const [styleGuideSelection, setStyleGuideSelection] = useState(null);
  const [homeFewBottles, setHomeFewBottles] = useState([]);
  const [lastResults, setLastResults] = useState(() => {
    try {
      const stored = localStorage.getItem("truepour_last_results");
      if (stored) {
        const parsed = JSON.parse(stored);
        const ageMs = Date.now() - (parsed.timestamp || 0);
        const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
        if (ageMs < sevenDaysMs) return parsed;
      }
    } catch {}
    return null;
  });
  const [session, setSession] = useState(null);
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authMode, setAuthMode] = useState('signup');
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [authMessage, setAuthMessage] = useState('');
  const [authConfirmPending, setAuthConfirmPending] = useState(false);
  const [authConfirmEmail, setAuthConfirmEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [scannedWines, setScannedWines] = useState(null);
  const [scannedRows, setScannedRows] = useState([]);
  const [scannedDish, setScannedDish] = useState(null);
  const [scanMode, setScanMode] = useState(null);
  const [scanTarget, setScanTarget] = useState("wines");
  const [showScanner, setShowScanner] = useState(false);
  const [pairings, setPairings] = useState([]);
  const [emptyReason, setEmptyReason] = useState(null);
  // ── Produce-on-write recommendation-history state (Commit 1) ──
  // writtenGenerationsRef: synchronous dedupe set of generationIds already
  //   persisted (defeats the async double-insert race).
  // generationSupabaseRef: generationId -> server row id (once POST resolves).
  // pendingGenerationUpdateRef: generationId -> latest {wines,reviewed} to
  //   flush as a PATCH the moment the create POST returns an id.
  // currentGenerationIdRef: the generationId of the currently-shown results.
  const writtenGenerationsRef = useRef(new Set());
  const generationSupabaseRef = useRef({});
  const pendingGenerationUpdateRef = useRef({});
  const currentGenerationIdRef = useRef(null);
  const [ratings, setRatings] = useState({});
  const [triedIt, setTriedIt] = useState({});
  const [reviewTags, setReviewTags] = useState({});
  const [savedWines, setSavedWines] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showToast, setShowToast] = useState(null);
  const [showScanModal, setShowScanModal] = useState(false);
  const [scanModalMode, setScanModalMode] = useState(false);
  const [wineModal, setWineModal] = useState(null);
  const [expandedCard, setExpandedCard] = useState(0);
  const [pendingCellarWine, setPendingCellarWine] = useState(null);
  const [pendingBottleMatchWine, setPendingBottleMatchWine] = useState(null);
  const [pendingBottleMatchStars, setPendingBottleMatchStars] = useState(0);
  const [showRatingHint, setShowRatingHint] = useState(true);
  const [dismissedHints, setDismissedHints] = useState({});
  const [activitySearch, setActivitySearch] = useState('');
  useEffect(() => { if (screen === 'activity') setShowRatingHint(true); }, [screen]);
  const [pendingCellarStars, setPendingCellarStars] = useState(0);
  const [cellarSort, setCellarSort] = useState('rating');
  const [showReturnReview, setShowReturnReview] = useState(false);
  const [userProfile, setUserProfile] = useState({ likedWines:[], history:[] });
  const cellarRatings = userProfile.cellarRatings || {};
  const historyRatedWines = (userProfile.history || []).flatMap(h => h.wines || []).filter(w => w.rating > 0).map(w => ({ name: `${w.producer} ${w.wine}`, rating: w.rating }));
  const mergedRatedCount = (() => { const merged = { ...Object.fromEntries(Object.entries(cellarRatings)) }; historyRatedWines.forEach(w => { if (!merged[w.name]) merged[w.name] = w.rating; else merged[w.name] = Math.max(merged[w.name], w.rating); }); return Object.keys(merged).length; })();
  // Names already counted toward the Profile Score (unique rated wines). Used to
  // decide whether a rating is a NEW wine (raises the score → Layer A toast) vs a
  // re-rate (count unchanged → no toast). Mirrors mergedRatedCount's dedupe.
  const countedRatedNames = () => {
    const s = new Set();
    Object.entries(cellarRatings).forEach(([k, v]) => { if (v) s.add(k); });
    historyRatedWines.forEach(w => s.add(w.name));
    return s;
  };
  const isNewRatedWine = (label) => !!label && !countedRatedNames().has(label);
  const SCORE_NEW_WINE_MSG = "Nice — rating this wine improved your taste profile! See your updated score on the Discover tab.";
  // Step 7: `response` is the parsed /api/rate-wine JSON body (or
  // null/undefined at the two call sites that don't read it — see
  // decideRatingToast's own doc for why that's always a safe
  // 'fallthrough', identical to passing a flag-off response). When the
  // server has the step7 fields (flag on), decideRatingToast (pure,
  // tested in test_client_step7_pure.js) decides the pending/terminal/
  // nothing cases FIRST; only its 'fallthrough' result reaches the
  // original drift/new-wine logic below, so a flag-off response (the
  // step7 fields simply absent) produces EXACTLY today's behavior.
  const maybeShowRatingToast = (label, stars, nudgedSliders, nudgedExtSliders, response) => {
    const decision = decideRatingToast(response, stars);
    if (decision.type === 'none') return;
    if (decision.type === 'message') { showPalateToast(decision.text); return; }
    // decision.type === 'fallthrough' — original logic, unchanged.
    if (nudgedSliders && nudgedExtSliders) {
      const drift = computeToastMessage(nudgedSliders, nudgedExtSliders);
      if (drift) { showPalateToast(drift); return; }
    }
    if (isNewRatedWine(label)) { showPalateToast(SCORE_NEW_WINE_MSG); return; }
  };
  // Score count-up trigger: the previous (seen) score to animate up from, or null.
  // The durable fire-once watermark is profiles.last_seen_score (Commit 2a); the old
  // truepour_last_score localStorage scaffold is removed (it overwrote at rating time).
  const [scoreAnimateFrom, setScoreAnimateFrom] = useState(null);
  const [scanAddedCount, setScanAddedCount] = useState(0);
  const [deepProfile, setDeepProfile] = useState(false);
  const [extendedSliders, setExtendedSliders] = useState({tannin:null,oak:null,alcohol:null,aromatics:null,complexity:null,color:null});
  const [userArchetype, setUserArchetype] = useState(null);
  const [showArchetypeReveal, setShowArchetypeReveal] = useState(false);
  const [quizSliders, setQuizSliders] = useState(null);
  const [quizExtendedSliders, setQuizExtendedSliders] = useState(null);
  const [pendingArchetypeReveal, setPendingArchetypeReveal] = useState(null);
  const [revealStep, setRevealStep] = useState(0);
  const [showRetakeWarning, setShowRetakeWarning] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteAccountStatus, setDeleteAccountStatus] = useState('idle');
  const [deleteAccountError, setDeleteAccountError] = useState('');
  const [palateUpdateSeen, setPalateUpdateSeen] = useState(() => {
    try { return localStorage.getItem('truepour_palate_update_seen') === 'true'; } catch { return false; }
  });
  // Step 7 (server-driven "changed since last seen"): populated from
  // GET /api/profile's own row (see rehydrateSession, where
  // profile.archetype_changed is read) — null/absent when the server
  // flag is off, in which case step7HasServerFields stays false and
  // shouldShowProfileBadge falls back to palateUpdateSeen/drift exactly
  // as today. step7HasServerFields is a SEPARATE boolean (not inferred
  // from profileChangeReason being non-null) because a null reason with
  // the flag ON is a real, meaningful state (silent drift) that must
  // still suppress the legacy fallback — inferring "flag on" from
  // "reason is non-null" would wrongly fall back to the legacy check on
  // every silent-drift profile.
  const [step7HasServerFields, setStep7HasServerFields] = useState(false);
  const [profileChangedAt, setProfileChangedAt] = useState(null);
  const [profileChangeReason, setProfileChangeReason] = useState(null);
  const [profileLastSeenChangeAt, setProfileLastSeenChangeAt] = useState(null);
  // Step 7 fix (item 2): captured ONCE when entering the Profile tab
  // (handleTabChange), via shouldShowCauseBannerOnEntry — NOT re-derived
  // from profileChangeReason on every render, which would show the
  // banner forever (the reason stays non-null after a genuine change).
  // Kept visible for the rest of THIS visit even after
  // profileLastSeenChangeAt is updated locally below.
  const [causeBannerVisibleThisVisit, setCauseBannerVisibleThisVisit] = useState(false);
  const [palateToast, setPalateToast] = useState(null);
  const palateToastTimerRef = useRef(null);
  // "Saved to Activity" confirmation: first-time-richer flag + last-toasted
  // generationId (so leaving one result via multiple exits doesn't stack).
  const [savedToActivitySeen, setSavedToActivitySeen] = useState(() => {
    try { return localStorage.getItem('truepour_saved_to_activity_seen') === 'true'; } catch { return false; }
  });
  const lastSavedToastGenRef = useRef(null);
  const [arrowEducationSeen, setArrowEducationSeen] = useState(() => {
    try { return localStorage.getItem('truepour_arrow_education_seen') === 'true'; } catch { return false; }
  });
  const [supabaseRatingCount, setSupabaseRatingCount] = useState(0);
  // Score-loop foundation: the Profile Score value the user has already SEEN
  // animate on Discover (durable, from profiles.last_seen_score). Read at the end
  // of rehydrateSession — i.e. AFTER session + profile are rehydrated — so it never
  // races the PKCE cold-load. Consumed by Commit 2b's animation, not here.
  const [lastSeenScore, setLastSeenScore] = useState(null);
  const [pendingDeleteWine, setPendingDeleteWine] = useState(null);
  const [profileComplete, setProfileComplete] = useState(false);
  const [activeTab, setActiveTab] = useState('discover');
  const [navVisible, setNavVisible] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [showMidQuizInterstitial, setShowMidQuizInterstitial] = useState(false);

  const [loveWineInput, setLoveWineInput] = useState("");
  const [showSplash, setShowSplash] = useState(false);
  const [reviewDismissed, setReviewDismissed] = useState(true);
  const shareCardRef = useRef(null);
  const modalShareCardRef = useRef(null);
  const profileShareCardRef = useRef(null);
  const [archScrolled, setArchScrolled] = useState(false);
  const [archCtaVisible, setArchCtaVisible] = useState(false);
  const archScrollRef = useRef(null);
  const archDescRef = useRef(null);
  const [archInlineCTAInView, setArchInlineCTAInView] = useState(false);
  const archInlineCTARef = useRef(null);
  const rehydratedRef = useRef(null);
  const recoveryInProgressRef = useRef(false);
  const rehydrateSessionRef = useRef(null);
  const [rehydrating, setRehydrating] = useState(true);

  useEffect(()=>{
    const rehydratingCountRef = { current: 0 };
    async function rehydrateSession(session) {
      rehydratingCountRef.current++;
      setRehydrating(true);
      try {
      if (!session?.user?.id) return;
      if (rehydratedRef.current === session.user.id) return;
      rehydratedRef.current = session.user.id;
      const { profile, status: profileStatus, raw: profileRaw } = await fetchSupabaseProfile(session);

      if (profile && profile.archetype) {
        setUserArchetype(toArchetypeKey(profile.archetype));
        setProfileComplete(true);
        setDeepProfile(!!profile.deep_profile);
        // Durable "already seen" score — available here, after session+profile are
        // rehydrated (safe scope class; not racing PKCE cold-load). For Commit 2b.
        setLastSeenScore(Number.isFinite(profile.last_seen_score) ? profile.last_seen_score : null);
        if (profile.sliders) {
          const validatedSliders = {};
          for (const k of Object.keys(DEFAULT_SLIDERS)) {
            const v = profile.sliders[k];
            validatedSliders[k] = (Number.isFinite(v) && v >= 1 && v <= 6) ? v : null;
          }
          setSliders(validatedSliders);
        }
        if (profile.extended_sliders) {
          setExtendedSliders(profile.extended_sliders);
        }
        if (profile.quiz_sliders) {
          setQuizSliders(profile.quiz_sliders);
        }
        if (profile.quiz_extended_sliders) {
          setQuizExtendedSliders(profile.quiz_extended_sliders);
        }
        if (profile.archetype_changed) {
          setPendingArchetypeReveal({
            previous: profile.previous_archetype,
            next: profile.archetype,
          });
        }
        if (profile.archetype_changed && profile.archetype) {
          const convertedKey = toArchetypeKey(profile.archetype);
          setUserArchetype(convertedKey);
          try {
            localStorage.setItem("truepour_archetype", convertedKey);
          } catch {}
        }
        // Step 7: these three keys are present on the profile row ONLY
        // when the server flag is on (GET /api/profile's own select list
        // is flag-gated) — 'profile_change_reason' in profile, not a
        // value check, is what detects that, so a genuinely null reason
        // (silent drift) is still correctly recognized as "flag on".
        const hasStep7Fields = Object.prototype.hasOwnProperty.call(profile, 'profile_change_reason');
        setStep7HasServerFields(hasStep7Fields);
        if (hasStep7Fields) {
          setProfileChangedAt(profile.profile_changed_at || null);
          setProfileChangeReason(profile.profile_change_reason || null);
          setProfileLastSeenChangeAt(profile.profile_last_seen_change_at || null);
        }
        // Fetch accurate rating count and run backfill if needed
        try {
          const countRes = await fetch('/api/rating-count', {
            headers: { 'Authorization': `Bearer ${session.access_token}` }
          });
          const countData = await countRes.json();
          setSupabaseRatingCount(countData.count || 0);

          const hasRatings = (countData.count || 0) > 0;

          // Requires last_backfill_at column in profiles table — add via Supabase SQL editor if missing:
          // ALTER TABLE profiles ADD COLUMN IF NOT EXISTS last_backfill_at timestamptz;
          if (hasRatings) {
            const lastBackfill = profile.last_backfill_at ? new Date(profile.last_backfill_at) : null;
            const hoursSinceBackfill = lastBackfill ? (Date.now() - lastBackfill.getTime()) / (1000 * 60 * 60) : Infinity;
            const storedRatingCount = (() => { try { return parseInt(localStorage.getItem('truepour_rating_count') || '0'); } catch { return 0; } })();
            const newRatingsAdded = (countData.count || 0) > storedRatingCount;
            const shouldBackfill = !lastBackfill || newRatingsAdded || hoursSinceBackfill > 24;

            try { localStorage.setItem('truepour_rating_count', String(countData.count || 0)); } catch {}

            if (shouldBackfill) {
              const backfillRes = await fetch('/api/backfill-ratings', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${session.access_token}` }
              });
              const backfillData = await backfillRes.json();
              if (backfillData.sliders) {
                setSliders(prev => ({ ...prev, ...backfillData.sliders }));
                setPalateUpdateSeen(false);
                try { localStorage.removeItem('truepour_palate_update_seen'); } catch {}
              }
              if (backfillData.extSliders) {
                setExtendedSliders(prev => ({ ...prev, ...backfillData.extSliders }));
              }
              if (backfillData.archetypeChanged && backfillData.newArchetype) {
                const convertedKey = toArchetypeKey(backfillData.newArchetype);
                setUserArchetype(convertedKey);
                try { localStorage.setItem('truepour_archetype', convertedKey); } catch {}
                setPendingArchetypeReveal({
                  previous: profile.archetype,
                  next: backfillData.newArchetype,
                  wineExamples: Object.entries(userProfile?.cellarRatings || {})
                    .filter(([_, r]) => r >= 4)
                    .sort((a,b) => b[1] - a[1])
                    .slice(0,2)
                    .map(([name]) => name),
                });
              }
            }
          }
        } catch(e) {
        }
        try {
          localStorage.setItem("truepour_profile_complete", "true");
          localStorage.setItem("truepour_archetype", profile.archetype);
          localStorage.setItem("truepour_archetype_version", String(profile.archetype_version || 2));
          if (profile.deep_profile) localStorage.setItem("truepour_deep_profile", "true");
        } catch {}
        try {
          const cellarRes = await fetch('/api/user-cellar', {
            headers: { 'Authorization': `Bearer ${session.access_token}` }
          });
          if (cellarRes.ok) {
            const cd = await cellarRes.json();
            try { localStorage.setItem('truepour_cellar_meta', JSON.stringify(cd.cellarMeta || {})); } catch {}
            setUserProfile(prev => ({
              ...prev,
              likedWines:    cd.likedWines,
              cellarRatings: cd.cellarRatings,
            }));
          }
        } catch (_) {}
        try {
          const histRes = await fetch('/api/recommendation-history', { headers: { 'Authorization': `Bearer ${session.access_token}` } });
          if (histRes.ok) { const hd = await histRes.json(); setUserProfile(prev => ({ ...prev, history: hd.history || prev.history || [] })); }
        } catch (_) {}
        setActiveTab('discover');
        setScreen('home');
        setShowSplash(false);
        try { localStorage.setItem("truepour_splash_seen", Date.now().toString()); } catch {}
      } else {
        // No real Supabase profile → route to onboarding.
        // (Legacy localStorage-migration path removed — it caused cross-account
        // profile bleed where a new account inherited a prior account's archetype.)
        // Show the FTUE welcome splash OVER the quiz screen so a brand-new user
        // gets context before Question 1. Clear any stale splash-seen flag so the
        // splash is not suppressed for a genuinely new user.
        try { localStorage.removeItem("truepour_splash_seen"); } catch {}
        setScreen('quiz');
        setShowSplash(true);
      }
      } finally {
        if (--rehydratingCountRef.current === 0) setRehydrating(false);
      }
    }
    // Expose rehydrateSession to the render scope (e.g. the setNewPassword submit
    // handler) — it is a closure-local function and otherwise out of scope there.
    rehydrateSessionRef.current = rehydrateSession;

    // Shared recovery detection: a password-reset session's access_token JWT carries
    // an amr claim whose most-recent entry has method "recovery". Synchronous and
    // available at getSession()/event time. Returns false on any decode failure.
    function isRecoverySessionFromToken(session) {
      try {
        const payload = JSON.parse(atob(session.access_token.split('.')[1]));
        const amr = Array.isArray(payload.amr) ? payload.amr : [];
        const mostRecent = amr.reduce((acc, e) =>
          (!acc || (e?.timestamp ?? 0) > (acc?.timestamp ?? 0)) ? e : acc, null);
        return mostRecent?.method === 'recovery';
      } catch (e) {
        console.error('[CLIENT DEBUG] JWT recovery decode failed:', e);
        return false;
      }
    }

    (async () => {
      // Capture the recovery marker BEFORE getSession() — the SDK clears the hash
      // during initialization, and amr is 'otp' not 'recovery', so the token check
      // cannot detect this flow. type=recovery in the hash is the SDK's own signal.
      let hashIsRecovery = false;
      try { hashIsRecovery = window.location.hash.indexOf('type=recovery') !== -1; } catch {}

      if (
        localStorage.getItem("truepour_archetype") &&
        localStorage.getItem("truepour_archetype") !== "null" &&
        localStorage.getItem("truepour_archetype") !== "undefined" &&
        localStorage.getItem("truepour_pending_migration") === null
      ) {
        localStorage.setItem("truepour_pending_migration", "true");
      }

      const { data: { session } } = await supabase.auth.getSession();
      setSession(session);

      if (session) {
        // Recovery detection: route to setNewPassword WITHOUT depending on the
        // PASSWORD_RECOVERY onAuthStateChange event (which races this IIFE and the
        // SIGNED_IN handler). Uses the shared synchronous amr check.
        const isRecoverySession = isRecoverySessionFromToken(session);

        // If a password reset already completed in this browser session, the
        // persisted token still carries amr:recovery until it rotates. Don't trap
        // the user back on setNewPassword on a manual refresh — treat it as normal.
        let recoveryAlreadyCompleted = false;
        try { recoveryAlreadyCompleted = sessionStorage.getItem('truepour_recovery_completed') === '1'; } catch {}

        if ((isRecoverySession || hashIsRecovery) && !recoveryAlreadyCompleted) {
          recoveryInProgressRef.current = true;
          setRecoveryMode(true);
          setScreen('setNewPassword');
          setShowSplash(false);
          setRehydrating(false);
          return;
        }
        // If PASSWORD_RECOVERY already fired and set the ref, skip rehydration here.
        if (recoveryInProgressRef.current) return;
        // PKCE cold-load migration: SIGNED_IN may have fired before listener
        // was registered. Run metadata migration here to cover that case.
        try {
          const meta = session.user?.user_metadata || {};
          const metaArch = meta.ftue_archetype;
          if (metaArch && metaArch !== 'null' && metaArch !== 'undefined') {
            const { profile: existingProfile } = await fetchSupabaseProfile(session);
            if (!existingProfile || !existingProfile.archetype) {
              await migrateProfileToSupabase(session, {
                archetype: metaArch,
                archetypeVersion: meta.ftue_archetype_version || 2,
                sliders: meta.ftue_sliders || {},
                extendedSliders: meta.ftue_extended_sliders || {},
                deepProfile: meta.ftue_deep_profile ?? true,
                likedWines: [],
                cellarRatings: {},
              });
            }
          }
        } catch (e) { console.error('[CLIENT DEBUG] PKCE cold-load migration failed:', e); }
        await rehydrateSession(session);
      } else {
        const hasLocalProfile = !!(localStorage.getItem('truepour_profile_complete') || localStorage.getItem('truepour_archetype'));
        if (hasLocalProfile) {
          // Returning user who is signed out → send to auth to sign back in.
          setScreen('auth');
          setShowSplash(false);
        } else {
          // Brand-new visitor, no session and no local profile → enter the FTUE:
          // splash renders over the quiz. Quiz completion routes to the archetype
          // screen, whose CTA gates on session and sends them to auth/signup.
          setScreen('quiz');
          setShowSplash(true);
        }
        setRehydrating(false);
      }
    })();

    supabase.auth.onAuthStateChange(async (event, session) => {
      setSession(session);
      if (event === 'PASSWORD_RECOVERY' && session) {
        recoveryInProgressRef.current = true;
        setRecoveryMode(true);
        setScreen('setNewPassword');
        setShowSplash(false);
        return;
      }
      if (event === 'SIGNED_IN' && session) {
        // Recovery cold-load: SIGNED_IN can fire during the IIFE's getSession() yield,
        // before recoveryInProgressRef is set, so the ref guard alone is unreliable.
        // Detect recovery synchronously from the token and bail BEFORE migration or
        // rehydrate, so this handler never sets rehydratedRef on a recovery load.
        if (isRecoverySessionFromToken(session)) {
          recoveryInProgressRef.current = true;
          setRecoveryMode(true);
          setScreen('setNewPassword');
          setShowSplash(false);
          return;
        }
        // Skip normal sign-in flow when a password reset is in progress.
        if (recoveryInProgressRef.current) return;
        // FTUE persistence: the quiz completes before signup, so the profile lives
        // only in localStorage. On first sign-in, if localStorage has a completed
        // profile but Supabase does not, migrate it up FIRST so the subsequent
        // rehydrate finds a real profile and routes to home (never a re-quiz).
        // Read from localStorage — this handler closes over stale React state.
        // Guard on null Supabase profile so an existing user is NEVER overwritten;
        // combined with the SIGNED_OUT clear above, this cannot bleed across accounts.
        try {
          const meta = session.user?.user_metadata || {};
          const metaArch = meta.ftue_archetype;
          if (metaArch && metaArch !== 'null' && metaArch !== 'undefined') {
            const { profile: existingProfile } = await fetchSupabaseProfile(session);
            if (!existingProfile || !existingProfile.archetype) {
              await migrateProfileToSupabase(session, {
                archetype: metaArch,
                archetypeVersion: meta.ftue_archetype_version || 2,
                sliders: meta.ftue_sliders || {},
                extendedSliders: meta.ftue_extended_sliders || {},
                deepProfile: meta.ftue_deep_profile ?? true,
                likedWines: [],
                cellarRatings: {},
              });
            }
          }
        } catch (e) { console.error('[CLIENT DEBUG] FTUE metadata migration failed:', e); }
        await rehydrateSession(session);
      }
      if (event === 'SIGNED_OUT') {
        rehydratedRef.current = null;
        recoveryInProgressRef.current = false;
        try { sessionStorage.removeItem('truepour_recovery_completed'); } catch {}
        // Clear the local profile so a signed-out user's archetype cannot bleed
        // into the NEXT account that signs up on this device. This is the guard
        // that makes migrate-on-auth safe to restore (see SIGNED_IN below).
        try {
          localStorage.removeItem('truepour_profile_complete');
          localStorage.removeItem('truepour_archetype');
          localStorage.removeItem('truepour_archetype_version');
          localStorage.removeItem('truepour_extended_sliders');
          localStorage.removeItem('truepour_deep_profile');
          localStorage.removeItem('truepour_love_wine');
          localStorage.removeItem('truepour_cellar_meta');
          localStorage.removeItem('truepour_rating_count');
          localStorage.removeItem(SK);
        } catch {}
      }
    });
    try { window.gtag && window.gtag('event', 'app_loaded', { test: true }); } catch(e) { console.error('gtag error', e); }
    if (window.__cancelShell) window.__cancelShell();
    const shell = document.getElementById('app-shell');
    if (shell) shell.style.display = 'none';
    try {
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('truepour_reviewed_')) {
          localStorage.removeItem(key);
        }
      });
      localStorage.removeItem('truepour_last_session_id');
    } catch {}
    if (!profileComplete) {
    const d = loadData();
    if (d) {
      setUserProfile(d.userProfile||{ likedWines:[], history:[] });
      if (d.sliders) {
        const validatedSliders = {};
        for (const k of Object.keys(DEFAULT_SLIDERS)) {
          const v = d.sliders[k];
          validatedSliders[k] = (Number.isFinite(v) && v >= 1 && v <= 6) ? v : null;
        }
        setSliders(validatedSliders);
        // Migration: saved sliders means the user completed at least the basic profile
        setProfileComplete(true);
        try { localStorage.setItem("truepour_profile_complete","true"); } catch {}
      }
    }
    try {
      const s = localStorage.getItem("truepour_last_results");
      if (s) {
        const r = JSON.parse(s);
        if (r && r.pairings && r.pairings.length > 0) setLastResults(r);
      }
    } catch {}
    try {
      const savedArchetype = localStorage.getItem("truepour_archetype");
      const savedDeepProfile = localStorage.getItem("truepour_deep_profile");

      if (savedArchetype && savedArchetype !== "null" && savedArchetype !== "undefined") {
        const validKey = ARCHETYPES[savedArchetype] ? savedArchetype : toArchetypeKey(savedArchetype);
        setUserArchetype(validKey);
        setProfileComplete(true);
        setDeepProfile(true);
        try {
          localStorage.setItem("truepour_profile_complete", "true");
          localStorage.setItem("truepour_deep_profile", "true");
        } catch {}
      }

      // Check if user has old archetype saved under old name keys
      // Show update message if they have a v1 archetype
      try {
        const savedArch = localStorage.getItem("truepour_archetype");
        const archetypeVersion = localStorage.getItem("truepour_archetype_version");
        if (savedArch && !archetypeVersion) {
          // User has old archetype — flag for update message
          localStorage.setItem("truepour_archetype_needs_update", "true");
        }
      } catch {}

      if (savedDeepProfile === "true") {
        setDeepProfile(true);
      }
    } catch {}
    try {
      if (localStorage.getItem("truepour_profile_complete")) setProfileComplete(true);
    } catch {}
    try {
      const ext = localStorage.getItem("truepour_extended_sliders");
      if (ext && ext !== "null" && ext !== "undefined") {
        const raw = JSON.parse(ext);
        const extKeys = ['tannin','oak','alcohol','aromatics','complexity','color'];
        const validatedExt = {};
        for (const k of extKeys) {
          const v = raw[k];
          validatedExt[k] = (Number.isFinite(v) && v >= 1 && v <= 6) ? v : null;
        }
        setExtendedSliders(validatedExt);
      }
    } catch {}
    } // end if (!profileComplete)
    // Always return to home screen on app load
    // Sessions are discrete — users should start fresh each time
    // The "Your last picks" card on home handles resume use case
    setScreen("home");
    setHomeFlowPath(null);
    setSpecificBottle("");
    setHomeFewBottles([]);
    setCurrentQuestionIndex(0);
    scrollToTop();
  },[]);

  useEffect(() => {

    if (['home'].includes(screen)) {
      setActiveTab('discover');
    } else if (screen === 'activity') {
      setActiveTab('activity');
    } else if (screen === 'cellar') {
      setActiveTab('cellar');
    } else if (['profile', 'archetype'].includes(screen)) {
      setActiveTab('profile');
    }

    window.scrollTo({ top: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    if (screen === 'archetype') {
      fireEvent('archetype_revealed', { archetype: userArchetype || 'unknown' });
    }
    if (screen === 'results') {
      fireEvent('recommendation_viewed', { context: context || 'unknown', pairing_count: pairings?.length || 0 });
    }
    if (screen === 'home') {
      if (document.activeElement) document.activeElement.blur();
      setTimeout(() => {
        if (document.activeElement) document.activeElement.blur();
      }, 100);
    }
    if (screen === "homebottlematch" && specificBottle && homeVibe) {
      setBottleMatchLoading(true);
      setBottleMatchError(null);
      setBottleMatch(null);
      // Mint a per-generation id at REQUEST time (same pattern as the 4 flows).
      const bottleGenerationId = makeGenerationId();
      currentGenerationIdRef.current = bottleGenerationId;
      evaluateBottle({
        bottle: specificBottle,
        occasion: homeVibe,
        sliders,
        extendedSliders: deepProfile ? extendedSliders : null,
        userArchetype: userArchetype ? ARCHETYPES[userArchetype]?.name : null,
        likedWines: userProfile.likedWines || [],
        cellarRatings: userProfile.cellarRatings || {},
        ratedWines: (userProfile.history || [])
          .flatMap(h => h.wines || [])
          .filter(w => w.rating > 0)
          .map(w => ({ wine: `${w.producer} ${w.wine}`, rating: w.rating, tags: w.tags || [] })),
      }, session).then(data => {
        setBottleMatch(data);
        setBottleMatchLoading(false);
        // Persist ON PRODUCE (single-bottle eval) — one-wine bottle_eval row,
        // exactly once (persistGeneration dedupes on bottleGenerationId).
        if (data && data.producer && data.wine) {
          persistGeneration(bottleGenerationId, 'bottle_eval', [mapBottleWine(data)]);
        }
      }).catch(err => {
        setBottleMatchLoading(false);
        if (!handleAuthExpired(err)) setBottleMatchError({ status: err.status ?? null, message: err.message });
      });
    }
  }, [screen]); // eslint-disable-line react-hooks/exhaustive-deps
  // SINGLE OWNER of navVisible. Derived from real auth/profile state — NOT from
  // localStorage, which a freshly-signed-in user (profile in Supabase only) lacks.
  // profileComplete is set by rehydrateSession from the Supabase profile, so this
  // re-runs and shows nav the moment the profile finishes loading.
  useEffect(() => {
    const hideNavScreens = ['quiz', 'archetype', 'auth', 'resetRequest', 'setNewPassword'];
    const hasProfile = profileComplete || !!(session && userArchetype);
    setNavVisible(hasProfile && !hideNavScreens.includes(screen) && !showSplash);
  }, [screen, profileComplete, showSplash, session, userArchetype]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (screen !== 'archetype') {
      setArchScrolled(false);
      setArchCtaVisible(false);
      setArchInlineCTAInView(false);
      return;
    }
    const container = archScrollRef.current;
    if (!container) return;
    const handleScroll = () => {
      const st = container.scrollTop;
      setArchScrolled(st > 40);
      if (archDescRef.current) {
        const rect = archDescRef.current.getBoundingClientRect();
        if (rect.bottom < window.innerHeight) setArchCtaVisible(true);
      }
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    let observer;
    if (archInlineCTARef.current) {
      observer = new IntersectionObserver(
        ([entry]) => setArchInlineCTAInView(entry.isIntersecting),
        { threshold: 0.1 }
      );
      observer.observe(archInlineCTARef.current);
    }
    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (observer) observer.disconnect();
    };
  }, [screen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (screen === "homestyleguide" && homeVibe) {
      setStyleGuideLoading(true);
      setStyleGuideError(null);
      setStyleGuide(null);
      fetchStyleGuide({
        occasion: homeVibe,
        sliders,
        extendedSliders: deepProfile ? extendedSliders : null,
        userArchetype: userArchetype ? ARCHETYPES[userArchetype]?.name : null,
      }, session).then(data => {
        setStyleGuide(data);
        setStyleGuideLoading(false);
      }).catch(err => {
        setStyleGuideLoading(false);
        if (!handleAuthExpired(err)) setStyleGuideError({ status: err.status ?? null, message: err.message });
      });
    }
  }, [screen]); // eslint-disable-line react-hooks/exhaustive-deps

  const persist = (p) => { setUserProfile(p); saveData({ userProfile:p, sliders }); };
  const persistSliders = (s) => { setSliders(s); saveData({ userProfile, sliders:s }); };
  const markProfileComplete = () => { try { localStorage.setItem("truepour_profile_complete","true"); } catch {} setProfileComplete(true); };
  const persistExtendedSliders = (ext) => { setExtendedSliders(ext); try { localStorage.setItem("truepour_extended_sliders", JSON.stringify(ext)); } catch {} };

  // Option C reconcile: refetch the authoritative server rating count and snap
  // supabaseRatingCount to it. Called after a successful rate POST and on Discover
  // mount. The displayed home count is max(server, client-optimistic) so this never
  // double-counts — it snaps the baseline up to server truth.
  const refreshRatingCount = async () => {
    if (!session) return null;
    try {
      const r = await fetch('/api/rating-count', { headers: { 'Authorization': `Bearer ${session.access_token}` } });
      if (r.ok) { const d = await r.json(); const c = d.count || 0; setSupabaseRatingCount(c); return c; }
    } catch {}
    return null;
  };
  // Score-loop foundation: persist the score the user has already seen (Commit 2b
  // calls this after the animation). Auth-gated server write; failure is non-fatal.
  const persistLastSeenScore = async (score) => {
    if (!session) return;
    try {
      await fetch('/api/last-seen-score', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` }, body: JSON.stringify({ score }) });
    } catch {}
  };
  // Layer B — fire-once celebratory count-up on Discover.
  // Runs ONLY after session+profile are rehydrated (guarded by !rehydrating &&
  // profileComplete) so last_seen_score is read post-rehydrate, never racing the
  // PKCE cold-load. Reconciles the count to server truth, then compares the current
  // score to the durable last_seen_score: if it rose, trigger the count-up and
  // persist the new value (fire-once per gain). First-ever (null) → persist baseline
  // silently. Never animates a decrease (up-only).
  useEffect(() => {
    if (screen !== 'home' || activeTab !== 'discover') return;
    if (!session || rehydrating || !profileComplete) return;
    (async () => {
      const serverCount = await refreshRatingCount();
      const effCount = Math.max(serverCount ?? 0, supabaseRatingCount || 0, mergedRatedCount);
      const cur = Math.round(palateScorePct(effCount) * 100);
      if (lastSeenScore == null) {
        setLastSeenScore(cur);
        persistLastSeenScore(cur);
      } else if (cur > lastSeenScore) {
        setScoreAnimateFrom(lastSeenScore);
        setLastSeenScore(cur);
        persistLastSeenScore(cur);
      }
    })();
  }, [screen, activeTab, rehydrating, profileComplete]); // eslint-disable-line react-hooks/exhaustive-deps

  const DRIFT_EXTENDED_KEYS = ['tannin', 'oak', 'complexity'];
  const hasDimensionDrifted = (key, overrideSliders, overrideExtSliders) => {
    if (DRIFT_EXTENDED_KEYS.includes(key)) {
      const effExt = overrideExtSliders !== undefined ? overrideExtSliders : extendedSliders;
      const quizExt = quizExtendedSliders?.[key];
      const current = effExt?.[key];
      // quiz_extended_sliders is null for all pre-v231 rows and is unrecoverable
      // for them — no fallback to a fixed number here. A missing baseline means
      // no arrow, not a fabricated one.
      if (quizExt == null || current == null) return false;
      return Math.abs(current - quizExt) >= 0.3;
    }
    const eff = overrideSliders !== undefined ? overrideSliders : sliders;
    const quiz = quizSliders?.[key];
    const current = eff?.[key];
    if (quiz == null || current == null) return false;
    return Math.abs(current - quiz) >= 0.3;
  };

  const computeToastMessage = (newSliders, newExtSliders) => {
    const ALL_DIMS = ['body', 'tannin', 'acidity', 'oak', 'sweetness', 'complexity'];
    let bestDim = null;
    let bestDelta = 0;
    for (const key of ALL_DIMS) {
      if (!hasDimensionDrifted(key, newSliders, newExtSliders)) continue;
      // hasDimensionDrifted already returned false (above) when
      // quizExtendedSliders[key] is null, so it's guaranteed non-null here.
      const delta = DRIFT_EXTENDED_KEYS.includes(key)
        ? (newExtSliders?.[key] ?? 3) - quizExtendedSliders[key]
        : (newSliders?.[key] ?? 3) - (quizSliders?.[key] ?? 3);
      if (Math.abs(delta) > Math.abs(bestDelta)) { bestDim = key; bestDelta = delta; }
    }
    if (!bestDim) return null;
    const up = bestDelta > 0;
    const copy = {
      tannin:     up ? "Noted — your taste for grippy tannins is growing" : "Noted — you seem to prefer smoother, softer tannins",
      oak:        up ? "Noted — you're drawn to more oak-forward styles" : "Noted — you're leaning toward leaner, less oaked wines",
      complexity: up ? "Noted — your palate is seeking more complexity" : "Noted — you're enjoying simpler, more approachable styles",
      body:       up ? "Noted — you're gravitating toward fuller-bodied wines" : "Noted — you're leaning toward lighter-bodied wines",
      acidity:    up ? "Noted — you're enjoying crisper, more vibrant wines" : "Noted — you're leaning toward rounder, softer wines",
      sweetness:  up ? "Noted — you're enjoying fruitier, off-dry styles" : "Noted — you're leaning toward drier, more savory styles",
    };
    return copy[bestDim] || null;
  };

  const showPalateToast = (msg) => {
    if (!msg) return;
    if (palateToastTimerRef.current) clearTimeout(palateToastTimerRef.current);
    setPalateToast(msg);
    palateToastTimerRef.current = setTimeout(() => setPalateToast(null), 4500);
  };


  const budget = selectedBudget ? selectedBudget : [0, 9999];
  const selectBudget = (range) => setSelectedBudget(range);

  const handleGetPairings = async () => {
    if (context === "home" ? !homeVibe : (context !== "online" && !occasion)) return;
    setPairings([]); setEmptyReason(null);
    // Mint a per-generation id at REQUEST time; carry it for produce-time persist.
    const generationId = makeGenerationId();
    currentGenerationIdRef.current = generationId;
    setLoading(true); setError(null);
    try {
      const ratedWines = (userProfile.history||[]).flatMap(h=>h.wines||[]).filter(w=>w.rating>0).map(w=>({ wine:`${w.producer} ${w.wine}`, rating:w.rating, tags:w.tags||[] }));
      let res;
      if (homeFlowPath === "few" && homeFewBottles.length > 0) {
        res = await evaluateBottles({
          bottles: homeFewBottles,
          occasion: homeVibe || occasion,
          sliders,
          extendedSliders: deepProfile ? extendedSliders : null,
          userArchetype: userArchetype ? ARCHETYPES[userArchetype]?.name : null,
          likedWines: userProfile.likedWines || [],
          cellarRatings: userProfile.cellarRatings || {},
          ratedWines,
        }, session);
      } else if (context === 'online') {
        // Streaming flow for online ordering
        const streamParams = { meal, occasion, wineColor, sliders, knownWines, likedWines:userProfile.likedWines, ratedWines, cellarRatings: userProfile.cellarRatings || {}, context, scannedWines, budget, homeVibe, extendedSliders: deepProfile ? extendedSliders : null, userArchetype: userArchetype ? `${ARCHETYPES[userArchetype]?.name} — ${ARCHETYPES[userArchetype]?.tagline}` : null, loveWine: loveWineInput || "", lastResults: lastResults ? (lastResults.pairings || []).map(p => ({ producer: p.producer, wine: p.wine })) : [] };
        setRatings({}); setTriedIt({}); setReviewTags({}); setSavedWines({});
        setReviewDismissed(false);
        setPairings([]);
        let streamError = false;
        await new Promise((resolve) => {
          getRecommendationsStream(
            streamParams,
            (wine, index) => {
              if (index === 0) {
                flushSync(() => {
                  setPairings(prev => {
                    const next = [...prev];
                    next[index] = wine;
                    return next;
                  });
                  setLoading(false);
                  setScreen("results");
                });
              } else {
                setPairings(prev => {
                  const next = [...prev];
                  next[index] = wine;
                  return next;
                });
              }
            },
            (total) => {
              // All wines received — re-sort by confidence in case reserve wine slotted in
              try {
                setPairings(prev => {
                  const sorted = [...prev].filter(Boolean).sort((a, b) => (b.confidence || 0) - (a.confidence || 0));
                  const lastResultsData = { pairings:sorted, context, occasion, meal, homeVibe, timestamp:Date.now() };
                  localStorage.setItem("truepour_last_results", JSON.stringify(lastResultsData));
                  setLastResults(lastResultsData);
                  // Persist ON PRODUCE (online) — final sorted set, exactly once.
                  persistGeneration(generationId, 'online', sorted);
                  return sorted;
                });
              } catch {}
              resolve();
            },
            (err) => {
              streamError = true;
              if (!handleAuthExpired(err)) setError({ status: err.status ?? null, message: err.message });
              resolve();
            },
            (enrichData) => {
              setPairings(prev => {
                const next = [...prev];
                if (next[enrichData.index]) {
                  next[enrichData.index] = {
                    ...next[enrichData.index],
                    ws_score: enrichData.ws_score,
                    ws_price_average: enrichData.ws_price_average,
                    ws_price_min: enrichData.ws_price_min,
                    ws_price_max: enrichData.ws_price_max,
                    ws_matched: enrichData.ws_matched,
                    confidence: enrichData.confidence ?? next[enrichData.index].confidence,
                  };
                }
                return next;
              });
            },
            session
          );
        });
        if (streamError) {
          setLoading(false);
          return;
        }
        setLoading(false);
        return;
      } else if (context === 'restaurant') {
        const restaurantParams = { scannedWines, scannedRows, meal, occasion, wineColor, budget, sliders, extendedSliders: deepProfile ? extendedSliders : null, userArchetype: userArchetype ? `${ARCHETYPES[userArchetype]?.name} — ${ARCHETYPES[userArchetype]?.tagline}` : null, likedWines: userProfile.likedWines, ratedWines, cellarRatings: userProfile.cellarRatings || {}, restaurantOrderType };
        const restaurantRes = await fetch('/api/evaluate-restaurant', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}) },
          body: JSON.stringify(restaurantParams)
        });
        const restaurantData = await restaurantRes.json();
        if (!restaurantRes.ok || restaurantData.error) { const e = new Error(restaurantData.error || `HTTP ${restaurantRes.status}`); e.status = restaurantRes.status; throw e; }
        res = { recommendations: restaurantData.recommendations, emptyReason: restaurantData.emptyReason };
      } else {
        res = await getRecommendations({ meal, occasion, wineColor, sliders, knownWines, likedWines:userProfile.likedWines, ratedWines, cellarRatings: userProfile.cellarRatings || {}, context, scannedWines, scannedRows, budget, homeVibe, extendedSliders: deepProfile ? extendedSliders : null, userArchetype: userArchetype ? `${ARCHETYPES[userArchetype]?.name} — ${ARCHETYPES[userArchetype]?.tagline}` : null, loveWine: loveWineInput || "", restaurantOrderType: null }, session);
      }
      const recsArr = (res && Array.isArray(res.recommendations)) ? res.recommendations : (Array.isArray(res) ? res : []);
      const sorted = [...recsArr].sort((a, b) => (b.confidence || 0) - (a.confidence || 0));
      setEmptyReason((res && !Array.isArray(res)) ? (res.emptyReason || null) : null);
      setPairings(sorted); setRatings({}); setTriedIt({}); setReviewTags({}); setSavedWines({});
      setReviewDismissed(false);
      // Persist ON PRODUCE (restaurant / store / multi-bottle) — exactly once.
      // context is 'restaurant' | 'store' | 'home' (multi-bottle) here.
      if (sorted.length > 0) persistGeneration(generationId, context, sorted);
      try {
        const lastResultsData = { pairings:sorted, context, occasion, meal, homeVibe, timestamp:Date.now() };
        localStorage.setItem("truepour_last_results", JSON.stringify(lastResultsData));
        setLastResults(lastResultsData);
      } catch {}
      setScreen("results");
    } catch(e) { if (!handleAuthExpired(e)) setError({ status: e.status ?? null, message: e.message }); }
    setLoading(false);
  };

  const sendImplicitSignal = async (wine, signalType) => {
    if (!wine || !wine.producer || !(wine.wine || wine.name)) return;
    try {
      await fetch('/api/implicit-signal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          producer: wine.producer,
          wineName: wine.wine || wine.name,
          signalType,
          userArchetype: userArchetype ? (ARCHETYPES[userArchetype]?.name || '') : '',
          occasion: occasion || homeVibe || '',
          context: context || ''
        })
      });
    } catch (err) {
      // Fail silently — implicit signals are best-effort
    }
  };

  const handleRate = async (i, stars) => {
    setRatings(r=>({...r,[i]:stars}));
    // Step 7 copy-change fix (item 1): make the IN-SESSION cellarRatings
    // match what GET /api/user-cellar's own reload already produces —
    // it rebuilds cellarRatings straight from wine_ratings with
    // .gt('rating', 0), independent of likedWines (see
    // server.js:5605-5619). Before this fix, a sub-4-star pairings
    // rating never updated userProfile.cellarRatings at all (the
    // persist(...) call lived entirely inside `if (stars>=4)`), so it
    // was missing from buildPrompt's disliked/neutral lists and
    // scoreCatalogWines' producer affinity until the NEXT session
    // reload re-hydrated it from the server. Now every stars>0 rating
    // records into cellarRatings immediately, matching the reload;
    // stars===0 removes the key, matching gt('rating', 0) excluding it.
    // Only stars>=4 still adds to likedWines/savedWines/cellar_meta and
    // POSTs /api/cellar — unchanged from today.
    const w = pairings[i];
    if (w) {
      const l=`${w.producer} ${w.wine}`;
      let newCellarRatings;
      if (stars > 0) {
        newCellarRatings = {...(userProfile.cellarRatings||{}), [l]:stars};
      } else {
        newCellarRatings = {...(userProfile.cellarRatings||{})};
        delete newCellarRatings[l];
      }
      if (stars>=4 && !userProfile.likedWines.includes(l)) {
        persist({...userProfile,likedWines:[...userProfile.likedWines,l],cellarRatings:newCellarRatings});
        setSavedWines(s=>({...s,[i]:true}));
        const _cm = JSON.parse(localStorage.getItem('truepour_cellar_meta') || '{}');
        _cm[l] = { addedAt: Date.now() };
        localStorage.setItem('truepour_cellar_meta', JSON.stringify(_cm));
      } else {
        persist({...userProfile,cellarRatings:newCellarRatings});
      }
      if (stars>=4 && session) { fetch('/api/cellar', { method:'POST', headers:{'Content-Type':'application/json','Authorization':`Bearer ${session.access_token}`}, body:JSON.stringify({ wine_name: l }) }).catch(()=>{}); }
    }
    try {
      const w = pairings[i];
      if (w) {
        await fetch('/api/ratings', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            producer: w.producer || '',
            wineName: w.wine || '',
            rating: stars
          })
        });
      }
    } catch (err) {
      // Fail silently — local rating already saved
    }
    try {
      if (session) {
        const w = pairings[i];
        if (w) {
          const rateRes = await fetch('/api/rate-wine', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
            body: JSON.stringify({ producer: w.producer || '', wine_name: w.wine || '', rating: stars })
          });
          // Option C reconcile: snap the count to server truth after a persisted rating.
          if (rateRes.ok) refreshRatingCount();
          const rateData = await rateRes.json();
          if (rateData.sliders) {
            setSliders(prev => ({ ...prev, ...rateData.sliders }));
            setPalateUpdateSeen(false);
            try { localStorage.removeItem('truepour_palate_update_seen'); } catch {}
          }
          if (rateData.extSliders) {
            setExtendedSliders(prev => ({ ...prev, ...rateData.extSliders }));
          }
          maybeShowRatingToast(
            `${w.producer} ${w.wine}`, stars,
            rateData.sliders ? { ...sliders, ...rateData.sliders } : sliders,
            rateData.extSliders ? { ...extendedSliders, ...rateData.extSliders } : extendedSliders,
            rateData
          );
        }
      }
    } catch (err) {
      // fail silently
    }
  };
  const handleTried = (i, v) => setTriedIt(t=>({...t,[i]:v}));
  const handleTag = (i, tag) => setReviewTags(t=>{ const c=t[i]||[]; return {...t,[i]:c.includes(tag)?c.filter(x=>x!==tag):[...c,tag]}; });
  const handleSave = (i) => {
    const w=pairings[i]; if (!w) return;
    const l=`${w.producer} ${w.wine}`;
    if (!userProfile.likedWines.includes(l)) {
      persist({...userProfile,likedWines:[...userProfile.likedWines,l]});
      const _cm = JSON.parse(localStorage.getItem('truepour_cellar_meta') || '{}');
      _cm[l] = { addedAt: Date.now() };
      localStorage.setItem('truepour_cellar_meta', JSON.stringify(_cm));
    }
    if (session) { fetch('/api/cellar', { method:'POST', headers:{'Content-Type':'application/json','Authorization':`Bearer ${session.access_token}`}, body:JSON.stringify({ wine_name: l }) }).catch(()=>{}); }
    setSavedWines(s=>({...s,[i]:!s[i]}));
  };

  // ── ONE create path: persist a produced result set exactly once ──────────
  // Called at PRODUCE time (when a flow populates `pairings`), NOT on exit.
  // `wines` is passed explicitly (the final sorted array) to avoid reading a
  // stale `pairings` closure right after setPairings. Dedupe is synchronous
  // via writtenGenerationsRef so a double-fire of the same generationId can
  // never create two rows; distinct generationIds (re-runs) DO create
  // distinct rows (policy (a)).
  const persistGeneration = (generationId, ctx, wines) => {
    if (!claimGeneration(writtenGenerationsRef.current, generationId)) return; // already persisted
    const entry = buildProduceEntry({
      generationId,
      date: new Date().toLocaleDateString(),
      meal: meal || occasion,
      occasion,
      context: ctx,
      wines,
    });
    persist({ ...userProfile, history: [...(userProfile.history || []), entry] });
    if (session) {
      fetch('/api/recommendation-history', { method:'POST', headers:{'Content-Type':'application/json','Authorization':`Bearer ${session.access_token}`}, body:JSON.stringify({ date:entry.date, meal:entry.meal, occasion:entry.occasion, context:entry.context, wines:entry.wines, reviewed:entry.reviewed }) })
        .then(r=>r.json()).then(data=>{
          if (!data || !data.id) return;
          generationSupabaseRef.current[generationId] = data.id;
          // Stamp the in-memory row with its server id (match by generationId).
          setUserProfile(prev => {
            const h = (prev.history || []).map(e => e.generationId === generationId ? { ...e, supabaseId: data.id } : e);
            return { ...prev, history: h };
          });
          // Flush any interaction that landed before the id existed.
          const pending = pendingGenerationUpdateRef.current[generationId];
          if (pending) {
            delete pendingGenerationUpdateRef.current[generationId];
            fetch(`/api/recommendation-history/${data.id}`, { method:'PATCH', headers:{'Content-Type':'application/json','Authorization':`Bearer ${session.access_token}`}, body:JSON.stringify({ wines: pending.wines, reviewed: pending.reviewed }) }).catch(()=>{});
          }
        }).catch(()=>{});
    }
  };

  // ── ONE update path: interaction-time PATCH of the current generation row ─
  // Rebuilds the row's wines/reviewed from live interaction state and PATCHes
  // the existing row (never inserts). If the create POST hasn't resolved yet
  // (no supabaseId), the update is queued and flushed on resolve.
  const patchGeneration = (generationId, wines, reviewed) => {
    if (!generationId) return;
    // Update in-memory history so Activity reflects the interaction immediately.
    setUserProfile(prev => {
      const h = (prev.history || []).map(e => e.generationId === generationId ? { ...e, wines, reviewed } : e);
      return { ...prev, history: h };
    });
    if (!session) return;
    const sbId = generationSupabaseRef.current[generationId];
    if (sbId) {
      fetch(`/api/recommendation-history/${sbId}`, { method:'PATCH', headers:{'Content-Type':'application/json','Authorization':`Bearer ${session.access_token}`}, body:JSON.stringify({ wines, reviewed }) }).catch(()=>{});
    } else {
      // Create POST still in flight — remember latest, flush when id arrives.
      pendingGenerationUpdateRef.current[generationId] = { wines, reviewed };
    }
  };

  // Map a single-bottle eval result (bottleMatch) to the verbatim per-wine
  // history shape. Only fields the /api/evaluate-bottle response actually
  // carries are mapped; the rest are null/[]. (No `varietal` in the response;
  // `whyItWorks` is the fit explanation → pairingReason; `ws_price_average`
  // → price.) tried/rating are zeroed here and filled by the update path.
  const mapBottleWine = (bm) => ({
    producer: bm.producer,
    wine: bm.wine,
    vintage: bm.vintage,
    confidence: bm.confidence != null ? bm.confidence : null,
    description: bm.description || null,
    pairingReason: bm.whyItWorks || null,
    styleTags: bm.styleTags || [],
    price: bm.ws_price_average != null ? bm.ws_price_average : null,
    region: bm.region || null,
    varietal: null,
  });

  // Screens that render a persisted result (produce-time write already ran):
  // the 4 pairings flows land on 'results'; single-bottle on 'homebottlematch'.
  // NOTE: intentionally NOT IN_FLOW_SCREENS — those are pre-produce input
  // screens (results isn't even in that list).
  const PERSISTED_RESULT_SCREENS = ['results', 'homebottlematch'];

  // Non-blocking "Saved to Activity" confirmation, fired on exit from a
  // persisted result. No-ops unless a generation was actually persisted for
  // the current screen, and only once per generationId (so leaving one result
  // via multiple exit paths doesn't stack toasts). First-time copy is richer.
  const maybeShowSavedToast = () => {
    const genId = currentGenerationIdRef.current;
    if (!genId) return;                                   // no generation in flight
    if (!writtenGenerationsRef.current.has(genId)) return; // nothing was actually persisted (e.g. no-match)
    if (!PERSISTED_RESULT_SCREENS.includes(screen)) return; // not on a result screen
    if (lastSavedToastGenRef.current === genId) return;   // already toasted this generation
    lastSavedToastGenRef.current = genId;
    if (!savedToActivitySeen) {
      setShowToast('Saved to Activity — find all your evaluations there');
      setSavedToActivitySeen(true);
      try { localStorage.setItem('truepour_saved_to_activity_seen', 'true'); } catch {}
    } else {
      setShowToast('Saved to Activity');
    }
    setTimeout(() => setShowToast(null), 2500);
  };

  // Interaction-time UPDATE: whenever the user rates/tries/tags a wine on the
  // results screen, PATCH the current generation's row (created at produce
  // time). Last-write-wins; no new rows are ever inserted here.
  useEffect(() => {
    if (screen !== 'results') return;
    const genId = currentGenerationIdRef.current;
    if (!genId || pairings.filter(Boolean).length === 0) return;
    const { wines, reviewed } = buildUpdateWines(pairings, ratings, triedIt, reviewTags);
    patchGeneration(genId, wines, reviewed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ratings, triedIt, reviewTags]);

  const submitReturnReview = (hIdx, wIdx, tried, stars, tags) => {
    const u={...userProfile}; const e={...u.history[hIdx]}; e.wines=[...e.wines]; e.wines[wIdx]={...e.wines[wIdx],tried,rating:stars,tags}; e.reviewed=true;
    u.history=[...u.history]; u.history[hIdx]=e; persist(u);
    if (session && e.supabaseId) { fetch(`/api/recommendation-history/${e.supabaseId}`, { method:'PATCH', headers:{'Content-Type':'application/json','Authorization':`Bearer ${session.access_token}`}, body:JSON.stringify({ wines:e.wines, reviewed:e.reviewed }) }).catch(()=>{}); }
    const allActioned = e.wines.every(w => w.tried !== undefined && w.tried !== null);
    if (allActioned) {
      setReviewDismissed(true);
    }
  };

  const dismissSplash = () => {
    try {
      localStorage.setItem("truepour_splash_seen", Date.now().toString());
    } catch {}
    setShowSplash(false);
    setScreenHistory([]);
    if (!(profileComplete || userArchetype)) {
      scrollToTop();
      setCurrentQuestionIndex(0);
      setScreen("quiz");
    }
    // Returning user — splash dismisses and home screen shows naturally
  };

  const reset = () => {
    // History persistence is now produce-time (persistGeneration); the old
    // exit-time saveToHistory create call has been removed. reset() only
    // clears flow state.
    setScreen("home"); setScreenHistory([]); setHomeFlowPath(null); setSpecificBottle(""); setHomeFewBottles([]); setContext(null); setMeal(""); setOccasion(null); setWineColor(null); setRestaurantOrderType(null); setPairings([]); setScanModalMode(false);
    setScannedWines(null); setScannedRows([]); setScannedDish(null); setScanMode(null); setError(null); setExpandedCard(0);
    setRatings({}); setTriedIt({}); setReviewTags({}); setSavedWines({});
    setSelectedStyleCard(null); setStyleGuideSelection(null);
  };

  // Clear all TRANSIENT scan-session state so each flow STARTS clean, instead
  // of relying on every exit path to clear correctly. Call at true flow ENTRY
  // (the context tiles / online / homefew), NOT on each scan within a session —
  // in-session accumulation happens on the scan screen itself (no re-entry), so
  // this never wipes multi-page/multi-shot progress. Does NOT touch durable
  // user state (profile, sliders, archetype, cellar, session) or navigation
  // (screen/screenHistory/context — the caller sets those).
  const resetScanState = () => {
    setScannedWines(null); setScannedRows([]); setScannedDish(null);
    setHomeFewBottles([]); setPairings([]);
    setSpecificBottle(""); setScanMode(null); setScanTarget("wines");
    setRestaurantOrderType(null); setHomeFlowPath(null);
    setMeal(""); setOccasion(null); setWineColor(null);
    setError(null);
    setRatings({}); setTriedIt({}); setReviewTags({}); setSavedWines({}); setExpandedCard(0);
  };

  const scrollToTop = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      });
    });
  };

  // Shared 401 handler for the 8 cost-bearing fetch wrappers below. Lives here
  // (inside the App component), not inside those wrappers themselves — they're
  // top-level functions outside the component and have no access to
  // setScreen/scrollToTop. Each wrapper attaches `.status` to a thrown Error;
  // call sites check `handleAuthExpired(err)` in their catch block BEFORE
  // storing { status, message } for the status→copy map (see STATUS_COPY),
  // and skip that error display when it returns true. Mirrors the existing
  // setScreen('auth') + scrollToTop()
  // pattern from handleFindWine.
  const handleAuthExpired = (err) => {
    if (err && err.status === 401) {
      setScreen('auth');
      scrollToTop();
      return true;
    }
    return false;
  };

  const navigateTo = (newScreen) => {
    setError(null); // a stale error must not follow the user across screens
    setBottleMatchError(null); // same — only cleared by their own effect's guard otherwise
    setStyleGuideError(null);
    setScreenHistory(prev => {
      const updated = [...prev, screen];
      return updated.length > 20 ? updated.slice(-20) : updated;
    });
    setScreen(newScreen);
    scrollToTop();
  };

  const navigateBack = () => {
    setError(null); // a stale error must not follow the user across screens
    setBottleMatchError(null); // same — only cleared by their own effect's guard otherwise
    setStyleGuideError(null);
    // If leaving a persisted result (any of the 4 pairings flows or
    // single-bottle), confirm it was saved. No-ops off result screens.
    maybeShowSavedToast();
    if (screenHistory.length === 0) {
      setScreen("home");
      setHomeFlowPath(null);
      setSpecificBottle("");
      setHomeFewBottles([]);
      scrollToTop();
      return;
    }
    const history = [...screenHistory];
    const previous = history.pop();
    setScreenHistory(history);
    // Orphan guard: never navigate to a stale/unknown screen (would render blank).
    setScreen(KNOWN_SCREENS.has(previous) ? previous : "home");
    scrollToTop();
  };

  const IN_FLOW_SCREENS = [
    'restauranttype', 'winelist', 'winelistscan',
    'storescan', 'homepath', 'homespecific', 'homefew', 'homebottles',
    'homescan', 'winescan', 'meal', 'homebottlematch', 'homestyleguide'
  ];

  const handleTabChange = (tab) => {
    // No blocking confirm on tab exit: everything persists on produce
    // (Commits 1/2), so leaving a flow never loses a saved recommendation —
    // the old "Leave this recommendation? Your progress will be lost." modal
    // was false and has been removed. On the pre-produce INPUT screens we
    // still clear in-progress flow state so a stale half-entered flow isn't
    // carried into the next tab, then confirm any produced result via toast.
    const inFlow = IN_FLOW_SCREENS.includes(screen);
    if (inFlow) {
      setContext(null);
      setHomeFlowPath(null);
      setSpecificBottle('');
      setHomeFewBottles([]);
      setScannedWines(null);
      setMeal('');
      setOccasion(null);
      setWineColor(null);
      setPairings([]);
      setError(null);
    }
    // Consistent "Saved to Activity" confirmation on tab-tap exit — now covers
    // all persisted-result screens (the 4 pairings flows on 'results' AND
    // single-bottle on 'homebottlematch'), replacing the old homebottlematch-
    // only toast. No-ops off persisted-result screens.
    maybeShowSavedToast();

    // NOTE: history is now created at PRODUCE time (persistGeneration) and
    // updated at interaction time (patchGeneration effect). The old exit-time
    // create writer that lived here has been removed — leaving results no
    // longer creates (or double-creates) a row.
    setActiveTab(tab);
    setScreenHistory([]);
    if (tab === 'discover') {
      setScreen('home');
      scrollToTop();
    } else if (tab === 'activity') {
      setScreen('activity');
      scrollToTop();
    } else if (tab === 'cellar') {
      setScreen('cellar');
      scrollToTop();
    } else if (tab === 'profile') {
      setScreen('profile');
      scrollToTop();
      if (!pendingArchetypeReveal) {
        setPalateUpdateSeen(true);
        try { localStorage.setItem('truepour_palate_update_seen', 'true'); } catch {}
      }
      // Step 7 fix (item 2): capture the banner's visibility for THIS
      // visit BEFORE marking the notice seen below — once
      // profileLastSeenChangeAt is updated, isChangeUnseen would flip to
      // false, and re-deriving the banner from live state on every
      // render would yank it away mid-visit instead of keeping it for
      // the visit that revealed it.
      setCauseBannerVisibleThisVisit(shouldShowCauseBannerOnEntry({
        flagOn: step7HasServerFields,
        profileChangeReason,
        profileChangedAt,
        profileLastSeenChangeAt,
      }));
      // Viewing the Profile tab is "viewing the notice" for the
      // server-driven badge too — mark it seen server-side so the badge
      // clears without a reload, alongside (not instead of) the legacy
      // mark-seen above (both run together during the transition period).
      if (step7HasServerFields && profileChangeReason != null && session) {
        const seenAt = new Date().toISOString();
        setProfileLastSeenChangeAt(seenAt);
        fetch('/api/profile/seen-change', { method: 'POST', headers: { 'Authorization': `Bearer ${session.access_token}` } }).catch(() => {});
      }
    }
  };

  const S = {
    app: { minHeight:"100vh", background:C.cream, "--fd":F.display, "--fb":F.body, fontFamily:F.body, overflowX:"hidden", WebkitOverflowScrolling:"touch" },
    wrap: { maxWidth:420, margin:"0 auto", padding:"0 20px 100px" },
    back: { background:"none", border:"none", color:C.charcoalSoft, cursor:"pointer", fontSize:15, fontFamily:F.body, padding:"0 0 20px", display:"block" },
    h: { fontSize:26, fontWeight:800, color:C.charcoal, fontFamily:F.display, lineHeight:1.2, marginBottom:6 },
    sub: { fontSize:15, color:C.charcoalSoft, fontFamily:F.body, lineHeight:1.6, marginBottom:24 },
    cta: { width:"100%", padding:"17px", borderRadius:14, fontSize:16, fontWeight:700, background:C.burgundy, color:"#fff", border:"none", cursor:"pointer", fontFamily:F.display, letterSpacing:0.3, boxShadow:"none", transition:"all 0.2s" },
  };

  return (
    <div key={screen} style={S.app}>
      <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@400;600;700;800&family=DM+Sans:wght@400;500;600&display=swap" rel="stylesheet" />
      <style>{`@keyframes tpPulse{0%{transform:scale(1);box-shadow:0 4px 12px rgba(145,18,52,0.30)}50%{transform:scale(1.1);box-shadow:0 6px 20px rgba(145,18,52,0.55)}100%{transform:scale(1);box-shadow:0 4px 12px rgba(145,18,52,0.30)}} button{-webkit-tap-highlight-color:transparent;outline:none;} @keyframes tpSpin{to{transform:rotate(360deg)}}`}</style>
      {rehydrating && (
        <div style={{ position:'fixed', inset:0, zIndex:9999, background:'#FAF4F0', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:16 }}>
          <div style={{ fontSize:28, fontWeight:800, color:'#1A0F0E', fontFamily:'var(--fd)', letterSpacing:'-0.5px' }}>TruePour</div>
          <div style={{ width:36, height:36, border:'3px solid #F0EAE6', borderTopColor:C.burgundy, borderRadius:'50%', animation:'tpSpin 0.8s linear infinite' }} />
        </div>
      )}

      {showScanner && <ImageCapture mode={scanMode} onCapture={(w, rows)=>{ if (scanTarget==="dish"){ setScannedDish(w); setMeal(w); } else if (scanTarget==="winescan") { const firstLine = w.split('\n').map(l=>l.trim()).filter(l=>l && !isNonWineLine(l))[0] || w; setSpecificBottle(firstLine.trim()); if (scanModalMode) { setHomeVibe('Just a glass'); navigateTo('homebottlematch'); } else { navigateTo('meal'); } } else { const existingLines = scannedWines ? dedupeWineText(scannedWines).split("\n").filter(Boolean) : []; const newLines = dedupeWineText(w).split("\n").filter(Boolean); const existingNorm = new Set(existingLines.map(l=>normalizeForDedup(l))); const toAdd = newLines.filter(l=>{ const n=normalizeForDedup(l); return n && !existingNorm.has(n); }); const merged = [...existingLines, ...toAdd].join("\n"); setScannedWines(merged || null); setScannedRows(prev=>mergeScanRows(prev, rows||[])); setScanAddedCount(toAdd.length); setTimeout(()=>setScanAddedCount(0), 3000); } setShowScanner(false); }} onClose={()=>setShowScanner(false)} session={session} onAuthExpired={handleAuthExpired} />}

      <div style={S.wrap}>

        {showSplash && (()=>{
          const hasProfile = profileComplete || userArchetype;
          return (
            <div style={{
              position: "fixed",
              top: 0, left: 0, right: 0, bottom: 0,
              minHeight: "100dvh",
              background: "#FFFAF8",
              zIndex: 9999,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              paddingTop: "60px",
              paddingBottom: "52px",
              paddingLeft: "28px",
              paddingRight: "28px",
              textAlign: "center"
            }}>
              <div style={{ marginBottom: 10 }}>
                <TruePourMark size={200} animated diamondColor="#1A1A1A" />
              </div>
              <div style={{ fontSize: 52, fontWeight: 800, color: C.charcoal, fontFamily: "var(--fd, 'Outfit', sans-serif)", letterSpacing: -0.5, marginBottom: 20 }}>
                True<span style={{ color: C.burgundy }}>Pour</span>
              </div>
              <div style={{
                fontSize: 24,
                fontWeight: 400,
                color: C.burgundy,
                fontFamily: "var(--fd, 'Outfit', sans-serif)",
                lineHeight: 1.2,
                marginBottom: 12,
                maxWidth: 300
              }}>
                {"Wine matched to you."}<br/>{"Not the crowd."}
              </div>
              <div style={{
                fontSize: 16,
                color: "#666",
                fontFamily: "var(--fb, 'DM Sans', sans-serif)",
                lineHeight: 1.6,
                marginBottom: 40,
                maxWidth: 300
              }}>
                Confidently choose every bottle with recommendations made just for you.
              </div>
              <div style={{ width: "100%", maxWidth: 360 }}>
                <button
                  onClick={dismissSplash}
                  style={{
                    width: "100%",
                    padding: "18px",
                    borderRadius: 16,
                    background: C.burgundy,
                    border: "none",
                    color: "#fff",
                    fontSize: 17,
                    fontWeight: 700,
                    fontFamily: "var(--fd, 'Outfit', sans-serif)",
                    cursor: "pointer",
                    marginBottom: 12
                  }}>
                  {hasProfile ? "Let's find my wine →" : "Find my wine personality →"}
                </button>
                <div style={{
                  fontSize: 13,
                  color: "#bbb",
                  fontFamily: "var(--fb, 'DM Sans', sans-serif)"
                }}>
                  {hasProfile
                    ? "Your taste profile is ready"
                    : "60 seconds · No wine knowledge needed"}
                </div>
                {!hasProfile && (
                  <div
                    onClick={() => {
                      setShowSplash(false);
                      try { localStorage.setItem("truepour_splash_seen", String(Date.now())); } catch {}
                      setAuthMode('signin');
                      setShowPassword(false);
                      setScreen('auth');
                    }}
                    style={{
                      marginTop: 20,
                      fontSize: 14,
                      color: C.burgundy,
                      fontFamily: "var(--fb, 'DM Sans', sans-serif)",
                      textDecoration: 'underline',
                      cursor: 'pointer',
                    }}
                  >
                    Already have an account? Sign in
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {(loading || (styleGuideLoading && homeFlowPath === "notsure")) && <FullScreenLoader homeFlowPath={homeFlowPath} context={context} scannedWines={scannedWines} />}

        {/* RENDER SAFETY NET — if `screen` is ever an unknown/orphan/stale value
            (e.g. a legacy navigateTo target or a bad screenHistory pop), render a
            recoverable state instead of blank white. Fires only when no known
            screen branch matches. */}
        {!KNOWN_SCREENS.has(screen) && (
          <div style={{ minHeight:"60dvh", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", textAlign:"center", padding:"40px 24px", gap:14 }}>
            <div style={{ fontSize:20, fontWeight:800, color:C.charcoal, fontFamily:F.display }}>Something went off track</div>
            <div style={{ fontSize:14, color:C.charcoalSoft, fontFamily:F.body, lineHeight:1.6, maxWidth:300 }}>Let's head back home and try that again.</div>
            <button onClick={()=>{ setScreen("home"); setScreenHistory([]); }}
              style={{ ...S.cta, maxWidth:280 }}>
              Back to home →
            </button>
          </div>
        )}

        {/* ── HOME ── */}
        {screen==="home" && (
          <div style={{ display:"flex", flexDirection:"column", height:"calc(100dvh - 70px - env(safe-area-inset-bottom))" }}>
            {pendingArchetypeReveal && (() => {
  const ARCHETYPE_NAMES = {
    crisp_purist: 'The Crisp Purist',
    bold_traditionalist: 'The Bold Traditionalist',
    bold_modernist: 'The Bold Modernist',
    smooth_romantic: 'The Velvet Touch',
    bright_adventurer: 'The Bright Adventurer',
    sweet_sipper: 'The Sweet Sipper',
    terroir_hunter: 'The Terroir Hunter',
    elegant_classicist: 'The Elegant Classicist',
    celebration_specialist: 'The Celebration Specialist',
    rich_white_devotee: 'The Rich White Devotee',
    everyday_enthusiast: 'The Everyday Enthusiast',
  };
  const CHANGED_DIMS = ['body','tannin','acidity','oak','sweetness','complexity'];
  const DIM_LABELS = { body:'Body', tannin:'Tannin', acidity:'Acidity', oak:'Oak', sweetness:'Sweetness', complexity:'Complexity' };
  const DIM_HIGH = { body:'Full', tannin:'Grippy', acidity:'Crisp', oak:'Oaky', sweetness:'Sweet', complexity:'Complex' };
  const DIM_LOW = { body:'Light', tannin:'Soft', acidity:'Round', oak:'Unoaked', sweetness:'Bone Dry', complexity:'Simple' };
  // Resolve a display name for either key format: lowercase centroid keys
  // (e.g. 'elegant_classicist', how `next` is stored) OR uppercase archetype keys
  // (e.g. 'VELVET_TOUCH', how `previous` is stored). Previously `previous` fell
  // through to the raw key and rendered "VELVET_TOUCH".
  const archDisplayName = (k) => {
    if (!k) return k;
    if (ARCHETYPE_NAMES[k]) return ARCHETYPE_NAMES[k];
    const lower = String(k).toLowerCase();
    if (ARCHETYPE_NAMES[lower]) return ARCHETYPE_NAMES[lower];
    const inv = Object.keys(LOWERCASE_TO_ARCHETYPE_KEY).find(lk => LOWERCASE_TO_ARCHETYPE_KEY[lk] === k);
    if (inv && ARCHETYPE_NAMES[inv]) return ARCHETYPE_NAMES[inv];
    return k;
  };
  const prevName = archDisplayName(pendingArchetypeReveal.previous);
  const nextName = archDisplayName(pendingArchetypeReveal.next);
  // Canonical archetype icon key (UPPERCASE, e.g. ELEGANT_CLASSICIST) for the
  // TARGET archetype. toArchetypeKey handles both lowercase centroid keys
  // ('elegant_classicist') and already-uppercase keys ('VELVET_TOUCH').
  const targetArchKey = toArchetypeKey(pendingArchetypeReveal.next);
  const nextArchObj = ARCHETYPES[toArchetypeKey(pendingArchetypeReveal.next)];
  const wineExamples = pendingArchetypeReveal.wineExamples || [];

  const handleDismiss = async () => {
    setPendingArchetypeReveal(null);
    try {
      await fetch('/api/clear-archetype-changed', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session.access_token}` },
      });
    } catch(e) {}
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 100,
      background: '#0e0608',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24,
    }}>
      <style>{`
        @keyframes tpScaleIn { 0%{transform:scale(0.7);opacity:0} 60%{transform:scale(1.08)} 100%{transform:scale(1);opacity:1} }
        @keyframes tpFadeUp { 0%{transform:translateY(18px);opacity:0} 100%{transform:translateY(0);opacity:1} }
        @keyframes tpRingPulse { 0%,100%{opacity:0.12;transform:translate(-50%,-50%) scale(1)} 50%{opacity:0.28;transform:translate(-50%,-50%) scale(1.06)} }
        @keyframes tpConfettiFall { 0%{transform:translateY(0) rotate(0deg);opacity:1} 80%{opacity:1} 100%{transform:translateY(110vh) rotate(720deg);opacity:0} }
      `}</style>

      {(() => {
        const colors = [C.burgundy,C.burgundy,C.burgundyLight,'#ffffff','#F9C74F'];
        return Array.from({length:55},(_,i)=>(
          <div key={i} style={{
            position:'fixed',
            left:`${Math.random()*100}%`,
            top:`${-5-Math.random()*15}%`,
            width: Math.random()>0.5 ? 6+Math.random()*6 : 4+Math.random()*4,
            height: Math.random()>0.5 ? 6+Math.random()*6 : 8+Math.random()*6,
            borderRadius: Math.random()>0.5 ? '50%' : 2,
            background: colors[Math.floor(Math.random()*colors.length)],
            animation:`tpConfettiFall ${1.8+Math.random()*1.4}s ${Math.random()*0.8}s ease-in forwards`,
            pointerEvents:'none', zIndex:101,
          }}/>
        ));
      })()}

      {revealStep === 0 ? (
        <div style={{ textAlign:'center', maxWidth:320, width:'100%', position:'relative' }}>
          <div style={{ position:'relative', width:120, height:120, margin:'0 auto 24px' }}>
            {[140,110,80].map((size,i) => (
              <div key={i} style={{
                position:'absolute', top:'50%', left:'50%',
                width:size, height:size, borderRadius:'50%',
                border:`1.5px solid ${i===0?C.burgundy:i===1?C.burgundyLight:C.burgundyMid}`,
                transform:'translate(-50%,-50%)',
                animation:`tpRingPulse ${2.2+i*0.3}s ease-in-out infinite ${i*0.25}s`,
              }}/>
            ))}
            <div style={{
              position:'absolute', inset:0,
              display:'flex', alignItems:'center', justifyContent:'center',
              lineHeight:1, textAlign:'center',
              animation:'tpScaleIn 0.7s ease forwards',
            }}><ArchetypeIcon archetypeKey={targetArchKey} size={64} color="#FFFFFF" /></div>
          </div>

          <div style={{ fontSize:11, color:'rgba(255,255,255,0.4)', letterSpacing:3, textTransform:'uppercase', fontFamily:"'DM Sans',sans-serif", marginBottom:10, animation:'tpFadeUp 0.5s 0.3s ease both' }}>
            Your palate has evolved
          </div>
          <div style={{ fontSize:28, fontWeight:800, color:'#fff', fontFamily:"'Outfit',sans-serif", lineHeight:1.2, marginBottom:10, animation:'tpFadeUp 0.5s 0.5s ease both' }}>
            You're now<br/>{nextName}
          </div>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:10, marginBottom:28, animation:'tpFadeUp 0.5s 0.7s ease both' }}>
            <span style={{ fontSize:13, color:'rgba(255,255,255,0.28)', fontFamily:"'DM Sans',sans-serif", textDecoration:'line-through' }}>{prevName}</span>
            <span style={{ fontSize:13, color:'rgba(255,255,255,0.2)' }}>→</span>
            <span style={{ fontSize:13, color:C.burgundy, fontFamily:"'DM Sans',sans-serif", fontWeight:700 }}>{nextName}</span>
          </div>
          <div style={{ animation:'tpFadeUp 0.5s 0.9s ease both' }}>
            <button onClick={()=>setRevealStep(1)} style={{
              width:'100%', padding:'15px', borderRadius:14,
              background:`linear-gradient(135deg,${C.burgundy},${C.burgundyLight})`,
              border:'none', color:'#fff', fontSize:15, fontWeight:700,
              fontFamily:"'Outfit',sans-serif", cursor:'pointer', marginBottom:12,
              boxShadow:'0 8px 24px rgba(160,36,58,0.5)'
            }}>See what changed →</button>
            <button onClick={handleDismiss} style={{
              width:'100%', padding:'12px', borderRadius:14,
              background:'transparent', border:'1px solid rgba(255,255,255,0.1)',
              color:'rgba(255,255,255,0.35)', fontSize:14, fontFamily:"'DM Sans',sans-serif", cursor:'pointer'
            }}>Maybe later</button>
          </div>
        </div>
      ) : (
        <div style={{ background:'#1a0f0d', borderRadius:24, padding:'28px 24px', maxWidth:340, width:'100%', animation:'tpFadeUp 0.4s ease both', maxHeight:'80vh', overflowY:'auto' }}>
          <div style={{ textAlign:'center', marginBottom:20 }}>
            <div style={{ display:'flex', justifyContent:'center', marginBottom:8 }}><ArchetypeIcon archetypeKey={targetArchKey} size={40} color="#FFFFFF" /></div>
            <div style={{ fontSize:20, fontWeight:800, color:'#fff', fontFamily:"'Outfit',sans-serif", marginBottom:4 }}>{nextName}</div>
            {nextArchObj?.tagline && <div style={{ fontSize:13, color:'rgba(255,255,255,0.5)', fontFamily:"'DM Sans',sans-serif" }}>{nextArchObj.tagline}</div>}
          </div>
          {nextArchObj?.chips && (
            <div style={{ display:'flex', gap:6, justifyContent:'center', flexWrap:'wrap', marginBottom:20 }}>
              {nextArchObj.chips.map((chip,i) => (
                <span key={i} style={{ fontSize:11, color:C.burgundy, background:"#fff", border:`1px solid ${C.burgundy}`, borderRadius:20, padding:'4px 10px', fontFamily:"'DM Sans',sans-serif" }}>{chip}</span>
              ))}
            </div>
          )}
          {wineExamples.length > 0 && (
            <div style={{ marginBottom:20 }}>
              <div style={{ fontSize:11, color:'rgba(255,255,255,0.35)', letterSpacing:2, textTransform:'uppercase', fontFamily:"'DM Sans',sans-serif", marginBottom:10 }}>Wines that shaped this</div>
              {wineExamples.map((w,i) => (
                <div key={i} style={{ fontSize:13, color:'rgba(255,255,255,0.65)', fontFamily:"'DM Sans',sans-serif", padding:'8px 0', borderBottom: i < wineExamples.length-1 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
                  ★ {w}
                </div>
              ))}
            </div>
          )}
          <div style={{ borderTop:'1px solid rgba(255,255,255,0.07)', paddingTop:16, marginBottom:20 }}>
            <div style={{ fontSize:11, color:'rgba(255,255,255,0.35)', letterSpacing:2, textTransform:'uppercase', fontFamily:"'DM Sans',sans-serif", marginBottom:12 }}>Your wine personality updated</div>
            <div style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 16px', background:'rgba(255,255,255,0.04)', borderRadius:12 }}>
              <span style={{ fontSize:13, color:'rgba(255,255,255,0.4)', fontFamily:"'DM Sans',sans-serif", textDecoration:'line-through' }}>{prevName}</span>
              <span style={{ fontSize:16, color:'rgba(255,255,255,0.2)' }}>→</span>
              <span style={{ fontSize:13, color:C.burgundy, fontFamily:"'DM Sans',sans-serif", fontWeight:700 }}>{nextName}</span>
            </div>
          </div>
          <button onClick={handleDismiss} style={{
            width:'100%', padding:'14px', borderRadius:14,
            background:`linear-gradient(135deg,${C.burgundy},${C.burgundyLight})`,
            border:'none', color:'#fff', fontSize:15, fontWeight:700,
            fontFamily:"'Outfit',sans-serif", cursor:'pointer',
            boxShadow:'0 8px 24px rgba(160,36,58,0.4)'
          }}>Explore my new wine personality →</button>
        </div>
      )}
    </div>
  );
})()}
            {/* Header */}
            <div onClick={() => handleTabChange('activity')} style={{ cursor: "pointer", WebkitTapHighlightColor: "transparent" }}>
              <div style={{ background: C.burgundy, paddingTop: "calc(env(safe-area-inset-top) + 16px)", paddingBottom: 28, position: "relative", overflow: "hidden", flexShrink: 0 }}>
                <svg style={{ position: "absolute", right: -32, top: "50%", transform: "translateY(-50%)", opacity: 0.08, pointerEvents: "none" }} width="160" height="160" viewBox="0 0 160 160" fill="none">
                  <circle cx="80" cy="80" r="74" stroke="white" strokeWidth="1"/>
                  <circle cx="80" cy="80" r="55" stroke="white" strokeWidth="1"/>
                  <circle cx="80" cy="80" r="36" stroke="white" strokeWidth="1"/>
                  <circle cx="80" cy="80" r="17" stroke="white" strokeWidth="1"/>
                  <path d="M80 73 L85 80 L80 87 L75 80 Z" stroke="white" strokeWidth="1" fill="none"/>
                </svg>
                <div style={{ paddingLeft: 20, paddingRight: 20, position: "relative", zIndex: 1 }}>
                  <div style={{ fontFamily: F.display, fontWeight: 700, fontSize: 16, color: "rgba(255,255,255,0.6)", letterSpacing: "0.05em", marginBottom: 12 }}>TruePour</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 14 }}>
                    <ArchetypeIcon archetypeKey={userArchetype} size={28} color="rgba(255,255,255,0.8)" />
                    <div style={{ fontFamily: F.display, fontWeight: 800, fontSize: 'min(26px, 5.8vw)', color: "#FFFFFF", letterSpacing: -0.3, lineHeight: 1.05, whiteSpace: 'nowrap', overflow: 'hidden' }}>{ARCHETYPES[userArchetype]?.name}</div>
                  </div>
                </div>
                <PalateProgressBar count={Math.max(supabaseRatingCount || 0, mergedRatedCount)} onCtaPress={() => handleTabChange('activity')} context="home" animateFrom={scoreAnimateFrom} onAnimateDone={() => setScoreAnimateFrom(null)} />
              </div>
            </div>


            {/* Context tiles */}
            <div style={{ marginTop:-18, position:"relative", zIndex:2, flex:1, display:"flex", flexDirection:"column", padding:"0 14px 20px" }}>
              <div style={{ display:"flex", flexDirection:"column", flex:1, gap:12 }}>
                {[
                  { id:"restaurant", label:"At a restaurant", action:"Scan the list — find your match" },
                  { id:"store",      label:"At a wine or grocery store", action:"Scan the shelf — find your match" },
                  { id:"home",       label:"I have wine at home", action:"Pick the perfect bottle tonight" },
                  { id:"online",     label:"I want to order online", action:"Personalized picks to your door" },
                ].map(c=>(
                  <button key={c.id} onClick={()=>{
                    resetScanState(); // start every flow clean — no cross-flow stale state
                    setContext(c.id);
                    navigateTo(
                      c.id === "restaurant" ? "restauranttype" :
                      c.id === "home" ? "homepath" :
                      c.id === "store" ? "storescan" :
                      "meal"
                    );
                  }}
                    tabIndex={-1}
                    disabled={!tilesReady}
                    style={{ width:"100%", flex:1, minHeight:0, padding:"0 20px", borderRadius:16, background:"#fff", border:`1px solid ${C.divider}`, cursor:"pointer", textAlign:"left", transition:"all 0.2s", display:"flex", alignItems:"center", justifyContent:"flex-start", boxShadow:"0 2px 8px rgba(0,0,0,0.04)", outline:"none", WebkitTapHighlightColor:"transparent", pointerEvents: tilesReady ? "auto" : "none" }}
>
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"flex-start", flex:1, paddingTop:20, paddingBottom:20 }}>
                      <div style={{ width:48, height:48, flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center", marginRight:18 }}>
                        {c.id === "restaurant" ? <RestaurantIcon size={38} /> : c.id === "store" ? <StoreIcon size={38} /> : c.id === "home" ? <HomeIcon size={38} /> : <OnlineIcon size={38} />}
                      </div>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:17, fontWeight:700, color:C.charcoal, fontFamily:F.display, marginBottom:3 }}>{c.label}</div>
                        <div style={{ fontSize:13, color:C.charcoalSoft, fontFamily:F.body, lineHeight:1.4 }}>{c.action}</div>
                      </div>
                      <div style={{ fontSize:18, color:C.burgundy, flexShrink:0 }}>›</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── SCAN SCREENS ── */}

        {screen==="restauranttype" && (
          <div style={{ paddingTop:36 }}>
            <button style={S.back} onClick={navigateBack}>← Back</button>
            <div style={{ fontSize:10, color:C.burgundy, letterSpacing:2, textTransform:"uppercase", marginBottom:8, fontFamily:F.body, fontWeight:600 }}>Step 1 of 3</div>
            <div style={S.h}>How are you ordering?</div>
            <div style={S.sub}>We'll match you to what's actually available.</div>
            <div style={{ display:"flex", flexDirection:"column", gap:12, marginTop:24 }}>
              {[
                { id:"glass", label:"By the glass", hint:"Wines listed by the glass on the menu" },
                { id:"bottle", label:"By the bottle", hint:"Wines available as a full bottle" },
              ].map(o=>(
                <button key={o.id} onClick={()=>{ setRestaurantOrderType(o.id); setSelectedBudget(null); navigateTo("winelist"); }}
                  style={{ width:"100%", padding:"18px", borderRadius:16, background:"#fff", border:`1px solid ${C.divider}`, cursor:"pointer", textAlign:"left", display:"flex", alignItems:"center", gap:14, boxShadow:"0 2px 8px rgba(0,0,0,0.04)", transition:"all 0.2s" }}>
                  <div style={{ width:48, height:48, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>{o.id === "glass" ? <GlassOrderIcon /> : <BottleOrderIcon />}</div>
                  <div>
                    <div style={{ fontSize:16, fontWeight:700, color:C.charcoal, fontFamily:F.display, marginBottom:2 }}>{o.label}</div>
                    <div style={{ fontSize:13, color:C.charcoalSoft, fontFamily:F.body }}>{o.hint}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {screen==="winelist" && (
          <div style={{ paddingTop:36 }}>
            <button style={S.back} onClick={navigateBack}>← Back</button>
            <div style={{ fontSize:10, color:C.burgundy, letterSpacing:2, textTransform:"uppercase", fontFamily:F.body, fontWeight:600, marginBottom:8 }}>AT A RESTAURANT</div>
            <div style={S.h}>What's on the wine list?</div>
            <div style={S.sub}>{restaurantOrderType==="glass" ? "Add wines available by the glass — type them or scan the menu." : "Add wines available by the bottle — type them or scan the menu."}</div>
            <div style={{ marginTop:16, marginBottom:16 }}>
              <WineAutocomplete onSelect={(wine)=>{
                const existing = scannedWines ? scannedWines.split("\n").filter(Boolean) : [];
                const norm = s => (s||'').toLowerCase().replace(/[^a-z0-9]/g,'');
                if (!existing.some(e => norm(e) === norm(wine))) {
                  setScannedWines([...existing, wine].join("\n"));
                }
              }} mode="evaluation" />
              {scannedWines && scannedWines.split("\n").filter(Boolean).length > 0 && (
                <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginTop:12 }}>
                  {scannedWines.split("\n").filter(Boolean).map((wine, i) => (
                    <div key={i} style={{ display:"flex", alignItems:"center", gap:5, padding:"5px 10px 5px 12px", borderRadius:20, background:"#fff", border:`1px solid ${C.burgundy}`, fontSize:13, color:C.charcoal, fontFamily:F.body }}>
                      <span>{wine}</span>
                      <button onClick={()=>{ const updated = scannedWines.split("\n").filter((_, idx) => idx !== i).join("\n"); setScannedWines(updated || null); }} style={{ background:"none", border:"none", color:C.charcoalSoft, cursor:"pointer", fontSize:15, padding:0, lineHeight:1, marginLeft:2 }}>×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div onClick={()=>{ _scanTipsShown=false; setScanMode("restaurant"); setScanTarget("wines"); navigateTo("winelistscan"); }}
              style={{ borderRadius:12, padding:"28px 24px", textAlign:"center", cursor:"pointer", background:C.cream, border:`1.5px solid ${C.burgundy}`, marginBottom:16, display:"flex", flexDirection:"column", alignItems:"center", gap:10 }}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={C.burgundy} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                <circle cx="12" cy="13" r="4"/>
              </svg>
              <div style={{ fontSize:15, fontWeight:700, color:C.burgundy, fontFamily:F.display }}>Take a photo of the wine list</div>
            </div>
            {scannedWines && scannedWines.split("\n").filter(Boolean).length > 0 && (
              <button style={{ width:"100%", padding:"16px", borderRadius:14, fontSize:16, fontWeight:700, background:C.burgundy, color:"#fff", border:"none", cursor:"pointer", fontFamily:F.display, letterSpacing:0.3, transition:"all 0.2s", marginTop:8 }}
                onClick={()=>{ navigateTo("meal"); }}>
                Continue to find matches →
              </button>
            )}
          </div>
        )}

        {screen==="winelistscan" && (
          <div><button style={S.back} onClick={navigateBack}>← Back</button>
            <ScanScreen title="Scan the wine list." sub={restaurantOrderType==="glass" ? "Scan any page of the menu — we'll match wines available by the glass." : "Scan any page of the menu — we'll match wines available by the bottle."} icon="📋" scanMode="restaurant" scannedWines={scannedWines} setScannedWines={setScannedWines} setScanMode={setScanMode} setScanTarget={setScanTarget} setShowScanner={setShowScanner}
              onContinue={()=>{ const pc=!!(localStorage.getItem("truepour_profile_complete")||profileComplete); if(pc){navigateTo("meal");}else{setCurrentQuestionIndex(0);navigateTo("quiz");} }}
              onSkip={()=>{ const pc=!!(localStorage.getItem("truepour_profile_complete")||profileComplete); setScannedWines(null); if(pc){navigateTo("meal");}else{setCurrentQuestionIndex(0);navigateTo("quiz");} }}
              scanAddedCount={scanAddedCount} requireScan={true}
              continueLabel={(profileComplete||userArchetype)?"Continue →":"Next — build my taste profile →"}
              onDirectScanResult={(w, rows)=>{ const existingLines=scannedWines?dedupeWineText(scannedWines).split("\n").filter(Boolean):[]; const newLines=dedupeWineText(w).split("\n").filter(Boolean); const existingNorm=new Set(existingLines.map(l=>normalizeForDedup(l))); const toAdd=newLines.filter(l=>{ const n=normalizeForDedup(l); return n&&!existingNorm.has(n); }); const merged=[...existingLines,...toAdd].join("\n"); setScannedWines(merged||null); setScannedRows(prev=>mergeScanRows(prev, rows||[])); setScanAddedCount(toAdd.length); setTimeout(()=>setScanAddedCount(0),3000); }} session={session} onAuthExpired={handleAuthExpired} />
          </div>
        )}

        {screen==="storescan" && (
          <div><button style={S.back} onClick={navigateBack}>← Back</button>
            <ScanScreen title="What's on the shelf?" sub="Scan the wine section and tell us what you're eating — we'll tell you exactly which bottle to grab." icon="🛒" scanMode="store" scannedWines={scannedWines} setScannedWines={setScannedWines} setScanMode={setScanMode} setScanTarget={setScanTarget} setShowScanner={setShowScanner} onContinue={()=>{ navigateTo("meal"); }} onSkip={()=>{ setScannedWines(null); navigateTo("meal"); }} scanAddedCount={scanAddedCount} requireScan={true} scanLabel="Scan the shelf to continue →"
              onDirectScanResult={(w, rows)=>{ const existingLines=scannedWines?dedupeWineText(scannedWines).split("\n").filter(Boolean):[]; const newLines=dedupeWineText(w).split("\n").filter(Boolean); const existingNorm=new Set(existingLines.map(l=>normalizeForDedup(l))); const toAdd=newLines.filter(l=>{ const n=normalizeForDedup(l); return n&&!existingNorm.has(n); }); const merged=[...existingLines,...toAdd].join("\n"); setScannedWines(merged||null); setScannedRows(prev=>mergeScanRows(prev, rows||[])); setScanAddedCount(toAdd.length); setTimeout(()=>setScanAddedCount(0),3000); }} session={session} onAuthExpired={handleAuthExpired} /* __SCAN_DEBUG_FIX__ */ />
          </div>
        )}
        {(screen==="homebottles" || screen==="homescan") && (
          <div><button style={S.back} onClick={navigateBack}>← Back</button>
            <ScanScreen title="What's in your rack?" sub="Scan your bottles and tell us what you're having — we'll pick the best one for tonight." icon="🍾" scanMode="home" scannedWines={scannedWines} setScannedWines={setScannedWines} setScanMode={setScanMode} setScanTarget={setScanTarget} setShowScanner={setShowScanner} onContinue={()=>{ navigateTo("meal"); }} onSkip={()=>{ setScannedWines(null); navigateTo("meal"); }} scanAddedCount={scanAddedCount}
              onDirectScanResult={w=>{ const existingLines=scannedWines?dedupeWineText(scannedWines).split("\n").filter(Boolean):[]; const newLines=dedupeWineText(w).split("\n").filter(Boolean); const existingNorm=new Set(existingLines.map(l=>normalizeForDedup(l))); const toAdd=newLines.filter(l=>{ const n=normalizeForDedup(l); return n&&!existingNorm.has(n); }); const merged=[...existingLines,...toAdd].join("\n"); setScannedWines(merged||null); setScanAddedCount(toAdd.length); setTimeout(()=>setScanAddedCount(0),3000); }} session={session} onAuthExpired={handleAuthExpired} /* __SCAN_DEBUG_FIX__ */ />
          </div>
        )}

        {/* ── HOME: PATH SELECTOR ── */}
        {screen==="homepath" && (
          <div style={{ paddingTop:36 }}>
            <button style={S.back} onClick={navigateBack}>← Back</button>
            <div style={{ fontSize:10, color:C.burgundy, letterSpacing:2, textTransform:"uppercase", fontFamily:F.body, fontWeight:600, marginBottom:8 }}>I HAVE WINE AT HOME</div>
            <div style={S.h}>What are you working with tonight?</div>
            <div style={{ display:"flex", flexDirection:"column", gap:12, marginTop:24 }}>
              {[
                { label:"I have a specific bottle", hint:"Is it a good match for tonight?", path:"specific", dest:"homespecific", icon:"single" },
                { label:"I have a few bottles", hint:"Help me choose the best one", path:"few", dest:"homefew", icon:"double" },
                { label:"Not sure yet", hint:"Tell me what to look for", path:"notsure", dest:"meal", icon:"sparkle" },
              ].map(t=>(
                <button key={t.path} onClick={()=>{ setHomeFlowPath(t.path); navigateTo(t.dest); }}
                  style={{ display:"flex", alignItems:"center", gap:14, padding:"20px", borderRadius:16, background:"#fff", border:`1.5px solid #ecddd7`, cursor:"pointer", textAlign:"left", width:"100%", boxSizing:"border-box" }}>
                  <div style={{ width:48, height:48, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                    {t.icon === "single" ? (
                      <svg width="24" height="40" viewBox="10 0 16 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M20.0848 3.12544C20.3641 2.39639 20.3563 1.92852 20.0848 1H16.3653C16.225 1.92479 16.2092 2.40108 16.3653 3.12544M20.0848 3.12544V10.5645C21.6602 14.002 22.3812 15.9423 23.4501 19.4205V21.723M20.0848 3.12544H16.3653M16.3653 3.12544V10.5645C14.8377 13.9563 14.1119 15.8987 13 19.4205V21.723M13 21.723H23.4501M13 21.723V30.0477M23.4501 21.723V30.0477M13 30.0477V33.9443C13.325 34.5989 13.5522 34.7756 14.0627 34.6528H22.7416L23.4501 33.9443V30.0477M13 30.0477H23.4501" stroke={C.burgundy} strokeWidth="0.5"/>
                        <path d="M16.3653 8.08484H19.9077" stroke={C.burgundy} strokeWidth="0.5"/>
                      </svg>
                    ) : t.icon === "double" ? (
                      <div style={{ display:'flex', alignItems:'center' }}>
                        <svg width="24" height="40" viewBox="10 0 16 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M20.0848 3.12544C20.3641 2.39639 20.3563 1.92852 20.0848 1H16.3653C16.225 1.92479 16.2092 2.40108 16.3653 3.12544M20.0848 3.12544V10.5645C21.6602 14.002 22.3812 15.9423 23.4501 19.4205V21.723M20.0848 3.12544H16.3653M16.3653 3.12544V10.5645C14.8377 13.9563 14.1119 15.8987 13 19.4205V21.723M13 21.723H23.4501M13 21.723V30.0477M23.4501 21.723V30.0477M13 30.0477V33.9443C13.325 34.5989 13.5522 34.7756 14.0627 34.6528H22.7416L23.4501 33.9443V30.0477M13 30.0477H23.4501" stroke={C.burgundy} strokeWidth="0.5"/>
                          <path d="M16.3653 8.08484H19.9077" stroke={C.burgundy} strokeWidth="0.5"/>
                        </svg>
                        <svg width="24" height="40" viewBox="10 0 16 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M20.0848 3.12544C20.3641 2.39639 20.3563 1.92852 20.0848 1H16.3653C16.225 1.92479 16.2092 2.40108 16.3653 3.12544M20.0848 3.12544V10.5645C21.6602 14.002 22.3812 15.9423 23.4501 19.4205V21.723M20.0848 3.12544H16.3653M16.3653 3.12544V10.5645C14.8377 13.9563 14.1119 15.8987 13 19.4205V21.723M13 21.723H23.4501M13 21.723V30.0477M23.4501 21.723V30.0477M13 30.0477V33.9443C13.325 34.5989 13.5522 34.7756 14.0627 34.6528H22.7416L23.4501 33.9443V30.0477M13 30.0477H23.4501" stroke={C.burgundy} strokeWidth="0.5"/>
                          <path d="M16.3653 8.08484H19.9077" stroke={C.burgundy} strokeWidth="0.5"/>
                        </svg>
                      </div>
                    ) : (
                      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={C.burgundy} strokeWidth="0.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z"/>
                      </svg>
                    )}
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:16, fontWeight:700, color:C.burgundy, fontFamily:F.display, marginBottom:2 }}>{t.label}</div>
                    <div style={{ fontSize:14, color:"#888", fontFamily:F.body }}>{t.hint}</div>
                  </div>
                  <span style={{ fontSize:18, color:C.burgundy, fontFamily:F.body, flexShrink:0 }}>→</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── HOME PATH 1: SPECIFIC BOTTLE INPUT ── */}
        {screen==="winescan" && (
          <div style={{ paddingTop:36 }}>
            <button style={S.back} onClick={() => { setScanModalMode(false); navigateBack(); }}>← Back</button>
            <div style={{ fontSize:10, color:C.burgundy, letterSpacing:2, textTransform:'uppercase', fontFamily:F.body, fontWeight:600, marginBottom:8 }}>WINE MATCH</div>
            <div style={S.h}>Is this wine a match for me?</div>
            <div style={{ fontSize:14, color:C.charcoalSoft, fontFamily:F.body, marginBottom:24 }}>Scan the label or type the wine name — we'll match it to your palate instantly.</div>
            {specificBottle ? (
              <div>
                <div style={{ padding:'14px 16px', background:C.burgundyBg, border:`1.5px solid ${C.burgundy}`, borderRadius:14, display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <div>
                      <div style={{ fontSize:14, fontWeight:700, color:C.charcoal, fontFamily:F.display }}>{specificBottle}</div>
                      <div style={{ fontSize:13, color:C.green, fontFamily:F.body, marginTop:2 }}>✓ Identified</div>
                    </div>
                  </div>
                  <button onClick={() => setSpecificBottle('')} style={{ background:'none', border:'none', color:C.charcoalSoft, fontSize:13, fontFamily:F.body, cursor:'pointer', textDecoration:'underline' }}>Change</button>
                </div>
                <button onClick={() => { if (!homeVibe) setHomeVibe('Just a glass'); navigateTo('homebottlematch'); }} style={{ ...S.cta }}>See if it's a match →</button>
              </div>
            ) : (
              <div>
                <div onClick={() => { setScanMode('winescan'); setScanTarget('winescan'); setShowScanner(true); }}
                  style={{ borderRadius:16, padding:'32px 24px', textAlign:'center', cursor:'pointer', background:C.burgundy, marginBottom:16, display:'flex', flexDirection:'column', alignItems:'center', gap:10 }}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={C.cream} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                    <circle cx="12" cy="13" r="4"/>
                  </svg>
                  <div style={{ fontSize:16, fontWeight:700, color:C.cream, fontFamily:F.display }}>Scan the label</div>
                  <div style={{ fontSize:13, color:'rgba(255,250,248,0.8)', fontFamily:F.body }}>Point your camera at the bottle</div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16 }}>
                  <div style={{ flex:1, height:1, background:C.divider }} />
                  <span style={{ fontSize:13, color:C.charcoalSoft, fontFamily:F.body }}>or type it</span>
                  <div style={{ flex:1, height:1, background:C.divider }} />
                </div>
                <WineAutocomplete onSelect={(wine) => { setSpecificBottle(wine); if (!homeVibe) setHomeVibe('Just a glass'); navigateTo('homebottlematch'); }} mode="evaluation" />
              </div>
            )}
          </div>
        )}

        {screen==="homespecific" && (
          <div style={{ paddingTop:36 }}>
            <button style={S.back} onClick={navigateBack}>← Back</button>
            <div style={{ fontSize:10, color:C.burgundy, letterSpacing:2, textTransform:"uppercase", fontFamily:F.body, fontWeight:600, marginBottom:8 }}>{scanModalMode ? 'WINE MATCH' : 'YOUR BOTTLE'}</div>
            <div style={S.h}>{scanModalMode ? 'Which wine are you curious about?' : 'Which bottle are you thinking of opening?'}</div>
            <div style={{ marginTop:24 }}>
              {specificBottle ? (
                <div>
                  <div style={{ padding:"14px 16px", background:C.burgundyBg, border:`1.5px solid ${C.burgundy}`, borderRadius:14, display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                      <div>
                        <div style={{ fontSize:14, fontWeight:700, color:C.charcoal, fontFamily:F.display }}>{specificBottle}</div>
                        <div style={{ fontSize:13, color:C.green, fontFamily:F.body, marginTop:2 }}>✓ Confirmed</div>
                      </div>
                    </div>
                    <button onClick={()=>setSpecificBottle("")} style={{ background:"none", border:"none", color:C.charcoalSoft, fontSize:13, fontFamily:F.body, cursor:"pointer", textDecoration:"underline" }}>Change</button>
                  </div>
                  <button onClick={()=>{ if (scanModalMode) { if (!homeVibe) setHomeVibe('Just a glass'); navigateTo("homebottlematch"); } else { navigateTo("meal"); } }} style={{ ...S.cta }}>Continue →</button>
                </div>
              ) : (
                <div>
                  <WineAutocomplete onSelect={(wine)=>{ setSpecificBottle(wine); if (scanModalMode) { if (!homeVibe) setHomeVibe('Just a glass'); navigateTo("homebottlematch"); } else { navigateTo("meal"); } }} mode="evaluation" />
                  <div onClick={()=>{ setScanMode('winescan'); setScanTarget('winescan'); setShowScanner(true); }}
                    style={{ borderRadius:12, padding:"28px 24px", textAlign:"center", cursor:"pointer", background:C.burgundy, marginTop:16, display:"flex", flexDirection:"column", alignItems:"center", gap:10 }}>
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={C.cream} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                      <circle cx="12" cy="13" r="4"/>
                    </svg>
                    <div style={{ fontSize:15, fontWeight:700, color:C.cream, fontFamily:F.display }}>Take a photo of the label</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── HOME PATH 2: MULTI-BOTTLE ENTRY ── */}
        {screen==="homefew" && (
          <div style={{ paddingTop:36 }}>
            <button style={S.back} onClick={navigateBack}>← Back</button>
            <div style={{ fontSize:10, color:C.burgundy, letterSpacing:2, textTransform:"uppercase", fontFamily:F.body, fontWeight:600, marginBottom:8 }}>I HAVE WINE AT HOME</div>
            <div style={S.h}>What bottles do you have?</div>
            <div style={S.sub}>Add up to 5 — we'll find the best one for tonight.</div>
            {styleGuideSelection && (
              <div style={{ fontSize:13, color:C.burgundy, fontFamily:"var(--fb)", background:"#fff", border:`1px solid ${C.burgundy}`, borderRadius:20, padding:"4px 12px", display:"inline-block", marginBottom:12 }}>
                Looking for: {styleGuideSelection.styleName || styleGuideSelection.name || "your selected style"}
              </div>
            )}
            <div style={{ marginTop:16 }}>
              {homeFewBottles.length < 5 && (
                <WineAutocomplete onSelect={(wine)=>{
                  if (homeFewBottles.length < 5 && wine.trim() && !homeFewBottles.includes(wine.trim())) {
                    setHomeFewBottles(prev=>[...prev, wine.trim()]);
                  }
                }} mode="evaluation" />
              )}
              {homeFewBottles.length > 0 && (
                <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginTop:10, marginBottom:4 }}>
                  {homeFewBottles.map((b,i)=>(
                    <div key={i} style={{ display:"flex", alignItems:"center", gap:6, padding:"8px 14px", background:"#fff", border:`1px solid #ecddd7`, borderRadius:20, fontSize:14, color:C.burgundy, fontFamily:F.body }}>
                      <span>{b}</span>
                      <button onClick={()=>setHomeFewBottles(prev=>prev.filter((_,j)=>j!==i))}
                        style={{ background:"none", border:"none", color:"#bbb", cursor:"pointer", fontSize:16, padding:0, lineHeight:1, marginLeft:2 }}>×</button>
                    </div>
                  ))}
                </div>
              )}
              {homeFewBottles.length > 0 && (
                <div style={{ fontSize:13, color:C.charcoalSoft, fontFamily:F.body, marginTop:6, marginBottom:16 }}>{homeFewBottles.length} of 5 added</div>
              )}
            </div>
            <div onClick={()=>navigateTo("homescan")}
              style={{ borderRadius:12, padding:"28px 24px", textAlign:"center", cursor:"pointer", background:C.cream, border:`1.5px solid ${C.burgundy}`, marginBottom:16, display:"flex", flexDirection:"column", alignItems:"center", gap:10 }}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={C.burgundy} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                <circle cx="12" cy="13" r="4"/>
              </svg>
              <div style={{ fontSize:15, fontWeight:700, color:C.burgundy, fontFamily:F.display }}>Take a photo of your bottles</div>
            </div>
            {homeFewBottles.length > 0 && (
              <button onClick={()=>{ setHomeVibe(null); navigateTo("meal"); }}
                style={{ ...S.cta, marginTop:0 }}>
                Find the best match →
              </button>
            )}
          </div>
        )}

        {/* ── UNIFIED QUIZ (Q1–Q10) ── */}
        {screen==="quiz" && (()=>{
          if (showMidQuizInterstitial) {
            return (
              <div style={{ position:"fixed", inset:0, background:"#FFFAF8", zIndex:9999, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", textAlign:"center", padding:"0 24px" }}>
                <TruePourMark size={120} animated={true} diamondColor="#1A1A1A" />
                <div style={{ fontSize:28, fontWeight:800, color:C.charcoal, fontFamily:F.display, marginTop:24, marginBottom:10 }}>Your palate is coming into focus.</div>
                <div style={{ fontSize:15, color:"#888", fontFamily:F.body }}>Just 5 more questions.</div>
              </div>
            );
          }
          const question = ALL_TASTE_QUESTIONS[currentQuestionIndex];
          const isBasic = currentQuestionIndex < 5;
          const value = isBasic ? sliders[question.id] : extendedSliders[question.id];
          const handleChange = (v) => {
            if (isBasic) persistSliders({...sliders, [question.id]: v});
            else persistExtendedSliders({...extendedSliders, [question.id]: v});
          };
          return (
            <div style={{ paddingTop:36 }}>
              {currentQuestionIndex > 0 && (
                <button style={S.back} onClick={()=>{ setCurrentQuestionIndex(i => i - 1); scrollToTop(); }}>← Back</button>
              )}
              <div style={{ textAlign:"center", fontSize:13, color:C.charcoalSoft, fontFamily:F.body, marginBottom:8 }}>
                Question {currentQuestionIndex + 1} of 11
              </div>
              <div style={{ height:4, borderRadius:2, background:C.cream, overflow:"hidden", marginBottom:24 }}>
                <div style={{ height:"100%", width:`${((currentQuestionIndex + 1) / 11) * 100}%`, background:C.burgundy, borderRadius:2, transition:"width 0.3s" }} />
              </div>
              <SpectrumSlider key={question.id} question={question} value={value} onChange={handleChange} />
              <button
                disabled={!value}
                style={{ ...S.cta, background:(!value)?"#e8d5cf":C.burgundy, color:(!value)?"#c4a49d":"#fff", cursor:(!value)?"default":"pointer" }}
                onClick={()=>{
                  if (!value) return;
                  scrollToTop();
                  if (currentQuestionIndex === 10) {
                    const finalExtSliders = { ...extendedSliders, [question.id]: value };
                    persistExtendedSliders(finalExtSliders);
                    const arch = calculateArchetype(sliders, finalExtSliders);
                    fireEvent('quiz_completed', { archetype: arch, deep_profile: true });
                    const prev = userArchetype;
                    setUserArchetype(arch);
                    setDeepProfile(true);
                    setProfileComplete(true);
                    try {
                      localStorage.setItem("truepour_archetype", arch);
                      localStorage.setItem("truepour_archetype_version", "2");
                      localStorage.setItem("truepour_profile_complete", "true");
                      localStorage.setItem("truepour_deep_profile", "true");
                      localStorage.setItem("truepour_extended_sliders", JSON.stringify(finalExtSliders));
                    } catch {}
                    // Persist the completed profile DIRECTLY to Supabase (no migration-path dependency).
                    if (session) {
                      migrateProfileToSupabase(session, {
                        archetype: arch,
                        archetypeVersion: 2,
                        sliders: sliders,
                        extendedSliders: finalExtSliders,
                        deepProfile: true,
                        likedWines: userProfile.likedWines || [],
                        cellarRatings: userProfile.cellarRatings || {},
                      }).catch(() => {});
                    }
                    setScreen("archetype");
                  } else if (currentQuestionIndex === 5) {
                    setShowMidQuizInterstitial(true);
                    setTimeout(() => {
                      setShowMidQuizInterstitial(false);
                      setCurrentQuestionIndex(6);
                    }, 3000);
                  } else {
                    setCurrentQuestionIndex(i => i + 1);
                  }
                }}>
                {currentQuestionIndex === 10 ? "Reveal my wine personality →" : "Continue →"}
              </button>
            </div>
          );
        })()}


        {/* ── ARCHETYPE REVEAL ── */}
        {screen==="archetype" && userArchetype && ARCHETYPES[userArchetype] && (()=>{
          const arch = ARCHETYPES[userArchetype];
          const handleShare = async () => {
            if (!shareCardRef.current) return;
            try {
              const canvas = await html2canvas(shareCardRef.current, { scale:2, useCORS:true, backgroundColor:C.burgundyBg });
              const blob = await new Promise(res=>canvas.toBlob(res,"image/png"));
              if (navigator.share && blob) {
                await navigator.share({ title:`I'm ${arch.name} on TruePour`, text:arch.shareText, files:[new File([blob],"truepour-archetype.png",{type:"image/png"})] });
              } else {
                try { await navigator.clipboard.writeText(arch.shareText); alert("Link copied to clipboard!"); } catch {}
              }
            } catch(e) { console.error(e); }
          };
          const handleFindWine = () => {
            if (!session) {
              setScreen('auth');
              scrollToTop();
              return;
            }
            setNavVisible(true);
            setActiveTab('discover');
            setScreen('home');
            scrollToTop();
          };
          const handleRetake = () => {
            const def = {sweetness:null,fruit:null,body:null,acidity:null,adventure:null};
            persistSliders(def);
            persistExtendedSliders({tannin:null,oak:null,alcohol:null,aromatics:null,complexity:null,color:null});
            setCurrentQuestionIndex(0);
            navigateTo("quiz");
          };
          return (
  <>
  <div ref={archScrollRef} style={{
    position: 'fixed', inset: 0, zIndex: 800,
    background: C.burgundy,
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    WebkitMaskImage: archScrolled ? 'none' : 'linear-gradient(to bottom, black calc(100% - 80px), transparent 100%)',
    maskImage: archScrolled ? 'none' : 'linear-gradient(to bottom, black calc(100% - 80px), transparent 100%)',
  }}>
    <style>{`
      @keyframes archRevealFade {
        from { opacity: 0; transform: translateY(24px); }
        to   { opacity: 1; transform: translateY(0); }
      }
      @keyframes archEmojiPop {
        0%   { transform: scale(0.6); opacity: 0; }
        70%  { transform: scale(1.12); opacity: 1; }
        100% { transform: scale(1); opacity: 1; }
      }
      @keyframes archRingPulse {
        0%,100% { opacity: 0.15; transform: scale(1); }
        50%     { opacity: 0.35; transform: scale(1.08); }
      }
      @keyframes archScrollBounce {
        0%, 100% { transform: translateY(0); }
        50%      { transform: translateY(6px); }
      }
      @keyframes archCtaFadeIn {
        from { opacity: 0; transform: translateY(20px); }
        to   { opacity: 1; transform: translateY(0); }
      }
    `}</style>

    {/* Ring decoration */}
    <div style={{
      position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
      width: 340, height: 340, pointerEvents: 'none',
    }}>
      {[200, 260, 320].map((size, i) => (
        <div key={i} style={{
          position: 'absolute',
          top: '50%', left: '50%',
          width: size, height: size,
          marginTop: -size/2, marginLeft: -size/2,
          borderRadius: '50%',
          border: '1px solid rgba(255,255,255,0.12)',
          animation: `archRingPulse ${2.5 + i * 0.4}s ease-in-out infinite`,
          animationDelay: `${i * 0.3}s`,
        }} />
      ))}
    </div>

    {/* Scrollable content */}
    <div style={{
      display: 'flex', flexDirection: 'column',
      alignItems: 'center',
      padding: '60px 28px',
      paddingBottom: 24,
      textAlign: 'center',
      animation: 'archRevealFade 0.6s ease both',
    }}>

      {/* Eyebrow */}
      <div style={{
        fontSize: 13, fontWeight: 600, letterSpacing: 3,
        textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)',
        fontFamily: F.body, marginBottom: 28,
      }}>
        Your Wine Personality
      </div>

      {/* Emoji */}
      <div style={{marginBottom:24,animation:'archEmojiPop 0.7s cubic-bezier(0.34,1.56,0.64,1) 0.2s both'}}>
        <ArchetypeIcon archetypeKey={userArchetype} size={88} color="#FFFFFF"/>
      </div>

      {/* Archetype name */}
      <div style={{
        fontSize: 36, fontWeight: 800, color: '#FFFFFF',
        fontFamily: F.display, lineHeight: 1.1, marginBottom: 10,
        letterSpacing: -0.5,
      }}>
        {arch.name}
      </div>

      {/* Tagline */}
      <div style={{
        fontSize: 16, fontWeight: 400, color: 'rgba(255,255,255,0.75)',
        fontFamily: F.display, letterSpacing: 0.3, marginBottom: 32,
        fontStyle: 'italic',
      }}>
        {arch.tagline}
      </div>

      {/* Chips */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 8,
        justifyContent: 'center', marginBottom: 36,
      }}>
        {((getArchetypeVariant(userArchetype, extendedSliders)?.chips) || arch.chips || []).map((chip, i) => (
          <span key={i} style={{
            fontSize: 13, color: 'rgba(255,255,255,0.9)',
            background: 'rgba(255,255,255,0.12)',
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: 20, padding: '5px 14px',
            fontFamily: F.body, fontWeight: 500,
            backdropFilter: 'blur(4px)',
          }}>{chip}</span>
        ))}
      </div>

      {/* Description card */}
      <div ref={archDescRef} style={{
        background: 'rgba(255,255,255,0.10)',
        border: '1px solid rgba(255,255,255,0.15)',
        borderRadius: 20, padding: '20px 22px',
        marginBottom: 16, textAlign: 'left', width: '100%', maxWidth: 380,
      }}>
        <div style={{
          fontSize: 15, color: 'rgba(255,255,255,0.85)',
          fontFamily: F.body, lineHeight: 1.7,
        }}>
          {getArchetypeVariant(userArchetype, extendedSliders)?.description || arch.description}
        </div>
      </div>

      {/* Regions */}
      <div style={{
        width: '100%', maxWidth: 380,
        borderTop: '1px solid rgba(255,255,255,0.15)',
        paddingTop: 16, marginBottom: 40, textAlign: 'left',
      }}>
        <div style={{
          fontSize: 12, color: 'rgba(255,255,255,0.45)',
          letterSpacing: 2, textTransform: 'uppercase',
          fontFamily: F.body, fontWeight: 600, marginBottom: 8,
        }}>
          Your Regions & Varieties
        </div>
        <div style={{
          fontSize: 14, color: 'rgba(255,255,255,0.65)',
          fontFamily: F.body, lineHeight: 1.6,
        }}>
          {getArchetypeVariant(userArchetype, extendedSliders)?.regions || arch.regions}
        </div>
      </div>

      {/* Primary CTA */}
      <button ref={archInlineCTARef} onClick={handleFindWine} style={{
        width: '100%', maxWidth: 380,
        padding: '17px 24px', borderRadius: 16,
        background: C.cream,
        border: 'none', color: C.burgundy,
        fontSize: 16, fontWeight: 800,
        fontFamily: F.display, cursor: 'pointer',
        letterSpacing: 0.2,
        boxShadow: 'none',
        marginBottom: 12,
      }}>
        Find my {arch.name.replace('The ', '')} wine →
      </button>

      {/* Share */}
      <button onClick={handleShare} style={{
        width: '100%', maxWidth: 380,
        background: 'none', border: '1px solid rgba(255,255,255,0.3)',
        borderRadius: 14, padding: '12px 16px',
        color: 'rgba(255,255,255,0.8)', fontSize: 14,
        fontWeight: 600, fontFamily: F.body,
        cursor: 'pointer', marginBottom: 12,
      }}>
        Share ↗
      </button>

      {/* Let's try again */}
      <button onClick={handleRetake} style={{
        width: '100%', maxWidth: 380,
        background: 'none', border: '1px solid rgba(255,255,255,0.15)',
        borderRadius: 14, padding: '12px 16px',
        color: 'rgba(255,255,255,0.4)', fontSize: 14,
        fontFamily: F.body, cursor: 'pointer',
      }}>
        Let's try again →
      </button>

    </div>

    {/* Hidden share card for html2canvas */}
    <div ref={shareCardRef} style={{ position:'absolute', left:-9999, top:0, background:C.burgundyBg, borderRadius:24, border:`1px solid ${C.burgundyMid}`, padding:'28px 24px', width:320, textAlign:'center' }}>
    {userArchetype && ARCHETYPES[userArchetype] && (() => {
      const _arch = ARCHETYPES[userArchetype];
      const _variant = getArchetypeVariant(userArchetype, extendedSliders);
      const _chips = _variant?.chips || _arch.chips || [];
      const _regions = _variant?.regions || _arch.regions;
      const _desc = _variant?.description || _arch.description;
      return (<>
        <div style={{marginBottom:8,display:'flex',justifyContent:'center'}}>
          <ArchetypeIcon archetypeKey={userArchetype} size={80} color={C.burgundy} />
        </div>
        <div style={{fontSize:22,fontWeight:800,color:C.burgundy,fontFamily:F.display,marginBottom:6}}>{_arch.name}</div>
        <div style={{fontSize:14,fontWeight:600,color:C.charcoal,fontFamily:F.display,marginBottom:12}}>{_arch.tagline}</div>
        <div style={{fontSize:13,color:C.charcoalSoft,fontFamily:F.body,lineHeight:1.6,marginBottom:12,textAlign:'left'}}>{_desc}</div>
        {_chips.length > 0 && (
          <div style={{display:'flex',flexWrap:'wrap',gap:5,justifyContent:'center',marginBottom:12}}>
            {_chips.map((chip,i) => (
              <span key={i} style={{fontSize:11,color:C.burgundy,background:'rgba(160,36,58,0.08)',padding:'3px 10px',borderRadius:20,border:`1px solid rgba(160,36,58,0.2)`,fontFamily:F.body,fontWeight:500}}>{chip}</span>
            ))}
          </div>
        )}
        {_regions && (
          <div style={{borderTop:`1px solid rgba(160,36,58,0.15)`,paddingTop:12,marginBottom:12,textAlign:'left'}}>
            <div style={{fontSize:10,color:C.burgundy,letterSpacing:2,textTransform:'uppercase',fontFamily:F.body,fontWeight:600,marginBottom:5}}>Your regions & varieties</div>
            <div style={{fontSize:12,color:C.charcoalMid,fontFamily:F.body,lineHeight:1.5}}>{_regions}</div>
          </div>
        )}
        <div style={{fontSize:13,color:C.burgundy,fontFamily:F.body,fontWeight:600}}>truepour.ai</div>
      </>);
    })()}
    </div>

  </div>

  {/* Scroll indicator */}
  <div style={{
    position: 'fixed',
    bottom: 80,
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 100,
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
    opacity: archScrolled ? 0 : 1,
    transition: 'opacity 0.4s ease',
    pointerEvents: 'none',
  }}>
    <div style={{
      fontSize: 20, color: 'rgba(255,255,255,0.6)',
      animation: 'archScrollBounce 1.2s ease-in-out infinite',
    }}>↓</div>
    <div style={{
      fontSize: 12, color: '#999',
      fontFamily: F.body,
    }}>scroll to continue</div>
  </div>

  {/* Sticky CTA footer */}
  {archCtaVisible && !archInlineCTAInView && (
    <div style={{
      position: 'fixed',
      bottom: 0, left: 0, right: 0,
      zIndex: 900,
      padding: `12px 24px calc(env(safe-area-inset-bottom) + 16px)`,
      background: `linear-gradient(to bottom, transparent, ${C.burgundy} 20%, ${C.burgundy})`,
      animation: 'archCtaFadeIn 0.4s ease both',
    }}>
      <button onClick={handleFindWine} style={{
        width: '100%', maxWidth: 400,
        display: 'block', margin: '0 auto',
        padding: '17px 24px', borderRadius: 16,
        background: C.cream,
        border: 'none', color: C.burgundy,
        fontSize: 16, fontWeight: 800,
        fontFamily: F.display, cursor: 'pointer',
        letterSpacing: 0.2,
        boxShadow: 'none',
      }}>
        Find my {arch.name.replace('The ', '')} wine →
      </button>
    </div>
  )}
  </>
);
        })()}


        {screen === "auth" && (
  <div style={{
    position: 'fixed', inset: 0, zIndex: 850,
    display: 'flex', flexDirection: 'column',
    overflowY: 'auto',
  }}>
    {/* Zone 1 — Burgundy panel */}
    <div style={{
      background: C.burgundy,
      minHeight: '45vh',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: '32px 24px',
    }}>
      <style>{`
        @keyframes authFadeIn {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      <div style={{ animation: 'authFadeIn 0.5s ease forwards', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
        <TruePourMark size={120} color="#FFFFFF" animated={true} />
        {userArchetype && ARCHETYPES[userArchetype] && (
          <div style={{
            fontFamily: 'Outfit', fontWeight: 700, fontSize: 22,
            color: '#fff', textAlign: 'center',
          }}>
            {ARCHETYPES[userArchetype].name}
          </div>
        )}
        <div style={{
          fontFamily: 'Outfit', fontWeight: 700, fontSize: 26,
          color: '#fff', textAlign: 'center',
        }}>
          Your wines are waiting.
        </div>
        <div style={{
          fontFamily: "var(--fb, 'DM Sans', sans-serif)", fontSize: 14,
          color: 'rgba(255,255,255,0.85)', textAlign: 'center',
        }}>
          {authMode === 'signup' ? (userArchetype ? 'Save your profile to unlock your matches.' : 'Create an account to get started.') : 'Welcome back.'}
        </div>
      </div>
    </div>

    {/* Zone 2 — Cream panel */}
    <div style={{
      background: '#FFFAF8',
      flex: 1,
      display: 'flex', flexDirection: 'column',
      alignItems: 'center',
      padding: '28px 24px',
    }}>
      <div style={{ width: '100%', maxWidth: 360, display: 'flex', flexDirection: 'column', gap: 12 }}>

        {authConfirmPending ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 14, padding: '8px 0' }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: C.burgundyBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={C.burgundy} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="4" width="20" height="16" rx="2"/>
                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
              </svg>
            </div>
            <div style={{ fontFamily: 'Outfit', fontWeight: 700, fontSize: 22, color: '#2A2A2A' }}>
              Check your email
            </div>
            <div style={{ fontFamily: "var(--fb, 'DM Sans', sans-serif)", fontSize: 15, color: '#666', lineHeight: 1.6 }}>
              We sent a confirmation link to{' '}
              <span style={{ fontWeight: 700, color: '#2A2A2A' }}>{authConfirmEmail}</span>.
              Tap it to activate your account and you'll be signed in automatically.
            </div>
            <div
              onClick={() => { setAuthConfirmPending(false); setAuthPassword(''); }}
              style={{
                marginTop: 10,
                fontFamily: "var(--fb, 'DM Sans', sans-serif)", fontSize: 14,
                color: C.burgundy, textDecoration: 'underline',
                cursor: 'pointer', textAlign: 'center',
              }}>
              Confirmed on another device? Sign in
            </div>
          </div>
        ) : (
        <>

        {/* Google OAuth */}
        <button
          onClick={async () => {
            setAuthError('');
            const { error } = await supabase.auth.signInWithOAuth({
              provider: 'google',
              options: { redirectTo: window.location.origin }
            });
            if (error) setAuthError(error.message);
          }}
          style={{
            width: '100%', padding: '14px', borderRadius: 12,
            background: '#fff', color: '#333', border: '1px solid #E0D5CC',
            fontSize: 15, fontFamily: "var(--fb, 'DM Sans', sans-serif)", fontWeight: 600,
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            boxSizing: 'border-box',
          }}
        >
          <svg width="18" height="18" viewBox="0 0 18 18">
            <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
            <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/>
            <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.9 8.9 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
            <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
          </svg>
          Continue with Google
        </button>

        {/* Divider */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1, height: 1, background: '#E0D5CC' }} />
          <span style={{ fontFamily: "var(--fb, 'DM Sans', sans-serif)", fontSize: 13, color: '#999' }}>or</span>
          <div style={{ flex: 1, height: 1, background: '#E0D5CC' }} />
        </div>

        {/* Email */}
        <input
          type="email"
          placeholder="Email"
          value={authEmail}
          onChange={(e) => setAuthEmail(e.target.value)}
          style={{
            width: '100%', border: '1px solid #E0D5CC', borderRadius: 12,
            padding: '14px 16px', fontFamily: "var(--fb, 'DM Sans', sans-serif)", fontSize: 15,
            background: '#fff', boxSizing: 'border-box', outline: 'none',
          }}
        />

        {/* Password with eye toggle */}
        <div style={{ position: 'relative', width: '100%' }}>
          <input
            type={showPassword ? 'text' : 'password'}
            placeholder="Password"
            value={authPassword}
            onChange={(e) => setAuthPassword(e.target.value)}
            style={{
              width: '100%', border: '1px solid #E0D5CC', borderRadius: 12,
              padding: '14px 48px 14px 16px', fontFamily: "var(--fb, 'DM Sans', sans-serif)", fontSize: 15,
              background: '#fff', boxSizing: 'border-box', outline: 'none',
            }}
          />
          <button
            onClick={() => setShowPassword(v => !v)}
            style={{
              position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)',
              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
              color: '#999', display: 'flex', alignItems: 'center',
            }}
          >
            {showPassword ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                <line x1="1" y1="1" x2="23" y2="23"/>
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
            )}
          </button>
        </div>


        {/* CTA submit */}
        <button
          onClick={async () => {
            setAuthError('');
            setAuthMessage('');
            setAuthLoading(true);
            try {
              if (authMode === 'signup') {
                const { data, error } = await supabase.auth.signUp({
                  email: authEmail,
                  password: authPassword,
                  options: {
                    emailRedirectTo: window.location.origin,
                    data: {
                      ftue_archetype: userArchetype,
                      ftue_sliders: sliders,
                      ftue_extended_sliders: extendedSliders,
                      ftue_archetype_version: 2,
                      ftue_deep_profile: true,
                    },
                  },
                });
                if (error) throw error;
                if (data?.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
                  // Email already registered and confirmed — signUp() returns success-shaped
                  // response without creating a new user or sending an email (anti-enumeration).
                  setAuthError('This email is already registered. Please sign in instead.');
                  setAuthMode('signin');
                  setShowPassword(false);
                } else {
                  setAuthConfirmEmail(authEmail);
                  setAuthConfirmPending(true);
                  setAuthMode('signin');
                  setShowPassword(false);
                }
              } else {
                const { data, error } = await supabase.auth.signInWithPassword({
                  email: authEmail,
                  password: authPassword,
                });
                if (error) throw error;
                setSession(data.session);
                // Routing is owned by the onAuthStateChange SIGNED_IN listener →
                // rehydrateSession. navVisible is derived. Do not set them here.
                scrollToTop();
              }
            } catch (e) {
              setAuthError(e.message || 'Something went wrong.');
            }
            setAuthLoading(false);
          }}
          disabled={authLoading}
          style={{
            width: '100%', padding: '16px', borderRadius: 12,
            background: C.burgundy, color: '#fff', border: 'none',
            fontFamily: 'Outfit', fontWeight: 700, fontSize: 16,
            cursor: authLoading ? 'default' : 'pointer',
            opacity: authLoading ? 0.6 : 1,
            boxSizing: 'border-box',
          }}
        >
          {authLoading ? 'Please wait...' : (authMode === 'signup' ? (userArchetype ? 'Save my profile' : 'Create account') : 'Sign in')}
        </button>

        {/* Mode toggle */}
        {authMode === 'signin' && (
          <div
            onClick={() => {
              setResetEmail(authEmail);
              setResetError('');
              setResetMessage('');
              setScreen('resetRequest');
            }}
            style={{
              fontFamily: "var(--fb, 'DM Sans', sans-serif)", fontSize: 14,
              color: C.burgundy, textDecoration: 'underline',
              cursor: 'pointer', textAlign: 'center',
            }}
          >
            Forgot password?
          </div>
        )}

        <div
          onClick={() => {
            setAuthMode(authMode === 'signup' ? 'signin' : 'signup');
            setAuthError('');
            setAuthMessage('');
            setAuthConfirmPending(false);
            setShowPassword(false);
          }}
          style={{
            fontFamily: "var(--fb, 'DM Sans', sans-serif)", fontSize: 14,
            color: C.burgundy, textDecoration: 'underline',
            cursor: 'pointer', textAlign: 'center',
          }}
        >
          {authMode === 'signup' ? 'Already have an account? Sign in' : 'New here? Create your free account'}
        </div>

        {/* Error / success message */}
        {authError && (
          <div style={{ fontFamily: "var(--fb, 'DM Sans', sans-serif)", fontSize: 13, color: C.error, textAlign: 'center' }}>
            {authError}
          </div>
        )}
        {authMessage && (
          <div style={{ fontFamily: "var(--fb, 'DM Sans', sans-serif)", fontSize: 13, color: C.green, textAlign: 'center' }}>
            {authMessage}
          </div>
        )}

        </>
        )}

      </div>
    </div>
  </div>
)}

{screen === 'resetRequest' && (
  <div style={{ minHeight: '100dvh', position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', background: '#FFFAF8', padding: '14vh 24px 40px', boxSizing: 'border-box' }}>
    {/* Floating back arrow — no bar behind it */}
    <div
      onClick={() => { setScreen('auth'); setAuthMode('signin'); setResetError(''); setResetMessage(''); }}
      style={{ position: 'absolute', top: 20, left: 16, cursor: 'pointer', color: C.burgundy, display: 'flex', alignItems: 'center', padding: 8 }}
    >
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M19 12H5M12 5l-7 7 7 7"/>
      </svg>
    </div>

    {/* Content */}
    <div style={{ width: '100%', maxWidth: 360, display: 'flex', flexDirection: 'column', gap: 18 }}>
        {resetMessage ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 14, padding: '8px 0' }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: C.burgundyBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={C.burgundy} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="4" width="20" height="16" rx="2"/>
                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
              </svg>
            </div>
            <div style={{ fontFamily: 'Outfit', fontWeight: 700, fontSize: 22, color: '#2A2A2A' }}>Check your email</div>
            <div style={{ fontFamily: "var(--fb, 'DM Sans', sans-serif)", fontSize: 15, color: '#666', lineHeight: 1.6 }}>
              We sent a reset link to <span style={{ fontWeight: 700, color: '#2A2A2A' }}>{resetEmail}</span>. Tap it to set a new password.
            </div>
            <div
              onClick={() => { setScreen('auth'); setAuthMode('signin'); setResetMessage(''); setResetEmail(''); }}
              style={{ marginTop: 10, fontFamily: "var(--fb, 'DM Sans', sans-serif)", fontSize: 14, color: C.burgundy, textDecoration: 'underline', cursor: 'pointer' }}
            >
              Back to sign in
            </div>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
              <TruePourMark size={88} animated diamondColor="#1A1A1A" />
            </div>
            <div style={{ fontFamily: 'Outfit', fontWeight: 700, fontSize: 24, color: '#2A2A2A', textAlign: 'center' }}>
              Reset your password
            </div>
            <div style={{ fontFamily: "var(--fb, 'DM Sans', sans-serif)", fontSize: 15, color: '#666', lineHeight: 1.6, textAlign: 'center' }}>
              Enter your email and we'll send you a link to reset your password.
            </div>
            <input
              type="email"
              placeholder="Email"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              style={{
                width: '100%', border: '1px solid #E0D5CC', borderRadius: 12,
                padding: '14px 16px', fontFamily: "var(--fb, 'DM Sans', sans-serif)", fontSize: 15,
                background: '#fff', boxSizing: 'border-box', outline: 'none',
              }}
            />
            {resetError && (
              <div style={{ fontFamily: "var(--fb, 'DM Sans', sans-serif)", fontSize: 13, color: C.error, textAlign: 'center' }}>
                {resetError}
              </div>
            )}
            <button
              onClick={async () => {
                setResetError('');
                if (!resetEmail) { setResetError('Please enter your email.'); return; }
                setResetLoading(true);
                const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
                  redirectTo: window.location.origin,
                });
                setResetLoading(false);
                if (error) { setResetError(error.message); return; }
                setResetMessage('sent');
              }}
              disabled={resetLoading}
              style={{
                width: '100%', padding: '16px', borderRadius: 12,
                background: C.burgundy, color: '#fff', border: 'none',
                fontFamily: 'Outfit', fontWeight: 700, fontSize: 16,
                cursor: resetLoading ? 'default' : 'pointer',
                opacity: resetLoading ? 0.6 : 1,
                boxSizing: 'border-box',
              }}
            >
              {resetLoading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </>
        )}
    </div>
  </div>
)}

{screen === 'setNewPassword' && (
  <div style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', alignItems: 'center', background: '#FFFAF8', padding: '14vh 24px 40px', boxSizing: 'border-box' }}>
    {/* Content — no back arrow: user arrived via a one-time link */}
    <div style={{ width: '100%', maxWidth: 360, display: 'flex', flexDirection: 'column', gap: 18 }}>
        {resetMessage === 'passwordSet' ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 14, padding: '8px 0' }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: C.burgundyBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={C.burgundy} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6 9 17l-5-5"/>
              </svg>
            </div>
            <div style={{ fontFamily: 'Outfit', fontWeight: 700, fontSize: 24, color: '#2A2A2A' }}>Password updated</div>
            <div style={{ fontFamily: "var(--fb, 'DM Sans', sans-serif)", fontSize: 15, color: '#666', lineHeight: 1.6 }}>Taking you home…</div>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
              <TruePourMark size={88} animated diamondColor="#1A1A1A" />
            </div>
            <div style={{ fontFamily: 'Outfit', fontWeight: 700, fontSize: 24, color: '#2A2A2A', textAlign: 'center' }}>
              Set a new password
            </div>
            <div style={{ fontFamily: "var(--fb, 'DM Sans', sans-serif)", fontSize: 15, color: '#666', lineHeight: 1.6, textAlign: 'center' }}>
              Choose a new password for your account.
            </div>
            {/* New password input */}
            <div style={{ position: 'relative', width: '100%' }}>
              <input
                type={showNewPassword ? 'text' : 'password'}
                placeholder="New password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                style={{
                  width: '100%', border: '1px solid #E0D5CC', borderRadius: 12,
                  padding: '14px 48px 14px 16px', fontFamily: "var(--fb, 'DM Sans', sans-serif)", fontSize: 15,
                  background: '#fff', boxSizing: 'border-box', outline: 'none',
                }}
              />
              <button
                onClick={() => setShowNewPassword(v => !v)}
                style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#999', display: 'flex', alignItems: 'center' }}
              >
                {showNewPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
            {/* Confirm password input */}
            <input
              type={showNewPassword ? 'text' : 'password'}
              placeholder="Confirm new password"
              value={newPasswordConfirm}
              onChange={(e) => setNewPasswordConfirm(e.target.value)}
              style={{
                width: '100%', border: '1px solid #E0D5CC', borderRadius: 12,
                padding: '14px 16px', fontFamily: "var(--fb, 'DM Sans', sans-serif)", fontSize: 15,
                background: '#fff', boxSizing: 'border-box', outline: 'none',
              }}
            />
            {resetError && (
              <div style={{ fontFamily: "var(--fb, 'DM Sans', sans-serif)", fontSize: 13, color: C.error, textAlign: 'center' }}>
                {resetError}
              </div>
            )}
            <button
              onClick={async () => {
                setResetError('');
                if (!newPassword) { setResetError('Please enter a new password.'); return; }
                if (newPassword !== newPasswordConfirm) { setResetError('Passwords do not match.'); return; }
                if (newPassword.length < 6) { setResetError('Password must be at least 6 characters.'); return; }
                setResetLoading(true);
                const { error } = await supabase.auth.updateUser({ password: newPassword });
                setResetLoading(false);
                if (error) { setResetError(error.message); return; }
                try { sessionStorage.setItem('truepour_recovery_completed', '1'); } catch {}
                setResetMessage('passwordSet');
                setRecoveryMode(false);
                recoveryInProgressRef.current = false;
                setNewPassword('');
                setNewPasswordConfirm('');
                const { data: { session: currentSession } } = await supabase.auth.getSession();
                // Clear the dedupe ref: a SIGNED_IN/IIFE rehydrate during the recovery
                // cold-load may have already set it to this user id, which would make
                // the guard short-circuit this rehydrate and leave us stuck here.
                rehydratedRef.current = null;
                if (currentSession) await rehydrateSessionRef.current?.(currentSession);
              }}
              disabled={resetLoading}
              style={{
                width: '100%', padding: '16px', borderRadius: 12,
                background: C.burgundy, color: '#fff', border: 'none',
                fontFamily: 'Outfit', fontWeight: 700, fontSize: 16,
                cursor: resetLoading ? 'default' : 'pointer',
                opacity: resetLoading ? 0.6 : 1,
                boxSizing: 'border-box',
              }}
            >
              {resetLoading ? 'Updating...' : 'Set Password'}
            </button>
          </>
        )}
    </div>
  </div>
)}

        {/* ── ARCHETYPE MODAL (from home pill tap) ── */}
        {false && showArchetypeReveal && userArchetype && ARCHETYPES[userArchetype] && (()=>{
          const arch = ARCHETYPES[userArchetype];
          const handleModalShare = async () => {
            try {
              const canvas = await html2canvas(modalShareCardRef.current, { scale:2, useCORS:true, backgroundColor:C.burgundyBg });
              const blob = await new Promise(res=>canvas.toBlob(res,"image/png"));
              if (navigator.share && blob) {
                await navigator.share({ title:`I'm ${arch.name} on TruePour`, text:arch.shareText, files:[new File([blob],"truepour-archetype.png",{type:"image/png"})] });
              } else {
                try { await navigator.clipboard.writeText(arch.shareText); alert("Link copied to clipboard!"); } catch {}
              }
            } catch(e) { console.error(e); }
          };
          return (
            <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:200, display:"flex", alignItems:"flex-end", justifyContent:"center" }} onClick={()=>setShowArchetypeReveal(false)}>
              <div onClick={e=>e.stopPropagation()} style={{ background:"#fff", borderRadius:"24px 24px 0 0", width:"100%", maxWidth:480, padding:"28px 24px 40px", maxHeight:"85vh", overflowY:"auto" }}>
                <div style={{ width:36, height:4, borderRadius:2, background:C.divider, margin:"0 auto 20px" }} />
                {/* Hidden share card for html2canvas */}
                <div ref={modalShareCardRef} style={{ background:C.burgundyBg, borderRadius:24, border:`1px solid ${C.burgundyMid}`, padding:"28px 24px", marginBottom:20, textAlign:"center" }}>
                  <div style={{marginBottom:8,display:'flex',justifyContent:'center'}}><ArchetypeIcon archetypeKey={userArchetype} size={80} color={C.burgundy} /></div>
                  <div style={{ fontSize:22, fontWeight:800, color:C.burgundy, fontFamily:F.display, lineHeight:1.2, marginBottom:6 }}>{arch.name}</div>
                  <div style={{ fontSize:15, fontWeight:600, color:C.charcoal, fontFamily:F.display, marginBottom:16, letterSpacing:0.3 }}>{arch.tagline}</div>
                  <div style={{ fontSize:14, color:C.charcoalSoft, fontFamily:F.body, lineHeight:1.6, marginBottom:16, textAlign:"left" }}>{getArchetypeVariant(userArchetype,extendedSliders)?.description || arch.description}</div>
                  {((getArchetypeVariant(userArchetype,extendedSliders)?.chips) || arch.chips || []).length > 0 && (
                    <div style={{display:'flex',flexWrap:'wrap',gap:5,justifyContent:'center',marginBottom:12}}>
                      {(getArchetypeVariant(userArchetype,extendedSliders)?.chips || arch.chips || []).map((chip,i) => (
                        <span key={i} style={{fontSize:11,color:C.burgundy,background:'rgba(160,36,58,0.08)',padding:'3px 10px',borderRadius:20,border:`1px solid rgba(160,36,58,0.2)`,fontFamily:F.body,fontWeight:500}}>{chip}</span>
                      ))}
                    </div>
                  )}
                  <div style={{ borderTop:`1px solid ${C.burgundyMid}`, paddingTop:14 }}>
                    <div style={{ fontSize:10, color:C.burgundy, letterSpacing:2, textTransform:"uppercase", fontFamily:F.body, fontWeight:600, marginBottom:8 }}>Your regions & varieties</div>
                    <div style={{ fontSize:13, color:C.charcoalSoft, fontFamily:F.body, lineHeight:1.6, textAlign:"left" }}>{getArchetypeVariant(userArchetype,extendedSliders)?.regions || arch.regions}</div>
                  </div>
                  <div style={{ marginTop:16, fontSize:13, color:C.burgundyLight, fontFamily:F.body, fontWeight:600, letterSpacing:0.5 }}>truepour.ai</div>
                </div>
                <div style={{ display:"flex", gap:10 }}>
                  <button onClick={handleModalShare} style={{ flex:1, padding:"14px", borderRadius:14, background:"transparent", border:`2px solid ${C.burgundy}`, color:C.burgundy, fontSize:15, fontWeight:700, fontFamily:F.display, cursor:"pointer" }}>Share my wine personality</button>
                  <button onClick={()=>setShowArchetypeReveal(false)} style={{ flex:1, padding:"14px", borderRadius:14, background:C.burgundy, border:"none", color:"#fff", fontSize:15, fontWeight:700, fontFamily:F.display, cursor:"pointer" }}>Got it →</button>
                </div>
                <div style={{ textAlign:"center", marginTop:16 }}>
                  <button onClick={()=>setShowArchetypeReveal(false)}
                    style={{ background:"none", border:"none", color:C.burgundy, fontSize:14, fontFamily:F.body, cursor:"pointer" }}>
                    This doesn't feel right →
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ── OCCASION + MEAL + BUDGET ── */}
        {screen==="meal" && (
          <div style={{ paddingTop:36 }}>
            <button style={S.back} onClick={navigateBack}>← Back</button>
            {context==="restaurant" && <div style={{ fontSize:10, color:C.burgundy, letterSpacing:2, textTransform:"uppercase", marginBottom:8, fontFamily:F.body, fontWeight:600 }}>Step 3 of 3</div>}
            <div style={S.h}>What's the occasion?</div>
            <div style={S.sub}>Tell us the vibe — we'll find the right bottle.</div>

            {/* Food input — shown first for restaurant (primary signal) */}
            {context==="restaurant" && (
              <div style={{ marginBottom:22 }}>
                <div style={{ fontSize:14, fontWeight:700, color:C.charcoal, fontFamily:F.display, marginBottom:3 }}>What are you in the mood to eat?</div>
                <div style={{ fontSize:13, color:"#aaa", fontFamily:F.body, marginBottom:8 }}>Optional — skip if you haven't decided</div>
                {scannedDish ? (
                  <div style={{ padding:"10px 14px", background:C.greenBg, borderRadius:12, border:`1px solid ${C.green}`, display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8 }}>
                    <div style={{ fontSize:13, color:C.green, fontFamily:F.body, fontWeight:500 }}>🍽️ Dish read from photo</div>
                    <button onClick={()=>{ setScannedDish(null); setMeal(""); }} style={{ fontSize:13, color:C.charcoalSoft, background:"none", border:"none", cursor:"pointer" }}>Clear ✕</button>
                  </div>
                ) : (
                  <>
                    <textarea value={meal} onChange={e=>setMeal(e.target.value)}
                      placeholder="e.g. steak, pasta, seafood..."
                      rows={2}
                      style={{ width:"100%", padding:"12px 14px", borderRadius:12, fontSize:16, resize:"none", background:"#fff", border:`1px solid ${C.divider}`, color:C.charcoal, fontFamily:F.body, outline:"none", boxSizing:"border-box", lineHeight:1.6 }} />
                  </>
                )}
              </div>
            )}

            {/* Occasion grid — restaurant gets its own tiles */}
            {context === "restaurant" && (
              <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:24 }}>
                {(restaurantOrderType === "glass" ? [
                  { id:"meal",    label:"With my meal",         hint:"Pair to what I'm ordering" },
                  { id:"solo",    label:"Sipping solo",          hint:"Something I'll enjoy" },
                  { id:"hosting", label:"We're celebrating",     hint:"Mark the moment" },
                  { id:"new",     label:"Something new",         hint:"Off the beaten path" },
                ] : [
                  { id:"meal",    label:"With our meal",         hint:"Pair to what we're ordering" },
                  { id:"hosting", label:"We're celebrating",     hint:"Mark the moment" },
                  { id:"sharing", label:"Sharing the table",     hint:"Something for everyone" },
                  { id:"new",     label:"Something new",         hint:"Off the beaten path" },
                ]).map(o=>(
                  <button key={o.id} onClick={()=>{ setOccasion(o.id); setWineColor(null); }}
                    style={{ textAlign:"left", width:"100%", display:"flex", alignItems:"baseline", gap:8, padding:"15px 16px", borderRadius:14, cursor:"pointer",
                      background:occasion===o.id?C.burgundyBg:"#fff",
                      border:`1.5px solid ${occasion===o.id?C.burgundy:C.divider}`,
                      boxShadow:"0 1px 4px rgba(0,0,0,0.04)",
                      transition:"all 0.2s" }}>
                    <span style={{ fontSize:15, fontWeight:700, fontFamily:F.body, whiteSpace:"nowrap", color:occasion===o.id?C.burgundy:C.charcoal }}>{o.label}</span>
                    <span style={{ fontSize:13, fontFamily:F.body, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", color:occasion===o.id?C.burgundy:C.charcoalSoft }}>{o.hint}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Occasion grid — online context */}
            {context === "online" && (
              <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:24 }}>
                {[
                  { id:"hosting",  label:"Having people over",   hint:"Crowd-pleasing picks" },
                  { id:"gift",     label:"Gift for someone",      hint:"Find the perfect bottle" },
                  { id:"cellar",   label:"Restock my cellar",     hint:"Add to my collection" },
                  { id:"special",  label:"Special occasion",      hint:"Toast to the celebration" },
                  { id:"new",      label:"Something new",         hint:"Off the beaten path" },
                  { id:"treat",    label:"Just treat myself",     hint:"Find something I'll love" },
                ].map(o=>(
                  <button key={o.id} onClick={()=>{ setOccasion(o.id); if (!["meal","hosting"].includes(o.id)) setMeal(""); if (["meal","hosting"].includes(o.id)) setWineColor(null); }}
                    style={{ textAlign:"left", width:"100%", display:"flex", alignItems:"baseline", gap:8, padding:"15px 16px", borderRadius:14, cursor:"pointer",
                      background:occasion===o.id?C.burgundyBg:"#fff",
                      border:`1.5px solid ${occasion===o.id?C.burgundy:C.divider}`,
                      boxShadow:"0 1px 4px rgba(0,0,0,0.04)",
                      transition:"all 0.2s" }}>
                    <span style={{ fontSize:15, fontWeight:700, fontFamily:F.body, whiteSpace:"nowrap", color:occasion===o.id?C.burgundy:C.charcoal }}>{o.label}</span>
                    <span style={{ fontSize:13, fontFamily:F.body, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", color:occasion===o.id?C.burgundy:C.charcoalSoft }}>{o.hint}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Occasion grid — non-home, non-restaurant, non-online contexts */}
            {context !== "home" && context !== "restaurant" && context !== "online" && (
              <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:24 }}>
                {[
                  { id:"meal",     label:"Cooking a meal",         hint:"Pair it to dinner" },
                  { id:"hosting",  label:"Having people over",     hint:"Crowd-pleasing picks" },
                  { id:"gift",     label:"Gift for someone",       hint:"Find the perfect bottle" },
                  { id:"glass",    label:"Just a glass",           hint:"Something easy tonight" },
                  { id:"outside",  label:"Drinks outside",         hint:"Fresh and light" },
                  { id:"cheese",   label:"Cheese & charcuterie",   hint:"Wine to graze with" },
                ].map(o=>(
                  <button key={o.id} onClick={()=>{ setOccasion(o.id); if (!["meal","hosting"].includes(o.id)) setMeal(""); if (["meal","hosting"].includes(o.id)) setWineColor(null); }}
                    style={{ textAlign:"left", width:"100%", display:"flex", alignItems:"baseline", gap:8, padding:"15px 16px", borderRadius:14, cursor:"pointer",
                      background:occasion===o.id?C.burgundyBg:"#fff",
                      border:`1.5px solid ${occasion===o.id?C.burgundy:C.divider}`,
                      boxShadow:"0 1px 4px rgba(0,0,0,0.04)",
                      transition:"all 0.2s" }}>
                    <span style={{ fontSize:15, fontWeight:700, fontFamily:F.body, whiteSpace:"nowrap", color:occasion===o.id?C.burgundy:C.charcoal }}>{o.label}</span>
                    <span style={{ fontSize:13, fontFamily:F.body, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", color:occasion===o.id?C.burgundy:C.charcoalSoft }}>{o.hint}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Color picker for no-food occasions (non-restaurant, non-store, non-online, non-specific-bottle) */}
            {occasion && !(context==="home" && ["meal","hosting"].includes(occasion)) && context!=="store" && context!=="online" && homeFlowPath!=="specific" && homeFlowPath!=="few" && (
              <div style={{ marginBottom:24 }}>
                <div style={{ fontSize:14, fontWeight:700, color:C.charcoal, fontFamily:F.display, marginBottom:10 }}>What sounds good right now?</div>
                <div style={{ position: 'relative' }}>
                  <div style={{ display: 'flex', gap: 10, overflowX: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: 4, paddingRight: 40, scrollbarWidth: 'none' }}>
                    {[
                      { id:"red", label:"Red" },
                      { id:"white", label:"White" },
                      { id:"rosé", label:"Rosé" },
                      { id:"sparkling", label:"Sparkling" },
                    ].map(c=>(
                      <button key={c.id} onClick={()=>{ setWineColor(c.id); if (context === 'restaurant') setSelectedBudget(null); }} style={{
                        flexShrink: 0,
                        height: 42,
                        boxSizing: 'border-box',
                        padding: '0 13px',
                        borderRadius: 12,
                        cursor: 'pointer',
                        fontSize: 14,
                        background: wineColor===c.id ? C.burgundyBg : '#fff',
                        border: `1.5px solid ${wineColor===c.id ? C.burgundy : C.divider}`,
                        color: wineColor===c.id ? C.burgundy : C.charcoalMid,
                        fontFamily: F.body,
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                      }}>
                        <WineColorIcon colorId={c.id} />{c.label}
                      </button>
                    ))}
                  </div>
                  <div style={{
                    position: 'absolute', right: 0, top: 0, bottom: 4,
                    width: 48,
                    background: 'linear-gradient(to right, transparent, #FFFAF8)',
                    pointerEvents: 'none',
                  }} />
                </div>
              </div>
            )}

            {/* Food input — non-restaurant contexts (meal occasion, hosting) */}
            {context!=="restaurant" && ["meal","hosting"].includes(occasion) && (
              <div style={{ marginBottom:22 }}>
                <div style={{ fontSize:14, fontWeight:700, color:C.charcoal, fontFamily:F.display, marginBottom:8 }}>
                  {occasion==="hosting"?"What are you serving?":"What are you eating?"}
                </div>
                <textarea value={meal} onChange={e=>setMeal(e.target.value)}
                  placeholder={context==="store"?"e.g. Grilling salmon, or making pasta…":"e.g. Slow-roasted lamb with rosemary and garlic…"}
                  rows={2}
                  style={{ width:"100%", padding:"12px 14px", borderRadius:12, fontSize:16, resize:"none", background:"#fff", border:`1px solid ${C.divider}`, color:C.charcoal, fontFamily:F.body, outline:"none", boxSizing:"border-box", lineHeight:1.6 }} />
              </div>
            )}

            {/* Budget (hidden for home context) / Home vibe */}
            {context === "home" ? (
              <div style={{ marginBottom:24 }}>
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  {[
                    { label:"Casual night in",      hint:"Easy drinking, no fuss" },
                    { label:"Pairing with food",      hint:"Tell us what you're eating" },
                    { label:"Having people over",   hint:"Crowd-pleasing picks" },
                    { label:"Making it special",     hint:"Dig into the good stuff" },
                    { label:"Wine & snacks",         hint:"No meal, just grazing" },
                    { label:"Outside / patio",       hint:"Fresh, light, refreshing" },
                  ].map(v=>{
                    const val = `${v.label} — ${v.hint}`;
                    const sel = homeVibe === val;
                    return (
                      <button key={v.label} onClick={()=>{ setHomeVibe(val); if (!v.label.includes("Pairing")) setMeal(""); }}
                        style={{ textAlign:"left", width:"100%", display:"flex", alignItems:"baseline", gap:8, padding:"15px 16px", borderRadius:14, cursor:"pointer",
                          background:sel?C.burgundyBg:"#fff",
                          border:`1.5px solid ${sel?C.burgundy:C.divider}`,
                          boxShadow:"0 1px 4px rgba(0,0,0,0.04)",
                          transition:"all 0.2s" }}>
                        <span style={{ fontSize:15, fontWeight:700, fontFamily:F.body, whiteSpace:"nowrap", color:sel?C.burgundy:C.charcoal }}>{v.label}</span>
                        <span style={{ fontSize:13, fontFamily:F.body, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", color:sel?C.burgundy:C.charcoalSoft }}>{v.hint}</span>
                      </button>
                    );
                  })}
                </div>
              {homeVibe && homeVibe.includes("Pairing with food") && (
                <div style={{ marginTop:16, marginBottom:16, padding:"14px 16px", background:"#fff", border:`1px solid ${C.divider}`, borderRadius:14 }}>
                  <div style={{ fontSize:14, fontWeight:700, color:C.charcoal, fontFamily:F.display, marginBottom:8 }}>What are you eating?</div>
                  <textarea value={meal} onChange={e=>setMeal(e.target.value)}
                    placeholder="e.g. Grilled salmon, pasta with tomato sauce, steak…"
                    rows={2}
                    style={{ width:"100%", padding:"10px 12px", borderRadius:10, fontSize:16, resize:"none", background:C.cream, border:`1px solid ${C.divider}`, color:C.charcoal, fontFamily:F.body, outline:"none", boxSizing:"border-box", lineHeight:1.6 }} />
                </div>
              )}
              </div>
            ) : context !== "restaurant" ? (
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontFamily: 'Outfit', fontWeight: 700, fontSize: 15, color: '#1a1a1a', marginBottom: 12 }}>
                  Budget per bottle
                </div>
                <div style={{ position: 'relative' }}>
                  <div style={{ display: 'flex', gap: 10, overflowX: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: 4, paddingRight: 40, scrollbarWidth: 'none' }}>
                    {[
                      { label: 'Under $20', range: [0, 20] },
                      { label: '$20–$35', range: [20, 35] },
                      { label: '$35–$60', range: [35, 60] },
                      { label: '$60–$100', range: [60, 100] },
                      { label: 'No limit', range: [100, 9999] },
                    ].map((tier) => {
                      const isSelected = selectedBudget && selectedBudget[0] === tier.range[0] && selectedBudget[1] === tier.range[1];
                      return (
                        <button
                          key={tier.label}
                          onClick={() => selectBudget(tier.range)}
                          style={{
                            flexShrink: 0,
                            height: 48,
                            padding: '0 20px',
                            borderRadius: 12,
                            border: isSelected ? `1.5px solid ${C.burgundy}` : '2px solid #E0D5CC',
                            background: isSelected ? C.burgundyBg : '#fff',
                            color: isSelected ? C.burgundy : '#333',
                            fontSize: 15,
                            fontFamily: "var(--fb, 'DM Sans', sans-serif)",
                            fontWeight: 600,
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {tier.label}
                        </button>
                      );
                    })}
                  </div>
                  <div style={{
                    position: 'absolute', right: 0, top: 0, bottom: 4,
                    width: 48,
                    background: 'linear-gradient(to right, transparent, #FFFAF8)',
                    pointerEvents: 'none',
                  }} />
                </div>
              </div>
            ) : context === "restaurant" ? (() => {
              // Budget is MANDATORY and ALWAYS visible (Commit 3.2). Three honest
              // states (Commit 3.3), keyed on the color+format priced set:
              //   ≥2 distinct prices + usable spread → derived tiers (3.1/3.2);
              //   exactly 1 distinct price          → a single exact-price CONFIRMATION
              //     chip (range [0,9999] → filters nothing; absent-price rows still flow
              //     through the 3-slot fallback untouched);
              //   0 priced rows (or tight multi-price cluster we can't tier) → "No limit"
              //     only — the honest anchorless case.
              const derived = deriveRestaurantBudgetTiers(scannedRows, restaurantOrderType, wineColor);
              const budgetPrices = restaurantPricedPrices(scannedRows, restaurantOrderType, wineColor);
              const budgetDistinct = [...new Set(budgetPrices)];
              let tiers, budgetState, budgetBasis;
              if (derived) {
                tiers = derived; budgetState = 'derived-tiers';
                budgetBasis = `$${budgetPrices[0]}-$${budgetPrices[budgetPrices.length - 1]} (${budgetPrices.length} priced)`;
              } else if (budgetDistinct.length === 1) {
                const p = budgetDistinct[0];
                // Exact single price — a confirmation, NOT a constraint: range [0,9999]
                // passes every wine, so nothing is filtered and absent-price fallback is
                // untouched. Format-consistent with the card render ("/ glass" glass only).
                tiers = [{ label: restaurantOrderType === 'glass' ? `$${p} / glass` : `$${p}`, range: [0, 9999] }];
                budgetState = 'single-price'; budgetBasis = `$${p}`;
              } else if (budgetPrices.length > 0) {
                // Tight cluster (Commit 3.4): ≥2 distinct prices too close to tier. Show
                // the REAL min–max of the captured prices — actual menu numbers, never
                // synthesized/rounded-outward. Confirmation not constraint: range [0,9999]
                // so nothing is filtered and the 3-slot absent-price fallback is untouched.
                const lo = budgetPrices[0], hi = budgetPrices[budgetPrices.length - 1];
                tiers = [{ label: restaurantOrderType === 'glass' ? `$${lo}–$${hi} / glass` : `$${lo}–$${hi}`, range: [0, 9999] }];
                budgetState = 'range-chip'; budgetBasis = `$${lo}–$${hi}`;
              } else {
                // Genuinely anchorless: zero captured prices for this color+format.
                tiers = [{ label: 'No limit', range: [0, 9999] }];
                budgetState = 'no-limit-only'; budgetBasis = 'no priced rows';
              }
              const colorSub = !wineColor ? 'Based on this menu'
                : wineColor === 'red' ? 'Based on the reds on this menu'
                : wineColor === 'white' ? 'Based on the whites on this menu'
                : (wineColor === 'rosé' || wineColor === 'rose') ? 'Based on the rosés on this menu'
                : wineColor === 'sparkling' ? 'Based on the sparkling wines on this menu'
                : wineColor === 'orange' ? 'Based on the orange wines on this menu'
                : 'Based on this menu';
              try { console.log(`[RESTAURANT BUDGET TIERS] state=${budgetState} derivedFrom=${wineColor ? wineColor : 'all-eligible(pre-color)'} format=${restaurantOrderType} basis=${budgetBasis} tiers=${tiers.map(t => t.label + JSON.stringify(t.range)).join(' | ')}`); } catch {}
              return (
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontFamily: 'Outfit', fontWeight: 700, fontSize: 15, color: '#1a1a1a', marginBottom: 2 }}>
                  Budget per {restaurantOrderType === 'glass' ? 'glass' : 'bottle'}
                </div>
                <div style={{ fontSize: 12, fontWeight: 500, color: C.charcoalSoft, fontFamily: F.body, marginBottom: 12 }}>{colorSub}</div>
                <div style={{ position: 'relative' }}>
                  <div style={{ display: 'flex', gap: 10, overflowX: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: 4, paddingRight: 40, scrollbarWidth: 'none' }}>
                    {tiers.map((tier) => {
                      const isSelected = selectedBudget && selectedBudget[0] === tier.range[0] && selectedBudget[1] === tier.range[1];
                      return (
                        <button key={tier.label} onClick={() => selectBudget(tier.range)} style={{ flexShrink: 0, height: 48, padding: '0 20px', borderRadius: 12, border: isSelected ? `1.5px solid ${C.burgundy}` : '2px solid #E0D5CC', background: isSelected ? C.burgundyBg : '#fff', color: isSelected ? C.burgundy : '#333', fontSize: 15, fontFamily: "var(--fb, 'DM Sans', sans-serif)", fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                          {tier.label}
                        </button>
                      );
                    })}
                  </div>
                  <div style={{ position: 'absolute', right: 0, top: 0, bottom: 4, width: 48, background: 'linear-gradient(to right, transparent, #FFFAF8)', pointerEvents: 'none' }} />
                </div>
              </div>
              );
            })() : null}

            {/* Color picker — online flow, positioned after budget */}
            {context === "online" && occasion && (
              <div style={{ marginBottom:24 }}>
                <div style={{ fontSize:14, fontWeight:700, color:C.charcoal, fontFamily:F.display, marginBottom:10 }}>What sounds good right now?</div>
                <div style={{ position: 'relative' }}>
                  <div style={{ display: 'flex', gap: 10, overflowX: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: 4, paddingRight: 40, scrollbarWidth: 'none' }}>
                    {[
                      { id:"red", label:"Red" },
                      { id:"white", label:"White" },
                      { id:"rosé", label:"Rosé" },
                      { id:"sparkling", label:"Sparkling" },
                    ].map(c => (
                      <button key={c.id} onClick={()=>setWineColor(c.id)} style={{
                        flexShrink: 0,
                        height: 42,
                        boxSizing: 'border-box',
                        padding: '0 13px',
                        borderRadius: 12,
                        cursor: 'pointer',
                        fontSize: 14,
                        background: wineColor===c.id ? C.burgundyBg : '#fff',
                        border: `1.5px solid ${wineColor===c.id ? C.burgundy : C.divider}`,
                        color: wineColor===c.id ? C.burgundy : C.charcoalMid,
                        fontFamily: F.body,
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                      }}>
                        <WineColorIcon colorId={c.id} />{c.label}
                      </button>
                    ))}
                  </div>
                  <div style={{
                    position: 'absolute', right: 0, top: 0, bottom: 4,
                    width: 48,
                    background: 'linear-gradient(to right, transparent, #FFFAF8)',
                    pointerEvents: 'none',
                  }} />
                </div>
              </div>
            )}

            {error && (() => { const copy = resolveErrorCopy(error); return (
              <div style={{ padding:"11px 14px", background:C.errorBg, border:`1px solid ${C.errorBorder}`, borderRadius:10, fontSize:13, color:C.error, fontFamily:F.body, marginBottom:14 }}>
                <div style={{ fontWeight:700, marginBottom:2 }}>{copy.heading}</div>
                <div>{copy.body}</div>
              </div>
            ); })()}


            {(() => {
              const needColor = occasion && !(context==="home" && ["meal","hosting"].includes(occasion)) && homeFlowPath!=="specific" && homeFlowPath!=="few";
              const needsMeal = homeVibe && homeVibe.includes("Pairing with food") && !meal.trim();
              // Budget is REQUIRED for restaurant (Commit 3.2) — "No limit" is a valid
              // pick, so the requirement is always satisfiable (even on degenerate menus).
              const needsBudget = context === "restaurant" && !selectedBudget;
              const disabled = (context==="home" ? !homeVibe : !occasion) || loading || needsMeal || needsBudget;
              return (
                <>
                  {needsBudget && !loading && (
                    <div style={{ textAlign:"center", fontSize:13, fontWeight:600, color:C.charcoalSoft, fontFamily:F.body, marginBottom:10 }}>Pick a budget to continue</div>
                  )}
                  <button onClick={()=>{
                    if (context==="home" && homeFlowPath==="specific") { navigateTo("homebottlematch"); return; }
                    if (context==="home" && homeFlowPath==="notsure") { navigateTo("homestyleguide"); return; }
                    handleGetPairings();
                  }} disabled={disabled} style={{ ...S.cta, opacity:disabled?0.45:1, cursor:disabled?"default":"pointer" }}>
                    {loading?"Finding your perfect wine…":
                      (context==="home"&&homeFlowPath==="specific")?"Evaluate this bottle →":
                      (context==="home"&&homeFlowPath==="notsure")?"Show me what to look for →":
                      context==="online"?"Find bottles to order →":
                      "Find my perfect wine →"}
                  </button>
                  {context === "online" && (
                    <div style={{ textAlign:"center", fontSize:13, color:"#aaa", fontFamily:F.body, marginTop:14, marginBottom:10 }}>
                      Delivered to your door — ships in 1-3 days
                    </div>
                  )}
                </>
              );
            })()}

          </div>
        )}

        {/* ── HOME PATH 1: BOTTLE MATCH RESULT ── */}
        {screen==="homebottlematch" && (
          <div style={{ paddingTop:36 }}>
            <button style={S.back} onClick={navigateBack}>← Back</button>
            {bottleMatchLoading && <BottleMatchLoader />}
            {bottleMatchError && (() => { const copy = resolveErrorCopy(bottleMatchError); return (
              <div style={{ padding:"11px 14px", background:C.errorBg, border:`1px solid ${C.errorBorder}`, borderRadius:10, fontSize:13, color:C.error, fontFamily:F.body, marginBottom:14 }}>
                <div style={{ fontWeight:700, marginBottom:2 }}>{copy.heading}</div>
                <div>{copy.body}</div>
              </div>
            ); })()}
            {bottleMatch && !bottleMatchLoading && (() => {
              const bm = bottleMatch;
              return (
                <div>
                  {(() => {
                    const v = getVerdict(bm.confidence);
                    return (
                      <div style={{
                        background: v.bg,
                        border: `1.5px solid ${v.border}`,
                        borderRadius: 24,
                        padding: '28px 24px',
                        textAlign: 'center',
                        marginBottom: 20,
                        position: 'relative',
                        overflow: 'hidden',
                      }}>
                        <div style={{
                          position: 'absolute', top: -30, right: -30, fontSize: 140,
                          opacity: 0.04, color: v.color, fontFamily: 'serif', lineHeight: 1,
                        }}>{v.symbol}</div>

                        <div style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          background: '#fff', borderRadius: 20, padding: '5px 14px',
                          marginBottom: 14, border: `1px solid ${v.border}`,
                        }}>
                          <span style={{ fontSize: 13, color: v.color, fontWeight: 700 }}>{v.symbol}</span>
                          <span style={{ fontSize: 11, color: v.color, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', fontFamily: F.body }}>{v.badge}</span>
                        </div>

                        <div style={{
                          fontSize: 34, fontWeight: 800, color: C.charcoal,
                          fontFamily: F.display, lineHeight: 1.1, marginBottom: 6,
                        }}>
                          {v.headline}
                        </div>

                        <div style={{ fontSize: 14, color: C.charcoalSoft, fontFamily: F.body, marginBottom: 16 }}>
                          {v.subline}
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                          <div style={{
                            flex: '0 0 auto', width: 100, height: 6, background: 'rgba(0,0,0,0.06)',
                            borderRadius: 6, position: 'relative', overflow: 'hidden',
                          }}>
                            <div style={{
                              position: 'absolute', left: 0, top: 0, height: '100%',
                              width: `${bm.confidence}%`, background: v.color, borderRadius: 6,
                            }} />
                          </div>
                          <span style={{ fontSize: 13, fontWeight: 700, color: v.color, fontFamily: F.body }}>{bm.confidence}%</span>
                        </div>
                      </div>
                    );
                  })()}

                  <div style={{ textAlign: 'center', marginBottom: 24 }}>
                    <div style={{ fontSize: 20, fontWeight: 700, color: C.charcoal, fontFamily: F.display, marginBottom: 2 }}>
                      {bm.producer}
                    </div>
                    <div style={{ fontSize: 14, color: C.charcoalSoft, fontFamily: F.body, marginBottom: 12 }}>
                      {bm.wine}{bm.vintage && bm.vintage.toLowerCase() !== "unknown" ? ` ${bm.vintage}` : ""} · {bm.region}
                    </div>
                    {/* claude/wine-quality-signal-unification (goal 3): this screen never
                        read ws_score/ws_price_average before — the server has always sent
                        both (now reliably, since evaluate-bottle's WS lookup was fixed to
                        pass real producer/wine), same fields and same >=90 gate the shared
                        results card uses, just never wired up here. */}
                    {bm.ws_score >= 90 && (
                      <div style={{ fontSize: 11, fontWeight: 700, color: C.burgundy, letterSpacing: 1.2, textTransform: 'uppercase', fontFamily: F.body, marginBottom: 12 }}>Critically Acclaimed</div>
                    )}
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap' }}>
                      {(bm.styleTags||[]).map((t,i) => (
                        <span key={i} style={{ fontSize: 12, color: C.charcoalMid, background: '#F5F0EE', border: `1px solid ${C.divider}`, borderRadius: 20, padding: '3px 10px', fontFamily: F.body }}>{t}</span>
                      ))}
                    </div>
                  </div>

                  {bm.ws_price_average && (
                    <div style={{ textAlign: 'center', marginBottom: 16 }}>
                      <span style={{ fontSize: 13, color: '#888', fontFamily: F.body }}>Avg. </span>
                      <span style={{ fontSize: 15, fontWeight: 700, color: C.charcoal, fontFamily: F.display }}>${Math.round(bm.ws_price_average)}</span>
                    </div>
                  )}
                  <div style={{ background:"#fff", border:`1px solid ${C.divider}`, borderRadius:16, padding:"16px 18px", marginBottom:12 }}>
                    <div style={{ fontSize:10, color:getConfidenceTier(bm.confidence).color, letterSpacing:2, textTransform:"uppercase", fontFamily:F.body, fontWeight:600, marginBottom:8 }}>Why It Works</div>
                    <div style={{ fontSize:14, color:C.charcoalSoft, fontFamily:F.body, lineHeight:1.6 }}>{bm.whyItWorks}</div>
                  </div>
                  {(bm.foodPairings||[]).length > 0 && (
                    <div style={{ background:"#fff", border:`1px solid ${C.divider}`, borderRadius:16, padding:"16px 18px", marginBottom:12 }}>
                      <div style={{ fontSize:10, color:C.burgundy, letterSpacing:2, textTransform:"uppercase", fontFamily:F.body, fontWeight:600, marginBottom:8 }}>Pairs Well With</div>
                      <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
                        {(bm.foodPairings||[]).map((f,i)=>(
                          <span key={i} style={{ padding:"5px 10px", borderRadius:20, background:"#f9f5f0", border:`1px solid ${C.divider}`, fontSize:13, color:C.charcoalMid, fontFamily:F.body }}>🍽️ {f}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {bm.servingTip && (
                    <div style={{ background:"#fff", border:`1px solid ${C.divider}`, borderRadius:16, padding:"16px 18px", marginBottom:12 }}>
                      <div style={{ fontSize:10, color:C.burgundy, letterSpacing:2, textTransform:"uppercase", fontFamily:F.body, fontWeight:600, marginBottom:8 }}>Serving Tip</div>
                      <div style={{ fontSize:14, color:C.charcoalSoft, fontFamily:F.body, lineHeight:1.6 }}>🌡️ {bm.servingTip}</div>
                    </div>
                  )}
                  {bm.ws_price_average && (
                    <div style={{ marginBottom:20 }}>
                      <span style={{ fontSize:11, color:C.charcoalSoft, fontFamily:F.body }}>Data supplied by Wine-Searcher</span>
                    </div>
                  )}
                  <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                    {scanModalMode ? (
                      <>
                        {pendingBottleMatchWine ? (
                          <div style={{ padding: '14px', background: '#FFFAF8', borderRadius: 12, border: `1.5px solid ${C.burgundy}` }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: C.charcoal, fontFamily: F.body, marginBottom: 2 }}>{pendingBottleMatchWine}</div>
                            <div style={{ fontSize: 12, color: C.charcoalSoft, fontFamily: F.body, marginBottom: 10 }}>Tap or swipe · half stars supported</div>
                            <div style={{ marginBottom: pendingBottleMatchStars > 0 ? 10 : 12 }}>
                              <StarRating value={pendingBottleMatchStars} onChange={(stars) => {
                                setPendingBottleMatchStars(stars);
                              }} hintVisible={showRatingHint} onHintDismiss={() => setShowRatingHint(false)} />
                            </div>
                            {pendingBottleMatchStars > 0 && (
                              <button onClick={() => {
                                const wine = pendingBottleMatchWine;
                                const stars = pendingBottleMatchStars;
                                // Step 7 fix (this round, item 1): flag off must be TODAY's
                                // (5b2fe67/9370ddb) behavior exactly, since the client ships
                                // before the server flag flips — same synchronous toast call
                                // BEFORE persist, same plain fire-and-forget fetch, no new
                                // state updates. Flag on: the response-driven behavior.
                                if (!step7HasServerFields) {
                                  maybeShowRatingToast(wine, stars); // Layer A (no server nudge here → new-wine-or-nothing)
                                  persist({ ...userProfile, likedWines: [...(userProfile.likedWines || []), wine], cellarRatings: { ...(userProfile.cellarRatings || {}), [wine]: stars } });
                                  const existingMeta = JSON.parse(localStorage.getItem('truepour_cellar_meta') || '{}');
                                  existingMeta[wine] = { addedAt: Date.now() };
                                  localStorage.setItem('truepour_cellar_meta', JSON.stringify(existingMeta));
                                  if (session) {
                                    fetch('/api/cellar', { method:'POST', headers:{'Content-Type':'application/json','Authorization':`Bearer ${session.access_token}`}, body:JSON.stringify({ wine_name: wine }) }).catch(()=>{});
                                    fetch('/api/rate-wine', { method:'POST', headers:{'Content-Type':'application/json','Authorization':`Bearer ${session.access_token}`}, body:JSON.stringify({ producer:'', wine_name: wine, rating: stars }) }).catch(()=>{});
                                  }
                                } else {
                                persist({ ...userProfile, likedWines: [...(userProfile.likedWines || []), wine], cellarRatings: { ...(userProfile.cellarRatings || {}), [wine]: stars } });
                                const existingMeta = JSON.parse(localStorage.getItem('truepour_cellar_meta') || '{}');
                                existingMeta[wine] = { addedAt: Date.now() };
                                localStorage.setItem('truepour_cellar_meta', JSON.stringify(existingMeta));
                                if (session) {
                                  fetch('/api/cellar', { method:'POST', headers:{'Content-Type':'application/json','Authorization':`Bearer ${session.access_token}`}, body:JSON.stringify({ wine_name: wine }) }).catch(()=>{});
                                  // Step 7: read the response (still fire-and-forget — the UI is
                                  // never blocked on it, same as today) so the toast can show the
                                  // pending/terminal copy or respect ratingMovedProfile when the
                                  // flag is on; on any network/parse failure, fall back to exactly
                                  // today's no-data toast call.
                                  fetch('/api/rate-wine', { method:'POST', headers:{'Content-Type':'application/json','Authorization':`Bearer ${session.access_token}`}, body:JSON.stringify({ producer:'', wine_name: wine, rating: stars }) })
                                    .then(r => r.json())
                                    .then(rateData => {
                                      if (rateData.sliders) setSliders(prev => ({ ...prev, ...rateData.sliders }));
                                      if (rateData.extSliders) setExtendedSliders(prev => ({ ...prev, ...rateData.extSliders }));
                                      maybeShowRatingToast(wine, stars, rateData.sliders ? { ...sliders, ...rateData.sliders } : sliders, rateData.extSliders ? { ...extendedSliders, ...rateData.extSliders } : extendedSliders, rateData);
                                    })
                                    .catch(() => { maybeShowRatingToast(wine, stars); });
                                } else {
                                  maybeShowRatingToast(wine, stars); // no session — never reaches the server, same as today
                                }
                                }
                                // UPDATE the existing bottle_eval row (never insert): the
                                // rated bottle is this generation's single wine.
                                if (bottleMatch && currentGenerationIdRef.current) {
                                  patchGeneration(currentGenerationIdRef.current, [{ ...mapBottleWine(bottleMatch), tried: true, rating: stars, tags: [] }], true);
                                }
                                setPendingBottleMatchWine(null);
                                setPendingBottleMatchStars(0);
                                setShowToast("Added to your cellar 🍷");
                                setTimeout(() => setShowToast(null), 2500);
                              }} style={{ width: '100%', padding: '10px', borderRadius: 10, background: 'transparent', border: `1.5px solid ${C.burgundy}`, color: C.burgundy, fontSize: 14, fontWeight: 700, fontFamily: F.display, cursor: 'pointer', marginBottom: 8 }}>
                                Save →
                              </button>
                            )}
                            <button onClick={() => {
                              const wine = pendingBottleMatchWine;
                              persist({ ...userProfile, likedWines: [...(userProfile.likedWines || []), wine] });
                              const existingMeta = JSON.parse(localStorage.getItem('truepour_cellar_meta') || '{}');
                              existingMeta[wine] = { addedAt: Date.now() };
                              localStorage.setItem('truepour_cellar_meta', JSON.stringify(existingMeta));
                              if (session) {
                                fetch('/api/cellar', { method:'POST', headers:{'Content-Type':'application/json','Authorization':`Bearer ${session.access_token}`}, body:JSON.stringify({ wine_name: wine }) }).catch(()=>{});
                              }
                              setPendingBottleMatchWine(null);
                              setPendingBottleMatchStars(0);
                              setShowToast("Added to your cellar 🍷");
                              setTimeout(() => setShowToast(null), 2500);
                            }} style={{ width: '100%', padding: '9px', borderRadius: 10, background: 'transparent', border: `1px solid ${C.divider}`, color: C.charcoalSoft, fontSize: 13, fontFamily: F.body, cursor: 'pointer', textAlign: 'center' }}>
                              Haven't tried it yet →
                            </button>
                          </div>
                        ) : (
                          <button onClick={() => {
                            if (bottleMatch && bottleMatch.producer && bottleMatch.wine) {
                              const cellarEntry = `${bottleMatch.producer} ${bottleMatch.wine}`;
                              if (!(userProfile.likedWines || []).includes(cellarEntry)) {
                                setPendingBottleMatchWine(cellarEntry);
                                setPendingBottleMatchStars(0);
                              }
                            }
                          }} style={{ ...S.cta }}>Save to my cellar →</button>
                        )}
                        <button onClick={()=>{ const dest = encodeURIComponent(`https://www.wine.com/search/${(bottleMatch.search_term||((bottleMatch.producer||'')+' '+(bottleMatch.wine||''))).trim()}/0`); window.location.href = `https://click.linksynergy.com/deeplink?id=WUGIyhMYkU4&mid=2025&murl=${dest}`; }} style={{ width:"100%", padding:"16px", borderRadius:16, background:"transparent", border:`1.5px solid ${C.burgundy}`, color:C.burgundy, fontSize:16, fontWeight:600, fontFamily:"var(--fd)", cursor:"pointer" }}>
                          Find this on Wine.com →
                        </button>
                        <button onClick={()=>{ setSpecificBottle(""); setScanModalMode(true); navigateTo("winescan"); }}
                          style={{ width:"100%", padding:"14px", borderRadius:14, background:"transparent", border:`2px solid ${C.burgundy}`, color:C.burgundy, fontSize:15, fontWeight:700, fontFamily:F.display, cursor:"pointer", boxSizing:"border-box" }}>
                          Scan another →
                        </button>
                      </>
                    ) : (
                      <>
                        <button onClick={()=>{
                          if (bottleMatch && bottleMatch.producer && bottleMatch.wine) {
                            const cellarEntry = `${bottleMatch.producer} ${bottleMatch.wine}`;
                            setUserProfile(prev => ({
                              ...prev,
                              likedWines: [...(prev.likedWines || []).filter(w => w !== cellarEntry), cellarEntry]
                            }));
                            try {
                              const profile = { ...userProfile, likedWines: [...(userProfile.likedWines || []).filter(w => w !== cellarEntry), cellarEntry] };
                              saveData({ ...loadData(), userProfile: profile });
                              if (session) { fetch('/api/cellar', { method:'POST', headers:{'Content-Type':'application/json','Authorization':`Bearer ${session.access_token}`}, body:JSON.stringify({ wine_name: cellarEntry }) }).catch(()=>{}); }
                            } catch {}
                          }
                          setShowToast("Added to your cellar 🍷");
                          setTimeout(() => {
                            setShowToast(null);
                            setHomeFlowPath(null);
                            setSpecificBottle("");
                            setScreen("home");
                            scrollToTop();
                          }, 2500);
                        }} style={{ ...S.cta }}>Open this bottle tonight →</button>
                        <button onClick={()=>{ const dest = encodeURIComponent(`https://www.wine.com/search/${(bottleMatch.search_term||((bottleMatch.producer||'')+' '+(bottleMatch.wine||''))).trim()}/0`); window.location.href = `https://click.linksynergy.com/deeplink?id=WUGIyhMYkU4&mid=2025&murl=${dest}`; }} style={{ width:"100%", padding:"16px", borderRadius:16, background:"transparent", border:`1.5px solid ${C.burgundy}`, color:C.burgundy, fontSize:16, fontWeight:600, fontFamily:"var(--fd)", cursor:"pointer" }}>
                          Find this on Wine.com →
                        </button>
                        <button onClick={()=>{ resetScanState(); setHomeFlowPath("few"); setSpecificBottle(""); navigateTo("homefew"); }}
                          style={{ width:"100%", padding:"14px", borderRadius:14, background:"transparent", border:`2px solid ${C.burgundy}`, color:C.burgundy, fontSize:15, fontWeight:700, fontFamily:F.display, cursor:"pointer", boxSizing:"border-box" }}>
                          See my other options →
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* ── HOME PATH 3: STYLE GUIDE ── */}
        {screen==="homestyleguide" && (
          <div style={{ paddingTop:36 }}>
            <button style={S.back} onClick={()=>{ setSelectedStyleCard(null); navigateBack(); }}>← Back</button>
            {styleGuideLoading && <FullScreenLoader homeFlowPath="notsure" />}
            {styleGuideError && (() => { const copy = resolveErrorCopy(styleGuideError); return (
              <div style={{ padding:"11px 14px", background:C.errorBg, border:`1px solid ${C.errorBorder}`, borderRadius:10, fontSize:13, color:C.error, fontFamily:F.body, marginBottom:14 }}>
                <div style={{ fontWeight:700, marginBottom:2 }}>{copy.heading}</div>
                <div>{copy.body}</div>
              </div>
            ); })()}
            {styleGuide && !styleGuideLoading && (
              <div>
                <div style={{ fontSize:10, color:C.burgundy, letterSpacing:2, textTransform:"uppercase", fontFamily:F.body, fontWeight:600, marginBottom:8 }}>YOUR STYLE GUIDE TONIGHT</div>
                <div style={S.h}>Here's what to look for</div>
                <div style={{ fontSize:15, color:C.charcoalSoft, fontFamily:F.body, lineHeight:1.6, marginBottom:24 }}>{styleGuide.intro}</div>
                {(styleGuide.styles||[]).map((s,i)=>{
                  const avail = s.availability==="grocery"
                    ? { label:"Available at most grocery stores", hint:"Tap 'Find it tonight' to locate a store near you", bg:C.greenBg, color:C.green, dot:"🟢" }
                    : s.availability==="wine_shop"
                    ? { label:"Wine shop run", hint:null, bg:C.amberBg, color:C.amber, dot:"🟡" }
                    : { label:"Specialty find", hint:null, bg:C.errorBg, color:C.error, dot:"🔴" };
                  const isSelected = selectedStyleCard?.styleName === s.styleName;
                  return (
                    <div key={i} onClick={()=>setSelectedStyleCard(s)} style={{ background: isSelected ? C.burgundyBg : "#fff", border: isSelected ? `1.5px solid ${C.burgundy}` : "1.5px solid #ecddd7", borderRadius:16, padding:"18px 20px", marginBottom:12, cursor:"pointer" }}>
                      <div style={{ fontSize:16, fontWeight:700, color:C.burgundy, fontFamily:F.display, marginBottom:4 }}>{s.styleName}</div>
                      <div style={{ fontSize:14, color:C.charcoalSoft, fontFamily:F.body, marginBottom:6 }}>{s.region} · {s.varietal}</div>
                      <div style={{ fontSize:14, color:C.charcoalMid, fontFamily:F.body, lineHeight:1.6, marginBottom:10 }}>{s.description}</div>
                      <span style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"4px 10px", borderRadius:20, background:avail.bg, color:avail.color, fontSize:13, fontFamily:F.body, fontWeight:600 }}><span style={{ width:7, height:7, borderRadius:"50%", background:avail.color, flexShrink:0 }} />{avail.label}</span>
                      {avail.hint && <div style={{ fontSize:13, color:"#aaa", fontFamily:F.body, marginTop:4 }}>{avail.hint}</div>}
                    </div>
                  );
                })}
                {!selectedStyleCard && (
                  <div style={{ fontSize:13, color:"#aaa", fontFamily:F.body, textAlign:"center", marginBottom:8 }}>Tap a style to select it</div>
                )}
                <div style={{ display:"flex", flexDirection:"column", gap:10, marginTop:8 }}>
                  <button onClick={()=>{ if (!selectedStyleCard) return; resetScanState(); setStyleGuideSelection(selectedStyleCard); setHomeFlowPath("few"); setSelectedStyleCard(null); navigateTo("homefew"); }}
                    style={{ ...S.cta, ...(selectedStyleCard ? {} : { background:"#f0e8e4", color:"#bbb", cursor:"default", boxShadow:"none" }) }}>
                    I have one of these — help me choose →
                  </button>
                  <button onClick={()=>{
                    if (!selectedStyleCard) return;
                    const availability = selectedStyleCard.availability || selectedStyleCard.availabilityTier || '';
                    const isGrocery = availability === 'grocery' || availability === 'grocery_store';
                    const mapsQuery = isGrocery ? 'grocery+store+near+me' : 'wine+shop+near+me';
                    const mapsUrl = 'https://maps.apple.com/?q=' + mapsQuery;
                    window.location.href = mapsUrl;
                  }}
                    style={{ width:"100%", padding:"14px", borderRadius:14, background:"transparent", border:`1.5px solid ${selectedStyleCard ? C.burgundy : "#e0d0cc"}`, color: selectedStyleCard ? C.burgundy : "#bbb", fontSize:15, fontWeight:700, fontFamily:F.display, cursor: selectedStyleCard ? "pointer" : "default", boxSizing:"border-box" }}>
                    🗺️ Find it tonight
                  </button>
                  <div style={{ fontSize:13, color:C.charcoalSoft, fontFamily:F.body, textAlign:"center", marginTop:-4 }}>Opens Maps — finds wine shops near you</div>
                  <button onClick={()=>{ resetScanState(); setContext("online"); setSelectedStyleCard(null); navigateTo("meal"); }}
                    style={{ background:"none", border:"none", color:C.burgundy, fontSize:14, fontFamily:F.body, cursor:"pointer", padding:"8px 0", textAlign:"center" }}>
                    Order for later →
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── ACTIVITY SCREEN ── */}
        {screen === 'activity' && (() => {
  const history = [...(userProfile.history || [])].reverse();
  const contextLabel = (ctx) => {
    const iconWrap = (Icon, label) => (
      <span style={{ display:'flex', alignItems:'center', gap:6 }}>
        <span style={{ width:18, height:18, display:'inline-flex', alignItems:'center', justifyContent:'center', overflow:'hidden', flexShrink:0 }}><Icon /></span>
        {label}
      </span>
    );
    if (ctx === 'restaurant') return iconWrap(RestaurantIcon, 'Restaurant');
    if (ctx === 'store') return iconWrap(StoreIcon, 'Wine store');
    if (ctx === 'home') return iconWrap(HomeIcon, 'Home');
    if (ctx === 'online') return iconWrap(OnlineIcon, 'Online order');
    if (ctx === 'bottle_eval') return iconWrap(HomeIcon, 'Bottle evaluation');
    return 'Recommendation';
  };
  const firstUnratedKey = (() => {
    for (let si = 0; si < history.length; si++) {
      for (let wi = 0; wi < (history[si].wines || []).length; wi++) {
        const key = `hist_${si}_${wi}`;
        const r = ratings[key] || (history[si].wines[wi].rating) || 0;
        if (r === 0) return key;
      }
    }
    return null;
  })();
  return (
    <div style={{ paddingTop: 24, paddingBottom: 80 }}>
      <div style={{ fontSize: 24, fontWeight: 800, color: C.charcoal, fontFamily: F.display, marginBottom: 4 }}>Activity</div>
      <div style={{ fontSize: 14, color: C.charcoalSoft, fontFamily: F.body, marginBottom: 20, lineHeight: 1.5 }}>
        Every wine TruePour has recommended to you. Rate or save any bottle to sharpen future picks.
      </div>
      <div style={{ position:'relative', marginBottom:16 }}>
        <input
          type="text"
          placeholder="Search wines..."
          value={activitySearch}
          onChange={e => setActivitySearch(e.target.value)}
          style={{ width:'100%', boxSizing:'border-box', padding:'10px 36px 10px 14px', borderRadius:10, border:`1px solid ${C.divider}`, fontSize:14, fontFamily:F.body, color:C.charcoal, background:'#FFFFFF', outline:'none', WebkitAppearance:'none' }}
        />
        {activitySearch.length > 0 && (
          <div onClick={() => setActivitySearch('')} style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', fontSize:16, color:C.charcoalSoft, cursor:'pointer', padding:4 }}>✕</div>
        )}
      </div>
      {activitySearch.trim().length > 0 ? (() => {
        const q = activitySearch.toLowerCase();
        const matches = [];
        history.forEach((session, i) => {
          (session.wines || []).forEach((wine, wi) => {
            const label = `${wine.producer} ${wine.wine}`.toLowerCase();
            if (label.includes(q)) matches.push({ session, i, wine, wi });
          });
        });
        if (matches.length === 0) return (
          <div style={{ textAlign:'center', padding:'40px 20px', color:C.charcoalSoft, fontFamily:F.body, fontSize:14 }}>No wines found</div>
        );
        return (
          <div style={{ borderRadius:16, overflow:'hidden', border:`1px solid ${C.divider}` }}>
            {matches.map(({ session: histEntry, i, wine, wi }, idx) => {
              const wineKey = `hist_${i}_${wi}`;
              const currentRating = ratings[wineKey] !== undefined ? ratings[wineKey] : (wine.rating || 0);
              const wineLabel = `${wine.producer} ${wine.wine}`;
              const isLast = idx === matches.length - 1;
              return (
                <div key={wineKey} onClick={() => setWineModal({ type:'activity', wine })} style={{ padding:'12px 16px', cursor:'pointer', borderBottom: isLast ? 'none' : `1px solid #F0EAE6`, background:'#FFFFFF' }}>
                  <div style={{ fontSize:15, fontWeight:700, color:C.charcoal, fontFamily:F.display, lineHeight:1.2 }}>{wine.producer}</div>
                  <div style={{ fontSize:13, color:C.charcoalMid, fontFamily:F.body, marginBottom:6 }}>{wine.wine}{wine.vintage && wine.vintage !== 'NV' ? ` ${wine.vintage}` : ''}</div>
                  <div onClick={e => e.stopPropagation()}>
                    <StarRating value={currentRating} onChange={async (stars) => {
                      setRatings(r => ({ ...r, [wineKey]: stars }));
                      const newCellarRatings = { ...(userProfile.cellarRatings || {}), [wineLabel]: stars };
                      const isFirstRating = !(userProfile.cellarRatings || {})[wineLabel];
                      const alreadySaved = (userProfile.likedWines || []).includes(wineLabel);
                      if (isFirstRating) {
                        const newLikedWines = alreadySaved ? (userProfile.likedWines || []) : [...(userProfile.likedWines || []), wineLabel];
                        persist({ ...userProfile, likedWines: newLikedWines, cellarRatings: newCellarRatings });
                        if (!alreadySaved) {
                          setSavedWines(s => ({ ...s, [wineKey]: true }));
                          const _m = JSON.parse(localStorage.getItem('truepour_cellar_meta') || '{}');
                          _m[wineLabel] = { addedAt: Date.now() };
                          localStorage.setItem('truepour_cellar_meta', JSON.stringify(_m));
                          setShowToast('Added to your cellar');
                          setTimeout(() => setShowToast(null), 2500);
                        }
                      } else {
                        persist({ ...userProfile, cellarRatings: newCellarRatings });
                      }
                      if (session) { fetch('/api/cellar', { method:'POST', headers:{'Content-Type':'application/json','Authorization':`Bearer ${session.access_token}`}, body:JSON.stringify({ wine_name: wineLabel }) }).catch(()=>{}); }
                      try {
                        if (session) {
                          const rateRes = await fetch('/api/rate-wine', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
                            body: JSON.stringify({ producer: '', wine_name: wineLabel, rating: stars })
                          });
                          const rateData = await rateRes.json();
                          if (rateData.sliders) {
                            setSliders(prev => ({ ...prev, ...rateData.sliders }));
                            setPalateUpdateSeen(false);
                            try { localStorage.removeItem('truepour_palate_update_seen'); } catch {}
                          }
                          if (rateData.extSliders) {
                            setExtendedSliders(prev => ({ ...prev, ...rateData.extSliders }));
                          }
                          if (rateData.sliders || rateData.extSliders) { setShowToast(null); }
                          maybeShowRatingToast(
                            wineLabel, stars,
                            rateData.sliders ? { ...sliders, ...rateData.sliders } : sliders,
                            rateData.extSliders ? { ...extendedSliders, ...rateData.extSliders } : extendedSliders,
                            rateData
                          );
                        }
                      } catch (err) {
                        // fail silently
                      }
                    }} hintVisible={showRatingHint} onHintDismiss={() => setShowRatingHint(false)} />
                    {currentRating > 0 && (
                      <div onClick={async e => {
                        e.stopPropagation();
                        setRatings(r => ({ ...r, [wineKey]: 0 }));
                        persist({ ...userProfile, cellarRatings: { ...(userProfile.cellarRatings || {}), [wineLabel]: 0 } });
                        // claude/every-rating-real-signal: this used to be
                        // local-only — the server never learned the rating
                        // was cleared, so whatever it had already nudged
                        // stayed nudged forever. Now a real retraction:
                        // /api/rate-wine's rating=0 path recomputes sliders
                        // from scratch over the surviving ratings.
                        if (session) {
                          try {
                            const clearRes = await fetch('/api/rate-wine', {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
                              body: JSON.stringify({ producer: '', wine_name: wineLabel, rating: 0 })
                            });
                            const clearData = await clearRes.json();
                            if (clearData.sliders) { setSliders(prev => ({ ...prev, ...clearData.sliders })); }
                            if (clearData.extSliders) { setExtendedSliders(prev => ({ ...prev, ...clearData.extSliders })); }
                          } catch (err) { /* fail silently — local state already cleared */ }
                        }
                      }} style={{ fontSize:11, color:C.charcoalSoft, fontFamily:F.body, marginTop:4, cursor:'pointer' }}>Clear rating</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })() : null}
      {activitySearch.trim().length === 0 && (
        history.length === 0 && !lastResults ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: C.charcoal, fontFamily: F.display, marginBottom: 8 }}>No activity yet</div>
            <div style={{ fontSize: 14, color: C.charcoalSoft, fontFamily: F.body, lineHeight: 1.6 }}>Your recommendation history will appear here. Tap Discover to get your first recommendation.</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ padding: '10px 14px 10px 17px', background: '#FFFFFF', borderRadius: 12, borderLeft: `3px solid ${C.burgundy}`, border: `1px solid ${C.divider}`, borderLeftWidth: 3, borderLeftColor: C.burgundy, marginBottom: 4 }}>
              <div style={{ fontSize: 13, color: C.charcoal, fontFamily: F.body, lineHeight: 1.5 }}>
                Rating wines you've tried improves every future recommendation — and saves them to My Cellar.
              </div>
            </div>
            {history.map((histItem, i) => (
              <div key={i} style={{ borderRadius: 16, overflow: 'hidden', marginBottom: 8, border: `1px solid ${C.divider}` }}>
                <div style={{ padding: '12px 16px 10px', background: '#FAF4F0', borderBottom: '1px solid #E8DDD8', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.charcoal, fontFamily: F.display }}>
                      {contextLabel(histItem.context)}
                    </div>
                    {histItem.meal && histItem.meal !== histItem.occasion && (
                      <div style={{ fontSize: 13, color: C.charcoalSoft, fontFamily: F.body, marginTop: 1 }}>{histItem.meal}</div>
                    )}
                  </div>
                  <div style={{ fontSize: 13, color: C.charcoalSoft, fontFamily: F.body }}>{histItem.date}</div>
                </div>
                {(histItem.wines || []).map((wine, wi) => {
                  const wineKey = `hist_${i}_${wi}`;
                  const currentRating = ratings[wineKey] !== undefined ? ratings[wineKey] : (wine.rating || 0);
                  const wineLabel = `${wine.producer} ${wine.wine}`;
                  const isLast = wi === (histItem.wines || []).length - 1;
                  const showHint = showRatingHint;
                  return (
                    <div key={wi} onClick={() => setWineModal({ type: 'activity', wine })} style={{ padding: '12px 16px', cursor: 'pointer', borderBottom: isLast ? 'none' : '1px solid #F0EAE6' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 15, fontWeight: 700, color: C.charcoal, fontFamily: F.display, lineHeight: 1.2 }}>{wine.producer}</div>
                          <div style={{ fontSize: 13, color: C.charcoalMid, fontFamily: F.body }}>{wine.wine}{wine.vintage && wine.vintage !== 'NV' ? ` ${wine.vintage}` : ''}</div>
                        </div>
                      </div>
                      <div onClick={(e) => e.stopPropagation()} style={{ position: 'relative' }}>
                        {i === 0 && wi === 0 && showRatingHint && (
                          <TeachStars key="teach-stars-singleton" onDone={() => setShowRatingHint(false)} />
                        )}
                        <StarRating value={currentRating} onChange={(stars) => {
                          setRatings(r => ({ ...r, [wineKey]: stars }));
                          const newCellarRatings = { ...(userProfile.cellarRatings || {}), [wineLabel]: stars };
                          const isFirstRating = !(userProfile.cellarRatings || {})[wineLabel];
                          const alreadySaved = (userProfile.likedWines || []).includes(wineLabel);
                          if (isFirstRating) {
                            const newLikedWines = alreadySaved ? (userProfile.likedWines || []) : [...(userProfile.likedWines || []), wineLabel];
                            persist({ ...userProfile, likedWines: newLikedWines, cellarRatings: newCellarRatings });
                            if (!alreadySaved) {
                              setSavedWines(s => ({ ...s, [wineKey]: true }));
                              const _m = JSON.parse(localStorage.getItem('truepour_cellar_meta') || '{}');
                              _m[wineLabel] = { addedAt: Date.now() };
                              localStorage.setItem('truepour_cellar_meta', JSON.stringify(_m));
                              setShowToast('Added to your cellar');
                              setTimeout(() => setShowToast(null), 2500);
                            }
                          } else {
                            persist({ ...userProfile, cellarRatings: newCellarRatings });
                          }
                          if (session) { fetch('/api/cellar', { method:'POST', headers:{'Content-Type':'application/json','Authorization':`Bearer ${session.access_token}`}, body:JSON.stringify({ wine_name: wineLabel }) }).catch(()=>{}); }
                          if (session) {
                            fetch('/api/rate-wine', { method:'POST', headers:{'Content-Type':'application/json','Authorization':`Bearer ${session.access_token}`}, body:JSON.stringify({ producer:'', wine_name: wineLabel, rating: stars }) })
                              .then(r=>r.json()).then(rateData=>{
                                if (rateData.sliders) { setSliders(prev=>({...prev,...rateData.sliders})); setPalateUpdateSeen(false); try{localStorage.removeItem('truepour_palate_update_seen');}catch{} }
                                if (rateData.extSliders) { setExtendedSliders(prev=>({...prev,...rateData.extSliders})); }
                                if (rateData.sliders || rateData.extSliders) { setShowToast(null); }
                                maybeShowRatingToast(wineLabel, stars, rateData.sliders?{...sliders,...rateData.sliders}:sliders, rateData.extSliders?{...extendedSliders,...rateData.extSliders}:extendedSliders, rateData);
                              }).catch(()=>{});
                          }
                        }} hintVisible={false} onHintDismiss={() => setShowRatingHint(false)} />
                        {currentRating > 0 && (
                          <div onClick={async e => {
                            e.stopPropagation();
                            setRatings(r => ({ ...r, [wineKey]: 0 }));
                            persist({ ...userProfile, cellarRatings: { ...(userProfile.cellarRatings || {}), [wineLabel]: 0 } });
                            // claude/every-rating-real-signal: real retraction — see the
                            // matching Clear-rating handler above in this file.
                            if (session) {
                              try {
                                const clearRes = await fetch('/api/rate-wine', {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
                                  body: JSON.stringify({ producer: '', wine_name: wineLabel, rating: 0 })
                                });
                                const clearData = await clearRes.json();
                                if (clearData.sliders) { setSliders(prev => ({ ...prev, ...clearData.sliders })); }
                                if (clearData.extSliders) { setExtendedSliders(prev => ({ ...prev, ...clearData.extSliders })); }
                              } catch (err) { /* fail silently — local state already cleared */ }
                            }
                          }} style={{ fontSize:11, color:C.charcoalSoft, fontFamily:F.body, marginTop:4, cursor:'pointer' }}>Clear rating</div>
                        )}
                      </div>
                      <div style={{ display:'flex', justifyContent:'flex-end', marginTop:8 }}>
                        <span style={{ fontSize:13, fontWeight:600, color:C.burgundy, fontFamily:F.body }}>View details →</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
})()}

        {/* ── MY CELLAR SCREEN ── */}
        {screen === 'cellar' && (() => {
  const likedWines = userProfile.likedWines || [];

  // Layer C — cellar rate-nudge: count UNRATED cellar wines only (re-rating a rated
  // wine doesn't move the score, so nudging rated wines is pointless).
  const unratedCellar = likedWines.filter(w => !cellarRatings[w]);
  const unratedCount = unratedCellar.length;
  const firstUnratedLabel = unratedCellar[0] || null;

  // Cellar metadata — timestamps and sort state
  const cellarMeta = JSON.parse(localStorage.getItem('truepour_cellar_meta') || '{}');

  // Sort wines based on selected sort
  const sortedWines = [...likedWines].sort((a, b) => {
    if (cellarSort === 'rating') {
      const rA = cellarRatings[a] || 0;
      const rB = cellarRatings[b] || 0;
      if (rB !== rA) return rB - rA;
      // Tiebreak: most recently added first
      return (cellarMeta[b]?.addedAt || 0) - (cellarMeta[a]?.addedAt || 0);
    } else {
      // Recently added: newest first — use addedAt if available, fall back to reverse insertion order
      const tA = cellarMeta[a]?.addedAt || likedWines.indexOf(a);
      const tB = cellarMeta[b]?.addedAt || likedWines.indexOf(b);
      return tB - tA;
    }
  });

  const reviewedHistory = (userProfile.history || [])
    .map((h, originalIdx) => ({ ...h, _originalIdx: originalIdx }))
    .filter(h => (h.wines || []).some(w => w.tried === true || w.rating > 0))
    .reverse();
  // claude/every-rating-real-signal (device-testing follow-up): removal
  // used to branch on the local cellarRatings cache — if that cache read
  // as unrated (stale, or never refreshed for this wine), the wine was
  // dropped from the cellar table only, leaving its wine_ratings row (and
  // whatever it had already nudged) alive and uncounted-for in every
  // future recompute, while the UI showed one fewer cellar wine and an
  // unchanged rating count. DELETE /api/wine-rating is a no-op when no row
  // exists, so it's always safe to call regardless of what the cache
  // believes — only the confirmation dialog's copy still depends on it.
  const performRemoveWine = async (index, label) => {
    const updated = likedWines.filter((_, j) => j !== index);
    const newCellarRatings = { ...(userProfile.cellarRatings || {}) };
    delete newCellarRatings[label];
    persist({ ...userProfile, likedWines: updated, cellarRatings: newCellarRatings });

    if (!session) return;
    fetch('/api/cellar', { method: 'DELETE', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` }, body: JSON.stringify({ wine_name: label }) }).catch(()=>{});

    try {
      const res = await fetch('/api/wine-rating', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({ wine_name: label })
      });
      const data = await res.json();
      if (data.sliders) {
        setSliders(prev => ({ ...prev, ...data.sliders }));
        setPalateUpdateSeen(false);
        try { localStorage.removeItem('truepour_palate_update_seen'); } catch {}
      }
      const countRes = await fetch('/api/rating-count', {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      const countData = await countRes.json();
      setSupabaseRatingCount(countData.count || 0);
    } catch(e) {}
  };

  const removeWine = (i) => {
    const wineLabel = likedWines[i];
    const hasRating = (userProfile.cellarRatings || {})[wineLabel] > 0;
    if (hasRating) {
      setPendingDeleteWine({ index: i, label: wineLabel });
    } else {
      performRemoveWine(i, wineLabel);
    }
  };

  const confirmDeleteWine = async () => {
    if (!pendingDeleteWine) return;
    const { index, label } = pendingDeleteWine;
    setPendingDeleteWine(null);
    await performRemoveWine(index, label);
  };
  return (
    <div style={{ paddingTop: 24, paddingBottom: 80 }}>
      {pendingDeleteWine && (
        <div style={{ position:'fixed', inset:0, zIndex:9900, background:'rgba(20,10,8,0.65)', display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}>
          <div style={{ background:'#fff', borderRadius:24, padding:'28px 24px', width:'100%', maxWidth:390 }}>
            <div style={{ fontSize:20, fontWeight:800, color:C.charcoal, fontFamily:F.display, marginBottom:8 }}>Remove this wine?</div>
            <div style={{ fontSize:14, color:C.charcoalSoft, fontFamily:F.body, lineHeight:1.65, marginBottom:24 }}>
              You rated this wine, and that rating has been helping refine your wine personality. Removing it will also clear its influence on your taste profile.
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              <button onClick={confirmDeleteWine} style={{ width:'100%', padding:'14px', borderRadius:14, background:'#8A7A77', border:'none', color:'#fff', fontSize:15, fontWeight:700, fontFamily:F.display, cursor:'pointer' }}>
                Remove and clear its influence
              </button>
              <button onClick={()=>setPendingDeleteWine(null)} style={{ width:'100%', padding:'14px', borderRadius:14, background:'transparent', border:`1.5px solid ${C.divider}`, color:C.charcoalSoft, fontSize:15, fontFamily:F.body, cursor:'pointer' }}>
                Keep it in my cellar
              </button>
            </div>
          </div>
        </div>
      )}
      <div style={{ fontSize: 24, fontWeight: 800, color: C.charcoal, fontFamily: F.display, marginBottom: 4 }}>My Cellar</div>
      <div style={{ fontSize: 14, color: C.charcoalSoft, fontFamily: F.body, marginBottom: 20, lineHeight: 1.5 }}>
        Wines you love — saved from recommendations or added directly. Every bottle here calibrates your future picks.
      </div>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.charcoal, fontFamily: F.display, marginBottom: 8 }}>Add a wine you love</div>
        {/* claude/every-rating-real-signal: mode="evaluation" reused verbatim
            (the same suggestion-only fix already shipped for bottle
            evaluation) — the catalog-match gate this box carried only made
            sense while an unmatched add would create an orphan with no
            signal. Cellar-add now classifies structural attributes for any
            add (see /api/cellar's classifyWineForCellar), so that's no
            longer true. */}
        <WineAutocomplete mode="evaluation" onSelect={(wine) => {
          if (!wine || likedWines.includes(wine)) return;
          setPendingCellarWine(wine);
          setPendingCellarStars(0);
        }} />
        {pendingCellarWine && (
          <div style={{ marginTop: 12, padding: '14px', background: '#FFFAF8', borderRadius: 12, border: `1.5px solid ${C.burgundy}` }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.charcoal, fontFamily: F.body, marginBottom: 8 }}>{pendingCellarWine}</div>
            <div style={{ marginBottom: 10 }}>
              <StarRating value={pendingCellarStars} onChange={setPendingCellarStars} hintVisible={showRatingHint} onHintDismiss={() => setShowRatingHint(false)} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <button disabled={pendingCellarStars === 0} onClick={() => {
                  const wine = pendingCellarWine, stars = pendingCellarStars;
                  // Step 7 fix (this round, item 1): flag off must be TODAY's
                  // (5b2fe67/9370ddb) behavior exactly.
                  if (!step7HasServerFields) {
                    maybeShowRatingToast(wine, stars); // Layer A (no server nudge here → new-wine-or-nothing)
                    persist({ ...userProfile, likedWines: [...likedWines, wine], cellarRatings: { ...(userProfile.cellarRatings || {}), [wine]: stars } });
                    const existingMeta = JSON.parse(localStorage.getItem('truepour_cellar_meta') || '{}');
                    existingMeta[wine] = { addedAt: Date.now() };
                    localStorage.setItem('truepour_cellar_meta', JSON.stringify(existingMeta));
                    if (session) {
                      fetch('/api/cellar', { method:'POST', headers:{'Content-Type':'application/json','Authorization':`Bearer ${session.access_token}`}, body:JSON.stringify({ wine_name: wine }) }).catch(()=>{});
                      fetch('/api/rate-wine', { method:'POST', headers:{'Content-Type':'application/json','Authorization':`Bearer ${session.access_token}`}, body:JSON.stringify({ producer:'', wine_name: wine, rating: stars }) }).catch(()=>{});
                    }
                  } else {
                  persist({ ...userProfile, likedWines: [...likedWines, wine], cellarRatings: { ...(userProfile.cellarRatings || {}), [wine]: stars } });
                  const existingMeta = JSON.parse(localStorage.getItem('truepour_cellar_meta') || '{}');
                  existingMeta[wine] = { addedAt: Date.now() };
                  localStorage.setItem('truepour_cellar_meta', JSON.stringify(existingMeta));
                  if (session) {
                    fetch('/api/cellar', { method:'POST', headers:{'Content-Type':'application/json','Authorization':`Bearer ${session.access_token}`}, body:JSON.stringify({ wine_name: wine }) }).catch(()=>{});
                    // Step 7: still fire-and-forget — UI not blocked — but now reads
                    // the response so the toast can show pending/terminal copy or
                    // respect ratingMovedProfile when the flag is on.
                    fetch('/api/rate-wine', { method:'POST', headers:{'Content-Type':'application/json','Authorization':`Bearer ${session.access_token}`}, body:JSON.stringify({ producer:'', wine_name: wine, rating: stars }) })
                      .then(r => r.json())
                      .then(rateData => {
                        if (rateData.sliders) setSliders(prev => ({ ...prev, ...rateData.sliders }));
                        if (rateData.extSliders) setExtendedSliders(prev => ({ ...prev, ...rateData.extSliders }));
                        maybeShowRatingToast(wine, stars, rateData.sliders ? { ...sliders, ...rateData.sliders } : sliders, rateData.extSliders ? { ...extendedSliders, ...rateData.extSliders } : extendedSliders, rateData);
                      })
                      .catch(() => { maybeShowRatingToast(wine, stars); });
                  } else {
                    maybeShowRatingToast(wine, stars); // no session — never reaches the server, same as today
                  }
                  }
                  setPendingCellarWine(null);
                  setPendingCellarStars(0);
                }} style={{ flex: 1, padding: '9px', borderRadius: 10, background: 'transparent', border: `1.5px solid ${C.burgundy}`, color: C.burgundy, fontSize: 13, fontWeight: 700, fontFamily: F.display, cursor: pendingCellarStars === 0 ? 'not-allowed' : 'pointer', opacity: pendingCellarStars === 0 ? 0.4 : 1 }}>
                  Add to cellar
                </button>
                <button onClick={() => { setPendingCellarWine(null); setPendingCellarStars(0); }} style={{ padding: '9px 14px', borderRadius: 10, background: 'transparent', border: `1px solid ${C.divider}`, color: C.charcoalSoft, fontSize: 13, fontFamily: F.body, cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
              <button onClick={() => {
                persist({ ...userProfile, likedWines: [...likedWines, pendingCellarWine] });
                const existingMeta = JSON.parse(localStorage.getItem('truepour_cellar_meta') || '{}');
                existingMeta[pendingCellarWine] = { addedAt: Date.now() };
                localStorage.setItem('truepour_cellar_meta', JSON.stringify(existingMeta));
                if (session) {
                  fetch('/api/cellar', { method:'POST', headers:{'Content-Type':'application/json','Authorization':`Bearer ${session.access_token}`}, body:JSON.stringify({ wine_name: pendingCellarWine }) }).catch(()=>{});
                }
                setPendingCellarWine(null);
                setPendingCellarStars(0);
                setShowToast("Added to your cellar 🍷");
                setTimeout(() => setShowToast(null), 2500);
              }} style={{ width: '100%', padding: '9px', borderRadius: 10, background: 'transparent', border: `1px solid ${C.divider}`, color: C.charcoalSoft, fontSize: 13, fontFamily: F.body, cursor: 'pointer', textAlign: 'center' }}>
                Haven't tried it yet →
              </button>
            </div>
          </div>
        )}
      </div>
      {likedWines.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 20px', background: '#fff', borderRadius: 16, border: `1px solid ${C.divider}`, marginBottom: 20 }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>🗝️</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: C.charcoal, fontFamily: F.display, marginBottom: 6 }}>Your cellar is empty</div>
          <div style={{ fontSize: 14, color: C.charcoalSoft, fontFamily: F.body, lineHeight: 1.6 }}>Save wines you love — from recommendations or add them above — and they'll appear here.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
          {/* Sort toggle */}
          <div style={{ display:'flex', gap:8, marginBottom:16, marginTop:4 }}>
            <button
              onClick={() => setCellarSort('rating')}
              style={{ fontSize:13, fontWeight:600, fontFamily:'var(--fb)', padding:'6px 14px', borderRadius:20, border:`1.5px solid ${cellarSort==='rating' ? C.burgundy : '#ddd'}`, background: cellarSort==='rating' ? C.burgundyBg : 'white', color: cellarSort==='rating' ? C.burgundy : C.charcoalMid, cursor:'pointer' }}
            >Top Rated</button>
            <button
              onClick={() => setCellarSort('recent')}
              style={{ fontSize:13, fontWeight:600, fontFamily:'var(--fb)', padding:'6px 14px', borderRadius:20, border:`1.5px solid ${cellarSort==='recent' ? C.burgundy : '#ddd'}`, background: cellarSort==='recent' ? C.burgundyBg : 'white', color: cellarSort==='recent' ? C.burgundy : C.charcoalMid, cursor:'pointer' }}
            >Recently Added</button>
          </div>

          {/* Layer C — rate-nudge banner: only when there are UNRATED cellar wines */}
          {unratedCount > 0 && (
            <div onClick={() => { const el = document.getElementById('cellar-unrated-first'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }}
              style={{ background:'#FFFFFF', borderRadius:12, border:`1px solid ${C.divider}`, borderLeft:`3px solid ${C.burgundy}`, padding:'12px 14px', marginBottom:14, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'space-between', gap:8 }}>
              <div>
                <div style={{ fontSize:13, fontWeight:700, color:C.charcoal, fontFamily:F.body }}>You have {unratedCount} unrated bottle{unratedCount!==1?'s':''}</div>
                <div style={{ fontSize:12, color:C.charcoalSoft, fontFamily:F.body, marginTop:1 }}>Rate them to sharpen your matches</div>
              </div>
              <span style={{ fontSize:16, color:C.burgundy, flexShrink:0 }}>→</span>
            </div>
          )}

          {sortedWines.map((w, i) => (
            <div key={i}
              id={w === firstUnratedLabel ? 'cellar-unrated-first' : undefined}
              onClick={() => setWineModal({ type: 'cellar', wine: w })}
              style={{ background:'white', borderRadius:16, padding:'14px 16px', marginBottom:12, boxShadow:'0 1px 4px rgba(0,0,0,0.07)', border:'1.5px solid #f0ece8', cursor:'pointer', transition:'transform 0.12s ease, box-shadow 0.12s ease' }}
              onTouchStart={e => e.currentTarget.style.transform='scale(0.98)'}
              onTouchEnd={e => { e.currentTarget.style.transform='scale(1)'; }}
            >
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
                <span style={{ fontSize:15, fontWeight:700, color:C.charcoal, fontFamily:'var(--fd)', lineHeight:1.3, flex:1, paddingRight:8 }}>{w}</span>
                <button
                  onClick={e => { e.stopPropagation(); removeWine(likedWines.indexOf(w)); }}
                  style={{ background:'none', border:'none', color:'#bbb', fontSize:18, cursor:'pointer', padding:'0 0 0 8px', lineHeight:1 }}
                >✕</button>
              </div>
              <StarRating value={cellarRatings[w]} onChange={v => {
                // Step 7 fix (this round, item 1): flag off must be TODAY's
                // (5b2fe67/9370ddb) behavior exactly.
                if (!step7HasServerFields) {
                  maybeShowRatingToast(w, v); // Layer A (no server nudge here → new-wine-or-nothing)
                  setUserProfile(prev => {
                    const next = { ...prev, cellarRatings: { ...(prev.cellarRatings||{}), [w]: v } };
                    localStorage.setItem('truepour_user_profile', JSON.stringify(next));
                    return next;
                  });
                  if (session) {
                    fetch('/api/rate-wine', { method:'POST', headers:{'Content-Type':'application/json','Authorization':`Bearer ${session.access_token}`}, body:JSON.stringify({ producer:'', wine_name: w, rating: v }) }).then(r => { if (r.ok) refreshRatingCount(); }).catch(()=>{});
                  }
                  return;
                }
                setUserProfile(prev => {
                  const next = { ...prev, cellarRatings: { ...(prev.cellarRatings||{}), [w]: v } };
                  localStorage.setItem('truepour_user_profile', JSON.stringify(next));
                  return next;
                });
                if (session) {
                  // Step 7: still fire-and-forget — UI not blocked — but now reads
                  // the response so the toast can show pending/terminal copy or
                  // respect ratingMovedProfile when the flag is on.
                  fetch('/api/rate-wine', { method:'POST', headers:{'Content-Type':'application/json','Authorization':`Bearer ${session.access_token}`}, body:JSON.stringify({ producer:'', wine_name: w, rating: v }) })
                    .then(r => { if (r.ok) refreshRatingCount(); return r.json(); })
                    .then(rateData => {
                      if (rateData.sliders) setSliders(prev => ({ ...prev, ...rateData.sliders }));
                      if (rateData.extSliders) setExtendedSliders(prev => ({ ...prev, ...rateData.extSliders }));
                      maybeShowRatingToast(w, v, rateData.sliders ? { ...sliders, ...rateData.sliders } : sliders, rateData.extSliders ? { ...extendedSliders, ...rateData.extSliders } : extendedSliders, rateData);
                    })
                    .catch(() => { maybeShowRatingToast(w, v); });
                } else {
                  maybeShowRatingToast(w, v); // no session — never reaches the server, same as today
                }
              }} />
              <div style={{ display:'flex', justifyContent:'flex-end', marginTop:10, paddingTop:8, borderTop:'1px solid #f5f0eb' }}>
                <span style={{ fontSize:13, fontWeight:600, color:C.burgundy, fontFamily:'var(--fb)' }}>View details →</span>
              </div>
            </div>
          ))}
        </div>
      )}
      {reviewedHistory.length > 0 && (
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.charcoal, fontFamily: F.display, marginBottom: 12 }}>Tasting Notes</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {reviewedHistory.map((h, i) => (
              <div key={i} style={{ padding: '14px', background: '#fff', borderRadius: 14, border: `1px solid ${C.divider}` }}>
                <div style={{ fontSize: 13, color: C.charcoalSoft, fontFamily: F.body, marginBottom: 8 }}>{h.date} · {h.meal || h.occasion}</div>
                {(h.wines || []).filter(w => w.tried === true || w.rating > 0).map((w, j) => (
                  <div key={j} style={{ marginBottom: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 14, color: C.charcoal, fontFamily: F.body, fontWeight: 500 }}>{w.producer} {w.wine}</span>
                      <span style={{ fontSize: 13, color: C.charcoal, fontFamily: F.body }}>
                        {w.rating ? '★'.repeat(w.rating) : 'Tried'}
                      </span>
                    </div>
                    {w.tags?.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 3 }}>
                        {w.tags.map(t => (
                          <span key={t} style={{ fontSize: 11, color: C.burgundy, background: '#fff', padding: '1px 7px', borderRadius: 20, border: `1px solid ${C.burgundy}`, fontFamily: F.body }}>{t}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
})()}

        {/* ── PROFILE SCREEN ── */}
        {screen === 'profile' && (() => {
  const arch = userArchetype ? ARCHETYPES[userArchetype] : null;
  const variant = arch ? getArchetypeVariant(userArchetype, extendedSliders) : null;
  const handleShare = async () => {
    try {
      const canvas = await html2canvas(profileShareCardRef.current, { scale:2, useCORS:true, backgroundColor:C.burgundyBg });
      const blob = await new Promise(res=>canvas.toBlob(res,"image/png"));
      if (navigator.share && blob) {
        await navigator.share({ title:`I'm ${arch.name} on TruePour`, text:arch.shareText, files:[new File([blob],"truepour-archetype.png",{type:"image/png"})] });
      } else {
        try { await navigator.clipboard.writeText(arch.shareText); setShowToast('Link copied to clipboard!'); setTimeout(()=>setShowToast(null),2000); } catch {}
      }
    } catch(e) { console.error(e); }
  };
  return (
    <div style={{ paddingTop: 24, paddingBottom: 80 }}>
      <div style={{ fontSize: 24, fontWeight: 800, color: C.charcoal, fontFamily: F.display, marginBottom: 20 }}>Profile</div>
      {// Step 7 (item 4, fixed item 2): the stated cause, shown verbatim,
      // reusing the existing profile-change display (this screen) rather
      // than a new screen/component. Gated on causeBannerVisibleThisVisit
      // (captured ONCE on tab entry by handleTabChange, not re-derived
      // here) so it shows for the visit that revealed it and never again
      // afterward — see shouldShowCauseBannerOnEntry's own comment.
      causeBannerVisibleThisVisit && profileChangeReason != null && (
        <div style={{ background: '#fff', borderRadius: 14, border: `1px solid ${C.burgundyMid}`, padding: '14px 16px', marginBottom: 16, fontSize: 14, color: C.charcoal, fontFamily: F.body, lineHeight: 1.5 }}>
          {profileChangeReason}
        </div>
      )}
      {arch ? (
        <div style={{ background: C.burgundyBg, borderRadius: 20, border: `1px solid ${C.burgundyMid}`, padding: '24px 20px', marginBottom: 20, textAlign: 'center' }}>
          <div style={{marginBottom:8}}><ArchetypeIcon archetypeKey={userArchetype} size={52} color={C.burgundy}/></div>
          <div style={{ fontSize: 22, fontWeight: 800, color: C.burgundy, fontFamily: F.display, lineHeight: 1.2, marginBottom: 4 }}>{arch.name}</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: C.charcoal, fontFamily: F.display, marginBottom: 14, letterSpacing: 0.3 }}>{arch.tagline}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center', marginBottom: 16 }}>
            {((variant?.chips) || arch.chips || []).map((chip, i) => (
              <span key={i} style={{ fontSize: 13, color: C.burgundy, background: '#fff', border: `1px solid ${C.burgundyMid}`, borderRadius: 20, padding: '4px 12px', fontFamily: F.body, fontWeight: 500 }}>{chip}</span>
            ))}
          </div>
          <div style={{ fontSize: 14, color: C.charcoalSoft, fontFamily: F.body, lineHeight: 1.6, textAlign: 'left', marginBottom: 16 }}>
            {variant?.description || arch.description}
          </div>
          <div style={{ borderTop: `1px solid ${C.burgundyMid}`, paddingTop: 14, marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: C.burgundy, letterSpacing: 2, textTransform: 'uppercase', fontFamily: F.body, fontWeight: 600, marginBottom: 6 }}>Your regions & varieties</div>
            <div style={{ fontSize: 13, color: C.charcoalSoft, fontFamily: F.body, lineHeight: 1.6, textAlign: 'left' }}>{variant?.regions || arch.regions}</div>
          </div>
          <button onClick={handleShare}
            style={{ width: '100%', padding: '12px', borderRadius: 12, background: `linear-gradient(135deg, ${C.burgundy}, ${C.burgundyLight})`, border: 'none', color: '#fff', fontSize: 15, fontWeight: 700, fontFamily: F.display, cursor: 'pointer' }}>
            Share my wine personality<span style={{ marginLeft:8, display:'inline-flex', verticalAlign:'middle' }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg></span>
          </button>
        </div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 20, border: `1px solid ${C.divider}`, padding: '24px 20px', marginBottom: 20, textAlign: 'center' }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>🍷</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: C.charcoal, fontFamily: F.display, marginBottom: 8 }}>You haven't discovered your wine personality yet</div>
          <div style={{ fontSize: 14, color: C.charcoalSoft, fontFamily: F.body, lineHeight: 1.6, marginBottom: 16 }}>Answer 11 questions to unlock your archetype and get more accurate recommendations.</div>
          <button onClick={() => { setCurrentQuestionIndex(0); setScreen('quiz'); scrollToTop(); }}
            style={{ width: '100%', padding: '14px', borderRadius: 14, background: C.burgundy, border: 'none', color: '#fff', fontSize: 16, fontWeight: 700, fontFamily: F.display, cursor: 'pointer' }}>
            Discover my wine personality →
          </button>
        </div>
      )}
      {arch && (() => {
  const DIMS = [
    { key:'body', label:'Body', descriptors: ['Light','Medium','Medium-Full','Full'] },
    { key:'tannin', label:'Tannin', descriptors: ['Soft','Medium','Firm','Grippy'] },
    { key:'acidity', label:'Acidity', descriptors: ['Round','Medium','Bright','Crisp'] },
    { key:'oak', label:'Oak', descriptors: ['Unoaked','Lightly Oaked','Oak-Influenced','Oaky'] },
    { key:'sweetness', label:'Sweetness', descriptors: ['Bone Dry','Dry','Off-Dry','Sweet'] },
    { key:'complexity', label:'Complexity', descriptors: ['Simple','Approachable','Layered','Complex'] },
  ];
  const getDescriptor = (key, value) => {
    const dim = DIMS.find(d => d.key === key);
    if (!dim) return '';
    const v = value || 3;
    if (v <= 2) return dim.descriptors[0];
    if (v <= 3.5) return dim.descriptors[1];
    if (v <= 4.5) return dim.descriptors[2];
    return dim.descriptors[3];
  };
  const ratingCount = supabaseRatingCount;
  const hasQuizBaseline = quizSliders && Object.keys(quizSliders).length > 0;

  const getDrift = (key) => {
    if (!hasDimensionDrifted(key)) return null;
    if (DRIFT_EXTENDED_KEYS.includes(key)) {
      // hasDimensionDrifted already returned false (above) when
      // quizExtendedSliders[key] is null, so quizExt is guaranteed non-null here.
      const quizExt = quizExtendedSliders[key];
      const current = extendedSliders?.[key] ?? 3;
      return (current - quizExt) > 0 ? 'up' : 'down';
    }
    const delta = (sliders?.[key] ?? 3) - (quizSliders?.[key] ?? 3);
    return delta > 0 ? 'up' : 'down';
  };

  return (
    <div style={{ background:'#fff', borderRadius:20, border:`1px solid ${C.divider}`, padding:'20px', marginBottom:16 }}>
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:16 }}>
        <div>
          <div style={{ fontSize:13, fontWeight:700, color:C.burgundy, fontFamily:F.display }}>Your Palate</div>
          <div style={{ fontSize:11, color:C.charcoalSoft, fontFamily:F.body, marginTop:2 }}>
            {ratingCount === 0
              ? 'From your quiz · rate wines to refine this'
              : `From your quiz · refined by ${ratingCount} rating${ratingCount!==1?'s':''}`}
          </div>
        </div>
        {ratingCount > 0 && (
          <div style={{ display:'flex', alignItems:'center', gap:5, background:'#F0F7F4', padding:'4px 10px', borderRadius:20, border:'1px solid rgba(46,125,82,0.15)' }}>
            <div style={{ width:6, height:6, borderRadius:'50%', background:C.green }}/>
            <span style={{ fontSize:11, color:C.green, fontFamily:F.body, fontWeight:600 }}>Learning</span>
          </div>
        )}
      </div>
      {!arrowEducationSeen && DIMS.some(({key}) => hasDimensionDrifted(key)) && (
        <div style={{ display:'flex', alignItems:'flex-start', gap:10, background:'#FFF8F0', border:'1px solid rgba(160,36,58,0.15)', borderRadius:12, padding:'10px 14px', marginBottom:16 }}>
          <div style={{ flex:1, fontSize:12, color:C.charcoalSoft, fontFamily:F.body, lineHeight:1.55 }}>
            Arrows show when a preference has shifted based on your ratings — ↑ means this attribute is trending higher for you.
          </div>
          <button
            onClick={() => { setArrowEducationSeen(true); try { localStorage.setItem('truepour_arrow_education_seen', 'true'); } catch {} }}
            style={{ background:'none', border:'none', color:C.charcoalSoft, fontSize:18, lineHeight:1, cursor:'pointer', padding:'0 0 0 6px', flexShrink:0 }}
          >×</button>
        </div>
      )}
      <div style={{ display:'flex', flexDirection:'column', gap:13 }}>
        {DIMS.map(({key,label})=>{
          const EXTENDED_DIM_KEYS = ['tannin', 'oak', 'complexity'];
          const current = EXTENDED_DIM_KEYS.includes(key)
            ? (extendedSliders?.[key] || 3)
            : (sliders?.[key] || 3);
          const pct = ((current-1)/5)*100;
          const drift = getDrift(key);
          const isChanged = drift !== null;
          return (
            <div key={key}>

              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:5 }}>
                <div style={{ display:'flex', alignItems:'center' }}>
                  <span style={{ fontSize:12, fontWeight:isChanged?700:600, color:isChanged?C.charcoal:C.charcoalSoft, fontFamily:F.body }}>{label}</span>
                  {drift==='up' && <span style={{ fontSize:10, color:C.green, fontWeight:700, marginLeft:3 }}>↑</span>}
                  {drift==='down' && <span style={{ fontSize:10, color:C.burgundy, fontWeight:700, marginLeft:3 }}>↓</span>}
                </div>
                <span style={{
                  fontSize:11, fontFamily:F.body, fontWeight: isChanged ? 700 : 500,
                  color: isChanged ? C.burgundy : C.charcoalSoft,
                  letterSpacing: 0.2,
                }}>
                  {getDescriptor(key, current)}
                </span>
              </div>
              <div style={{ height:7, background:C.burgundyBg, borderRadius:8, position:'relative' }}>
                <div style={{ position:'absolute', left:0, top:0, height:'100%', width:`${pct}%`, background:isChanged?`linear-gradient(90deg,rgba(160,36,58,0.3),${C.burgundy})`:`linear-gradient(90deg,rgba(160,36,58,0.15),rgba(160,36,58,0.4))`, borderRadius:8 }}/>
                <div style={{ position:'absolute', top:'50%', left:`${pct}%`, transform:'translate(-50%,-50%)', width:13, height:13, borderRadius:'50%', background:isChanged?C.burgundy:'rgba(160,36,58,0.35)', border:'2px solid #fff', boxShadow:isChanged?`0 0 0 2px rgba(160,36,58,0.2)`:'none' }}/>
              </div>
            </div>
          );
        })}
      </div>
      {ratingCount === 0 && (
        <button onClick={()=>{ setActiveTab('activity'); setScreen('activity'); scrollToTop(); }} style={{
          marginTop:16, width:'100%', padding:'12px 14px',
          background:'#FFFFFF', borderRadius:10,
          border:`1px solid ${C.divider}`,
          borderLeft:`3px solid ${C.burgundy}`,
          display:'flex', alignItems:'center', justifyContent:'space-between',
          cursor:'pointer', textAlign:'left'
        }}>
          <div>
            <div style={{ fontSize:12, fontWeight:700, color:C.charcoal, fontFamily:F.body }}>Discover wines to rate</div>
            <div style={{ fontSize:11, color:C.charcoalSoft, fontFamily:F.body, marginTop:1 }}>Every rating makes your matches more accurate</div>
          </div>
          <span style={{ fontSize:16, color:C.burgundy, marginLeft:8 }}>→</span>
        </button>
      )}
    </div>
  );
})()}
      {arch && (() => {
  const ratingCount = supabaseRatingCount;

  const handleRetakeConfirm = async () => {
    setShowRetakeWarning(false);
    try {
      const res = await fetch('/api/reset-to-quiz', {
        method:'POST',
        headers:{ 'Authorization':`Bearer ${session.access_token}` }
      });
      const data = await res.json();
      if (data.sliders) setSliders(data.sliders);
    } catch(e) {}
    setCurrentQuestionIndex(0);
    setNavVisible(false);
    setScreen('quiz');
    scrollToTop();
  };

  if (ratingCount >= 5) return null;

  return (
    <>
      {showRetakeWarning && (
        <div style={{ position:'fixed', inset:0, zIndex:100, background:'rgba(20,10,8,0.65)', display:'flex', alignItems:'flex-end', justifyContent:'center', padding:16 }}>
          <div style={{ background:'#fff', borderRadius:24, padding:'28px 24px', width:'100%', maxWidth:390 }}>
            <div style={{ fontSize:20, fontWeight:800, color:C.charcoal, fontFamily:F.display, marginBottom:8 }}>Retake the quiz?</div>
            <div style={{ fontSize:14, color:C.charcoalSoft, fontFamily:F.body, lineHeight:1.65, marginBottom:24 }}>
              You have <strong style={{ color:C.charcoal }}>{ratingCount} rating{ratingCount!==1?'s':''}</strong> that have been refining your palate. Retaking will erase this data and reset your attributes to your new quiz answers.
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              <button onClick={handleRetakeConfirm} style={{ width:'100%', padding:'14px', borderRadius:14, background:'#8A7A77', border:'none', color:'#fff', fontSize:15, fontWeight:700, fontFamily:F.display, cursor:'pointer' }}>
                Retake and reset my data
              </button>
              <button onClick={()=>setShowRetakeWarning(false)} style={{ width:'100%', padding:'14px', borderRadius:14, background:'transparent', border:`1.5px solid ${C.divider}`, color:C.charcoalSoft, fontSize:15, fontFamily:F.body, cursor:'pointer' }}>
                Keep my ratings data
              </button>
            </div>
          </div>
        </div>
      )}
      <div style={{ marginBottom:20, textAlign:'center' }}>
        <button
          onClick={()=>{ if(ratingCount>0){ setShowRetakeWarning(true); } else { setCurrentQuestionIndex(0); setNavVisible(false); setScreen('quiz'); scrollToTop(); }}}
          style={{ padding:'10px 24px', borderRadius:12, background:'transparent', border:`1px solid ${C.divider}`, color:C.charcoalSoft, fontSize:13, fontFamily:F.body, cursor:'pointer' }}
        >
          Retake the wine personality quiz
        </button>
        {ratingCount > 0 && (
          <div style={{ fontSize:11, color:C.charcoalSoft, fontFamily:F.body, marginTop:6 }}>Note: retaking will reset your ratings data</div>
        )}
      </div>
    </>
  );
})()}
      <div style={{ borderTop: `1px solid ${C.divider}`, marginTop: 0 }}>
        <button onClick={() => window.open('https://tally.so/r/1AbJ11', '_blank')}
          style={{ width:'100%', padding:'16px 4px', background:'none', border:'none', display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer', textAlign:'left' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.charcoalSoft} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
            </svg>
            <span style={{ fontSize:15, color:C.charcoal, fontFamily:F.body }}>Send feedback</span>
          </div>
          <span style={{ fontSize:15, color:C.charcoalSoft }}>›</span>
        </button>
      </div>
      <div style={{ borderTop: `1px solid ${C.divider}`, marginTop: 0 }}>
        <button onClick={() => window.open('https://truepour.ai/privacy.html', '_blank')}
          style={{ width:'100%', padding:'16px 4px', background:'none', border:'none', display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer', textAlign:'left' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <span style={{ fontSize:15, color:C.charcoal, fontFamily:F.body }}>Privacy Policy</span>
          </div>
          <span style={{ fontSize:15, color:C.charcoalSoft }}>›</span>
        </button>
      </div>
      <div style={{ borderTop: `1px solid ${C.divider}`, marginTop: 0 }}>
        <button onClick={() => window.open('https://truepour.ai/terms.html', '_blank')}
          style={{ width:'100%', padding:'16px 4px', background:'none', border:'none', display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer', textAlign:'left' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <span style={{ fontSize:15, color:C.charcoal, fontFamily:F.body }}>Terms of Service</span>
          </div>
          <span style={{ fontSize:15, color:C.charcoalSoft }}>›</span>
        </button>
      </div>
      <div style={{ borderTop: `1px solid ${C.divider}`, marginTop: 0 }}>
        <button onClick={async () => {
          await supabase.auth.signOut();
          try { Object.keys(localStorage).forEach(k => { if (k.startsWith('truepour_') && k !== 'truepour_splash_seen') localStorage.removeItem(k); }); } catch {}
          setSession(null);
          setProfileComplete(false);
          setUserArchetype(null);
          setUserProfile({ likedWines: [], history: [] });
          setDeepProfile(false);
          setAuthMode('signin');
          setScreen('auth');
        }}
          style={{ width:'100%', padding:'16px 4px', background:'none', border:'none', display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer', textAlign:'left' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.charcoalSoft} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
            <span style={{ fontSize:15, color:C.charcoal, fontFamily:F.body }}>Sign Out</span>
          </div>
          <span style={{ fontSize:15, color:C.charcoalSoft }}>›</span>
        </button>
      </div>
      <div style={{ borderTop: `1px solid ${C.divider}`, marginTop: 0, marginBottom: 8 }}>
        <button onClick={() => { setShowDeleteConfirm(true); setDeleteAccountError(''); setDeleteAccountStatus('idle'); }}
          style={{ width:'100%', padding:'14px 4px', background:'none', border:'none', display:'flex', alignItems:'center', cursor:'pointer', textAlign:'left' }}>
          <span style={{ fontSize:14, color:'#C0392B', fontFamily:F.body }}>Delete Account</span>
        </button>
      </div>
      {showDeleteConfirm && (
        <div style={{ position:'fixed', inset:0, zIndex:200, background:'rgba(20,10,8,0.65)', display:'flex', alignItems:'flex-end', justifyContent:'center', padding:16 }}>
          <div style={{ background:'#fff', borderRadius:24, padding:'28px 24px', width:'100%', maxWidth:390 }}>
            {deleteAccountStatus === 'success' ? (
              <div>
                <div style={{ fontSize:20, fontWeight:800, color:C.charcoal, fontFamily:F.display, marginBottom:8 }}>Account deleted</div>
                <div style={{ fontSize:14, color:C.charcoalSoft, fontFamily:F.body, lineHeight:1.65 }}>Your account has been deleted.</div>
              </div>
            ) : (
              <>
                <div style={{ fontSize:20, fontWeight:800, color:C.charcoal, fontFamily:F.display, marginBottom:8 }}>Delete your account?</div>
                <div style={{ fontSize:14, color:C.charcoalSoft, fontFamily:F.body, lineHeight:1.65, marginBottom:24 }}>
                  This will permanently delete your <strong style={{ color:C.charcoal }}>palate profile and archetype</strong>, your <strong style={{ color:C.charcoal }}>cellar</strong>, your <strong style={{ color:C.charcoal }}>ratings history</strong>, and your account. <strong style={{ color:C.charcoal }}>This cannot be undone.</strong>
                </div>
                {deleteAccountError ? (
                  <div style={{ fontSize:13, color:'#C0392B', fontFamily:F.body, marginBottom:12, padding:'10px 14px', background:'#FFF5F5', borderRadius:10, border:'1px solid rgba(192,57,43,0.2)' }}>{deleteAccountError}</div>
                ) : null}
                <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  <button
                    disabled={deleteAccountStatus === 'loading'}
                    onClick={async () => {
                      setDeleteAccountStatus('loading');
                      setDeleteAccountError('');
                      try {
                        const r = await fetch('/api/account', {
                          method: 'DELETE',
                          headers: { 'Authorization': `Bearer ${session.access_token}` },
                        });
                        if (!r.ok) {
                          const d = await r.json().catch(() => ({}));
                          setDeleteAccountError(d.error || 'Deletion failed. Please try again.');
                          setDeleteAccountStatus('idle');
                          return;
                        }
                        setDeleteAccountStatus('success');
                        await supabase.auth.signOut();
                        try { Object.keys(localStorage).forEach(k => { if (k.startsWith('truepour_')) localStorage.removeItem(k); }); } catch {}
                        setTimeout(() => {
                          setShowDeleteConfirm(false);
                          setDeleteAccountStatus('idle');
                          setSession(null);
                          setProfileComplete(false);
                          setUserArchetype(null);
                          setUserProfile({ likedWines: [], history: [] });
                          setDeepProfile(false);
                          setExtendedSliders({tannin:null,oak:null,alcohol:null,aromatics:null,complexity:null,color:null});
                          setQuizSliders(null);
                          setCurrentQuestionIndex(0);
                          setNavVisible(false);
                          setScreen('quiz');
                        }, 2000);
                      } catch (e) {
                        setDeleteAccountError('Something went wrong. Please try again.');
                        setDeleteAccountStatus('idle');
                      }
                    }}
                    style={{ width:'100%', padding:'14px', borderRadius:14, background: deleteAccountStatus === 'loading' ? '#999' : '#C0392B', border:'none', color:'#fff', fontSize:15, fontWeight:700, fontFamily:F.display, cursor: deleteAccountStatus === 'loading' ? 'not-allowed' : 'pointer', opacity: deleteAccountStatus === 'loading' ? 0.7 : 1 }}>
                    {deleteAccountStatus === 'loading' ? 'Deleting…' : 'Delete my account'}
                  </button>
                  <button onClick={() => { setShowDeleteConfirm(false); setDeleteAccountError(''); setDeleteAccountStatus('idle'); }}
                    style={{ width:'100%', padding:'14px', borderRadius:14, background:'transparent', border:`1.5px solid ${C.divider}`, color:C.charcoalSoft, fontSize:15, fontFamily:F.body, cursor:'pointer' }}>
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      <div style={{ textAlign: 'center', marginTop: 28, fontSize: 13, color: C.charcoalSoft, fontFamily: F.body }}>
        TruePour · truepour.ai
      </div>
      <div ref={profileShareCardRef} style={{position:'absolute',left:-9999,top:0,background:C.burgundyBg,borderRadius:24,border:`1px solid ${C.burgundyMid}`,padding:'28px 24px',width:320,textAlign:'center'}}><div style={{marginBottom:8,display:'flex',justifyContent:'center'}}><ArchetypeIcon archetypeKey={userArchetype} size={80} color={C.burgundy} /></div><div style={{fontSize:22,fontWeight:800,color:C.burgundy,fontFamily:F.display,marginBottom:6}}>{arch ? arch.name : ''}</div><div style={{fontSize:14,fontWeight:600,color:C.charcoal,fontFamily:F.display,marginBottom:12}}>{arch ? arch.tagline : ''}</div><div style={{fontSize:13,color:C.charcoalSoft,fontFamily:F.body,lineHeight:1.6,marginBottom:16,textAlign:'left'}}>{arch ? (getArchetypeVariant(userArchetype,extendedSliders)?.description||arch.description) : ''}</div>
  {arch && ((getArchetypeVariant(userArchetype,extendedSliders)?.chips || arch.chips || []).length > 0) && (
    <div style={{display:'flex',flexWrap:'wrap',gap:5,justifyContent:'center',marginBottom:12}}>
      {(getArchetypeVariant(userArchetype,extendedSliders)?.chips || arch.chips || []).map((chip,i) => (
        <span key={i} style={{fontSize:11,color:C.burgundy,background:'rgba(160,36,58,0.08)',padding:'3px 10px',borderRadius:20,border:`1px solid rgba(160,36,58,0.2)`,fontFamily:F.body,fontWeight:500}}>{chip}</span>
      ))}
    </div>
  )}
  {arch && (getArchetypeVariant(userArchetype,extendedSliders)?.regions || arch.regions) && (
    <div style={{borderTop:`1px solid rgba(160,36,58,0.15)`,paddingTop:12,marginBottom:12,textAlign:'left'}}>
      <div style={{fontSize:10,color:C.burgundy,letterSpacing:2,textTransform:'uppercase',fontFamily:F.body,fontWeight:600,marginBottom:5}}>Your regions & varieties</div>
      <div style={{fontSize:12,color:C.charcoalMid,fontFamily:F.body,lineHeight:1.5}}>{getArchetypeVariant(userArchetype,extendedSliders)?.regions || arch.regions}</div>
    </div>
  )}
  <div style={{fontSize:13,color:C.burgundyLight,fontFamily:F.body,fontWeight:600}}>truepour.ai</div></div>
    </div>
  );
})()}

        {/* ── RESULTS ── */}
        {screen==="results" && (
          <div>
            <div style={{ paddingTop:36, paddingBottom:16 }}>
              <button style={S.back} onClick={() => {
  maybeShowSavedToast();
  if (context === 'home') {
    // Home-multi never visits 'meal' on the way in, so we jump to it to
    // re-pick — but WITHOUT pushing 'results' onto screenHistory (that caused
    // the results↔meal back-oscillation). Plain screen change, no push.
    setError(null); setScreen('meal'); scrollToTop();
  } else {
    navigateBack();
  }
}}>← Refine</button>
              <div style={S.h}>{homeFlowPath==="few" ? "Your best bottle tonight" : meal?(meal.length>48?meal.slice(0,48)+"…":meal):(occasion==="glass"?"An evening glass":occasion==="outside"?"Drinks outside":occasion==="gift"?"A gift bottle":occasion==="cheese"?"Cheese & charcuterie":occasion==="hosting"?"Hosting tonight":"Tonight's wine")}</div>
              {/* claude/evidence-surface-build: the chip and the per-card badge
                  are one contract (server historyInformed field — see
                  offLeanDisplayFlags/computeHistoryInformed in server.js) at
                  two granularities. Unanimous-or-silent: the chip only
                  asserts "informed" when EVERY card in this response agrees,
                  so there is no reachable state where a badge reads "Still
                  Calibrating" underneath a chip reading "Calibrated" — no
                  longer userProfile.likedWines (a persisted fact with no tie
                  to this specific response). Copy decided by Blake:
                  "Calibrated to your ratings" (covers cellar entries too —
                  the server signal merges both). */}
              {(() => {
                const historyInformedChip = pairings.length > 0 && pairings.every(p => p && p.historyInformed === true);
                return (
                  <div style={{ marginBottom:16 }}>
                    {(userArchetype && deepProfile) ? (
                      <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                        <span style={{ padding:"6px 14px", borderRadius:20, background:C.burgundy, color:"#fff", fontSize:14, fontFamily:F.body, fontWeight:500 }}>
                          <ArchetypeIcon archetypeKey={userArchetype} size={20} color="#FFFFFF"/> {ARCHETYPES[userArchetype].name}
                        </span>
                        {historyInformedChip && <span style={{ fontSize:12, color:C.navy, background:C.navyBg, padding:"2px 9px", borderRadius:20, border:`1px solid ${C.navyBorder}`, fontFamily:F.body, fontWeight:500 }}>🗝️ Calibrated to your ratings</span>}
                      </div>
                    ) : (
                      <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                        {historyInformedChip && <span style={{ fontSize:12, color:C.navy, background:C.navyBg, padding:"2px 9px", borderRadius:20, border:`1px solid ${C.navyBorder}`, fontFamily:F.body, fontWeight:500 }}>🗝️ Calibrated to your ratings</span>}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {(pairings.length === 0 || (pairings.length > 0 && pairings.every(p => p && p.unknown))) && context !== 'online' ? (() => {
              // Cause-accurate empty state, branched on the server's emptyReason.
              // PALATE_NO_FIT is intentionally absent — no server path returns an
              // empty array for taste reasons (weak fits come back as low-conf cards).
              const EMPTY_COPY = {
                CATALOG_MISS: {
                  heading: 'Not in our collection yet',
                  body: "We don't have these bottles in our collection yet — it's growing all the time. Try adding them manually to track them.",
                },
                PARSE_FAIL: {
                  heading: 'Something went wrong',
                  body: 'Something went wrong on our end — give it another try.',
                },
                NO_SCAN_MATCH: {
                  heading: "Couldn't confirm your scan",
                  body: "We couldn't confidently match what you scanned to real wines — try rescanning for a clearer read, or add them manually.",
                },
                NO_FORMAT_MATCH: {
                  heading: "Couldn't confirm availability",
                  body: "We couldn't confirm any of these wines are available in the service format you picked — try rescanning the menu, or switch between glass and bottle. (An all-unpriced set can be a scan miss rather than genuinely one format only.)",
                },
                NO_BUDGET_MATCH: {
                  heading: 'Nothing in that budget',
                  body: "None of the menu wines we could price landed in your budget — try widening it, or rescan if the prices didn't read clearly.",
                },
              };
              if (!emptyReason) {
                console.warn('[EMPTY STATE] Untagged empty result reached C2 (no emptyReason). context=', context);
              }
              const copy = EMPTY_COPY[emptyReason] || { heading: 'No matches found', body: 'We couldn’t put together recommendations this time — please try again.' };
              return (
              <div style={{ textAlign:"center", padding:"32px 16px", background:"#fff", borderRadius:16, border:`1px solid ${C.divider}` }}>
                <div style={{ fontSize:20, fontWeight:800, color:C.charcoal, fontFamily:F.display, marginBottom:8 }}>{copy.heading}</div>
                <div style={{ fontSize:14, color:C.charcoalSoft, fontFamily:F.body, lineHeight:1.6, marginBottom:20 }}>{copy.body}</div>
                <button onClick={() => {
  maybeShowSavedToast();
  if (context === 'home') {
    // Home-multi never visits 'meal' on the way in, so we jump to it to
    // re-pick — but WITHOUT pushing 'results' onto screenHistory (that caused
    // the results↔meal back-oscillation). Plain screen change, no push.
    setError(null); setScreen('meal'); scrollToTop();
  } else {
    navigateBack();
  }
}} style={{ width:"100%", padding:"12px", borderRadius:12, background:"transparent", border:`1px solid ${C.divider}`, color:C.charcoalSoft, cursor:"pointer", fontFamily:F.body, fontSize:14 }}>{emptyReason === 'PARSE_FAIL' ? '← Try again' : emptyReason === 'CATALOG_MISS' ? '← Add manually' : '← Rescan or add manually'}</button>
              </div>
              );
            })() : [0,1,2].map(i=>(
              pairings[i]
                ? <div key={i} style={{ animation: i > 0 ? "tpLoaderFadeIn 0.4s ease" : "none" }}>
                    <WineCard wine={pairings[i]} index={i} isExpanded={expandedCard===i} onToggle={()=>setExpandedCard(expandedCard===i?-1:i)} onRate={s=>handleRate(i,s)} rating={ratings[i]} tried={triedIt[i]} onTried={v=>handleTried(i,v)} tags={reviewTags[i]} onTag={tag=>handleTag(i,tag)} onSave={()=>handleSave(i)} saved={savedWines[i]} context={homeFlowPath==="few" ? "home" : context} onImplicitSignal={(signalType)=>sendImplicitSignal(pairings[i],signalType)} onAddManually={()=>{ if (context==='store') { navigateTo('storescan'); } else if (homeFlowPath==='few') { navigateTo('homefew'); } else if (context==='restaurant') { navigateTo('winelist'); } else { navigateBack(); } }} />
                  </div>
                : context === 'online' ? <WineCardSkeleton key={i} index={i} /> : null
            ))}

            <div style={{ textAlign:"center", marginTop:16, marginBottom:12 }}>
              <a href="https://tally.so/r/1AbJ11" target="_blank" rel="noopener noreferrer" style={{ display:"inline-block", fontSize:14, color:C.burgundy, fontFamily:F.body, fontWeight:500, padding:"10px 16px", border:`1px solid ${C.burgundy}`, borderRadius:20, textDecoration:"none", cursor:"pointer" }}>
                💬 How did we do? Share feedback
              </a>
            </div>
            <div style={{ display:"flex", gap:8 }}>
              <button onClick={() => {
  maybeShowSavedToast();
  if (context === 'home') {
    // Home-multi never visits 'meal' on the way in, so we jump to it to
    // re-pick — but WITHOUT pushing 'results' onto screenHistory (that caused
    // the results↔meal back-oscillation). Plain screen change, no push.
    setError(null); setScreen('meal'); scrollToTop();
  } else {
    navigateBack();
  }
}} style={{ width:"100%", padding:"12px", borderRadius:12, background:"transparent", border:`1px solid ${C.divider}`, color:C.charcoalSoft, cursor:"pointer", fontFamily:F.body, fontSize:14 }}>← Refine picks</button>
            </div>
          </div>
        )}

      </div>

      {palateToast && (
        <div
          onClick={() => { if (palateToastTimerRef.current) clearTimeout(palateToastTimerRef.current); setPalateToast(null); }}
          style={{
            position: 'fixed',
            bottom: 90,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9500,
            background: '#1A0F0E',
            color: '#fff',
            borderRadius: 28,
            padding: '13px 22px',
            maxWidth: 340,
            width: 'calc(100% - 48px)',
            textAlign: 'center',
            fontSize: 14,
            fontFamily: F.body,
            lineHeight: 1.45,
            cursor: 'pointer',
            boxShadow: '0 4px 28px rgba(0,0,0,0.35)',
          }}
        >
          {palateToast}
        </div>
      )}
      {navVisible && (
        <BottomTabBar
          activeTab={activeTab}
          onTabChange={handleTabChange}
          cellarCount={userProfile.likedWines?.length || 0}
          onScan={() => { maybeShowSavedToast(); setScanModalMode(true); navigateTo('winescan'); }}
          hasProfileBadge={shouldShowProfileBadge({
            pendingArchetypeReveal,
            flagOn: step7HasServerFields,
            profileChangeReason,
            profileChangedAt,
            profileLastSeenChangeAt,
            palateUpdateSeen,
            anyDimensionDrifted: ['body','tannin','acidity','oak','sweetness','complexity'].some(k => hasDimensionDrifted(k)),
          })}
          cellarBadge={(userProfile.likedWines || []).some(w => !(userProfile.cellarRatings || {})[w])}
        />
      )}
      {wineModal && wineModal.type === 'cellar' && (
        <CellarNoteModal
          wineName={wineModal.wine}
          onClose={() => setWineModal(null)}
          rating={(userProfile.cellarRatings || {})[wineModal.wine] || 0}
          onRate={(stars) => {
            // claude/rating-wiring-fixes: this modal's star used to be
            // LOCAL ONLY — persist() wrote cellarRatings to localStorage
            // but nothing ever reached the server, so a rating made here
            // never became a wine_ratings row (no palate nudge, no
            // canonical-store alias/wine_id link), and the next
            // /api/user-cellar hydration (rehydrateSession, on every
            // fresh login/reload) overwrote cellarRatings wholesale with
            // server truth, silently discarding it. Now mirrors site F's
            // (My Cellar inline star, ~L7036) exact call shape, so this
            // surface produces the same server state for the same wine.
            persist({ ...userProfile, cellarRatings: { ...(userProfile.cellarRatings || {}), [wineModal.wine]: stars } });
            if (session) {
              fetch('/api/rate-wine', { method:'POST', headers:{'Content-Type':'application/json','Authorization':`Bearer ${session.access_token}`}, body:JSON.stringify({ producer:'', wine_name: wineModal.wine, rating: stars }) })
                .then(r => { if (r.ok) refreshRatingCount(); return r.json(); })
                .then(rateData => {
                  if (rateData && rateData.sliders) {
                    setSliders(prev => ({ ...prev, ...rateData.sliders }));
                    setPalateUpdateSeen(false);
                    try { localStorage.removeItem('truepour_palate_update_seen'); } catch {}
                  }
                  if (rateData && rateData.extSliders) { setExtendedSliders(prev => ({ ...prev, ...rateData.extSliders })); }
                  // Step 7 fix (this round, item 1): this surface showed NO
                  // toast at all in 5b2fe67/9370ddb — flag off must stay that
                  // way exactly, since the client ships before the flag
                  // flips. Flag on: shows the pending/terminal copy or
                  // respects ratingMovedProfile, same as every other surface.
                  if (step7HasServerFields) {
                    maybeShowRatingToast(
                      wineModal.wine, stars,
                      rateData && rateData.sliders ? { ...sliders, ...rateData.sliders } : sliders,
                      rateData && rateData.extSliders ? { ...extendedSliders, ...rateData.extSliders } : extendedSliders,
                      rateData
                    );
                  }
                })
                .catch(()=>{});
            }
          }}
          session={session}
          onAuthExpired={handleAuthExpired}
        />
      )}
      {wineModal && wineModal.type === 'activity' && (() => {
        const mw = wineModal.wine;
        const mLabel = `${mw.producer} ${mw.wine}`;
        const orderUrl = `https://click.linksynergy.com/deeplink?id=WUGIyhMYkU4&mid=2025&murl=${encodeURIComponent('https://www.wine.com/search/' + (mw.search_term || mLabel).trim() + '/0')}`;
        // getDisplayTier reads mw.matchStage if the persisted history record
        // carries it (design 100734b: the flag round-trips through
        // recommendation_history's wines JSONB column unmodified, since
        // nothing on the save path projects/strips fields — see
        // persistGeneration/the POST body above). A historical record saved
        // before this build has no matchStage field, so this resolves
        // through the unmodified getConfidenceTier exactly as before.
        const tier = mw && mw.confidence != null ? getDisplayTier(mw) : null;
        const tags = mw ? (mw.styleTags || mw.style_tags || []) : [];
        return (
          <div onClick={() => setWineModal(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9000, display: 'flex', alignItems: 'flex-end' }}>
            <style>{`@keyframes tpSlideUp{from{transform:translateY(100%)}to{transform:translateY(0)}}`}</style>
            <div onClick={(e) => e.stopPropagation()} style={{ background: '#FFFAF8', borderRadius: '24px 24px 0 0', width: '100%', maxHeight: '85vh', overflowY: 'auto', padding: '20px 20px 40px', boxSizing: 'border-box', animation: 'tpSlideUp 0.25s ease' }}>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                <button onClick={() => setWineModal(null)} style={{ background: 'none', border: 'none', fontSize: 24, color: C.charcoalSoft, cursor: 'pointer', padding: '0 0 0 20px', lineHeight: 1 }}>×</button>
              </div>
              {tier && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: tier.color, fontFamily: F.body, letterSpacing: 1.2, textTransform: 'uppercase' }}>{tier.symbol} {tier.label}</span>
                  {tier.stage === 'number' || tier.stage === 'early_number' ? (
                    <span style={{ fontSize: 14, color: C.burgundy, fontFamily: F.body, fontWeight: 700 }}>{mw.confidence}% match{tier.stage === 'early_number' ? ` (${tier.qualifier})` : ''}</span>
                  ) : null}
                </div>
              )}
              {mw ? (
                <>
                  <div style={{ fontSize: 22, fontWeight: 800, color: C.charcoal, fontFamily: F.display, lineHeight: 1.2, marginBottom: 2 }}>{mw.producer}</div>
                  <div style={{ fontSize: 15, color: C.charcoalMid, fontFamily: F.body, marginBottom: mw.varietal ? 2 : 10 }}>{mw.wine}{mw.vintage && mw.vintage !== 'NV' ? ` ${mw.vintage}` : ''}</div>
                  {mw.varietal && mw.varietal.toLowerCase() !== mw.wine.toLowerCase() && <div style={{ fontSize: 13, color: C.charcoalSoft, fontFamily: F.body, marginBottom: 10 }}>{mw.varietal}</div>}
                  {(() => {
                    const hasWsPrice = mw.ws_price_average;
                    const hasRange = mw.price_low && mw.price_high;
                    const hasSingle = mw.price;
                    const regionStr = [mw.country, mw.region].filter(Boolean).join(' · ');
                    if (hasWsPrice) {
                      return (
                        <>
                          <div style={{ fontSize:13, color:C.charcoalSoft, letterSpacing:1.2, textTransform:'uppercase', fontFamily:F.body, marginBottom:14 }}>
                            {regionStr && <span>{regionStr} · </span>}
                            <span style={{ color:C.charcoal, fontWeight:700 }}>Avg. ${Math.round(mw.ws_price_average)}</span>
                            {mw.ws_score >= 90 && <span style={{ color:C.burgundy, fontWeight:700 }}> · Critically Acclaimed</span>}
                          </div>
                        </>
                      );
                    }
                    if (hasRange) {
                      return <div style={{ fontSize:13, color:C.charcoalSoft, letterSpacing:1.2, textTransform:'uppercase', fontFamily:F.body, marginBottom:14 }}>{[regionStr, `$${mw.price_low}–$${mw.price_high}`].filter(Boolean).join(' · ')}</div>;
                    }
                    if (hasSingle) {
                      return <div style={{ fontSize:13, color:C.charcoalSoft, letterSpacing:1.2, textTransform:'uppercase', fontFamily:F.body, marginBottom:14 }}>{[regionStr, `$${mw.price}`].filter(Boolean).join(' · ')}</div>;
                    }
                    if (regionStr) {
                      return <div style={{ fontSize:13, color:C.charcoalSoft, letterSpacing:1.2, textTransform:'uppercase', fontFamily:F.body, marginBottom:14 }}>{regionStr}</div>;
                    }
                    return null;
                  })()}
                </>
              ) : (
                <div style={{ fontSize: 20, fontWeight: 800, color: C.charcoal, fontFamily: F.display, lineHeight: 1.2, marginBottom: 16 }}>{mLabel}</div>
              )}
              {tags.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
                  {tags.map((t, ti) => (
                    <span key={ti} style={{ fontSize: 12, color: C.burgundy, background: '#fff', padding: '3px 10px', borderRadius: 20, border: `1px solid ${C.burgundy}`, fontFamily: F.body }}>{t}</span>
                  ))}
                </div>
              )}
              {mw && mw.description && (
                <div style={{ fontSize: 14, color: C.charcoalMid, lineHeight: 1.65, fontFamily: F.body, marginBottom: 14 }}>{mw.description}</div>
              )}
              {mw && (mw.pairingReason || mw.pairing_reason) && (
                <div style={{ background: '#F7F0EC', borderRadius: 12, padding: '12px 14px', marginBottom: 16 }}>
                  <div style={{ fontSize: 12, color: C.burgundy, letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 3, fontFamily: F.body, fontWeight: 600 }}>Why it works</div>
                  <div style={{ fontSize: 14, color: C.charcoalMid, lineHeight: 1.6, fontFamily: F.body }}>{mw.pairingReason || mw.pairing_reason}</div>
                </div>
              )}
              {mw && (mw.tags || []).length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 14 }}>
                  {mw.tags.map((t, ti) => (
                    <span key={ti} style={{ fontSize: 11, color: C.burgundy, background: '#fff', padding: '1px 7px', borderRadius: 20, border: `1px solid ${C.burgundy}`, fontFamily: F.body }}>{t}</span>
                  ))}
                </div>
              )}
              <div onClick={(e) => e.stopPropagation()} style={{ marginBottom: 16 }}>
                <StarRating value={(userProfile.cellarRatings || {})[mLabel] || mw.rating || 0} onChange={(stars) => {
                  // Step 7 fix (this round, item 1): flag off must be TODAY's
                  // (5b2fe67/9370ddb) behavior exactly.
                  if (!step7HasServerFields) {
                    maybeShowRatingToast(mLabel, stars); // Layer A (no server nudge here → new-wine-or-nothing)
                    const isFirstRating = !(userProfile.cellarRatings || {})[mLabel];
                    const newCellarRatings = { ...(userProfile.cellarRatings || {}), [mLabel]: stars };
                    if (isFirstRating && stars >= 4 && !(userProfile.likedWines || []).includes(mLabel)) {
                      persist({ ...userProfile, likedWines: [...(userProfile.likedWines || []), mLabel], cellarRatings: newCellarRatings });
                    } else {
                      persist({ ...userProfile, cellarRatings: newCellarRatings });
                    }
                    if (session) {
                      fetch('/api/rate-wine', { method:'POST', headers:{'Content-Type':'application/json','Authorization':`Bearer ${session.access_token}`}, body:JSON.stringify({ producer:'', wine_name: mLabel, rating: stars }) }).then(r => { if (r.ok) refreshRatingCount(); }).catch(()=>{});
                    }
                    return;
                  }
                  const isFirstRating = !(userProfile.cellarRatings || {})[mLabel];
                  const newCellarRatings = { ...(userProfile.cellarRatings || {}), [mLabel]: stars };
                  if (isFirstRating && stars >= 4 && !(userProfile.likedWines || []).includes(mLabel)) {
                    persist({ ...userProfile, likedWines: [...(userProfile.likedWines || []), mLabel], cellarRatings: newCellarRatings });
                  } else {
                    persist({ ...userProfile, cellarRatings: newCellarRatings });
                  }
                  if (session) {
                    // Step 7: still fire-and-forget — UI not blocked — but now
                    // reads the response so the toast can show pending/terminal
                    // copy or respect ratingMovedProfile when the flag is on.
                    fetch('/api/rate-wine', { method:'POST', headers:{'Content-Type':'application/json','Authorization':`Bearer ${session.access_token}`}, body:JSON.stringify({ producer:'', wine_name: mLabel, rating: stars }) })
                      .then(r => { if (r.ok) refreshRatingCount(); return r.json(); })
                      .then(rateData => {
                        if (rateData.sliders) setSliders(prev => ({ ...prev, ...rateData.sliders }));
                        if (rateData.extSliders) setExtendedSliders(prev => ({ ...prev, ...rateData.extSliders }));
                        maybeShowRatingToast(mLabel, stars, rateData.sliders ? { ...sliders, ...rateData.sliders } : sliders, rateData.extSliders ? { ...extendedSliders, ...rateData.extSliders } : extendedSliders, rateData);
                      })
                      .catch(() => { maybeShowRatingToast(mLabel, stars); });
                  } else {
                    maybeShowRatingToast(mLabel, stars); // no session — never reaches the server, same as today
                  }
                }} hintVisible={showRatingHint} onHintDismiss={() => setShowRatingHint(false)} />
              </div>
              <a href={orderUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'block', width: '100%', padding: '13px', borderRadius: 12, textAlign: 'center', textDecoration: 'none', background: C.burgundy, color: '#fff', fontSize: 14, fontWeight: 700, fontFamily: F.display, boxSizing: 'border-box' }}>
                Order this bottle →
              </a>
            </div>
          </div>
        );
      })()}
      {showToast && (
        <div style={{
          position: "fixed",
          bottom: 80,
          left: "50%",
          transform: "translateX(-50%)",
          background: C.burgundy,
          color: "#fff",
          padding: "12px 24px",
          borderRadius: 24,
          fontSize: 15,
          fontFamily: "var(--fb)",
          fontWeight: 600,
          zIndex: 9998,
          whiteSpace: "nowrap",
          boxShadow: "0 4px 16px rgba(160,36,58,0.3)"
        }}>
          {showToast}
        </div>
      )}
    </div>
  );
}
